// DataFittingPage.jsx

'use client';

import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import '../styles/DataFittingPage.css';
import '../styles/CompareFit.css';
import { BlockMath } from 'react-katex';
// import 'katex/dist/katex.min.css';
// NOTE: requires `npm install xlsx` (SheetJS). Used only to convert an
// uploaded Excel workbook (.xlsx/.xls) — including a "Download as .xlsx"
// export from Google Sheets — into CSV text in the browser before handing
// it off to exactly the same CSV pipeline a native .csv upload already
// goes through. Nothing is uploaded anywhere to do this conversion.
import * as XLSX from 'xlsx';
// Keeps an in-flight (or just-finished) fit alive across route navigation —
// see fittingSessionStore.js for why local component state can't do this.
import { useFittingSession } from './useFittingSession';
import * as FittingSession from './fittingSessionStore';
// Mirrors Data Input / Model Input field values across route navigation —
// see formSessionStore.js. Complements fittingSessionStore.js, which only
// covers the run's own lifecycle (loading/result/Stop), not the fields.
import { usePersistedState, clearFormState } from './formSessionStore';
// Compare Fits (colleague, 19-Aug-2026 merge) — reads/writes fitRecordStore.js
// via useFitRecords(). NOTE: these two companion files were not included in
// what was shared for this merge; see CompareFitsPanel below.
import { useFitRecords } from './useFitRecords';
import * as FitRecords from './fitRecordStore';
// Saved inputs + refresh-proof session (client items #23 and "Persistence").
// See useInputSession.js / inputSnapshot.js for the why.
import { useInputSession } from './useInputSession';
import { serializeInputs, deserializeInputs, onlyUserComponents, mergeUserComponents } from './inputSnapshot';
// Compare Fit — our own embedded tab (not a standalone route). Data shape
// (metrics.predicted_timeseries) comes from the already-deployed backend —
// see normalizeSeries below — but the UI/architecture here is ours:
// recharts-based overlay + a descriptive-only summary table, rendered as a
// tab inside Data Fitting rather than a separate page.
import {
  ResponsiveContainer, ComposedChart, Line, Scatter, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';

// API endpoint resolution without a separate apiConfig file.
// Local CRA development uses the existing Flask backend on :5000.
// Production can override with REACT_APP_API_ORIGIN; otherwise same-origin /api is used.
const API_ORIGIN = (process.env.REACT_APP_API_ORIGIN ||
  ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'https://www.kinetiqmodel.com/'
    : window.location.origin)).replace(/\/$/, '');
const API_BASE = `${API_ORIGIN}/api`;

// ── ACTION ITEM #8 (10-Sep-2026 call) ───────────────────────────────────
// Madhura: the run-specific "Optimizer Suggestion" card (PanelOptimize)
// is too system-dependent to generalize reliably right now — hide it and
// revisit once more case studies establish clearer patterns. Single
// toggle so it can be turned back on later without re-writing the card;
// see its usage in PanelOptimize below.
const SHOW_OPTIMIZER_SUGGESTION = false;

// ─── Compare Fits helpers ───────────────────────────────────────────────
// BUG FIX (POST /api/fit-records 500ing with "'list' object has no
// attribute 'values'"): fit_records_api.py's fitted_parameters_json column
// — and _n_params/_aic/_bic plus the /fit-records/compare and
// /fit-records/export endpoints that read it back — all expect a dict
// keyed by reaction name, per the schema comment:
//   {reaction_1: {Af, Ea_f, Ab, Ea_b, Keq, exponents...}, ...}
// but result.fit_results (see openmkm_api.py: 'fit_results': result['fitted_params'])
// is actually a LIST of per-reaction entries — this file already treats it
// as one everywhere else (result.fit_results?.[0]). Convert here, once, at
// the point it's handed to the save API, instead of pushing shape-handling
// into every backend consumer.
function toFittedParametersDict(fitResults) {
  if (!fitResults) return {};
  if (!Array.isArray(fitResults)) return fitResults; // already dict-shaped (e.g. aspen_ready fallback)
  return fitResults.reduce((acc, entry, i) => {
    const key = `reaction_${entry?.reaction_index ?? i + 1}`;
    acc[key] = entry;
    return acc;
  }, {});
}

// ─── Design token bridge — reads values from CSS custom properties ────────────
// All colours and font stacks are defined in DataFittingPage.css.
// This object simply re-exports the CSS variable strings so that the existing
// inline-style references (C.accent, C.text, …) continue to work unchanged.
const cssVar = (name) => `var(${name})`;

const C = {
  bg:           cssVar('--color-bg'),
  surface:      cssVar('--color-surface'),
  panel:        cssVar('--color-panel'),
  card:         cssVar('--color-card'),
  shell:        cssVar('--color-shell'),
  nav:          cssVar('--color-nav'),
  codeBg:       cssVar('--color-code-bg'),

  accent:       cssVar('--color-accent'),
  accentDim:    cssVar('--color-accent-dim'),
  accentGlow:   cssVar('--color-accent-glow'),
  accentHover:  cssVar('--color-accent-hover'),

  success:      cssVar('--color-success'),
  successDim:   cssVar('--color-success-dim'),
  warning:      cssVar('--color-warning'),
  warningDim:   cssVar('--color-warning-dim'),
  error:        cssVar('--color-error'),
  errorDim:     cssVar('--color-error-dim'),
  cyan:         cssVar('--color-cyan'),
  cyanDim:      cssVar('--color-cyan-dim'),
  toggle:       cssVar('--color-toggle'),
  toggleDim:    cssVar('--color-toggle-dim'),
  toggleGlow:   cssVar('--color-toggle-glow'),

  border:       cssVar('--color-border'),
  borderLight:  cssVar('--color-border-light'),
  borderMid:    cssVar('--color-border-mid'),

  text:         cssVar('--color-text'),
  textMuted:    cssVar('--color-text-muted'),
  textMid:      cssVar('--color-text-mid'),
  textDim:      cssVar('--color-text-dim'),

  tabActive:    cssVar('--color-tab-active'),
  tabHover:     cssVar('--color-tab-hover'),
  navActive:    cssVar('--color-nav-active'),

  inputBg:      cssVar('--color-input-bg'),
  inputBorder:  cssVar('--color-input-border'),

  tag:          cssVar('--color-tag'),
  purple:       cssVar('--color-purple'),
};

const font = {
  mono: cssVar('--font-ui'),   // Inter used throughout (matches TutorialsDashboard)
  ui:   cssVar('--font-ui'),
};

// ═══════════════════════════════════════════════════════════════════════════
// FILE FORMAT SUPPORT — Excel / Google Sheets uploads (auto-convert to CSV)
// Client feedback: "evaluate accepting Excel/Google Sheets uploads directly."
// Rather than teach the backend a second input format, we convert client-side:
// an uploaded .xlsx/.xls (this also covers a Google Sheets "File > Download >
// Microsoft Excel (.xlsx)" export) is parsed with SheetJS and its first sheet
// is serialized to CSV text, then wrapped back into a File so every existing
// CSV code path (auto-detect, preview, upload, manual-entry merge, etc.)
// continues to work completely unchanged downstream.
// ═══════════════════════════════════════════════════════════════════════════
const SPREADSHEET_EXTENSIONS = /\.(xlsx|xls)$/i;

const isSpreadsheetFile = (f) => !!f && SPREADSHEET_EXTENSIONS.test(f.name || '');

// Converts an .xlsx/.xls File into a new File containing CSV text (first
// sheet only). Non-spreadsheet files are returned unchanged. Throws with a
// friendly message on a corrupt/unreadable workbook.
const convertSpreadsheetToCsvFile = async (file) => {
  if (!isSpreadsheetFile(file)) return file;
  let workbook;
  try {
    const buf = await file.arrayBuffer();
    workbook = XLSX.read(buf, { type: 'array' });
  } catch (err) {
    throw new Error(`Could not read "${file.name}" as an Excel workbook: ${err.message}`);
  }
  const sheetName = workbook.SheetNames?.[0];
  const sheet = sheetName ? workbook.Sheets[sheetName] : null;
  if (!sheet) throw new Error(`"${file.name}" has no readable sheets.`);
  const csvText = XLSX.utils.sheet_to_csv(sheet, { blankrows: false });
  if (!csvText.trim()) throw new Error(`Sheet "${sheetName}" in "${file.name}" appears to be empty.`);
  const csvName = file.name.replace(SPREADSHEET_EXTENSIONS, '.csv');
  return new File([new Blob([csvText], { type: 'text/csv' })], csvName, { type: 'text/csv' });
};

// Accept string shared by every CSV file-picker in this page.
const CSV_UPLOAD_ACCEPT = '.csv,.txt,.xlsx,.xls';

// ═══════════════════════════════════════════════════════════════════════════
// RESIDUAL TABLE — per-row absolute error display with checkbox exclusion,
// merged from Shankar's branch (real backend wiring via excludedRows/
// excluded_rows) with Hanmanthu's sigma-based auto-exclude outlier detection
// (from ResidualsPanel, Madhura UI-feedback branch) folded in as an extra
// toolbar action. Renders below the AA output panel after a fitting run.
// ═══════════════════════════════════════════════════════════════════════════
//
// Props:
//   evpRows       array<array>   from result.metrics.exp_vs_pred_rows (row 0 = header)
//   excludedRows  Set<number>    1-indexed row numbers currently excluded
//   onToggleRow   (rowNum) => void
//   onResetExcluded () => void
// ═══════════════════════════════════════════════════════════════════════════
// AA product species shown in the residual table (ordered by importance).
// Hoisted to module scope (was a per-render local const) so it's a stable
// reference for the useMemo/useCallback hooks below — fixes the
// react-hooks/exhaustive-deps warnings on PROD.
const RESIDUAL_PROD_SPECIES = ['AA', 'ET', 'CO2'];

const ResidualTable = ({ evpRows, excludedRows, onToggleRow, onResetExcluded, weightsUsed }) => {
  const [sortKey, setSortKey]     = useState('weighted');   // 'row'|'T'|'tau'|species|'weighted'
  const [sortAsc, setSortAsc]     = useState(false);        // false = highest error first
  const [threshold, setThreshold] = useState('');

  const PROD = RESIDUAL_PROD_SPECIES;
  // Jun-26 2026 FIX: previously hardcoded {AA:25, ET:2, CO2:15} regardless of
  // what the user set in Lever 3 — table always showed the same numbers even
  // after changing weights. Now reads the ACTUAL weights used in this run
  // (returned by the backend as metrics.weights_used), falling back to the
  // documented defaults only if the backend didn't supply any.
  // Wrapped in useMemo (keyed only on weightsUsed) so it's a stable reference
  // across renders — fixes the react-hooks/exhaustive-deps warning on W.
  const W = React.useMemo(() => ({
    AA:  weightsUsed?.AA  ?? 15,
    ET:  weightsUsed?.ET  ?? 8,
    CO2: weightsUsed?.CO2 ?? 10,
  }), [weightsUsed]);

  // Parse the raw 2D array from the API ────────────────────────────────────
  const { dataRows } = React.useMemo(() => {
    if (!evpRows || evpRows.length < 2) return { dataRows: [] };
    const hdr = evpRows[0];
    const idx  = (col) => hdr.indexOf(col);
    const iRow = idx('Row'); const iT = idx('T (°C)'); const iTau = idx('tau (s)');
    const errI = {};
    PROD.forEach(sp => { errI[sp] = idx(`${sp}_abs_err`); });
    const parsed = evpRows.slice(1).map(r => {
      const errs = {};
      PROD.forEach(sp => { errs[sp] = errI[sp] >= 0 ? (parseFloat(r[errI[sp]]) || 0) : 0; });
      const weighted = PROD.reduce((s, sp) => s + (W[sp] || 1) * errs[sp], 0);
      return { rowNum: parseInt(r[iRow]) || 0, T: parseFloat(r[iT]) || 0,
               tau: parseFloat(r[iTau]) || 0, errs, weighted };
    });
    return { dataRows: parsed };
  }, [evpRows, PROD, W]);

  // Percentile thresholds for colour coding ────────────────────────────────
  const pctiles = React.useMemo(() => {
    const pc = (vals) => {
      const s = [...vals].sort((a, b) => a - b);
      return { p25: s[Math.floor(s.length * 0.25)] ?? 0,
               p75: s[Math.floor(s.length * 0.75)] ?? 0 };
    };
    const out = { weighted: pc(dataRows.map(r => r.weighted)) };
    PROD.forEach(sp => { out[sp] = pc(dataRows.map(r => r.errs[sp])); });
    return out;
  }, [dataRows, PROD]);

  const errBg = (v, sp) => {
    const { p25, p75 } = pctiles[sp] || {};
    if (v <= p25) return 'rgba(34,197,94,0.18)';  // green-tinted
    if (v <= p75) return 'rgba(234,179,8,0.18)';  // yellow-tinted
    return 'rgba(239,68,68,0.22)';                // red-tinted
  };

  const fmtV = (v) => {
    if (v == null || isNaN(v)) return '—';
    if (v === 0) return '0';
    return v < 0.00001 ? v.toExponential(2) : v.toFixed(5);
  };

  // Sort ───────────────────────────────────────────────────────────────────
  const sorted = React.useMemo(() => {
    const d = sortAsc ? 1 : -1;
    return [...dataRows].sort((a, b) => {
      if (sortKey === 'row')      return d * (a.rowNum - b.rowNum);
      if (sortKey === 'T')        return d * (a.T - b.T);
      if (sortKey === 'tau')      return d * (a.tau - b.tau);
      if (sortKey === 'weighted') return d * (a.weighted - b.weighted);
      return d * ((a.errs[sortKey] || 0) - (b.errs[sortKey] || 0));
    });
  }, [dataRows, sortKey, sortAsc]);

  const handleSortClick = (key) => {
    if (sortKey === key) setSortAsc(p => !p);
    else { setSortKey(key); setSortAsc(false); }
  };

  // Auto-select rows above threshold ───────────────────────────────────────
  const applyThreshold = () => {
    const t = parseFloat(threshold);
    if (isNaN(t)) return;
    dataRows.forEach(r => {
      const above = r.weighted > t;
      if (above  && !excludedRows.has(r.rowNum)) onToggleRow(r.rowNum);
      if (!above &&  excludedRows.has(r.rowNum)) onToggleRow(r.rowNum);
    });
  };

  const allExcluded = dataRows.length > 0 && dataRows.every(r => excludedRows.has(r.rowNum));
  const toggleAll   = () => {
    if (allExcluded) dataRows.forEach(r => { if (excludedRows.has(r.rowNum))  onToggleRow(r.rowNum); });
    else             dataRows.forEach(r => { if (!excludedRows.has(r.rowNum)) onToggleRow(r.rowNum); });
  };

  const nExcl = excludedRows.size;
  if (dataRows.length === 0) return null;

  const thSty = (key) => ({
    padding: '5px 8px', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
    background: sortKey === key ? C.panel : 'transparent',
    color: sortKey === key ? C.accent : C.textMuted,
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.07em', fontFamily: font.ui,
    borderBottom: `1px solid ${C.border}`,
  });

  return (
    <SectionBox
      title="Per-Row Residuals"
      badge={nExcl > 0 ? `${nExcl} excluded` : null}
      badgeColor={nExcl > 0 ? cssVar('--color-warning-dark') : C.accent}
      action={
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Auto-select threshold */}
          <span style={{ fontSize: 10, color: C.textMuted, fontFamily: font.ui }}>
            Weighted error &gt;
          </span>
          <input
            type="number" step="0.0005" value={threshold}
            onChange={e => setThreshold(e.target.value)}
            placeholder="e.g. 0.01"
            style={{
              width: 80, padding: '2px 5px', fontSize: 10, borderRadius: 3,
              border: `1px solid ${C.inputBorder}`, background: C.inputBg,
              color: C.text, fontFamily: font.ui,
            }}
          />
          <Btn variant="ghost" style={{ fontSize: 10, padding: '2px 8px' }}
            onClick={applyThreshold}>Auto-select</Btn>
          {nExcl > 0 && (
            <Btn variant="ghost" style={{ fontSize: 10, padding: '2px 8px', color: C.error }}
              onClick={onResetExcluded}>Clear</Btn>
          )}
        </div>
      }
      style={{ marginTop: 10 }}
    >
      {/* Info strip */}
      <div style={{ fontSize: 10, color: C.accent, fontFamily: font.ui,
        background: `${C.accent}11`, padding: '4px 8px', borderRadius: 3, marginBottom: 6 }}>
        ✓ Check a row's box to exclude it from the next run, or click anywhere on the row.&nbsp;
        Weighted error (this run's weights) = {PROD.map(sp => `${W[sp]}×|Δ${sp}|`).join(' + ')}.&nbsp;
        {nExcl > 0 && (
          <strong style={{ color: cssVar('--color-warning-dark'), marginLeft: 8 }}>
            ⚠ {nExcl} row{nExcl > 1 ? 's' : ''} excluded — {dataRows.length - nExcl}/{dataRows.length} will be used in next run.
          </strong>
        )}
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: 4 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontFamily: font.ui }}>
          <thead>
            <tr style={{ background: C.panel }}>
              {/* Checkbox-all column */}
              <th style={{ ...thSty('_chk'), padding: '5px 8px', cursor: 'pointer' }} onClick={toggleAll}>
                <input type="checkbox" checked={allExcluded} onChange={toggleAll}
                  style={{ cursor: 'pointer', accentColor: C.accent }} />
              </th>
              {[['row','Row'],['T','T (°C)'],['tau','τ (s)']].map(([k,l]) => (
                <th key={k} style={thSty(k)} onClick={() => handleSortClick(k)}>
                  {l}{sortKey === k ? (sortAsc ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
              {PROD.map(sp => (
                <th key={sp} style={thSty(sp)} onClick={() => handleSortClick(sp)}>
                  |Δ{sp}|{sortKey === sp ? (sortAsc ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
              <th style={thSty('weighted')} onClick={() => handleSortClick('weighted')}>
                Weighted Error{sortKey === 'weighted' ? (sortAsc ? ' ▲' : ' ▼') : ''}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => {
              const excl = excludedRows.has(r.rowNum);
              return (
                <tr
                  key={r.rowNum}
                  onClick={() => onToggleRow(r.rowNum)}
                  style={{
                    cursor: 'pointer',
                    opacity: excl ? 0.38 : 1,
                    background: excl ? C.panel : (i % 2 === 0 ? C.card : C.surface),
                    textDecoration: excl ? 'line-through' : 'none',
                    transition: 'opacity 0.12s',
                  }}
                >
                  <td style={{ padding: '4px 8px', borderBottom: `1px solid ${C.border}22`, textAlign: 'center' }}>
                    <input type="checkbox" checked={excl} onChange={() => onToggleRow(r.rowNum)}
                      onClick={e => e.stopPropagation()}
                      style={{ cursor: 'pointer', accentColor: C.accent }} />
                  </td>
                  <td style={{ padding: '4px 8px', borderBottom: `1px solid ${C.border}22`,
                    fontWeight: 700, color: C.text }}>{r.rowNum}</td>
                  <td style={{ padding: '4px 8px', borderBottom: `1px solid ${C.border}22`,
                    color: C.textMid }}>{r.T}</td>
                  <td style={{ padding: '4px 8px', borderBottom: `1px solid ${C.border}22`,
                    color: C.textMid }}>{r.tau.toFixed(4)}</td>
                  {PROD.map(sp => (
                    <td key={sp} style={{
                      padding: '4px 8px', borderBottom: `1px solid ${C.border}22`,
                      fontFamily: font.mono, textAlign: 'right',
                      background: excl ? 'transparent' : errBg(r.errs[sp], sp),
                      color: C.text,
                    }}>{fmtV(r.errs[sp])}</td>
                  ))}
                  <td style={{
                    padding: '4px 8px', borderBottom: `1px solid ${C.border}22`,
                    fontFamily: font.mono, fontWeight: 700, textAlign: 'right',
                    background: excl ? 'transparent' : errBg(r.weighted, 'weighted'),
                    color: C.text,
                  }}>{fmtV(r.weighted)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 10, color: C.textDim, fontFamily: font.ui, marginTop: 4, textAlign: 'right' }}>
        {dataRows.length} rows total · reload page to reset
      </div>
    </SectionBox>
  );
};



// ═══════════════════════════════════════════════════════════════════════════
// REQUIREMENT 2 — DATABASE-BACKED SPECIES CATALOG
// ═══════════════════════════════════════════════════════════════════════════
// Species identity is application data, not frontend source code. The browser
// loads the catalog from MySQL through Flask and keeps only the current page
// snapshot plus any user-defined custom components in registeredComponents.
const toSubscriptFormula = s => String(s).replace(/([A-Za-z])(\d+)/g, (_, l, d) =>
  l + d.split('').map(c => '₀₁₂₃₄₅₆₇₈₉'[c]).join(''));

// Normalize all database "Other Names" representations into one searchable
// aliases array. Different API/DB revisions have used aliases, aliases_json,
// other_names and other_names_json; the UI searches them uniformly.
const normalizeAliasList = (...values) => {
  const out = [];
  const pushValue = (value) => {
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach(pushValue);
      return;
    }
    if (typeof value === 'object') {
      Object.values(value).forEach(pushValue);
      return;
    }
    const raw = String(value).trim();
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (parsed !== raw) {
        pushValue(parsed);
        return;
      }
    } catch (_) {
      // Plain text / delimited "Other Names" falls through.
    }
    raw.split(/[;,|\n]+/)
      .map(v => v.trim())
      .filter(Boolean)
      .forEach(v => out.push(v));
  };
  values.forEach(pushValue);
  return [...new Set(out)];
};

const normalizeSpeciesCatalog = (rows = []) => rows
  .filter(r => r && (r.species_key || r.id || r.formula))
  .map(r => {
    const speciesKey = String(r.species_key || r.id || r.formula).trim();
    return {
      id: speciesKey,
      species_key: speciesKey,
      name: String(r.species_name || r.name || r.formula || '').trim(),
      formula: toAsciiDigits(String(r.formula || '').trim()),
      MW: r.molecular_weight ?? r.MW ?? 0,
      Tc: r.critical_temperature_k ?? r.Tc ?? null,
      Pc: r.critical_pressure_bar ?? r.Pc ?? null,
      cas: String(r.cas_number || r.cas || '').trim(),
      aliases: normalizeAliasList(r.aliases, r.aliases_json, r.other_names, r.other_names_json),
      source: 'db',
    };
  });

// ═══════════════════════════════════════════════════════════════════════════
// "ADD A NEW COMPONENT" (client item #11, 8-Oct-2026 call)
//   * offered at the bottom of the species search dropdown;
//   * CAS number is mandatory (format + check digit validated);
//   * the main DB catalog is checked for that CAS first, so an existing
//     component is reused instead of duplicated;
//   * new components are stored per LOGIN in their own table
//     (user_species_catalog, backend: user_components_api.py) — never in the
//     main species_catalog.
// ═══════════════════════════════════════════════════════════════════════════
const normalizeCasInput = (raw) =>
  String(raw ?? '').trim().replace(/[\u2010-\u2015\u2212]/g, '-').replace(/\s+/g, '');

// '' when valid, otherwise a short message for the form.
const validateCasClient = (raw) => {
  const cas = normalizeCasInput(raw);
  if (!cas) return 'CAS number is required.';
  if (!/^\d{2,7}-\d{2}-\d$/.test(cas)) return 'CAS number must look like 67-64-1.';
  const digits = cas.replace(/-/g, '');
  const body = digits.slice(0, -1).split('').reverse();
  const sum = body.reduce((acc, d, i) => acc + Number(d) * (i + 1), 0);
  return sum % 10 === Number(digits.slice(-1)) ? '' : "This CAS number's check digit is not valid — please re-check it.";
};

// Server rows -> the same component shape the catalog uses, tagged as user-owned.
const normalizeUserComponents = (rows = []) => {
  const byKey = new Map(rows.map(r => [String(r.species_key || '').trim(), r]));
  return normalizeSpeciesCatalog(rows).map(c => ({
    ...c, source: 'user', server_id: byKey.get(c.species_key)?.id ?? null,
  }));
};

const userComponentsFetch = async (path, { method = 'GET', body } = {}) => {
  const tk = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '';
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { Authorization: tk || '', ...(body ? { 'Content-Type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = {};
  try { data = await res.json(); } catch (_) { /* empty body */ }
  return { ok: res.ok, status: res.status, data };
};

// The dropdown (SpeciesAutocompleteInput) and the Component Registry panel ask
// the page to open the pop-up through this tiny hook, so no prop needs to be
// threaded through every place the autocomplete is used.
let addComponentHandler = null;
const requestAddComponent = (opts) => { if (addComponentHandler) addComponentHandler(opts || {}); };

// Canonical catalog identities for the supported research/reference systems.
// The UI keeps the paper/Madhura notation visible while binding each token to
// the exact species_key from species_catalog.
// Robust stoichiometric term parser.
// Accepts both "1.5 Oxygen" and compact "1.5Oxygen", while preserving
// legitimate chemical names such as "2-Propanol" and "1,2-Propylene carbonate".
const parseStoicTermText = (term) => {
  const raw = String(term ?? '').trim();
  if (!raw) return { coeff: 1, species: '' };

  const spaced = raw.match(/^(\d+(?:\.\d+)?)\s+(.+)$/);
  if (spaced) return { coeff: Number(spaced[1]), species: spaced[2].trim() };

  const compact = raw.match(/^(\d+(?:\.\d+)?)([A-Za-z].*)$/);
  if (compact) return { coeff: Number(compact[1]), species: compact[2].trim() };

  return { coeff: 1, species: raw };
};

const RESEARCH_SYSTEM_SPECIES_KEYS = {
  meoac: {
    hoac: 'ethanoic_acid_c2h4o2',
    meoh: 'methanol_ch4o',
    meoac: 'methyl_ethanoate_c3h6o2',
    h2o:  'water_h2o',
  },
  dme: {
    meoh: 'methanol_ch4o',
    dme:  'dimethyl_ether_c2h6o',
    h2o:  'water_h2o',
  },
  acetic_acid: {
    c2h6: 'ethane_c2h6',
    o2:   'oxygen_o2',
    aa:   'ethanoic_acid_c2h4o2',
    et:   'ethene_c2h4',
    co2:  'carbon_dioxide_co2',
    h2o:  'water_h2o',
    n2:   'nitrogen_n2',
  },
};

const buildExampleSpeciesIdentityMap = (ex) => {
  const aliases = RESEARCH_SYSTEM_SPECIES_KEYS[ex?.system] || {};
  const next = {};
  const sides = [
    ['r1.reactants', ex?.rxn1Reactants],
    ['r1.products',  ex?.rxn1Products],
    ['r2.reactants', ex?.rxn2Reactants],
    ['r2.products',  ex?.rxn2Products],
    ['r3.reactants', ex?.rxn3Reactants],
    ['r3.products',  ex?.rxn3Products],
  ];

  sides.forEach(([scope, side]) => {
    String(side || '').split('+').forEach((term, tokenIdx) => {
      const raw = term.trim();
      if (!raw) return;
      const token = normalizeSpeciesToken(parseStoicTermText(raw).species);
      const speciesKey = aliases[token];
      if (speciesKey) next[speciesIdentityKey(scope, tokenIdx)] = speciesKey;
    });
  });

  return next;
};

const speciesFieldValues = (component) => [
  component?.id,
  component?.name,
  component?.formula,
  component?.cas,
  ...(Array.isArray(component?.aliases) ? component.aliases : []),
].filter(Boolean).map(v => String(v).trim());

const normalizeSpeciesToken = (token) =>
  toAsciiDigits(String(token ?? '').trim()).replace(/[\u200B-\u200D\uFEFF]/g, '').toLowerCase();

const findSpeciesCandidates = (token, components = []) => {
  const needle = normalizeSpeciesToken(token);
  if (!needle) return [];
  return components.filter(c => speciesFieldValues(c).some(v =>
    normalizeSpeciesToken(v) === needle
  ));
};

// Formula is not a unique chemical identity. Never silently choose the first
// row when several catalog species share the same formula.
const findSpeciesComponent = (token, components = []) => {
  const hits = findSpeciesCandidates(token, components);
  return hits.length === 1 ? hits[0] : null;
};

const speciesDisplayName = (k, registeredComponents = []) => {
  const clean = String(k ?? '').trim();
  const hit = findSpeciesComponent(clean, registeredComponents);
  if (hit?.name) return hit.name;
  return toSubscriptFormula(clean);
};

// ── Formula-aware component ordering (Madhura feedback, Sep-2026) ────────
//
// Component lists/search results must NOT be alphabetized by species name.
// Madhura asked for chemistry-aware ordering using the element sequence
// C < H < O < N, with the example: CHO compounds before CHON/CHN compounds,
// and then ordered by the number of C atoms, H atoms, O atoms, and N atoms.
//
// Important: this is an ordering rule only. Formula is NEVER used as the
// chemical identity key; CAS/species_key remains the identity.
const CHON_ELEMENT_ORDER = ['C', 'H', 'O', 'N'];
const CHON_ELEMENT_RANK = { C: 0, H: 1, O: 2, N: 3 };

// Shared helper: derive inert species identity keys/names in a scope-safe way.
// Built-in Acetic Acid keeps N2 inert; custom systems derive inert entries
// from the user's Inert Components field. This helper lives at module scope
// so submit/validation handlers and child panels can use the same rule without
// referencing component-local variables.
const getInertSpeciesKeys = (reactionSystem, inertComponents) => {
  if (reactionSystem === 'acetic_acid') return ['N2'];

  if (Array.isArray(inertComponents)) {
    return inertComponents
      .map(v => String(v ?? '').trim())
      .filter(Boolean);
  }

  return String(inertComponents ?? '')
    .split(/[,+]/)
    .map(v => v.trim())
    .filter(Boolean);
};

// Match the UI's display fallback (`speciesWeights[sp] ?? 1`) at submit time.
// Missing weights for custom species default to 1; inert species stay at 0.
const buildEffectiveSpeciesWeights = (speciesList = [], weights = {}, inertComponents = []) => {
  const inertSet = new Set((Array.isArray(inertComponents) ? inertComponents : [inertComponents])
    .flatMap(v => String(v ?? '').split(/[,+]/))
    .map(v => normalizeSpeciesToken(v))
    .filter(Boolean));
  const out = { ...(weights || {}) };
  speciesList.forEach(sp => {
    const key = String(sp ?? '').trim();
    if (!key) return;
    if (inertSet.has(normalizeSpeciesToken(key))) {
      out[key] = 0;
      return;
    }
    const value = Number(weights?.[key]);
    out[key] = Number.isFinite(value) && value >= 0 ? value : 1;
  });
  return out;
};

// 23-Oct-2026 FIX - Model Input weights default (Madhura, 21-Oct call ~9:18:
// "it still shows Product Focus and all of them are one - that should be
// Balanced"). Cause: the preset toggle defaulted to 'productFocus', but for a
// custom system no species weights were ever written for the user's own
// species, so every box fell back to `?? 1` and showed 1 while the toggle
// claimed Product Focus. Default is now Balanced (every fitted species = 1)
// everywhere a default is set: first load, example load, Reinitialize, Reset
// and reaction-system change. Product Focus is still one click away on the
// toggle and sets reactants = 1, products = 2. Inert species stay out of the
// fit (weight 0, not listed) exactly as before - see buildEffectiveSpeciesWeights.
const DEFAULT_WEIGHT_PRESET = 'balanced';
const BALANCED_SYSTEM_WEIGHTS = {
  meoac:       { MeOAc: 1, HOAc: 1, MeOH: 1, H2O: 1 },
  dme:         { DME: 1, MeOH: 1, H2O: 1 },
  acetic_acid: { C2H6: 1, O2: 1, AA: 1, ET: 1, CO2: 1, H2O: 1, N2: 0 },
};
const balancedWeightsForSystem = (sys) => BALANCED_SYSTEM_WEIGHTS[sys] || BALANCED_SYSTEM_WEIGHTS.meoac;

const formulaSortMeta = (component) => {
  const formula = String(component?.formula || '').trim();
  if (!formula) {
    return {
      hasFormula: false,
      isChonOnly: false,
      presentElements: [],
      signature: [],
      otherElements: [],
      counts: { C: 0, H: 0, O: 0, N: 0 },
    };
  }

  let atoms = {};
  try {
    // Use the same parser as the atom-balance checker so grouped formulas
    // such as Ca(OH)2 and (NH4)2SO4 are classified correctly.
    atoms = parseAtomCounts(formula);
  } catch (_) {
    atoms = {};
  }

  const presentElements = Object.keys(atoms)
    .filter(el => Number(atoms[el]) > 0);

  // IMPORTANT: classify the COMPLETE formula before any C/H/O/N comparison.
  // This is the key Madhura rule: CHO/CHON-only species are always in the
  // first group. A formula such as C2H5FO is NOT a CHON species and must never
  // outrank C2H6O merely because its CHON subset looks similar.
  const isChonOnly = presentElements.length > 0
    && presentElements.every(el => CHON_ELEMENT_RANK[el] !== undefined);

  const signature = presentElements
    .filter(el => CHON_ELEMENT_RANK[el] !== undefined)
    .sort((a, b) => CHON_ELEMENT_RANK[a] - CHON_ELEMENT_RANK[b])
    .map(el => CHON_ELEMENT_RANK[el]);

  const otherElements = presentElements
    .filter(el => CHON_ELEMENT_RANK[el] === undefined)
    .sort((a, b) => a.localeCompare(b));

  return {
    hasFormula: presentElements.length > 0,
    isChonOnly,
    presentElements,
    signature,
    otherElements,
    counts: CHON_ELEMENT_ORDER.reduce((out, el) => {
      out[el] = Number(atoms[el] || 0);
      return out;
    }, {}),
  };
};

const compareArrayNumbers = (a = [], b = []) => {
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i += 1) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return a.length - b.length;
};

const compareSpeciesByFormulaOrder = (a, b) => {
  const ma = formulaSortMeta(a);
  const mb = formulaSortMeta(b);

  // PRIMARY GROUPING — never let a non-CHON formula enter the CHO/CHON block.
  //   0 = CHO / CHON only
  //   1 = contains any other element (F/Cl/Br/S/I/etc.)
  //   2 = no usable formula
  const groupRank = (m) => {
    if (!m.hasFormula) return 2;
    return m.isChonOnly ? 0 : 1;
  };

  const groupCmp = groupRank(ma) - groupRank(mb);
  if (groupCmp !== 0) return groupCmp;

  // SECONDARY ORDERING — only applied after the primary CHON/non-CHON group
  // has been decided. This is what makes the UI behaviour predictable.
  let cmp = compareArrayNumbers(ma.signature, mb.signature);
  if (cmp !== 0) return cmp;

  for (const el of CHON_ELEMENT_ORDER) {
    cmp = ma.counts[el] - mb.counts[el];
    if (cmp !== 0) return cmp;
  }

  // For the non-CHON group, keep other elements deterministic after CHON
  // counts; there is deliberately no special-case ranking for F/Cl/Br/S/I.
  cmp = ma.otherElements.join(',').localeCompare(
    mb.otherElements.join(','),
    undefined,
    { numeric: true },
  );
  if (cmp !== 0) return cmp;

  const formulaA = normalizeSpeciesToken(a?.formula || '');
  const formulaB = normalizeSpeciesToken(b?.formula || '');
  cmp = formulaA.localeCompare(formulaB, undefined, { numeric: true });
  if (cmp !== 0) return cmp;

  const nameA = normalizeSpeciesToken(a?.name || a?.species_name || '');
  const nameB = normalizeSpeciesToken(b?.name || b?.species_name || '');
  cmp = nameA.localeCompare(nameB, undefined, { numeric: true });
  if (cmp !== 0) return cmp;

  return String(a?.id || a?.species_key || '').localeCompare(
    String(b?.id || b?.species_key || ''),
    undefined,
    { numeric: true },
  );
};

const searchSpeciesCatalog = (q, registeredComponents = []) => {
  const needle = normalizeSpeciesToken(q);
  if (needle.length < 2) return [];
  const seen = new Set();
  const hits = [];
  registeredComponents.forEach(c => {
    if (!speciesFieldValues(c).some(v => normalizeSpeciesToken(v).includes(needle))) return;
    const key = String(c.id || c.name || c.formula || '').trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    hits.push(c);
  });
  return hits.sort(compareSpeciesByFormulaOrder);
};

// Bind identity to the stoichiometric token POSITION, never to the visible text.
// A common-name/formula can change while the user's selected catalog record remains fixed.
const speciesIdentityKey = (scope, tokenIdx) => `${scope}::${Number(tokenIdx)}`;

const getIdentityForToken = (identityMap, scope, tokenIdx) =>
  identityMap?.[speciesIdentityKey(scope, tokenIdx)] || null;

const setIdentityForToken = (identityMap, scope, tokenIdx, speciesKey) => {
  const next = { ...(identityMap || {}) };
  const key = speciesIdentityKey(scope, tokenIdx);
  if (speciesKey) next[key] = speciesKey;
  else delete next[key];
  return next;
};

const componentBySpeciesKey = (speciesKey, components = []) => {
  const needle = normalizeSpeciesToken(speciesKey);
  if (!needle) return null;
  return components.find(c => normalizeSpeciesToken(c?.species_key || c?.id) === needle) || null;
};

// Resolve a user-entered species token to exactly one catalog record.
// Explicit species-key identity wins; otherwise only a unique name/formula/CAS/alias
// match is accepted. Formula alone is never allowed to pick an arbitrary isomer.
const resolveCatalogComponentForToken = (
  token,
  components = [],
  identityMap = {},
  identityScope = '',
  tokenIdx = 0,
) => {
  const selectedKey = identityScope
    ? getIdentityForToken(identityMap, identityScope, tokenIdx)
    : null;
  if (selectedKey) {
    const selected = componentBySpeciesKey(selectedKey, components);
    if (selected) return selected;
  }
  const candidates = findSpeciesCandidates(token, components);
  return candidates.length === 1 ? candidates[0] : null;
};

// Canonicalize the visible reaction expression to resolved catalog names while
// preserving stoichiometric coefficients and '+' separators. This keeps the
// computational identity in species_key but makes the input human-readable.
const canonicalizeReactionSpeciesExpression = (
  expression,
  components = [],
  identityMap = {},
  identityScope = '',
) => {
  const tokens = String(expression || '').split('+');
  let changed = false;

  const next = tokens.map((term, tokenIdx) => {
    const raw = term.trim();
    if (!raw) return '';

    const parsed = parseStoicTermText(raw);
    const coeff = parsed.coeff !== 1 ? `${parsed.coeff} ` : '';
    const speciesToken = parsed.species;
    if (!speciesToken) return raw;

    const hit = resolveCatalogComponentForToken(
      speciesToken, components, identityMap, identityScope, tokenIdx
    );
    if (!hit?.name) return raw;

    if (normalizeSpeciesToken(speciesToken) !== normalizeSpeciesToken(hit.name)) {
      changed = true;
    }
    if (identityScope && hit.species_key) {
      // Selection persistence is handled by the caller when a dropdown item is
      // chosen. Blur canonicalization only changes the visible text.
    }
    return `${coeff}${hit.name}`;
  });

  return {
    value: next.join(' + '),
    changed,
  };
};

const speciesDisplayNameForToken = (token, components = []) => {
  const hit = componentBySpeciesKey(token, components) || (() => {
    const candidates = findSpeciesCandidates(token, components);
    return candidates.length === 1 ? candidates[0] : null;
  })();
  return hit?.name || String(token || '').trim();
};

const manualEntryHeaderLabel = (header, type, components = []) => {
  const raw = String(header ?? '');
  if (!raw) return raw;
  if (type === 'timeseries') {
    const token = raw.replace(/^x-/i, '');
    return speciesDisplayNameForToken(token, components);
  }
  const m = raw.match(/^(.*?)(In|Out)$/i);
  if (m) {
    const resolved = speciesDisplayNameForToken(m[1], components);
    return `${resolved} (${m[2].toUpperCase()})`;
  }
  return raw;
};

// ═══════════════════════════════════════════════════════════════════════════
// SPECIES AUTOCOMPLETE INPUT
// Search the DB-backed catalog already loaded into registeredComponents.
const SpeciesAutocompleteInput = ({ value, onChange, style, placeholder, registeredComponents = [], identityScope = '', speciesIdentityMap = {}, onSpeciesIdentityChange }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeTokenIdx, setActiveTokenIdx] = useState(null);   // which token the cursor is in
  const [lastQuery, setLastQuery] = useState('');   // text of the token being searched (for the Add-component button)
  const debounceRef  = useRef(null);
  const inputRef     = useRef(null);

  // Split "2 MeOH + H2O" into tokens, preserving separators
  const getTokens = (str) => str.split('+').map(t => t.trim());

  // Given cursor position in the raw string, return which '+'-separated index it's in
  const getActiveIdx = (str, cursorPos) => {
    let count = 0;
    for (let i = 0; i < cursorPos; i++) {
      if (str[i] === '+') count++;
    }
    return count;
  };

  // Parse coefficients without confusing names such as 2-Propanol.
  const speciesPart = (token) => parseStoicTermText(token).species;
  const coeffPart = (token) => {
    const parsed = parseStoicTermText(token);
    return parsed.coeff !== 1 ? `${parsed.coeff} ` : '';
  };

  // A token "looks complete" if it already resolves to a real, non-empty
  // formula — used below to decide whether a space the user just typed
  // should become " + " instead (see (a) above). Deliberately requires a
  // successful resolveFormula/parseAtomCounts round-trip, not just "has 2+
  // characters", so it doesn't fire mid-word on something not yet
  // recognizable (e.g. typing "Eth" on the way to "Ethane" as a bare word
  // rather than a formula).
  const tokenResolvesToFormula = (species) => {
    if (!species) return false;
    try {
      const candidates = findSpeciesCandidates(species, registeredComponents);
      if (candidates.length > 1 && !getIdentityForToken(speciesIdentityMap, identityScope, activeTokenIdx)) {
        return false;
      }
      const formula = resolveFormula(species, registeredComponents);
      const atoms = parseAtomCounts(formula);
      return Object.keys(atoms).length > 0;
    } catch (_) {
      return false;
    }
  };

  const handleInputChange = (e) => {
    const newVal    = e.target.value;
    const cursorPos = e.target.selectionStart;
    onChange(e);

    const idx     = getActiveIdx(newVal, cursorPos);
    const tokens  = getTokens(newVal);
    const current = tokens[idx] || '';
    const species = speciesPart(current);

    setActiveTokenIdx(idx);

    // Manual editing invalidates the old explicit species selection.
    if (identityScope && onSpeciesIdentityChange) {
      onSpeciesIdentityChange(prev => setIdentityForToken(prev, identityScope, idx, null));
    }

    if (!species || species.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const hits = searchSpeciesCatalog(species, registeredComponents).map(c => ({
        id: c.id,
        species_key: c.id,
        species_name: c.name,
        formula: c.formula,
        cas_number: c.cas,
        aliases: Array.isArray(c.aliases) ? c.aliases : [],
        _catalog: true,
      }));
      setSuggestions(hits);
      setLastQuery(species);
      // Show the dropdown even with 0 hits, so "Add a new component" is reachable.
      setShowDropdown(true);
    }, 280);
  };

  const handleSelect = (suggestion) => {
    const raw = String(value ?? '');
    const tokens = getTokens(raw);

    // If the user has just selected the second component after the automatic
    // ` + ` inserted for the first one, always target the trailing empty token.
    // Do not trust activeTokenIdx in that case: React state and the browser
    // selection/cursor can be one event behind when the dropdown is clicked.
    // This was the source of the stray `+` after the second component.
    const hasTrailingSeparator = /\+\s*$/.test(raw);
    const idx = hasTrailingSeparator
      ? tokens.length - 1
      : (activeTokenIdx !== null ? activeTokenIdx : tokens.length - 1);

    const coeff = coeffPart(tokens[idx] || '');
    const typedSpecies = speciesPart(tokens[idx] || '');
    // Always show the resolved catalog name in the Reactants/Products field.
    // The exact species_key is stored separately in speciesIdentityMap, so the
    // visible field stays readable even when the user searched by an alias,
    // formula, or CAS number.
    const displayToken = suggestion.species_name || typedSpecies;
    tokens[idx] = coeff + displayToken;

    if (identityScope && onSpeciesIdentityChange && suggestion.species_key) {
      onSpeciesIdentityChange(prev =>
        setIdentityForToken(prev, identityScope, idx, suggestion.species_key)
      );
    }

    // Only the FIRST selected component gets an automatic separator. Once a
    // second component has been selected, leave the value as `A + B` so the
    // user can explicitly continue with ` + C` if a third component is needed.
    const wasLastToken = idx === tokens.length - 1;
    const completedComponentCount = tokens.filter(t => t.trim() !== '').length;
    const shouldAutoAppendPlus = wasLastToken && completedComponentCount === 1;

    // Normalize separators while preserving the intended `A + B` form.
    let newVal = tokens.map(t => t.trim()).join(' + ');
    if (shouldAutoAppendPlus) newVal += ' + ';
    onChange({ target: { value: newVal } });
    setSuggestions([]);
    setShowDropdown(false);
    // Restore focus to the input
    setTimeout(() => {
      inputRef.current?.focus();
      // Place the cursor at the very end (after the trailing " + ") so
      // typing continues naturally into the new token.
      if (wasLastToken && inputRef.current) {
        const pos = newVal.length;
        inputRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  // GAP FIX (Madhura, 24-Aug-2026, item 3a) — the typed-not-selected path.
  // Handles the common case: cursor at the very end of the field (how
  // people normally type) and the token just finished already resolves to
  // a real formula — pressing space there means "done with this species",
  // so insert " + " instead of a plain space. Deliberately scoped to
  // "cursor at end of string" only (not general mid-string edits), so
  // fixing an earlier token doesn't misfire and insert a stray "+".
  const handleBlurCanonicalize = () => {
    const result = canonicalizeReactionSpeciesExpression(
      value, registeredComponents, speciesIdentityMap, identityScope
    );
    if (result.changed) {
      onChange({ target: { value: result.value } });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key !== ' ') return;
    const cursorPos = e.target.selectionStart;
    if (cursorPos !== value.length || value.length === 0) return;
    if (value.trimEnd().endsWith('+')) return; // already about to start a new token
    const tokens  = getTokens(value);
    const species = speciesPart(tokens[tokens.length - 1] || '');
    // Only auto-insert the separator after the first component. If the user
    // already has `+` in the field, they have moved on to the second/next
    // component and we must not append another trailing `+` automatically.
    const isFirstComponent = tokens.length === 1;
    if (isFirstComponent && tokenResolvesToFormula(species)) {
      e.preventDefault();
      const newVal = value.replace(/\s*$/, '') + ' + ';
      onChange({ target: { value: newVal } });
      setTimeout(() => inputRef.current?.setSelectionRange(newVal.length, newVal.length), 0);
    }
  };

  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <input
        ref={inputRef}
        value={value}
        placeholder={placeholder}
        style={{
          background: C.inputBg, border: `1px solid ${C.inputBorder}`, borderRadius: 4,
          color: C.text, fontSize: 14, fontFamily: font.ui, padding: '5px 8px',
          width: '100%', transition: 'border-color 0.12s, box-shadow 0.12s', boxSizing: 'border-box',
          ...style,
        }}
        onChange={handleInputChange}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setSuggestions([]); setShowDropdown(false); return; }
          handleKeyDown(e);
        }}
        onFocus={(e) => {
          e.target.style.borderColor = C.accent;
          e.target.style.boxShadow   = `0 0 0 2px ${C.accentGlow}`;
          if (suggestions.length > 0) setShowDropdown(true);
        }}
        onBlur={(e) => {
          e.target.style.borderColor = C.inputBorder;
          e.target.style.boxShadow   = 'none';
          // Resolve any uniquely-identifiable alias/formula/name that the user
          // typed directly, even when they did not click a dropdown result.
          handleBlurCanonicalize();
          setTimeout(() => { setSuggestions([]); setShowDropdown(false); }, 200);
        }}
      />
      {showDropdown && (suggestions.length > 0 || lastQuery.length >= 2) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 1200,
          background: C.card, border: `1px solid ${C.border}`,
          borderRadius: 6, boxShadow: '0 10px 28px rgba(0,0,0,0.45)',
          minWidth: '100%', width: 'max-content', maxWidth: 380,
          maxHeight: 320, overflowY: 'auto', marginTop: 3,
        }}>
          {suggestions.length === 0 && (
            <div style={{ padding: '10px 14px', fontSize: 12.5, color: C.textMuted, fontFamily: font.ui }}>
              No matching component in the database.
            </div>
          )}
          {suggestions.map((s, i) => (
            <div
              key={i}
              onMouseDown={() => handleSelect(s)}
              style={{
                padding: '9px 14px', cursor: 'pointer',
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10,
                borderBottom: `1px solid ${C.border}22`,
                background: i % 2 ? C.surface : C.card,
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = C.accentDim}
              onMouseLeave={e => e.currentTarget.style.background = i % 2 ? C.surface : C.card}
            >
              <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 14, color: C.text, fontFamily: font.ui, fontWeight: 700 }}>
                  {s.species_name}
                </span>
                {Array.isArray(s.aliases) && s.aliases.length > 0 && (
                  <span style={{ fontSize: 11, color: C.textMuted, fontFamily: font.ui, lineHeight: 1.4 }}>
                    Also known as: {s.aliases.join(', ')}
                  </span>
                )}
                {s.cas_number && (
                  <span style={{ fontSize: 10, color: C.textDim, fontFamily: font.mono }}>
                    CAS {s.cas_number}
                  </span>
                )}
              </span>
              <span style={{
                fontSize: 13, color: C.purple, fontFamily: font.mono, fontWeight: 600,
                background: `${C.purple}18`, borderRadius: 4, padding: '3px 8px', marginLeft: 8,
                whiteSpace: 'nowrap', flexShrink: 0,
              }}>{s.formula}</span>
            </div>
          ))}
          {/* Always-available footer: component missing from the catalog */}
          <div
            role="button"
            onMouseDown={(e) => {
              e.preventDefault();
              setShowDropdown(false);
              requestAddComponent({
                prefillName: lastQuery,
                onCreated: (comp) => handleSelect({
                  species_key: comp.species_key || comp.id,
                  species_name: comp.name,
                  formula: comp.formula,
                  cas_number: comp.cas,
                  aliases: Array.isArray(comp.aliases) ? comp.aliases : [],
                }),
              });
            }}
            style={{
              position: 'sticky', bottom: 0, padding: '10px 14px', cursor: 'pointer',
              background: C.card, borderTop: `1px solid ${C.border}`,
              color: C.accent, fontFamily: font.ui, fontSize: 13, fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 8,
            }}
            onMouseEnter={e => e.currentTarget.style.background = C.accentDim}
            onMouseLeave={e => e.currentTarget.style.background = C.card}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>＋</span>
            Add a new component{lastQuery ? ` “${lastQuery}”` : ''}
          </div>
        </div>
      )}
    </div>
  );
};

// eslint-disable-next-line no-unused-vars -- kept for later; no longer rendered in the Appendix
const ComponentRegistry = ({ registeredComponents, setRegisteredComponents }) => {
  const [query, setQuery] = useState('');

  // The registry list follows the same formula-aware ordering as the
  // autocomplete results, so the user sees one consistent component order
  // everywhere in the Data & Model input flow. Never mutate the DB-backed
  // array itself; only sort a shallow copy for rendering.
  const orderedComponents = React.useMemo(
    () => [...(Array.isArray(registeredComponents) ? registeredComponents : [])]
      .sort(compareSpeciesByFormulaOrder),
    [registeredComponents],
  );
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [customEntry, setCustomEntry] = useState({ id: '', name: '', formula: '', MW: '' });
  const lookupSuggestions = useMemo(
    () => searchSpeciesCatalog(query, registeredComponents).slice(0, 12),
    [query, registeredComponents],
  );

  const handleSpeciesLookup = () => {
    const hit = findSpeciesComponent(query, registeredComponents);
    if (hit) {
      setLookupResult(hit);
      setLookupError('');
    } else {
      setLookupResult(null);
      setLookupError(`"${query}" was not found in the database species catalog. Add manually below.`);
    }
  };

  const addToRegistry = (comp) => {
    if (registeredComponents.find(c => c.id === comp.id)) return;
    setRegisteredComponents(prev => [...prev, comp]);
    setLookupResult(null);
    setQuery('');
  };

  // "Add manually" now opens the same pop-up as the search dropdown, so every
  // user-defined component gets a mandatory CAS number, is checked against the
  // main database for duplicates, and is stored per login.
  const addCustom = () => {
    requestAddComponent({
      prefillName: customEntry.name,
      prefillFormula: customEntry.formula,
      prefillMW: customEntry.MW,
      onCreated: () => setCustomEntry({ id: '', name: '', formula: '', MW: '' }),
    });
  };

  const removeComponent = async (c) => {
    if (c.server_id) {
      const r = await userComponentsFetch(`/user-components/${c.server_id}`, { method: 'DELETE' });
      if (!r.ok && r.status !== 404) {
        window.alert(r.data?.error || "Couldn't delete this component. Please try again.");
        return;
      }
    }
    setRegisteredComponents(prev => prev.filter(x => x.id !== c.id));
  };

  return (
    <SectionBox title="Component Registry" badge="DB" badgeColor={C.cyan}
      info="Look up species by formula, name, CAS number, or alias. Components come from the database catalog; user-defined components can be added below.">
      {/* NIST Lookup */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Input placeholder="Search name, Other Name, formula, or CAS" value={query}
            onChange={e => { setQuery(e.target.value); setLookupError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleSpeciesLookup()}
            style={{ width: '100%' }} />
          {lookupSuggestions.length > 0 && query.trim().length >= 2 && (
            <div style={{
              position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 1500,
              marginTop: 3, maxHeight: 300, overflowY: 'auto',
              background: C.card, border: `1px solid ${C.border}`, borderRadius: 6,
              boxShadow: '0 12px 28px rgba(0,0,0,0.35)',
            }}>
              {lookupSuggestions.map((c) => (
                <button type="button" key={c.id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setLookupResult(c);
                    setLookupError('');
                    setQuery(c.name);
                  }}
                  style={{
                    width: '100%', border: 'none', borderBottom: `1px solid ${C.border}22`,
                    background: 'transparent', color: C.text, padding: '8px 10px',
                    textAlign: 'left', cursor: 'pointer', fontFamily: font.ui,
                  }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: C.textDim }}>
                    {c.formula || '—'}{c.cas ? ` · CAS ${c.cas}` : ''}
                  </div>
                  {Array.isArray(c.aliases) && c.aliases.length > 0 && (
                    <div style={{ fontSize: 10.5, color: C.textMuted, marginTop: 2 }}>
                      Other Names: {c.aliases.slice(0, 5).join(', ')}{c.aliases.length > 5 ? ' …' : ''}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <Btn variant="secondary" style={{ fontSize: 13, whiteSpace: 'nowrap' }} onClick={handleSpeciesLookup}>
          🔍 Component Lookup
        </Btn>
      </div>

      {lookupError && <Alert type="error">{lookupError}</Alert>}
      {lookupResult && (
        <div style={{ background: C.cyanDim, border: `1px solid ${C.cyan}44`, borderRadius: 5,
          padding: '8px 12px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ fontSize: 12, fontFamily: font.ui, lineHeight: 2 }}>
            <div><strong style={{ color: C.text }}>{lookupResult.name}</strong>
              <span style={{ color: C.textMuted, marginLeft: 8 }}>{lookupResult.formula}</span></div>
            <div style={{ color: C.textDim }}>MW: {lookupResult.MW} g/mol · Tc: {lookupResult.Tc} K · CAS: {lookupResult.cas}</div>
          </div>
          <Btn variant="primary" style={{ fontSize: 13 }} onClick={() => addToRegistry(lookupResult)}>
            + Add
          </Btn>
        </div>
      )}

      {/* Custom entry */}
      <Divider label="or add a component that is not in the database" />
      <div style={{ display: 'grid', gridTemplateColumns: '0.8fr 1.2fr 0.8fr 0.6fr auto', gap: 6, alignItems: 'flex-end' }}>
        <Field label="ID" style={{ marginBottom: 0 }}>
          <Input placeholder="e.g. C3H6" value={customEntry.id}
            onChange={e => setCustomEntry(p => ({ ...p, id: e.target.value.toUpperCase() }))} />
        </Field>
        <Field label="Name" style={{ marginBottom: 0 }}>
          <Input placeholder="e.g. Propylene" value={customEntry.name}
            onChange={e => setCustomEntry(p => ({ ...p, name: e.target.value }))} />
        </Field>
        <Field label="Formula" style={{ marginBottom: 0 }}>
          <Input placeholder="e.g. C₃H₆" value={customEntry.formula}
            onChange={e => setCustomEntry(p => ({ ...p, formula: e.target.value }))} />
        </Field>
        <Field label="MW (g/mol)" style={{ marginBottom: 0 }}>
          <Input type="number" placeholder="42.08" value={customEntry.MW}
            onChange={e => setCustomEntry(p => ({ ...p, MW: e.target.value }))} step="0.001" />
        </Field>
        <Btn variant="primary" style={{ fontSize: 13 }} onClick={addCustom}>Add</Btn>
      </div>

      {/* Registry table */}
      {registeredComponents.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Label>Registered Components ({registeredComponents.length})</Label>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%' }}>
              <thead>
                <tr style={{ background: C.panel }}>
                  {['ID', 'Name', 'Formula', 'MW', 'Tc (K)', 'CAS', ''].map(h => (
                    <th key={h} style={{ padding: '4px 8px', color: C.textMuted,
                      borderBottom: `1px solid ${C.border}`, textAlign: 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orderedComponents.map((c, i) => (
                  <tr key={c.id} style={{ background: i % 2 ? C.card : C.surface }}>
                    <td style={{ padding: '3px 8px', color: C.accent, fontWeight: 700 }}>{c.id}</td>
                    <td style={{ padding: '3px 8px', color: C.text }}>{c.name}</td>
                    <td style={{ padding: '3px 8px', color: C.textMid }}>{c.formula || '—'}</td>
                    <td style={{ padding: '3px 8px', color: C.textMid }}>{c.MW || '—'}</td>
                    <td style={{ padding: '3px 8px', color: C.textMid }}>{c.Tc || '—'}</td>
                    <td style={{ padding: '3px 8px', color: C.textDim, fontSize: 12 }}>{c.cas || '—'}</td>
                    <td style={{ padding: '3px 8px' }}>
                      <button onClick={() => removeComponent(c)}
                        style={{ background: 'none', border: 'none', color: C.textMuted,
                          cursor: 'pointer', fontSize: 13, padding: '0 2px' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </SectionBox>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// ADD-A-NEW-COMPONENT POP-UP (client item #11)
// ═══════════════════════════════════════════════════════════════════════════
const AddComponentModal = ({ opts, registeredComponents, setRegisteredComponents, onClose }) => {
  const [name, setName] = useState(opts.prefillName || '');
  const [formula, setFormula] = useState(opts.prefillFormula || '');
  const [cas, setCas] = useState('');
  const [mw, setMw] = useState(opts.prefillMW || '');
  const [tc, setTc] = useState('');
  const [pc, setPc] = useState('');
  const [aliases, setAliases] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const [serverDup, setServerDup] = useState(null);   // existing record returned by a 409

  const casNorm = normalizeCasInput(cas);
  const casError = cas.trim() ? validateCasClient(cas) : '';

  // Instant duplicate check against everything already loaded (main DB + this
  // user's own components) — the server repeats it authoritatively on submit.
  const localDup = useMemo(() => {
    if (!casNorm || casError) return null;
    return (Array.isArray(registeredComponents) ? registeredComponents : [])
      .find(c => normalizeCasInput(c?.cas) === casNorm) || null;
  }, [casNorm, casError, registeredComponents]);
  const existing = localDup || serverDup;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const useExisting = () => {
    const comp = serverDup
      ? normalizeSpeciesCatalog([serverDup])[0]
      : localDup;
    if (!comp) return;
    // make sure it is in the registry so the identity lookup can find it
    setRegisteredComponents(prev => (
      (Array.isArray(prev) ? prev : []).some(c => c.id === comp.id) ? prev : [...prev, comp]
    ));
    opts.onCreated?.(comp);
    onClose();
  };

  const submit = async () => {
    setTouched(true);
    setError('');
    if (!name.trim() || !formula.trim() || !cas.trim() || casError) return;
    if (localDup) return;   // the "already exists" box explains it
    setBusy(true);
    try {
      const r = await userComponentsFetch('/user-components', {
        method: 'POST',
        body: {
          species_name: name.trim(),
          formula: formula.trim(),
          cas_number: casNorm,
          molecular_weight: mw === '' ? null : mw,
          critical_temperature_k: tc === '' ? null : tc,
          critical_pressure_bar: pc === '' ? null : pc,
          aliases: aliases.trim(),
        },
      });
      if (r.status === 409 && r.data?.existing) {
        setServerDup(r.data.existing);
        setError(r.data.error || 'This CAS number already exists.');
        return;
      }
      if (!r.ok) {
        setError(r.data?.error || 'Could not add the component. Please try again.');
        return;
      }
      const raw = r.data.component;
      // 'db' = saved straight into the shared catalog (no per-login record);
      // otherwise it is this login's own component.
      const comp = (raw && raw.source === 'db' ? normalizeSpeciesCatalog([raw]) : normalizeUserComponents([raw]))[0];
      setRegisteredComponents(prev => {
        const list = Array.isArray(prev) ? prev : [];
        return list.some(c => c.id === comp.id) ? list : [...list, comp];
      });
      opts.onCreated?.(comp);
      onClose();
    } catch (e) {
      setError('Network error — the component was not added. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const req = (v) => touched && !String(v).trim();
  const fieldBorder = (bad) => (bad ? { borderColor: C.error } : {});

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div role="dialog" aria-modal="true" aria-label="Add a new component" style={{
        width: 'min(560px, 100%)', maxHeight: '92vh', overflowY: 'auto',
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)', padding: '18px 20px', fontFamily: font.ui,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>Add a new component</div>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close"
            style={{ background: 'none', border: 'none', color: C.textMuted, fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ fontSize: 12.5, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
          Use this only if the component is not in the database. It is checked by CAS number first and, when added,
          is saved to <strong>your login only</strong>.
        </div>

        {error && <Alert type="error">{error}</Alert>}

        {existing && (
          <Alert type="warning">
            CAS {casNorm} already exists in the database as <strong>{existing.name || existing.species_name}</strong>
            {existing.formula ? ` (${existing.formula})` : ''}. Please use that component instead of adding a new one.
            {' '}
            <button type="button" onClick={useExisting}
              style={{ background: 'none', border: 'none', color: C.accent, fontWeight: 700, cursor: 'pointer', padding: 0, fontFamily: font.ui, fontSize: 12 }}>
              Use “{existing.name || existing.species_name}”
            </button>
          </Alert>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="CAS number" required style={{ marginBottom: 0 }}>
            <Input value={cas} placeholder="e.g. 64-17-5" autoFocus
              onChange={e => { setCas(e.target.value); setServerDup(null); setError(''); }}
              style={{ width: '100%', ...fieldBorder(req(cas) || !!casError) }} />
            {(casError || req(cas)) && (
              <div style={{ color: C.error, fontSize: 11.5, marginTop: 3 }}>{casError || 'CAS number is required.'}</div>
            )}
          </Field>
          <Field label="Component name" required style={{ marginBottom: 0 }}>
            <Input value={name} placeholder="e.g. Ethanol"
              onChange={e => setName(e.target.value)} style={{ width: '100%', ...fieldBorder(req(name)) }} />
            {req(name) && <div style={{ color: C.error, fontSize: 11.5, marginTop: 3 }}>Name is required.</div>}
          </Field>
          <Field label="Formula" required style={{ marginBottom: 0 }}>
            <Input value={formula} placeholder="e.g. C2H6O"
              onChange={e => setFormula(e.target.value)} style={{ width: '100%', ...fieldBorder(req(formula)) }} />
            {req(formula) && <div style={{ color: C.error, fontSize: 11.5, marginTop: 3 }}>Formula is required.</div>}
          </Field>
          <Field label="Molecular weight (g/mol)" style={{ marginBottom: 0 }}>
            <Input type="number" step="0.001" value={mw} placeholder="optional"
              onChange={e => setMw(e.target.value)} style={{ width: '100%' }} />
          </Field>
          <Field label="Critical temperature (K)" style={{ marginBottom: 0 }}>
            <Input type="number" step="0.01" value={tc} placeholder="optional"
              onChange={e => setTc(e.target.value)} style={{ width: '100%' }} />
          </Field>
          <Field label="Critical pressure (bar)" style={{ marginBottom: 0 }}>
            <Input type="number" step="0.01" value={pc} placeholder="optional"
              onChange={e => setPc(e.target.value)} style={{ width: '100%' }} />
          </Field>
        </div>
        <Field label="Other names" sub="optional — separate with commas; they become searchable" style={{ marginTop: 10, marginBottom: 0 }}>
          <Input value={aliases} placeholder="e.g. ethyl alcohol, EtOH"
            onChange={e => setAliases(e.target.value)} style={{ width: '100%' }} />
        </Field>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn variant="primary" onClick={submit} disabled={busy || !!localDup}>
            {busy ? 'Checking & saving…' : 'Add component'}
          </Btn>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// REQUIREMENT 3 — STOICHIOMETRIC BALANCE VALIDATOR
// ═══════════════════════════════════════════════════════════════════════════

// Element symbol → full name, used only to phrase the balance-check message
// in plain English ("8 oxygen atoms" instead of "O(-8.00)"). Falls back to
// the bare symbol for anything not listed here (still readable, just less
// pretty) rather than failing.
const ELEMENT_NAMES = {
  H: 'hydrogen', He: 'helium', Li: 'lithium', Be: 'beryllium', B: 'boron',
  C: 'carbon', N: 'nitrogen', O: 'oxygen', F: 'fluorine', Ne: 'neon',
  Na: 'sodium', Mg: 'magnesium', Al: 'aluminum', Si: 'silicon', P: 'phosphorus',
  S: 'sulfur', Cl: 'chlorine', Ar: 'argon', K: 'potassium', Ca: 'calcium',
  Ti: 'titanium', Cr: 'chromium', Mn: 'manganese', Fe: 'iron', Ni: 'nickel',
  Cu: 'copper', Zn: 'zinc', Br: 'bromine', Ag: 'silver', Sn: 'tin',
  I: 'iodine', Pt: 'platinum', Au: 'gold', Pb: 'lead', Pd: 'palladium',
};

// FIX (Madhura report — manual-paste screenshot, "Missing required columns:
// ['H2O']"): root-caused to this function. A species token typed/pasted into
// Reactants/Products — e.g. "H₂O" copied from a paper/Word doc, unicode
// subscript ₂ instead of ASCII "2" — was kept verbatim as the species KEY
// here. That key flows straight into deriveSpeciesFromReactions →
// getManualEntryConfig, which then builds the manual-entry CSV header as
// literal "x-H₂O". Visually indistinguishable from "x-H2O" at normal zoom
// (hence "no cells were empty" and re-pasting the DATA never fixed it — the
// bad token lives in the reaction equation, not the grid), but the backend's
// column matcher only recognizes ASCII H2O variants, so the column silently
// vanished from validation. resolveFormula() a few lines up already guards
// against exactly this ("a typed 'H₂O' must still match the DB's 'H2O' key")
// — this was the one place that same normalization was missing, since it
// feeds the species LIST (columns) rather than a single formula lookup.
const parseStoicSide = (str) => {
  const out = {};
  const terms = String(str || '').split('+').map(t => t.trim()).filter(Boolean);

  for (const term of terms) {
    const parsed = parseStoicTermText(term);
    const coef = Number.isFinite(parsed.coeff) && parsed.coeff > 0 ? parsed.coeff : 1;
    const rawSpec = parsed.species.trim();
    const spec = rawSpec
      ? toAsciiDigits(rawSpec).replace(/[\u200B-\u200D\uFEFF]/g, '')
      : rawSpec;

    if (spec) out[spec] = (out[spec] || 0) + coef;
  }
  return out;
};

// Convert unicode subscript digits (₀-₉) to plain ASCII digits. Must be
// applied to EVERY formula/species string before atom-counting — not just
// ones resolved from the database catalog — otherwise a raw typed token like "H₂O"
// (subscript ₂) silently parses as "HO" (the ₂ isn't ASCII \d, so the
// atom-count regex just skips over it). This was undercounting H by 1 in
// the acetic-acid example and flagging a balanced reaction as unbalanced.
const toAsciiDigits = (str) => (str || '').replace(/[₀-₉]/g, d => String('₀₁₂₃₄₅₆₇₈₉'.indexOf(d)));

// Parse atom counts from a formula string, e.g. "C2H4" -> { C: 2, H: 4 }.
//
// FIX (Sagar's element-balance check, ref. "Stoichiometry of Chemical
// Reactions" §4.1 — atom counts must match exactly between reactants and
// products): the previous version used a flat regex
// (/([A-Z][a-z]?)(\d*)/g) with no concept of parentheses. Any formula
// containing a grouped, multiplied fragment — e.g. Ca(OH)2, (NH4)2SO4 —
// had that group's multiplier silently DROPPED, because a lone digit
// following ")" isn't a valid match for "[A-Z][a-z]?" and the regex just
// skips past it. Ca(OH)2 was parsed as {Ca:1, O:1, H:1} instead of the
// correct {Ca:1, O:2, H:2}, which surfaces as a spurious atom-imbalance
// (or, worse, a false "balanced" verdict if the miscount happens to
// cancel out on both sides). This rewrite walks the formula with an
// explicit paren stack so grouped multipliers are applied correctly.
const parseAtomCounts = (formula) => {
  const normalized = toAsciiDigits(formula); // safety net even if resolveFormula was bypassed

  // stack[0] is the running total for the whole formula; each "(" pushes
  // a fresh group that gets folded back in (multiplied) at its matching ")".
  const stack = [{}];
  const addAtoms = (target, atoms, multiplier) => {
    Object.entries(atoms).forEach(([el, count]) => {
      target[el] = (target[el] || 0) + count * multiplier;
    });
  };

  // Matches, in priority order: an element symbol with optional trailing
  // count (C2, Na, H), an opening paren, or a closing paren with optional
  // trailing multiplier ()2 or a bare )).
  const tokenRe = /([A-Z][a-z]?)(\d*)|(\()|(\))(\d*)/g;
  let m;
  while ((m = tokenRe.exec(normalized)) !== null) {
    if (m[1]) {
      // Element symbol, e.g. "C2" -> C, count 2 (defaults to 1 if no digits).
      const el = m[1];
      const count = parseInt(m[2] || '1', 10);
      const top = stack[stack.length - 1];
      top[el] = (top[el] || 0) + count;
    } else if (m[3]) {
      // Opening paren — start a new group to be multiplied on close.
      stack.push({});
    } else if (m[4]) {
      // Closing paren — fold the just-closed group into its parent,
      // multiplied by whatever integer (if any) follows the ")".
      const multiplier = parseInt(m[5] || '1', 10);
      const closed = stack.pop() || {};
      if (stack.length === 0) stack.push({}); // guard against a stray extra ")"
      addAtoms(stack[stack.length - 1], closed, multiplier);
    }
  }

  // Fold any unclosed groups (a stray "(" with no matching ")") back into
  // the parent at multiplier 1, rather than silently discarding those atoms.
  while (stack.length > 1) {
    const group = stack.pop();
    addAtoms(stack[stack.length - 1], group, 1);
  }

  return stack[0];
};

// Resolve a species token (e.g. "HOAc", "H2O", "MeOAc") to a formula string,
// checking the DB-backed species catalog first, then the user's registry,
// then falling back to the token itself (so raw formulas like "C2H6" still work).
const resolveFormula = (specId, registeredComponents = []) => {
  // Normalize the lookup key too — a typed "H₂O" (unicode subscript) must
  // still match the DB's "H2O" (ASCII) key, and the un-matched fallback
  // must still be usable by parseAtomCounts.
  const key = toAsciiDigits(specId.trim());
  const catalogHit = findSpeciesComponent(key, registeredComponents);
  if (catalogHit?.formula) {
    return toAsciiDigits(catalogHit.formula);
  }
  return key; // last resort: treat the (now ASCII-normalized) token itself as a formula
};

// Pure balance-check function — no React, reusable anywhere (live badge,
// Appendix validator, submit-time guard, etc).
//
// The identity map is intentionally part of validation. A user can select
// Ethanol while the formula is C2H6O, even though Dimethyl Ether also has
// C2H6O. Formula-only matching must never override the user's explicit choice.
//
// Returns:
// { balanced, incomplete, unbalancedAtoms, unresolvedSpecies, ambiguousSpecies }
const checkReactionBalance = (
  reactantStr = '',
  productStr = '',
  registeredComponents = [],
  reactantScope = '',
  productScope = '',
  speciesIdentityMap = {},
) => {
  const reactants = parseStoicSide(reactantStr);
  const products = parseStoicSide(productStr);

  const atomBalance = {};
  const unresolvedSpecies = [];
  const ambiguousSpecies = [];

  const sumAtoms = (str, specMap, sign, scope) => {
    const terms = String(str || '').split('+').map(t => t.trim()).filter(Boolean);

    Object.entries(specMap).forEach(([spec, coef]) => {
      const tokenIdx = terms.findIndex(term => {
        const m = term.match(/^(?:(\d+(?:\.\d+)?)\s+)?(.*?)\s*$/);
        const token = (m ? m[2] : term).trim();
        return normalizeSpeciesToken(token) === normalizeSpeciesToken(spec);
      });

      const selectedKey = tokenIdx >= 0
        ? getIdentityForToken(speciesIdentityMap, scope, tokenIdx)
        : null;
      const selected = selectedKey
        ? componentBySpeciesKey(selectedKey, registeredComponents)
        : null;

      if (selected?.formula) {
        const atoms = parseAtomCounts(toAsciiDigits(selected.formula));
        Object.entries(atoms).forEach(([el, count]) => {
          atomBalance[el] = (atomBalance[el] || 0) + sign * coef * count;
        });
        return;
      }

      const candidates = findSpeciesCandidates(spec, registeredComponents);
      if (candidates.length > 1) {
        ambiguousSpecies.push(spec);
        return;
      }

      const formula = resolveFormula(spec, registeredComponents);
      const atoms = parseAtomCounts(formula);
      if (Object.keys(atoms).length === 0) {
        unresolvedSpecies.push(spec);
        return;
      }
      Object.entries(atoms).forEach(([el, count]) => {
        atomBalance[el] = (atomBalance[el] || 0) + sign * coef * count;
      });
    });
  };

  sumAtoms(reactantStr, reactants, +1, reactantScope);
  sumAtoms(productStr, products, -1, productScope);

  const unbalancedAtoms = Object.entries(atomBalance)
    .filter(([, v]) => Math.abs(v) > 0.001)
    .map(([el, diff]) => {
      const name = ELEMENT_NAMES[el] || el;
      const count = Math.abs(diff);
      const countStr = Math.abs(count - Math.round(count)) < 0.005
        ? String(Math.round(count))
        : count.toFixed(2);
      const shortSide = diff < 0 ? 'reactant' : 'product';
      return `Missing ${countStr} ${name} atom${count === 1 ? '' : 's'} on the ${shortSide} side`;
    });

  const hasSpecies = Object.keys(reactants).length > 0 && Object.keys(products).length > 0;

  return {
    balanced: hasSpecies
      && unbalancedAtoms.length === 0
      && unresolvedSpecies.length === 0
      && ambiguousSpecies.length === 0,
    incomplete: !hasSpecies,
    unbalancedAtoms,
    unresolvedSpecies: [...new Set(unresolvedSpecies)],
    ambiguousSpecies: [...new Set(ambiguousSpecies)],
  };
};

// ═══════════════════════════════════════════════════════════════════════════
// DYNAMIC SPECIES DERIVATION — replaces the fixed meoac/dme/acetic_acid
// species lists with whatever the user has actually typed into Components &
// Stoichiometry (Reaction 1–10 + Inerts). This is what the Manual Data
// Entry columns, CSV template, and species-weights panel should read from
// for any non-AA system, instead of a hand-written per-preset array that
// only matches the 3 built-in Examples.
// ─────────────────────────────────────────────────────────────────────────

// Parse the configured Reaction 1–10 reactants+products plus Inerts into a
// de-duplicated, order-preserving species list. Reuses parseStoicSide, the
// same parser already driving the live "Equation balanced" badge, so the
// column list always matches what's shown as balanced/unbalanced on screen.
// Falls back to `fallback` only when nothing has been typed yet (e.g. right
// after page load, before an Example is chosen or a reaction is entered).
const deriveSpeciesFromReactions = (
  reactionRows = [],
  inertComponents = '',
  fallback = [],
  registeredComponents = [],
  speciesIdentityMap = {},
) => {
  const ordered = [];
  const seen = new Set();

  // A displayed name is not a reliable uniqueness key: "Ethane", "ethane",
  // a catalog alias, and a species_key can all refer to the same component.
  // Prefer the explicitly selected catalog identity, then a unique catalog
  // match, and finally a normalized text key for genuine user-defined tokens.
  const addToken = (rawTerm, scope, tokenIdx) => {
    const parsed = parseStoicTermText(rawTerm);
    const token = toAsciiDigits(String(parsed.species || '').trim())
      .replace(/[\u200B-\u200D\uFEFF]/g, '');
    if (!token) return;

    const selectedKey = getIdentityForToken(speciesIdentityMap, scope, tokenIdx);
    const selected = selectedKey
      ? componentBySpeciesKey(selectedKey, registeredComponents)
      : null;
    const candidates = findSpeciesCandidates(token, registeredComponents);
    const component = selected || (candidates.length === 1 ? candidates[0] : null);
    const displayName = String(component?.name || token).trim();
    const identity = component
      ? `catalog:${normalizeSpeciesToken(component.species_key || component.id || component.name)}`
      : `text:${normalizeSpeciesToken(displayName)}`;

    if (!displayName || seen.has(identity)) return;
    seen.add(identity);
    ordered.push(displayName);
  };

  const addSide = (expression, scope) => {
    String(expression || '').split('+').forEach((term, tokenIdx) => {
      if (term.trim()) addToken(term, scope, tokenIdx);
    });
  };

  (Array.isArray(reactionRows) ? reactionRows : []).forEach((rxn, idx) => {
    const reactionNumber = Number(rxn?.index || idx + 1);
    addSide(rxn?.reactants, `r${reactionNumber}.reactants`);
    addSide(rxn?.products, `r${reactionNumber}.products`);
  });

  // Inerts can be entered as "N2, Ar" or "N2 + Ar". Resolve them to the
  // same catalog identity key used by reaction species before de-duplicating.
  String(inertComponents || '').split(/[,+]/).map(v => v.trim()).filter(Boolean)
    .forEach((token) => {
      const candidates = findSpeciesCandidates(token, registeredComponents);
      const component = candidates.length === 1 ? candidates[0] : null;
      const displayName = String(component?.name || token).trim();
      const identity = component
        ? `catalog:${normalizeSpeciesToken(component.species_key || component.id || component.name)}`
        : `text:${normalizeSpeciesToken(displayName)}`;
      if (!displayName || seen.has(identity)) return;
      seen.add(identity);
      ordered.push(displayName);
    });

  return ordered.length > 0 ? ordered : fallback;
};

// Builds a MANUAL_ENTRY_CONFIG-shaped object on the fly for an arbitrary
// species list, instead of requiring a hand-written preset.
//
// FIX (Madhura, 17-Jul-2026 call — "your enter manually should look like
// this for PFR... inlet and outlet composition"): this previously ALWAYS
// returned the 'timeseries' (batch: Time + single composition-row) shape,
// regardless of which reactor type the user had selected. For a custom
// reaction run as a PFR, that's wrong — a PFR run is a single steady-state
// point defined by inlet AND outlet compositions (plus P, T, tau), not a
// time-series. This now branches on `reactorMode` and, for 'pfr', builds
// the same inlet/outlet shape the built-in AA preset uses, so a brand-new
// custom system gets the correct manual-entry table for its reactor type.
const buildDynamicManualEntryConfig = (speciesList, reactorMode = 'batch', label = null) => {
  if (reactorMode === 'pfr') {
    const inletFields  = speciesList.map((_, i) => `in${i + 1}`);
    const outletFields = speciesList.map((_, i) => `out${i + 1}`);
    return {
      label: label || 'Manual Data Entry – PFR',
      type: 'pfr',
      csvHeader:   ['Pressure', 'T', 'tau', ...speciesList.map(sp => `${sp}In`), ...speciesList.map(sp => `${sp}Out`)],
      // 24-Aug-2026: header now names the unit explicitly (Madhura doc,
      // item 1 — "if you mention P, you must mention the units") and the
      // app standardizes on psi as the one Pressure unit for this column
      // (see generic_kinetics_objective.py's PSI_TO_PA conversion / the
      // Reaction Pressure Unit field below, now fixed to psi).
      displayCols: ['P (psi)', 'T (°C)', 'τ (s)', ...speciesList.map(sp => `${sp} in`), ...speciesList.map(sp => `${sp} out`)],
      fields:       ['Pressure', 'T', 'tau', ...inletFields, ...outletFields],
      inletFields,
      outletFields,
      templateRows: [], // no representative sample data exists for a user-defined system
    };
  }

  const compositionFields = speciesList.map((_, i) => `c${i + 1}`);
  return {
    label: label || 'Manual Data Entry – Batch',
    type: 'timeseries',
    csvHeader:   ['Time (min)', ...speciesList.map(sp => `x-${sp}`)],
    displayCols: ['Time (min)', ...speciesList.map(sp => `x-${sp}`)],
    fields:            ['time', ...compositionFields],
    compositionFields,
    templateRows: [], // no representative sample data exists for a user-defined system
  };
};

// Single entry point every call site should use instead of indexing
// MANUAL_ENTRY_CONFIG[system] directly. If the caller's derived species
// exactly match one of the 3 built-in presets (e.g. the MeOAc/DME Example
// was loaded and untouched), keep using that preset's config — same labels,
// same representative template rows as before. Otherwise (a custom or
// edited reaction), build the config dynamically from the real species AND
// the currently selected reactor mode ('batch' | 'pfr'), so the manual-entry
// shape (time-series vs. inlet/outlet) matches the reactor actually in use.
const getManualEntryConfig = (system, activeSpecies, reactorMode = 'batch') => {
  if (system === 'acetic_acid') return MANUAL_ENTRY_CONFIG.acetic_acid;

  const preset = MANUAL_ENTRY_CONFIG[system];
  const presetSpecies = preset ? preset.displayCols.slice(1).map(c => c.replace(/^x-/, '')) : [];
  const matchesPreset = preset
    && reactorMode === 'batch' // built-in MeOAc/DME presets are batch-only
    && activeSpecies.length === presetSpecies.length
    && activeSpecies.every(sp => presetSpecies.includes(sp));

  return matchesPreset ? preset : buildDynamicManualEntryConfig(activeSpecies, reactorMode);
};

// Inline, live balance badge — drop directly under a Reactants/Products row
// so the user gets ✓/✕ feedback while typing (not just in the Appendix tab).
const InlineBalanceBadge = ({
  reactants,
  products,
  registeredComponents = [],
  reactantScope = '',
  productScope = '',
  speciesIdentityMap = {},
}) => {
  const check = React.useMemo(
    () => checkReactionBalance(
      reactants,
      products,
      registeredComponents,
      reactantScope,
      productScope,
      speciesIdentityMap,
    ),
    [reactants, products, registeredComponents, reactantScope, productScope, speciesIdentityMap]
  );

  if (check.incomplete) return null;

  if (check.ambiguousSpecies?.length > 0 || check.unresolvedSpecies.length > 0) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, marginTop: 4,
        fontSize: 13, fontFamily: font.ui, color: C.textDim,
      }}>
        <span>ℹ</span>
        <span>
          {check.ambiguousSpecies?.length > 0
            ? <>Select the intended compound for: {check.ambiguousSpecies.join(', ')} — formula alone is ambiguous.</>
            : <>Unknown species for balance check: {check.unresolvedSpecies.join(', ')} — add to Component Registry to enable full validation.</>}
        </span>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, marginTop: 4,
      padding: '3px 8px', borderRadius: 4, width: 'fit-content',
      fontSize: 13, fontWeight: 600, fontFamily: font.ui,
      color: check.balanced ? C.success : C.error,
      background: check.balanced ? C.successDim : C.errorDim,
      border: `1px solid ${check.balanced ? C.success : C.error}44`,
    }}>
      <span>{check.balanced ? '✓' : '✕'}</span>
      <span>
        {check.balanced
          ? 'Equation balanced'
          : check.unbalancedAtoms.join('; ')}
      </span>
    </div>
  );
};

// 16-Sep-2026 ADD — Action item #2 (call tracker, 10-Sep, and the reaction/
// Keq area Madhura circled in red on the Model Input screenshot): "Display
// component names below the reaction formula (reactants/products) so users
// can see and edit labels instead of only the formula." Until now the
// reaction boxes (Components & Stoichiometry) showed only the raw formula
// tokens the user typed (e.g. "C3H8 + O2") — ComponentRegistry/
// speciesDisplayName already resolve real names everywhere downstream
// (Graph/Parameters/Results/Compare Fits — see the NIST_SPECIES_DB comment
// above SpeciesAutocompleteInput), but there was no editable name shown at
// the point where names actually matter most: right under the equation
// where isomers/ambiguous formulas get typed in the first place. This
// component renders one small "<typed token> → <canonical name> <formula>"
// row per species. It uses the exact species_key selected in the autocomplete
// so ambiguous formulas never get silently mapped to an arbitrary compound.
// This section is intentionally read-only for catalog-backed species; the
// editable input is not the identity mechanism and must not create a duplicate
// user component when a user searches by an alias/common name.
const ReactionSpeciesNames = ({
  reactants,
  products,
  reactantScope,
  productScope,
  registeredComponents = [],
  speciesIdentityMap = {},
  // 23-Oct-2026 ADD (Madhura, 21-Oct call ~17:26): once the reactions are
  // entered and the components confirmed, the table is no longer needed and
  // pushes the next reaction off-screen. `collapsed` / `onToggleCollapsed`
  // are optional so any other caller keeps the old always-open behaviour.
  collapsed = false,
  onToggleCollapsed = null,
}) => {
  const sideRows = React.useMemo(() => {
    const collect = (str, scope) => String(str || '').split('+').map((term, tokenIdx) => {
      const raw = term.trim();
      if (!raw) return null;
      const m = raw.match(/^(\d+(?:\.\d+)?)\s*(.*)$/);
      const token = (m ? m[2] : raw).trim();
      const speciesKey = getIdentityForToken(speciesIdentityMap, scope, tokenIdx);
      const selected = speciesKey ? componentBySpeciesKey(speciesKey, registeredComponents) : null;
      const candidates = findSpeciesCandidates(token, registeredComponents);
      return { scope, tokenIdx, token, speciesKey, selected, candidates };
    }).filter(Boolean);
    return [...collect(reactants, reactantScope), ...collect(products, productScope)];
  }, [reactants, products, reactantScope, productScope, speciesIdentityMap, registeredComponents]);

  if (sideRows.length === 0) return null;

  const nameOf = row => {
    if (row.selected?.name) return row.selected.name;
    if (row.candidates.length === 1) return row.candidates[0].name;
    if (row.candidates.length > 1) return 'Multiple matches — select from the dropdown';
    return 'Not found in catalog';
  };
  const formulaOf = row => {
    if (row.selected?.formula) return row.selected.formula;
    if (row.candidates.length === 1 && row.candidates[0]?.formula) return row.candidates[0].formula;
    return toAsciiDigits(row.token);
  };

  const toggleHeader = onToggleCollapsed ? (
    <button type="button" onClick={onToggleCollapsed} aria-expanded={!collapsed}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
        padding: '2px 0', marginBottom: collapsed ? 0 : 4, cursor: 'pointer',
        fontSize: 11, fontWeight: 600, fontFamily: font.ui, color: C.textMuted,
        textTransform: 'uppercase', letterSpacing: '0.05em',
      }}>
      <span style={{ fontSize: 10 }}>{collapsed ? '\u25B8' : '\u25BE'}</span>
      <span>{collapsed ? `Show components (${sideRows.length})` : 'Hide components'}</span>
    </button>
  ) : null;

  return (
    <div style={{ marginTop: 6 }}>
      {toggleHeader}
      {!collapsed && (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', fontFamily: font.ui }}>
        <thead>
          <tr style={{ background: C.panel }}>
            {['Side', 'Typed', 'Resolved name', 'Formula', 'CAS'].map(h => (
              <th key={h} style={{ padding: '3px 8px', color: C.textMuted,
                borderBottom: `1px solid ${C.border}`, textAlign: 'left',
                fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sideRows.map((row, i) => {
            const ambiguous = row.candidates.length > 1 && !row.selected;
            const notFound = row.candidates.length === 0 && !row.selected;
            const rowBg = ambiguous ? C.warningDim : notFound ? C.errorDim : (i % 2 ? C.card : C.surface);
            return (
              <tr key={`${row.scope}-${row.tokenIdx}-${i}`} style={{ background: rowBg }}>
                <td style={{ padding: '3px 8px', color: C.textDim, fontSize: 11, whiteSpace: 'nowrap' }}>
                  {row.scope === reactantScope ? 'Reactant' : 'Product'}
                </td>
                <td style={{ padding: '3px 8px', color: C.textDim, whiteSpace: 'nowrap' }}>
                  {toSubscriptFormula(row.token)}
                </td>
                <td style={{ padding: '3px 8px', color: ambiguous ? C.warning : notFound ? C.error : C.text,
                  fontWeight: 600 }}>
                  {nameOf(row)}
                </td>
                <td style={{ padding: '3px 8px' }}>
                  {!ambiguous && !notFound && (
                    <span style={{
                      color: C.purple, fontFamily: font.mono, fontSize: 11,
                      background: `${C.purple}18`, borderRadius: 3, padding: '1px 5px',
                    }}>{formulaOf(row)}</span>
                  )}
                </td>
                <td style={{ padding: '3px 8px', color: C.textDim, fontFamily: font.mono, fontSize: 11 }}>
                  {/* Show CAS for a uniquely matched catalog component even if
                      the user typed its name instead of clicking the dropdown.
                      Ambiguous identities still require an explicit selection. */}
                  {row.selected?.cas || (row.candidates.length === 1 ? row.candidates[0]?.cas : null) || '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
      )}
    </div>
  );
};

const StoichiometryValidator = ({
  reactions,
  registeredComponents,
  speciesIdentityMap = {},
}) => {
  const results = reactions.map((rxn, i) => {
    const { reactants: reactantStr = '', products: productStr = '' } = rxn;
    const reactants = parseStoicSide(reactantStr);
    const products  = parseStoicSide(productStr);

    const check = checkReactionBalance(
      reactantStr,
      productStr,
      registeredComponents,
      `r${i + 1}.reactants`,
      `r${i + 1}.products`,
      speciesIdentityMap,
    );

    // Mole check (informational — kept from the original validator)
    const reactantMoles = Object.values(reactants).reduce((a, b) => a + b, 0);
    const productMoles  = Object.values(products).reduce((a, b) => a + b, 0);
    const moleChange = productMoles - reactantMoles;

    return {
      rxnIndex: i + 1,
      equation: `${reactantStr || '…'} → ${productStr || '…'}`,
      balanced: check.balanced,
      unbalancedAtoms: check.unbalancedAtoms,
      unresolvedSpecies: check.unresolvedSpecies,
      moleChange,
      reactantMoles,
      productMoles,
    };
  });

  return (
    <SectionBox title="Stoichiometric Balance Validator"
      badge={results.every(r => r.balanced) ? 'Balanced' : `${results.filter(r => !r.balanced).length} unbalanced`}
      badgeColor={results.every(r => r.balanced) ? C.success : C.error}
      info="Atom balance is computed using MW/formula data from the Component Registry. Add components above to enable element-level validation.">
      {results.map(r => (
        <div key={r.rxnIndex} style={{ marginBottom: 10, border: `1px solid ${r.balanced ? C.success : C.error}44`,
          borderLeft: `3px solid ${r.balanced ? C.success : C.error}`,
          borderRadius: 4, padding: '8px 12px', background: r.balanced ? C.successDim : C.errorDim }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.text, fontFamily: font.ui }}>
              Rxn {r.rxnIndex}: {r.equation}
            </span>
            <Badge color={r.balanced ? C.success : C.error}>{r.balanced ? '✓ Balanced' : '✕ Unbalanced'}</Badge>
          </div>
          {!r.balanced && r.unbalancedAtoms.length > 0 && (
            <div style={{ fontSize: 13, color: C.error, fontFamily: font.ui }}>
              {r.unbalancedAtoms.join('. ')}.
            </div>
          )}
          <div style={{ fontSize: 13, color: C.textDim, fontFamily: font.ui, marginTop: 3 }}>
            Moles: {r.reactantMoles} → {r.productMoles}
            {' '}({r.moleChange > 0 ? `+${r.moleChange.toFixed(2)} expansion` : r.moleChange < 0 ? `${r.moleChange.toFixed(2)} contraction` : 'no change'})
          </div>
        </div>
      ))}
      {registeredComponents.length === 0 && (
        <div style={{ fontSize: 13, color: C.textDim, fontFamily: font.ui, marginTop: 6 }}>
          ℹ Add components to the registry above to enable atom-level balance validation.
        </div>
      )}
    </SectionBox>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// REQUIREMENT 4 — AA MULTI-CSV GLOBAL FITTING PANEL
// ═══════════════════════════════════════════════════════════════════════════
const AAGlobalRunsPanel = ({ aaGlobalRuns, setAaGlobalRuns }) => {
  const addRun = () => setAaGlobalRuns(prev => [
    ...prev,
    { id: Date.now(), file: null, fileName: '', T_C: 293, tau_s: 3.0, P_psi: 240, weight: 1.0 }
  ]);
  const removeRun = (id) => setAaGlobalRuns(prev => prev.filter(r => r.id !== id));
  const updateRun = (id, key, val) =>
    setAaGlobalRuns(prev => prev.map(r => r.id === id ? { ...r, [key]: val } : r));
  const handleRunFile = async (id, e) => {
    const raw = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same filename later
    if (!raw) return;
    try {
      const f = await convertSpreadsheetToCsvFile(raw);
      updateRun(id, 'file', f);
      updateRun(id, 'fileName', f.name);
    } catch (err) {
      alert(err.message || `Could not read "${raw.name}".`);
    }
  };

  return (
    <SectionBox title="AA Global Multi-CSV Fitting" badge={`${aaGlobalRuns.length} runs`} badgeColor={C.accent}
      info="Upload one CSV per operating condition (T, P, τ). All runs share Af and Ea — only kf is varied per run. This is the AA analogue of the MeOAc/DME global multi-temperature fit.">
      <Btn variant="secondary" style={{ fontSize: 13, marginBottom: 8 }} onClick={addRun}>
        + Add AA Run
      </Btn>
      {aaGlobalRuns.length === 0 && (
        <Alert type="info">Click "+ Add AA Run" to add CSV files for global AA fitting.</Alert>
      )}
      <div style={{ maxHeight: 360, overflowY: 'auto', paddingRight: 2 }}>
        {aaGlobalRuns.map((run, idx) => (
          <div key={run.id} style={{ border: `1px solid ${C.border}`, borderRadius: 5,
            padding: '8px 10px', marginBottom: 8, background: C.surface }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#E65100', fontFamily: font.ui, minWidth: 16 }}>
                {idx + 1}
              </span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                background: '#E6510011', border: '1px solid #E6510044', borderRadius: 4,
                padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: '#E65100',
                fontWeight: 600, fontFamily: font.ui }}>
                📂 {run.fileName || 'Upload AA CSV'}
                <input type="file" accept={CSV_UPLOAD_ACCEPT} style={{ display: 'none' }}
                  onChange={e => handleRunFile(run.id, e)} />
              </label>
              {run.fileName && <Badge color={C.success}>●</Badge>}
              <div style={{ flex: 1 }} />
              <button onClick={() => removeRun(run.id)} title="Remove this data set" aria-label="Remove this data set" style={removeXButtonStyle}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 0.8fr', gap: 6 }}>
              <Field label="T (°C)">
                <Input type="number" value={run.T_C} step="1"
                  onChange={e => updateRun(run.id, 'T_C', parseFloat(e.target.value))} />
              </Field>
              <Field label="τ (s)">
                <Input type="number" value={run.tau_s} step="0.1"
                  onChange={e => updateRun(run.id, 'tau_s', parseFloat(e.target.value))} />
              </Field>
              <Field label="P (psi)">
                <Input type="number" value={run.P_psi} step="1"
                  onChange={e => updateRun(run.id, 'P_psi', parseFloat(e.target.value))} />
              </Field>
              <Field label="Weight">
                <Input type="number" value={run.weight} step="0.1" min="0"
                  onChange={e => updateRun(run.id, 'weight', parseFloat(e.target.value))} />
              </Field>
            </div>
          </div>
        ))}
      </div>
      {aaGlobalRuns.length >= 2 && (
        <Alert type="success">
          {aaGlobalRuns.filter(r => r.file).length}/{aaGlobalRuns.length} CSV files uploaded.
          {aaGlobalRuns.filter(r => r.file).length >= 2
            ? ' Ready to run global AA fitting.'
            : ' Upload at least 2 CSVs to enable global fit.'}
        </Alert>
      )}
    </SectionBox>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// REQUIREMENT 6 — ASPEN EXPORT DOCUMENTATION PANEL
// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
// PRIMITIVE UI COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

const Label = ({ children, sub, info, guideHref, required, compact, wrap }) => (
  <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.01em', textTransform: 'uppercase',
    color: C.textMuted, marginBottom: compact ? 2 : 4, fontFamily: font.ui,
    display: 'flex', alignItems: wrap ? 'flex-start' : 'center', gap: 4, flexWrap: 'wrap', minWidth: 0 }}>
    {/* [FIX] client doc (Sept-2026): "Headings for Initial Charge, Reactor
        Volume, and Catalyst Weight must be seen in their entirety, along
        with the units. They shouldn't run over like here." — the ellipsis
        truncation below is exactly right for tight single-line labels
        elsewhere in the app, but for a few labels in cramped grids
        (Multi-Run Manager) it was cutting off the unit entirely with no
        way to see it except a hover tooltip. `wrap` opts a label into
        wrapping onto a second line in full instead of truncating; every
        existing call site is unaffected since `wrap` defaults to off. */}
    <span style={wrap
      ? { whiteSpace: 'normal', wordBreak: 'break-word', minWidth: 0, lineHeight: 1.25 }
      : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
      title={!wrap && typeof children === 'string' ? children : undefined}>
      {children}
    </span>
    {required && <span style={{ color: C.error, fontSize: 13, flexShrink: 0 }}>*</span>}
    {sub && <span style={{ fontSize: 13, fontWeight: 400, textTransform: 'none',
      color: C.textDim, marginLeft: 2 }}>{sub}</span>}
    {info && <InfoTooltip text={info} guideHref={guideHref} size={12} />}
  </div>
);

const Field = ({ label, sub, info, guideHref, children, style, required, compact, wrap }) => (
  <div style={{ marginBottom: compact ? 1 : 3, ...style }}>
    {label && <Label sub={sub} info={info} guideHref={guideHref} required={required} compact={compact} wrap={wrap}>{label}</Label>}
    {children}
  </div>
);

const Input = ({ style, compact, ...props }) => (
  <input style={{
    background: C.inputBg, border: `1px solid ${C.inputBorder}`, borderRadius: 4,
    color: C.text, fontSize: 14, fontFamily: font.ui, padding: compact ? '2px 8px' : '4px 8px',
    width: '100%', transition: 'border-color 0.12s, box-shadow 0.12s', ...style,
  }}
  onFocus={e => { e.target.style.borderColor = C.accent; e.target.style.boxShadow = `0 0 0 2px ${C.accentGlow}`; }}
  onBlur={e => { e.target.style.borderColor = C.inputBorder; e.target.style.boxShadow = 'none'; }}
  {...props} />
);

// ─── Stepper — +/- numeric input, per the "Batch Parameters" reference mockup ──
const Stepper = ({ value, onChange, step = 1, min, max, unit, style }) => {
  const num = parseFloat(value);
  const safe = Number.isFinite(num) ? num : 0;
  const clamp = (v) => {
    if (min !== undefined) v = Math.max(min, v);
    if (max !== undefined) v = Math.min(max, v);
    return v;
  };
  const bump = (dir) => onChange({ target: { value: String(clamp(+(safe + dir * step).toFixed(6))) } });
  return (
    <div style={{
      display: 'flex', alignItems: 'center', background: C.inputBg,
      border: `1px solid ${C.inputBorder}`, borderRadius: 6, overflow: 'hidden', width: '100%',
      ...style,
    }}>
      <button type="button" onClick={() => bump(-1)} aria-label="Decrease" style={{
        width: 28, height: 30, flexShrink: 0, border: 'none', background: 'transparent',
        color: C.textMuted, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: font.ui,
      }}>−</button>
      <input type="number" value={value} step={step} min={min} max={max}
        className="df-stepper-input"
        onChange={e => onChange({ target: { value: e.target.value } })}
        style={{
          flex: 1, minWidth: 0, textAlign: 'center', background: 'transparent', border: 'none',
          color: C.text, fontSize: 14, fontFamily: font.ui, padding: '5px 2px', outline: 'none',
        }} />
      {unit && <span style={{ fontSize: 12, color: C.textDim, paddingRight: 6, fontFamily: font.ui, flexShrink: 0 }}>{unit}</span>}
      <button type="button" onClick={() => bump(1)} aria-label="Increase" style={{
        width: 28, height: 30, flexShrink: 0, border: 'none', background: 'transparent',
        color: C.textMuted, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: font.ui,
      }}>+</button>
    </div>
  );
};

// ─── Temperature slider — thermometer icon + range track, per reference mockup ──
// Action item #11 (call tracker, 02-Sep — Suresh's suggestion): the slider
// alone couldn't land on a precise value (drag increments aren't exact 1°),
// so Madhura had trouble hitting a specific temperature. An always-visible
// numeric entry box sits next to the readout for exact entry, in addition to
// the existing drag track; the slider stays as-is for quick coarse
// adjustment. (Kept always-visible rather than hidden behind a
// double-click, so the manual-entry provision is discoverable without a
// hidden gesture.)
const TemperatureSlider = ({ value, onChange, min = 0, max = 1000, unit = '°C', step = 1 }) => {
  const num = Number.isFinite(parseFloat(value)) ? parseFloat(value) : min;
  const pct = Math.max(0, Math.min(100, ((num - min) / (max - min)) * 100));
  const [draft, setDraft] = useState(String(num));

  // Keep the manual entry box in sync when the value changes from outside
  // (e.g. the slider is dragged, or a parent resets the value).
  useEffect(() => { setDraft(String(num)); }, [num]);

  const commit = (raw) => {
    const parsed = parseFloat(raw);
    if (!Number.isFinite(parsed)) { setDraft(String(num)); return; }
    const clamped = Math.max(min, Math.min(max, parsed));
    onChange({ target: { value: String(clamped) } });
    setDraft(String(clamped));
  };

  return (
    <div style={{ minWidth: 0 }}>
      {/* [FIX] Alignment overlap: once Temperature sits side-by-side with
          Reaction Pressure (half-width column), the label + manual-entry
          box no longer have room on one line and were spilling past the
          column edge into "REACTION PRESSURE". flexWrap lets the entry
          box drop to its own line instead of overlapping; minWidth: 0
          lets the label itself shrink/ellipsize rather than forcing the
          row wider than its column. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap', rowGap: 4, minWidth: 0 }}>
        <span style={{ fontSize: 13, flexShrink: 0 }}>🌡️</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase',
          letterSpacing: '0.06em', fontFamily: font.ui, whiteSpace: 'nowrap', overflow: 'hidden',
          textOverflow: 'ellipsis', minWidth: 0 }}>Temperature</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          <input
            type="number"
            className="df-stepper-input"
            value={draft}
            min={min}
            max={max}
            step={step}
            aria-label="Temperature (manual entry)"
            onChange={e => setDraft(e.target.value)}
            onBlur={e => commit(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { commit(e.target.value); e.target.blur(); }
              if (e.key === 'Escape') { setDraft(String(num)); e.target.blur(); }
            }}
            style={{
              width: 56, textAlign: 'right', background: C.inputBg,
              border: `1px solid ${C.inputBorder}`, borderRadius: 4,
              color: C.toggle, fontSize: 13, fontWeight: 700, fontFamily: font.ui,
              padding: '2px 5px', outline: 'none',
            }}
            onFocus={e => { e.target.style.borderColor = C.accent; }}
          />
          <span style={{ fontSize: 13, fontWeight: 700, color: C.toggle, fontFamily: font.ui }}>{unit}</span>
        </div>
      </div>
      <div style={{ position: 'relative', height: 22, display: 'flex', alignItems: 'center' }}>
        <div style={{ position: 'absolute', left: 0, right: 0, height: 4, borderRadius: 999, background: C.border }} />
        <div style={{ position: 'absolute', left: 0, width: `${pct}%`, height: 4, borderRadius: 999, background: C.toggle }} />
        <input type="range" min={min} max={max} step={step} value={num}
          onChange={e => { onChange({ target: { value: e.target.value } }); setDraft(e.target.value); }}
          style={{
            position: 'relative', width: '100%', margin: 0, WebkitAppearance: 'none',
            background: 'transparent', cursor: 'pointer', zIndex: 1,
          }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.textDim, fontFamily: font.ui }}>
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
};

const Select = ({ style, children, ...props }) => (
  <select style={{
    background: C.inputBg, border: `1px solid ${C.inputBorder}`, borderRadius: 4,
    color: C.text, fontSize: 14, fontFamily: font.ui, padding: '5px 8px',
    width: '100%', cursor: 'pointer', appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%239e6878'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', paddingRight: 24,
    transition: 'border-color 0.12s', ...style,
  }}
  onFocus={e => e.target.style.borderColor = C.accent}
  onBlur={e => e.target.style.borderColor = C.inputBorder}
  {...props}>
    {children}
  </select>
);

const Btn = ({ variant = 'primary', style, children, disabled, ...props }) => {
  const v = {
    primary:   { background: C.accent, color: '#fff', border: 'none', boxShadow: disabled ? 'none' : `0 2px 8px ${C.accent}55` },
    secondary: { background: 'transparent', color: C.accent, border: `2px solid ${C.accent}` },  // feedback 1c: thicker border
    ghost:     { background: C.surface, color: C.textMid, border: `1.5px solid ${C.border}` },  // feedback 1c: visible bg
    danger:    { background: 'transparent', color: C.error, border: `1.5px solid ${C.error}` },
    success:   { background: C.success, color: '#fff', border: 'none', boxShadow: disabled ? 'none' : `0 2px 8px ${C.success}55` },
    run: {
        background: cssVar('--color-accent'),
        color: '#fff',
        border: 'none',
        boxShadow: disabled ? 'none' : '0 3px 12px var(--color-accent-glow)'
      }
  };
  return (
    <button disabled={disabled} style={{
      ...v[variant], borderRadius: 6, padding: '8px 16px', fontSize: 14,
      fontWeight: 600, fontFamily: font.ui, cursor: disabled ? 'not-allowed' : 'pointer',
      letterSpacing: '0.02em', transition: 'all 0.12s', whiteSpace: 'nowrap',
      opacity: disabled ? 0.45 : 1, ...style,
    }} {...props}>
      {children}
    </button>
  );
};

// Client: the remove "X" for an uploaded data set was too small to notice. One shared,
// larger, bordered button is used for every data-set remove control (the single
// data set row and the Acetic Acid / MeOAc-DME / custom multi-dataset lists) so they all
// look and sit the same: at the far end of the row.
const removeXButtonStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 30, height: 30, flexShrink: 0, marginLeft: 'auto', padding: 0,
  background: C.errorDim, color: C.error, border: `1px solid ${C.error}66`, borderRadius: 6,
  fontSize: 20, fontWeight: 700, lineHeight: 1, cursor: 'pointer',
};

const Badge = ({ color = C.accent, children, style }) => (
  <span style={{
    background: color + '22', color, border: `1px solid ${color}44`,
    borderRadius: 3, fontSize: 13, fontWeight: 700, padding: '1px 6px',
    letterSpacing: '0.07em', textTransform: 'uppercase', fontFamily: font.ui,
    whiteSpace: 'nowrap', flexShrink: 0, ...style,
  }}>{children}</span>
);

const Chip = ({ color = C.accent, children, active, onClick }) => (
  <button onClick={onClick} style={{
    background: active ? color + '22' : C.surface, border: `1px solid ${active ? color : C.border}`,
    color: active ? color : C.textMuted, borderRadius: 4, padding: '3px 9px', fontSize: 12,
    fontFamily: font.ui, cursor: 'pointer', fontWeight: active ? 700 : 500, transition: 'all 0.12s',
  }}>
    {children}
  </button>
);

const Divider = ({ label, style }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '5px 0', ...style }}>
    <div style={{ flex: 1, height: 1, background: C.border }} />
    {label && <><span style={{ fontSize: 12, color: C.textDim, fontFamily: font.ui,
      textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
    <div style={{ flex: 1, height: 1, background: C.border }} /></>}
  </div>
);

const Alert = ({ type = 'info', children, style }) => {
  const map = {
    success: { c: C.success,   bg: C.successDim, icon: '✓' },
    warning: { c: cssVar('--color-warning-dark'),   bg: C.warningDim, icon: '⚠' },
    error:   { c: C.error,     bg: C.errorDim,   icon: '✕' },
    info:    { c: C.cyan,      bg: C.cyanDim,    icon: 'ℹ' },
  };
  const { c, bg, icon } = map[type] || map.info;
  return (
    <div style={{ background: bg, border: `1px solid ${c}44`, borderLeft: `3px solid ${c}`,
      borderRadius: 4, padding: '6px 10px', marginBottom: 8,
      display: 'flex', gap: 7, alignItems: 'flex-start', ...style }}>
      <span style={{ color: c, fontSize: 12, fontWeight: 800, marginTop: 1, flexShrink: 0 }}>{icon}</span>
      <span style={{ fontSize: 12, color: C.textMid, fontFamily: font.ui, lineHeight: 1.5 }}>{children}</span>
    </div>
  );
};



// ─── InfoTooltip — small ⓘ icon button ────────────────────────────────────
// Replaces inline explanatory paragraphs throughout Input / Model Input.
// Click OR hover reveals a short popover; "Learn more" deep-links into the
// Help/User Guide (guideHref). Target users already know kinetic modeling,
// so the popover text should stay to 1-2 sentences — anything longer belongs
// in the guide, not here.
const InfoTooltip = ({ text, guideHref, size = 14 }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const [pos, setPos] = useState(null);

  const place = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = 280;
    // Default: open below-left of the icon, but clamp so it never runs off
    // either edge of the viewport (this is what was getting clipped before,
    // since the popup used to live inside SectionBox's overflow:hidden box).
    let left = r.left;
    if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
    if (left < 8) left = 8;
    setPos({ top: r.bottom + 6, left, width });
  };

  const openTooltip = () => { place(); setOpen(true); };
  const closeTooltip = () => setOpen(false);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const onScrollOrResize = () => place();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={openTooltip} onMouseLeave={closeTooltip}>
      <button ref={btnRef} type="button" aria-label="More info"
        onClick={() => (open ? closeTooltip() : openTooltip())}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: size, height: size, borderRadius: '50%', border: `1px solid ${C.textDim}`,
          background: 'transparent', color: C.textMuted, fontSize: size * 0.72, fontWeight: 700,
          lineHeight: 1, cursor: 'pointer', padding: 0, fontFamily: font.ui, flexShrink: 0,
        }}>
        i
      </button>
      {open && pos && createPortal(
        <div style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6,
          padding: '8px 10px', fontSize: 12, color: C.text, lineHeight: 1.5, fontFamily: font.ui,
          boxShadow: `0 8px 28px rgba(0,0,0,0.35)` }}>
          {text}
          {guideHref && (
            <a href={guideHref} target="_blank" rel="noreferrer"
              style={{ display: 'block', marginTop: 6, color: C.accent, fontSize: 13, fontWeight: 600 }}>
              Learn more in the User Guide →
            </a>
          )}
        </div>,
        document.body
      )}
    </span>
  );
};

// ─── Theme toggle — dark (default) / light ────────────────────────────────
const ThemeToggle = ({ theme, setTheme }) => (
  <button type="button" onClick={() => setTheme(t => (t === 'dark' ? 'light' : 'dark'))}
    title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px',
      borderRadius: 999, border: `1px solid ${C.border}`, background: C.panel,
      color: C.textMid, fontSize: 12, fontFamily: font.ui, cursor: 'pointer',
    }}>
    <span style={{ fontSize: 13 }}>{theme === 'dark' ? '🌙' : '☀️'}</span>
    {theme === 'dark' ? 'Dark' : 'Light'}
  </button>
);

// ─── Session timeout — idle timer + "stay logged in?" prompt ─────────────
// Duration is 60 min, resets on any click/keypress/input change. After the
// idle threshold, a 1-minute grace window is shown before auto-logout.
//
// ROOT-CAUSE FIX (client report: "did not log me out after 1 hour" / clicking
// "Stay logged in" 3-4 hours later still logged the user back in):
// the previous implementation relied purely on `setTimeout(fn, 60*60*1000)`
// and `setTimeout(fn, 60*1000)`. Browsers throttle timers in backgrounded /
// inactive tabs (often to ~once/minute, sometimes much less for long delays),
// so neither the 60-min idle timer nor the 60-sec grace timer reliably fired
// on schedule — the idle prompt could sit open, un-expired, for hours.
// Fix: track a wall-clock `lastActivityAt` timestamp and re-check elapsed
// time (Date.now() - lastActivityAt) on a short recurring interval AND on
// `visibilitychange` (when the tab regains focus). This is immune to timer
// throttling because the check is always against real elapsed time, not
// against whether a scheduled callback happened to fire on time.
const IDLE_TIMEOUT_MS  = 60 * 60 * 1000;   // 60 min of inactivity before the warning
const IDLE_GRACE_MS    = 60 * 1000;        // 1 min to respond before auto-logout
const IDLE_CHECK_MS    = 2 * 1000;         // how often we re-check elapsed time

const useIdleSessionTimer = (onLogout) => {
  const [showPrompt, setShowPrompt]   = useState(false); // pre-logout "Session Expiring" popup
  const [loggedOut, setLoggedOut]     = useState(false); // post-logout "Logged out" popup
  const lastActivityAt = useRef(Date.now());
  const graceStartedAt = useRef(null);
  const showPromptRef  = useRef(false);
  const loggedOutRef   = useRef(false);

  const markActivity = React.useCallback(() => {
    // Ignore activity once the grace prompt is showing — the user must
    // explicitly choose "Stay Logged In" or "Log Out" at that point —
    // and ignore it entirely once logged out.
    if (showPromptRef.current || loggedOutRef.current) return;
    lastActivityAt.current = Date.now();
  }, []);

  useEffect(() => {
    const events = ['click', 'keydown', 'input', 'mousemove', 'scroll'];
    events.forEach(ev => window.addEventListener(ev, markActivity, { passive: true }));

    const tick = () => {
      if (loggedOutRef.current) return;
      const now = Date.now();
      if (!showPromptRef.current) {
        if (now - lastActivityAt.current >= IDLE_TIMEOUT_MS) {
          showPromptRef.current = true;
          graceStartedAt.current = now;
          setShowPrompt(true);
        }
      } else {
        if (now - graceStartedAt.current >= IDLE_GRACE_MS) {
          showPromptRef.current = false;
          loggedOutRef.current = true;
          setShowPrompt(false);
          setLoggedOut(true);
          onLogout(); // clears token / server-side session immediately
        }
      }
    };

    const interval = setInterval(tick, IDLE_CHECK_MS);
    // Re-check immediately whenever the tab becomes visible again — this is
    // what actually fixes the "3-4 hours later" case: a backgrounded tab's
    // interval may itself be throttled, but the moment the user comes back
    // and the tab is visible again, we re-evaluate real elapsed time.
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      events.forEach(ev => window.removeEventListener(ev, markActivity));
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
    };
  }, [markActivity, onLogout]);

  const stayLoggedIn = () => {
    showPromptRef.current = false;
    setShowPrompt(false);
    lastActivityAt.current = Date.now();
  };

  const dismissLoggedOut = () => {
    // "Log Back In" — send the user to the login page.
    // BUG FIX (Shankar, 25-Aug): this pointed at '/login', which isn't a
    // registered route (App.js mounts <Login/> at '/', not '/login') — so
    // this was 404ing instead of taking the user back to sign in.
    window.location.href = '/';
  };

  return { showPrompt, loggedOut, stayLoggedIn, dismissLoggedOut, logoutNow: onLogout };
};

// Option 1 — Pop-up (Action Required): shown while the user is still logged
// in but must click a button to stay.
// ─── Reset Confirmation Modal ────────────────────────────────────────────────
// Replaces the browser's native window.confirm() (plain, unstyled, shows the
// raw "localhost:3000" chrome) with an in-app modal that matches the rest of
// the UI's colour language — accent-glow header ring, warning-tinted message
// card, and the same Btn variants used everywhere else in this page.
const ResetConfirmModal = ({ show, onConfirm, onCancel }) => {
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(20,10,25,0.6)', zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
        padding: 0, width: 400, maxWidth: '90vw', overflow: 'hidden',
        boxShadow: `0 24px 70px rgba(0,0,0,0.55), 0 0 0 1px ${C.accent}22`,
        fontFamily: font.ui, animation: 'slideIn 0.16s ease',
      }}>
        {/* Gradient header strip — ties together accent + warning + purple,
            the same trio used for badges/highlights elsewhere in this page. */}
        <div style={{
          height: 5,
          background: `linear-gradient(90deg, ${C.accent}, ${cssVar('--color-warning-dark')}, ${C.purple})`,
        }} />
        <div style={{ padding: '22px 24px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <div style={{
              width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
              background: `${cssVar('--color-warning-dark')}22`,
              border: `1.5px solid ${cssVar('--color-warning-dark')}55`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18,
            }}>
              ↺
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>
              Reset this page?
            </div>
          </div>
          <div style={{
            fontSize: 13, color: C.textMid, lineHeight: 1.6, marginBottom: 20,
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 8,
            padding: '10px 12px',
          }}>
            This clears all entered data on this page and starts a blank system,
            just like reloading the page. Continue?
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={onCancel} style={{ fontSize: 13 }}>Cancel</Btn>
            <Btn variant="primary" onClick={onConfirm}
              style={{ fontSize: 13, background: cssVar('--color-mode-lhhw'),
                boxShadow: `0 2px 8px ${cssVar('--color-warning-dark')}55` }}>
              ↺ Yes, Reset
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
};

const SessionTimeoutModal = ({ show, onStay, onLogout }) => {
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
        padding: 24, width: 360, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', fontFamily: font.ui }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 8 }}>
          Session Expiring
        </div>
        <div style={{ fontSize: 13, color: C.textMid, marginBottom: 18, lineHeight: 1.5 }}>
          You will be logged out in 1 minute due to inactivity.
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onLogout}>Log Out</Btn>
          <Btn variant="primary" onClick={onStay}>Stay Logged In</Btn>
        </div>
      </div>
    </div>
  );
};

// Option 2 — On-screen pop-up (Post-Log Out): shown once the 1-minute grace
// window has expired and logout has actually completed.
const LoggedOutModal = ({ show, onLogBackIn }) => {
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
        padding: 24, width: 340, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', fontFamily: font.ui,
        textAlign: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 18 }}>
          Logged out due to inactivity.
        </div>
        <Btn variant="primary" onClick={onLogBackIn} style={{ width: '100%' }}>Log Back In</Btn>
      </div>
    </div>
  );
};

// ─── Error ⓘ — cause + fix lookup ──────────────────────────────────────────
// Every error surface (token expired, run error, incomplete input, …) gets a
// small "i" in its corner. Extend this table with new error codes as needed
// rather than hard-coding explanations at each call site.
const ERROR_HELP = {
  token_expired: {
    cause: 'Your login session expired after a period of inactivity.',
    fix: 'Click "Stay logged in" next time the prompt appears, or simply log back in — your unsaved input may be lost.',
  },
  run_error: {
    cause: 'The fitting run failed before a valid result could be produced — usually because of invalid data, reaction setup, units, or a numerical convergence problem.',
    fix: 'Check the reaction stoichiometry, composition/pressure units, temperature/time ranges, and model settings. If the problem persists, reduce the number of free parameters and re-run.',
  },
  input_incomplete: {
    cause: 'One or more required fields have not been filled in yet.',
    fix: 'Check the Run Status ribbon — it lists exactly which fields are still missing.',
  },
  mole_fraction_sum: {
    cause: 'The mole/mass fractions in one or more data rows do not sum to 1.',
    fix: 'Re-check the composition values for the flagged row(s); include all components and inerts in the sum.',
  },
  stoichiometry_imbalance: {
    cause: 'The entered reaction is not balanced — the atom count for at least one element differs between reactants and products.',
    fix: 'Re-check the stoichiometric coefficients and component formulas in the Reaction Builder / Appendix panel.',
  },
};

const ErrorInfoButton = ({ code }) => {
  const [open, setOpen] = useState(false);
  const help = ERROR_HELP[code];
  if (!help) return null;
  return (
    <span style={{ position: 'relative', display: 'inline-flex', marginLeft: 6 }}>
      <button type="button" aria-label="Why did this happen?" onClick={() => setOpen(o => !o)}
        style={{
          width: 15, height: 15, borderRadius: '50%', border: `1px solid ${C.error}`,
          background: 'transparent', color: C.error, fontSize: 12, fontWeight: 800,
          lineHeight: 1, cursor: 'pointer', padding: 0, fontFamily: font.ui,
        }}>i</button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 10001,
          background: C.surface, border: `1px solid ${C.error}66`, borderRadius: 6,
          padding: '10px 12px', fontSize: 12, color: C.text, width: 260,
          boxShadow: '0 8px 28px rgba(0,0,0,0.4)', lineHeight: 1.5, fontFamily: font.ui }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Why this happened</div>
          <div style={{ marginBottom: 8, color: C.textMid }}>{help.cause}</div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>How to fix it</div>
          <div style={{ color: C.textMid }}>{help.fix}</div>
        </div>
      )}
    </span>
  );
};

// ─── ToggleSwitch — side-by-side pill toggle (replaces dot/circle radios) ────
// Used for any strictly two/three-option choice (Rate Basis, Phase, Reactor,
// Reversible/Irreversible, Power Law/LHHW, Exponent treatment, …).
// A sliding accent-filled pill sits behind the active label; the whole
// control is one continuous rounded track so both options read as a single
// "switch" rather than two separate buttons. Deliberately compact — no
// helper text under the control (any explanatory copy is a hover tooltip
// only) so it never costs more vertical space than a normal input row.
const ToggleSwitch = ({ label, sub, info, options, value, onChange, compact, fullWidth = true, activeColor }) => {
  const idx = Math.max(0, options.findIndex(o => o.value === value));
  const n = options.length;
  // Unified toggle-pill blue across the whole page (matches the reference
  // "Batch Parameters" Liquid/Impeller pills) — activeColor can still
  // override for special cases, but nothing does by default anymore.
  const col = activeColor || C.toggle;
  return (
    <Field label={label} sub={sub} info={info} style={{ marginBottom: 6 }}>
      <div style={{
        position: 'relative', display: 'flex', width: fullWidth ? '100%' : 'max-content',
        background: C.panel, border: `1px solid ${C.border}`, borderRadius: 999,
        padding: 2, userSelect: 'none',
      }}>
        <div aria-hidden style={{
          position: 'absolute', top: 2, bottom: 2,
          left: `calc(${(idx / n) * 100}% + 2px)`,
          width: `calc(${100 / n}% - 4px)`,
          background: col, borderRadius: 999,
          boxShadow: `0 1px 4px ${col}55`,
          transition: 'left 0.18s cubic-bezier(.4,0,.2,1), background 0.15s',
        }} />
        {options.map(o => {
          const isActive = o.value === value;
          return (
            <button key={o.value} type="button" onClick={() => onChange(o.value)}
              title={o.desc || undefined}
              style={{
                position: 'relative', zIndex: 1, flex: 1, minWidth: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                border: 'none', background: 'transparent', cursor: 'pointer',
                padding: compact ? '3px 5px' : '4px 8px', borderRadius: 999,
                fontSize: compact ? 11 : 12.5, fontWeight: isActive ? 700 : 500,
                fontFamily: font.ui, whiteSpace: 'nowrap', lineHeight: 1.15,
                textAlign: 'center', transition: 'color 0.15s',
                color: isActive ? '#fff' : C.textMid,
              }}>
              {o.icon && <span style={{ fontSize: compact ? 12 : 13, lineHeight: 1 }}>{o.icon}</span>}
              <span>{o.label}</span>
              {o.badge && <Badge color={o.color}>{o.badge}</Badge>}
            </button>
          );
        })}
      </div>
    </Field>
  );
};

// ─── Inert components — 2 discrete boxes (was 1 comma-separated field) ────
// `value` stays a comma-joined string so the rest of the app / payload
// shape doesn't need to change; this component just gives it a friendlier
// front end. Most users have 0-1 inerts, so 2 boxes cover the common case;
// a "+ Add inert" reveals a 3rd for the edge case.
const InertComponentsInput = ({ value, onChange }) => {
  const parts = (value || '').split(',').map(s => s.trim());
  const [a, b, c] = [parts[0] || '', parts[1] || '', parts[2] || ''];
  const [showThird, setShowThird] = useState(!!c);

  const emit = (na, nb, nc) => {
    const joined = [na, nb, nc].map(s => s.trim()).filter(Boolean).join(', ');
    onChange({ target: { value: joined } });
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: showThird ? '1fr 1fr 1fr' : '1fr 1fr', gap: 6, alignItems: 'end' }}>
      <Field label="Inert 1"><Input value={a} onChange={e => emit(e.target.value, b, c)} /></Field>
      <Field label="Inert 2"><Input value={b} onChange={e => emit(a, e.target.value, c)} /></Field>
      {showThird && (
        <Field label="Inert 3"><Input value={c} onChange={e => emit(a, b, e.target.value)} /></Field>
      )}
      {!showThird && (
        <Btn variant="ghost" style={{ fontSize: 13, gridColumn: '1 / -1', justifySelf: 'start' }}
          onClick={() => setShowThird(true)}>+ Add inert</Btn>
      )}
    </div>
  );
};

const RadioGroup = ({ label, sub, info, options, value, onChange, inline = true, compact, toggle }) => {
  // toggle=true renders as a side-by-side pill toggle instead of dot/circle
  // radios — used for both 2-way and short 3-way choices per the modernised
  // UI spec (Rate Basis, Phase, Reactor, Exponent treatment, …).
  if (toggle && options.length >= 2) {
    return <ToggleSwitch label={label} sub={sub} info={info} options={options} value={value} onChange={onChange} compact={compact} />;
  }
  return (
    <Field label={label} sub={sub} info={info}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: inline ? 14 : 5,
        flexDirection: inline ? 'row' : 'column' }}>
        {options.map(o => (
          <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
            fontSize: compact ? 12 : 13, color: value === o.value ? C.text : C.textMid,
            fontFamily: font.ui, userSelect: 'none' }}>
            <span onClick={() => onChange(o.value)} style={{
              width: 13, height: 13, borderRadius: '50%', flexShrink: 0,
              border: `2px solid ${value === o.value ? C.accent : C.textDim}`,
              background: value === o.value ? C.accent : 'transparent',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.12s', cursor: 'pointer',
              boxShadow: value === o.value ? `0 0 6px ${C.accent}66` : 'none' }}>
              {value === o.value && <span style={{ width: 4, height: 4, borderRadius: '50%',
                background: '#fff', display: 'block' }} />}
            </span>
            <span onClick={() => onChange(o.value)}>{o.label}</span>
            {o.badge && <Badge color={o.color}>{o.badge}</Badge>}
          </label>
        ))}
      </div>
    </Field>
  );
};

const TwoCol = ({ left, right, ratio = '1fr 1fr', gap = 10 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: ratio, gap, alignItems: 'start', minWidth: 0 }}>
    <div style={{ minWidth: 0 }}>{left}</div><div style={{ minWidth: 0 }}>{right}</div>
  </div>
);

const ThreeCol = ({ a, b, c, gap = 8, ratio = '1fr 1fr 1fr' }) => (
  <div style={{ display: 'grid', gridTemplateColumns: ratio, gap, alignItems: 'start', minWidth: 0 }}>
    <div style={{ minWidth: 0 }}>{a}</div><div style={{ minWidth: 0 }}>{b}</div><div style={{ minWidth: 0 }}>{c}</div>
  </div>
);

const SectionBox = ({ title, badge, badgeColor, children, style, action, info, guideHref, collapsible, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const showBody = !collapsible || open;
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, marginBottom: 4,
      background: C.card, overflow: 'hidden', minWidth: 0, ...style }}>
      {title && (
        <div onClick={collapsible ? () => setOpen(o => !o) : undefined}
          style={{ padding: '2px 10px', background: C.panel, borderBottom: showBody ? `1px solid ${C.border}` : 'none',
            display: 'flex', flexWrap: 'wrap', rowGap: 4, alignItems: 'center', justifyContent: 'space-between',
            cursor: collapsible ? 'pointer' : 'default', userSelect: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, flex: '0 1 auto' }}>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.09em',
              textTransform: 'uppercase', color: C.textMuted, fontFamily: font.ui }}>{title}</span>
            {badge && <Badge color={badgeColor}>{badge}</Badge>}
            {info && <InfoTooltip text={info} guideHref={guideHref} size={13} />}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', flexShrink: 0 }}>
            {action && action}
            {collapsible && (
              <svg width="10" height="10" viewBox="0 0 9 9" fill="none"
                style={{ transform: open ? 'none' : 'rotate(180deg)', transition: 'transform 0.2s', flexShrink: 0 }}>
                <path d="M1 6L4.5 2.5L8 6" stroke={C.textMuted} strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            )}
          </div>
        </div>
      )}
      {showBody && <div style={{ padding: '5px 10px' }}>{children}</div>}
    </div>
  );
};

const DataTable = ({ rows, compact = false }) => {
  if (!rows || rows.length < 2) return (
    <div style={{ padding: 12, color: C.textDim, fontSize: 12, fontFamily: font.ui, textAlign: 'center' }}>No data</div>
  );
  const [header, ...body] = rows;
  // Client requirement: "Digits for a Scientific number or a decimal number
  // should be up to 4 decimal places and center-aligned in the table."
  const formatCell = (cell) => {
    if (typeof cell === 'number') {
      if (!isFinite(cell)) return String(cell);
      const abs = Math.abs(cell);
      return (cell !== 0 && (abs < 1e-3 || abs >= 1e6)) ? cell.toExponential(4) : cell.toFixed(4);
    }
    // Already-formatted numeric strings (e.g. "1.234567") get re-rounded to 4dp too.
    if (typeof cell === 'string' && /^-?\d+\.\d{5,}$/.test(cell)) {
      return parseFloat(cell).toFixed(4);
    }
    return cell;
  };
  return (
    <div style={{ overflowX: 'auto', borderRadius: 4, border: `1px solid ${C.border}` }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: compact ? 12 : 13, fontFamily: font.ui }}>
        <thead>
          <tr style={{ background: C.panel }}>
            {header.map((h, i) => (
              <th key={i} style={{ padding: compact ? '4px 8px' : '5px 10px', textAlign: 'center',
                color: C.textMuted, fontWeight: 700, borderBottom: `1px solid ${C.border}`,
                fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, ri) => (
            <tr key={ri} style={{ background: ri % 2 === 0 ? C.card : C.surface, borderBottom: `1px solid ${C.border}22` }}>
              {row.map((cell, ci) => (
                <td key={ci} style={{ padding: compact ? '3px 8px' : '4px 10px', textAlign: 'center', color: C.text }}>{formatCell(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Generic multi-row-header table (Reaction | Forward Reaction {Rate Constant,
// Activation Energy} | Backward Reaction {...} — and similar shapes). All
// cells center-aligned per client requirement ("Digits ... should be up to 4
// decimal places and center-aligned in the table").
const MultiHeaderTable = ({ headerRows, bodyRows }) => {
  const th = { padding: '5px 8px', color: C.textMuted, fontWeight: 700,
    borderBottom: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}22`,
    textAlign: 'center', fontSize: 12, fontFamily: font.ui };
  const td = { padding: '5px 8px', color: C.text, textAlign: 'center',
    borderRight: `1px solid ${C.border}22`, fontSize: 12, fontFamily: font.ui };
  return (
    <div style={{ overflowX: 'auto', borderRadius: 4, border: `1px solid ${C.border}` }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          {headerRows.map((row, ri) => (
            <tr key={ri} style={{ background: C.panel }}>
              {row.map((cell, ci) => (
                <th key={ci} rowSpan={cell.rowSpan || 1} colSpan={cell.colSpan || 1} style={th}>
                  {cell.label}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {bodyRows.map((row, ri) => (
            <tr key={ri} style={{ background: ri % 2 === 0 ? C.card : C.surface, borderBottom: `1px solid ${C.border}22` }}>
              {row.map((cell, ci) => <td key={ci} style={td}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CodeBlock = ({ children }) => (
  <div style={{ background: C.codeBg, border: `1px solid ${C.border}`, borderRadius: 5,
    padding: '8px 12px', fontFamily: font.ui, fontSize: 12, color: C.accent,
    lineHeight: 1.8, overflowX: 'auto' }}>{children}</div>
);

const Popup = ({ popup, onClose, onReview }) => {
  if (!popup.show) return null;
  const map = { error: C.error, success: C.success, warning: cssVar('--color-warning-dark'), info: C.cyan };
  const col = map[popup.type] || C.accent;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(61,31,48,0.55)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn 0.15s ease' }}>
      <div style={{ background: C.surface, border: `1px solid ${col}55`, borderTop: `3px solid ${col}`,
        borderRadius: 8, padding: '20px 24px', maxWidth: 420, width: '90%',
        boxShadow: `0 12px 40px ${C.accent}22` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: col, fontFamily: font.ui }}>
            {popup.type === 'error' ? '✕  Error' : popup.type === 'success' ? '✓  Success' : '⚠  Notice'}
          </div>
          {popup.type === 'error' && popup.errorCode && <ErrorInfoButton code={popup.errorCode} />}
        </div>
        <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.65, fontFamily: font.ui, marginBottom: 16 }}>{popup.message}</div>
        {/* Client feedback: "Replace Dismiss with Review and put it on the right side
            instead of the bottom left." Label was already "Review"; this wraps it in a
            right-justified row instead of leaving it as a plain left-aligned block.
            [FIX, Run Controls gap] "Review" should only appear once a run has actually
            completed — for anything else (a run the user Stopped mid-way, an error, or
            a plain info notice) there's nothing to review, so the button now reads
            "Close" in those cases. A completed run always reports type: 'success', so
            that's the single signal this checks.
            [FIX, client doc Sept-2026 — "Review button should lead to the Graph Page;
            currently, it only closes the popup."]: Review now closes the popup AND
            navigates to the Graph panel; Close (non-success case) only dismisses, since
            there's nothing to review yet. */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Btn onClick={() => (popup.type === 'success' ? (onReview ? onReview() : onClose()) : onClose())}
            style={{ fontSize: 13 }}>{popup.type === 'success' ? 'Review' : 'Close'}</Btn>
        </div>
      </div>
    </div>
  );
};

// ─── Plot viewer ─────────────────────────────────────────────────────────────
const PlotViewer = ({ plots, activePlot, setActivePlot, registeredComponents = [] }) => {
  const keys = plots ? Object.keys(plots) : [];

  // Auto-correct stale activePlot key (e.g. 'main' vs 'Overview' vs 'Parity Overview')
  // If the stored key doesn't exist in current plots, fall back to first available key.
  const displayKey = (activePlot && plots && plots[activePlot] != null) ? activePlot : (keys[0] ?? null);

  // Sync parent state so tab-chips stay consistent — only when actually different
  React.useEffect(() => {
    if (displayKey && displayKey !== activePlot) setActivePlot(displayKey);
  }, [displayKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Zoom in / zoom out for the plot image (client request: "we need to
  // zoom in and zoom out options for graphs section"). Plain CSS transform
  // on the <img> — no extra dependency needed. Scroll-wheel zoom + drag-to-
  // pan once zoomed in, plus +/−/reset buttons for anyone on a trackpad-less
  // setup. Zoom resets whenever the active tab changes so switching species
  // always starts from a clean, fully-visible view. ──
  const ZOOM_MIN = 1, ZOOM_MAX = 4, ZOOM_STEP = 0.25;
  const [zoom, setZoom] = React.useState(1);
  const [pan, setPan] = React.useState({ x: 0, y: 0 });
  const dragRef = React.useRef(null);
  React.useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [displayKey]);

  const clampZoom = z => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
  const zoomIn    = () => setZoom(z => clampZoom(+(z + ZOOM_STEP).toFixed(2)));
  const zoomOut    = () => setZoom(z => {
    const next = clampZoom(+(z - ZOOM_STEP).toFixed(2));
    if (next === ZOOM_MIN) setPan({ x: 0, y: 0 }); // snap back to centered when fully zoomed out
    return next;
  });
  const zoomReset  = () => { setZoom(1); setPan({ x: 0, y: 0 }); };
  const onWheel = e => {
    if (!e.ctrlKey && Math.abs(e.deltaY) < 1) return;
    e.preventDefault();
    (e.deltaY < 0 ? zoomIn : zoomOut)();
  };
  const onMouseDown = e => {
    if (zoom <= ZOOM_MIN) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
  };
  const onMouseMove = e => {
    if (!dragRef.current) return;
    const { startX, startY, panX, panY } = dragRef.current;
    setPan({ x: panX + (e.clientX - startX), y: panY + (e.clientY - startY) });
  };
  const endDrag = () => { dragRef.current = null; };

  if (!plots || keys.length === 0)
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100%', minHeight: 260, color: C.textDim, fontSize: 13,
        fontFamily: font.ui, border: `1px dashed ${C.border}`, borderRadius: 6,
        flexDirection: 'column', gap: 8, background: C.panel }}>
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <rect x="2" y="2" width="28" height="28" rx="4" stroke={C.border} strokeWidth="1.5"/>
          <path d="M6 22L11 14L16 18L21 10L26 16" stroke={C.borderMid} strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>Run fitting to generate plots</span>
      </div>
    );

  // Client feedback: "Chart/graph labels should use full component names
  // (e.g. 'Ethane'), not chemical formulas or storage tokens — but keep the
  // formulas/short tokens as-is in the data-entry tables, which use whatever
  // the user typed/uploaded (e.g. 'HOAc', 'MeOAc', 'H2O')." Uses the
  // module-scope speciesDisplayName helper (shared with the R² sidebar in
  // PanelGraph) so both places resolve a species token to its full name the
  // same way: DB-backed species catalog first, then any user-registered custom
  // component, then a formatted formula/token as a last resort.
  const fullSpeciesName = (k) => speciesDisplayName(k, registeredComponents);
  const keyLabel = k => {
    if (k === 'main' || k === 'Overview' || k === 'Prediction Overview' || k === 'Composition Profile Overview')
      return 'Composition Profile Overview';
    return `${fullSpeciesName(k)} Parity`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6,
      height: '100%', minHeight: 0, overflow: 'hidden' }}>
      {keys.length > 1 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', flexShrink: 0 }}>
          {keys.map(k => (
            <Chip key={k} active={displayKey === k} onClick={() => setActivePlot(k)}>{keyLabel(k)}</Chip>
          ))}
        </div>
      )}
      <div
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        style={{ position: 'relative',
          // Client feedback (Graph tab, 03-Aug-2026): the matplotlib plot
          // PNGs render on a white canvas, but objectFit:contain doesn't
          // always fill this box edge-to-edge (aspect-ratio mismatch), so
          // the app's dark surface color showed through as an ugly navy
          // margin around the chart. White here means the letterboxing —
          // if any — blends seamlessly with the image instead.
          background: plots[displayKey] ? '#ffffff' : C.surface,
          borderRadius: 6, overflow: 'hidden',
          border: `1px solid ${C.border}`, flex: 1, minHeight: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {plots[displayKey]
          ? <img
              src={`data:image/png;base64,${plots[displayKey]}`}
              alt={displayKey}
              draggable={false}
              style={{
                width: '100%', height: '100%', objectFit: 'contain', display: 'block',
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: 'center center',
                transition: dragRef.current ? 'none' : 'transform 0.12s ease-out',
                cursor: zoom > ZOOM_MIN ? (dragRef.current ? 'grabbing' : 'grab') : 'default',
                userSelect: 'none',
              }}
            />
          : <div style={{ padding: 40, color: C.textDim, textAlign: 'center', fontSize: 13,
              fontFamily: font.ui }}>
              Plot image not returned by backend for this key.
            </div>}
        {plots[displayKey] && (
          <div style={{ position: 'absolute', bottom: 8, right: 8, display: 'flex',
            alignItems: 'center', gap: 2, background: 'rgba(0,0,0,0.55)', borderRadius: 6,
            padding: 3, backdropFilter: 'blur(2px)' }}>
            <button onClick={zoomOut} disabled={zoom <= ZOOM_MIN} title="Zoom out"
              style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'transparent', border: 'none', borderRadius: 4, color: '#fff',
                fontSize: 15, lineHeight: 1, cursor: zoom <= ZOOM_MIN ? 'default' : 'pointer',
                opacity: zoom <= ZOOM_MIN ? 0.35 : 1 }}>−</button>
            <span style={{ minWidth: 38, textAlign: 'center', fontSize: 11, color: '#fff',
              fontFamily: font.ui }}>{Math.round(zoom * 100)}%</span>
            <button onClick={zoomIn} disabled={zoom >= ZOOM_MAX} title="Zoom in"
              style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'transparent', border: 'none', borderRadius: 4, color: '#fff',
                fontSize: 15, lineHeight: 1, cursor: zoom >= ZOOM_MAX ? 'default' : 'pointer',
                opacity: zoom >= ZOOM_MAX ? 0.35 : 1 }}>+</button>
            {zoom !== 1 && (
              <button onClick={zoomReset} title="Reset zoom"
                style={{ height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'transparent', border: 'none', borderRadius: 4, color: '#fff',
                  fontSize: 11, lineHeight: 1, cursor: 'pointer', padding: '0 6px',
                  borderLeft: '1px solid rgba(255,255,255,0.25)', marginLeft: 2 }}>Reset</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


// ─── Panel Shell ─────────────────────────────────────────────────────────────
const PanelShell = ({ title, subtitle, icon, actions, children, scrollable = true, hideHeader = false }) => (
  <div style={{ display: 'flex', flexDirection: 'column', height: '100%', animation: 'slideIn 0.16s ease' }}>
    {!hideHeader && (
      <div style={{ padding: '0 16px', height: 44, borderBottom: `1px solid ${C.border}`,
        background: C.surface, display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', flexShrink: 0, gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          {icon && <span style={{ color: C.accent, display: 'flex' }}>{icon}</span>}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text, fontFamily: font.ui, whiteSpace: 'nowrap' }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 1,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subtitle}</div>}
          </div>
        </div>
        {actions && <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>{actions}</div>}
      </div>
    )}
    {/* FIX: single unambiguous scroll container — overflowY always 'auto' for scrollable panels,
        no conflicting 'overflow' shorthand that would reset overflowY back to 'hidden'. */}
    <div style={{
      flex: 1,
      minHeight: 0,
      overflowY: scrollable ? 'auto' : 'hidden',
      overflowX: 'hidden',
      padding: scrollable ? '8px 14px 14px' : '8px 10px',
    }}>
      {children}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// ─── Song 1998 lookup tables (complete built-in run metadata) ───────────────
// These tables are used only for the built-in Methyl Acetate / DME reference
// workflows. Custom reaction systems never read these constants.
const SONG_MEOAC_RUNS = {
  1:  { n_mix: 5.61, W_cat: 24.0, T_C: 40, label: 'Run 1 — 40°C, 24g' },
  2:  { n_mix: 5.61, W_cat: 48.0, T_C: 40, label: 'Run 2 — 40°C, 48g' },
  3:  { n_mix: 3.74, W_cat: 48.0, T_C: 40, label: 'Run 3 — 40°C, 48g, 3.74mol' },
  4:  { n_mix: 5.61, W_cat: 48.0, T_C: 45, label: 'Run 4 — 45°C, 48g' },
  5:  { n_mix: 5.61, W_cat: 24.0, T_C: 45, label: 'Run 5 — 45°C, 24g' },
  6:  { n_mix: 3.74, W_cat: 48.0, T_C: 45, label: 'Run 6 — 45°C, 48g, 3.74mol' },
  7:  { n_mix: 5.61, W_cat: 24.0, T_C: 50, label: 'Run 7 — 50°C, 24g' },
  8:  { n_mix: 5.61, W_cat: 48.0, T_C: 50, label: 'Run 8 — 50°C, 48g' },
  9:  { n_mix: 3.74, W_cat: 48.0, T_C: 50, label: 'Run 9 — 50°C, 48g, 3.74mol' },
  10: { n_mix: 5.61, W_cat: 12.0, T_C: 50, label: 'Run 10 — 50°C, 12g' },
  11: { n_mix: 5.61, W_cat: 48.0, T_C: 50, label: 'Run 11 — 50°C, 48g (3.74+1.87)' },
  12: { n_mix: 3.74, W_cat: 24.0, T_C: 50, label: 'Run 12 — 50°C, 24g, 3.74mol' },
  13: { n_mix: 5.61, W_cat: 24.0, T_C: 50, label: 'Run 13 — 50°C, 24g (1.87+3.74)' },
  14: { n_mix: 5.60, W_cat: 48.0, T_C: 50, label: 'Run 14 — 50°C, 48g, 5.60mol' },
};

const SONG_DME_RUNS = {
  1:  { n_mix: 10.16, W_cat: 25.0, T_C: 100, label: 'Run 1 — 100°C, 25g', caution: null },
  2:  { n_mix: 10.54, W_cat: 49.0, T_C: 100, label: 'Run 2 — 100°C, 49g', caution: null },
  3:  { n_mix: 10.49, W_cat: 71.0, T_C: 100, label: 'Run 3 — 100°C, 71g', caution: null },
  4:  { n_mix: 10.50, W_cat: 0.0, T_C: 100, label: 'Run 4 — 100°C, no catalyst',
        caution: 'no_catalyst', caution_msg: 'No catalyst — blank control. Do NOT include in global fits.' },
  5:  { n_mix: 10.57, W_cat: 72.0, T_C: 100, label: 'Run 5 — 100°C, 72g', caution: null },
  6:  { n_mix: 10.50, W_cat: 72.0, T_C: 100, label: 'Run 6 — 100°C, 72g (sparse)',
        caution: 'sparse_data', caution_msg: 'Only 2 data points — ODE fitting under-constrained. Treat with caution.' },
  7:  { n_mix: 10.50, W_cat: 72.0, T_C: 100, label: 'Run 7 — 100°C, 72g (near-eq)',
        caution: 'near_equilibrium', caution_msg: 'Near-equimolar MeOH/H2O start — kf will be atypically low. Exclude from global fits.' },
  8:  { n_mix: 11.07, W_cat: 82.4, T_C: 100, label: 'Run 8 — 100°C, 82g', caution: null },
  9:  { n_mix: 10.54, W_cat: 72.9, T_C: 100, label: 'Run 9 — 100°C, 73g', caution: null },
  10: { n_mix: 10.45, W_cat: 71.0, T_C: 115, label: 'Run 10 — 115°C, 71g', caution: null },
  11: { n_mix: 10.47, W_cat: 72.0, T_C: 85, label: 'Run 11 — 85°C, 72g', caution: null },
};

// GLOBAL MULTI-RUN MANAGER
// ═══════════════════════════════════════════════════════════════════════════
const GlobalRunManager = ({ reactionSystem, activeSpecies = [], reactorMode = 'batch', globalRuns, setGlobalRuns, onManualEntryForRun }) => {
  const songTable = reactionSystem === 'dme'
    ? SONG_DME_RUNS
    : reactionSystem === 'meoac'
      ? SONG_MEOAC_RUNS
      : {};
  // FIX (Madhura, 17-Jul-2026 call, item 2): "Number of mols" is now labelled
  // "Initial Charge" per her terminology request, and V_rx_L (Reactor Volume)
  // has been added — it was previously missing from this per-run field set
  // even though it's one of the variables that can change between data sets.
  const isCustomPfr = reactionSystem === 'custom' && reactorMode === 'pfr';
  // Multi-dataset metadata is reactor-aware. Batch needs a user-supplied
  // initial charge; PFR does NOT — its n_mix is derived per row from P·V/R·T.
  // PFR fallback pressure follows the app-wide psi convention used by the
  // generic single-dataset PFR path. Keep P_atm too for legacy MeOAc/DME
  // batch compatibility; it is not used by the custom generic PFR route.
  // ADD (Madhura, 24-Aug-2026 doc, item 5 — "No tab for entry of 'Pressure'
  // for Multiple Data Sets"): the single-dataset Reaction Conditions panel
  // already has a Reaction Pressure + Unit field for Batch mode — this per-run
  // field set never had the equivalent, so P_atm is kept per run (it
  // legitimately differs between runs).
  const defaultRun = {
    file: null, fileName: '',
    T_C: isCustomPfr ? '' : 40,
    n_mix_mol: isCustomPfr ? '' : 5.61,
    V_rx_L: '', W_cat_g: 24.0, weight: 1.0,
    P_atm: 1,
    P_psi: isCustomPfr ? '' : 14.6959,
  };

  const addRun = () => setGlobalRuns(r => [...r, { ...defaultRun, id: Date.now() }]);
  const removeRun = id => setGlobalRuns(r => r.filter(x => x.id !== id));
  const updateRun = (id, key, val) => setGlobalRuns(r => r.map(x => x.id === id ? { ...x, [key]: val } : x));

  const handleRunFile = async (id, e) => {
    const raw = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same filename later
    if (!raw) return;
    let f;
    try {
      f = await convertSpreadsheetToCsvFile(raw);
    } catch (err) {
      alert(err.message || `Could not read "${raw.name}".`);
      return;
    }
    const fnMatch = f.name.match(/[Rr]un[_\s#-]*(\d+)/);
    const runNumber = fnMatch ? parseInt(fnMatch[1], 10) : null;
    let patch = { file: f, fileName: f.name };
    if (runNumber && songTable[runNumber]) {
      const m = songTable[runNumber];
      patch = { ...patch, T_C: m.T_C, n_mix_mol: m.n_mix ?? m.n_mix_mol, W_cat_g: m.W_cat ?? m.W_cat_g };
    }
    setGlobalRuns(r => r.map(x => x.id === id ? { ...x, ...patch } : x));
  };

  return (
    <SectionBox title="Multiple Data Set Manager" badge={`${globalRuns.length} runs`} badgeColor={C.accent}
      action={<div style={{ display: 'flex', gap: 6 }}>
        {/* Download Template — was missing for the "Multiple data sets" case
            (Madhura, follow-up call 8-Jul-2026, item A); same template each
            run's CSV upload or manual entry should match. */}
        <Btn variant="ghost" style={{ fontSize: 12, padding: '3px 8px' }}
          onClick={() => downloadCsvTemplate(getManualEntryConfig(reactionSystem, activeSpecies, reactorMode), reactionSystem)}>⬇  Download Template</Btn>
        <Btn variant="ghost" style={{ fontSize: 12, padding: '3px 8px' }} onClick={addRun}>+ Add Data Set</Btn>
      </div>}>
      {globalRuns.length === 0 && (
        <Alert type="info">Click "+ Add Run" to add a dataset. Upload one CSV file or enter the experimental data manually for each run.</Alert>
      )}
      {/* FIX: constrain run list height so it scrolls within the panel rather than overflowing */}
      <div style={{ maxHeight: 340, overflowY: 'auto', overflowX: 'hidden', paddingRight: 2 }}>
      {globalRuns.map((run, idx) => {
        const fnMatch = (run.fileName || '').match(/[Rr]un[_\s#-]*(\d+)/);
        const runNum = fnMatch ? parseInt(fnMatch[1], 10) : null;
        // Use only the metadata table selected for this built-in system.
        // Custom systems have an empty songTable, so they show no Song cautions.
        const songMeta = runNum != null ? (songTable[runNum] || null) : null;
        const caution = songMeta?.caution;
        return (
          <div key={run.id} style={{ border: `1px solid ${C.border}`, borderRadius: 5,
            padding: '8px 10px', marginBottom: 8, background: C.surface }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: C.accent, fontFamily: font.ui, minWidth: 16 }}>
                {idx + 1}
              </span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                background: C.accentDim, border: `1px solid ${C.accent}44`, borderRadius: 4,
                padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: C.accent,
                fontWeight: 600, fontFamily: font.ui }}>
                📂 {run.fileName || 'Upload CSV'}
                <input type="file" accept={CSV_UPLOAD_ACCEPT} style={{ display: 'none' }}
                  onChange={e => handleRunFile(run.id, e)} />
              </label>
              {onManualEntryForRun && (
                <Btn variant="secondary" style={{ fontSize: 12, padding: '4px 9px' }}
                  onClick={() => onManualEntryForRun(run.id)}>✏  Enter Manually</Btn>
              )}
              {run.fileName && <Badge color={C.success}>●</Badge>}
              {caution && <Badge color={cssVar('--color-warning-dark')}>⚠ {caution.replace('_', ' ')}</Badge>}
              <div style={{ flex: 1 }} />
              <button onClick={() => removeRun(run.id)} title="Remove this data set" aria-label="Remove this data set" style={removeXButtonStyle}>✕</button>
            </div>
            {caution && songMeta?.caution_msg && (
              <Alert type="warning" style={{ marginBottom: 8 }}>{songMeta.caution_msg}</Alert>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(132px, 1fr))', gap: 6 }}>
              <Field label={reactorMode === 'pfr' ? 'Fallback T (°C)' : 'T (°C)'}>
                <Input type="number" value={run.T_C ?? ''} step="1" placeholder={reactorMode === 'pfr' ? 'Optional if CSV has T' : 'e.g. 40'}
                  onChange={e => updateRun(run.id, 'T_C', e.target.value === '' ? '' : parseFloat(e.target.value))} />
              </Field>
              {/* FIX: "Number of mols" renamed to "Initial Charge" — Madhura,
                  17-Jul-2026 call, item 2 ("don't call it total mixture
                  amount, call it initial charge"). Batch only: PFR derives
                  n_mix per row from P·V/R·T. */}
              {reactorMode === 'batch' && (
                <Field label="Initial Charge (mol)" wrap>
                  <Input type="number" value={run.n_mix_mol ?? ''} step="0.01"
                    onChange={e => updateRun(run.id, 'n_mix_mol', e.target.value === '' ? '' : parseFloat(e.target.value))} />
                </Field>
              )}
              {/* FIX: Reactor Volume was missing from this per-run set even
                  though it's one of the variables Madhura listed as able to
                  vary between data sets (17-Jul-2026 call, item 2). */}
              <Field label="Reactor Volume (L)" wrap>
                <Input type="number" value={run.V_rx_L ?? ''} step="0.01" placeholder="e.g. 0.25"
                  onChange={e => updateRun(run.id, 'V_rx_L', e.target.value === '' ? '' : parseFloat(e.target.value))} />
              </Field>
              <Field label="Catalyst Weight (g)" wrap>
                <Input type="number" value={run.W_cat_g ?? ''} step="0.1"
                  onChange={e => updateRun(run.id, 'W_cat_g', e.target.value === '' ? '' : parseFloat(e.target.value))} />
              </Field>
              {/* ADD (Madhura, 24-Aug-2026 doc, item 5) — see defaultRun
                  comment above. Batch: plain atm, no per-run unit picker, to
                  keep this compact grid readable; matches the single-dataset
                  Batch field's default unit. PFR: fallback psi. */}
              {reactorMode === 'pfr' ? (
                <Field label="Fallback Pressure (psi)" wrap>
                  <Input type="number" value={run.P_psi ?? ''} step="0.01" min="0" placeholder="Optional if CSV has Pressure"
                    onChange={e => updateRun(run.id, 'P_psi', e.target.value === '' ? '' : parseFloat(e.target.value))} />
                </Field>
              ) : (
                <Field label="Pressure (atm)">
                  <Input type="number" value={run.P_atm ?? ''} step="0.01" min="0"
                    onChange={e => updateRun(run.id, 'P_atm', e.target.value === '' ? '' : parseFloat(e.target.value))} />
                </Field>
              )}
              {/* <Field label="Weight">
                <Input type="number" value={run.weight} step="0.1" min="0"
                  onChange={e => updateRun(run.id, 'weight', parseFloat(e.target.value))} />
              </Field> */}
            </div>
          </div>
        );
      })}
      </div>
    </SectionBox>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// AA MULTI-CSV GLOBAL MANAGER
// Madhura, follow-up call 24-Jun-2026, item (i): let the AA system fit 2+
// separately-uploaded CSVs (different water feeds, pressures, or catalyst
// loads) SIMULTANEOUSLY, the same way GlobalRunManager already does for
// MeOAc/DME. Each AA CSV already carries its own T/P/tau per row, so this
// manager only needs a per-CSV catalyst load + optional relative weight —
// not the T_C / n_mix_mol fields GlobalRunManager collects for MeOAc/DME.
// ═══════════════════════════════════════════════════════════════════════════
const AAMultiCsvManager = ({ globalRuns, setGlobalRuns, reactionSystem = 'acetic_acid', onManualEntryForRun }) => {
  // FIX (Madhura, 17-Jul-2026 call, item 2 — "you have two things missing:
  // reactor volume is missing and initial moles is missing, for the
  // multiple data set entry"): each AA dataset can, in general, come from a
  // different physical run — different catalyst load, reactor volume, or
  // total charge — so those three fields need to be captured per-dataset
  // here, not assumed to be one shared value for the whole system. tau
  // itself still comes from each CSV's own T/P/tau rows, per Madhura's
  // clarification that residence time is a per-row derived quantity (V/Q),
  // not something entered once per dataset.
  const defaultRun = { file: null, fileName: '', label: '', W_cat_g: '', V_rx_L: '', weight: 1.0 };

  const addRun = () => setGlobalRuns(r => [...r, { ...defaultRun, id: Date.now() }]);
  const removeRun = id => setGlobalRuns(r => r.filter(x => x.id !== id));
  const updateRun = (id, key, val) => setGlobalRuns(r => r.map(x => x.id === id ? { ...x, [key]: val } : x));

  const handleRunFile = async (id, e) => {
    const raw = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same filename later
    if (!raw) return;
    let f;
    try {
      f = await convertSpreadsheetToCsvFile(raw);
    } catch (err) {
      alert(err.message || `Could not read "${raw.name}".`);
      return;
    }
    setGlobalRuns(r => r.map(x => x.id === id ? { ...x, file: f, fileName: f.name,
      label: x.label || f.name.replace(/\.(csv|xlsx|xls)$/i, '') } : x));
  };

  return (
    <SectionBox title="Multiple Data Sets Manager" badge={`${globalRuns.length} CSVs`} badgeColor={C.accent}
      action={<div style={{ display: 'flex', gap: 6 }}>
        {/* Download Template — was missing for the "Multiple data sets" case
            (Madhura, follow-up call 8-Jul-2026, item A). */}
        <Btn variant="ghost" style={{ fontSize: 12, padding: '3px 8px' }}
          onClick={() => downloadCsvTemplate(getManualEntryConfig(reactionSystem, []), reactionSystem)}>⬇  Download Template</Btn>
        <Btn variant="ghost" style={{ fontSize: 12, padding: '3px 8px' }} onClick={addRun}>+ Add CSV</Btn>
      </div>}
      info="Upload 2 or more CSVs — e.g. different water feeds, pressures, or catalyst loads. A single shared parameter set is fit across all of them simultaneously.">
      {globalRuns.length === 0 && (
        <Alert type="info">Click "+ Add CSV" to add data sets for a multi-dataset fit — upload a CSV per data set or enter one manually.</Alert>
      )}
      <div style={{ maxHeight: 340, overflowY: 'auto', overflowX: 'hidden', paddingRight: 2 }}>
      {globalRuns.map((run, idx) => (
        <div key={run.id} style={{ border: `1px solid ${C.border}`, borderRadius: 5,
          padding: '8px 10px', marginBottom: 8, background: C.surface }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.accent, fontFamily: font.ui, minWidth: 16 }}>
              {idx + 1}
            </span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6,
              background: C.accentDim, border: `1px solid ${C.accent}44`, borderRadius: 4,
              padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: C.accent,
              fontWeight: 600, fontFamily: font.ui }}>
              📂 {run.fileName || 'Upload CSV'}
              <input type="file" accept={CSV_UPLOAD_ACCEPT} style={{ display: 'none' }}
                onChange={e => handleRunFile(run.id, e)} />
            </label>
            {onManualEntryForRun && (
              <Btn variant="secondary" style={{ fontSize: 12, padding: '4px 9px' }}
                onClick={() => onManualEntryForRun(run.id)}>✏  Enter Manually</Btn>
            )}
            {run.fileName && <Badge color={C.success}>●</Badge>}
            <div style={{ flex: 1 }} />
            <button onClick={() => removeRun(run.id)} title="Remove this data set" aria-label="Remove this data set" style={removeXButtonStyle}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px,1.4fr) 1fr 1fr', gap: 6 }}>
            <Field label="Label" info="e.g. water feed / pressure">
              <Input value={run.label} placeholder={run.fileName || `Dataset ${idx + 1}`}
                onChange={e => updateRun(run.id, 'label', e.target.value)} />
            </Field>
            <Field label="Catalyst Weight (g)" wrap>
              <Input type="number" value={run.W_cat_g} step="0.001" placeholder="auto-detect"
                onChange={e => updateRun(run.id, 'W_cat_g', e.target.value)} />
            </Field>
            {/* FIX: Reactor Volume — previously missing entirely from this
                manager (Madhura, 17-Jul-2026 call, item 2); now also actually
                sent to the backend (see runMeta in handleSubmit) instead of
                being silently dropped. "Initial Charge" was removed from
                this manager — traced through handleSubmit and
                aa_kinetics_objective.py, AA never reads it (mixture amount
                is derived per row from P/T/reactor volume via ideal-gas law). */}
            <Field label="Reactor Volume (L)" wrap>
              <Input type="number" value={run.V_rx_L} step="0.01" placeholder="e.g. 0.25"
                onChange={e => updateRun(run.id, 'V_rx_L', e.target.value)} />
            </Field>
            {/* <Field label="Weight">
              <Input type="number" value={run.weight} step="0.1" min="0"
                onChange={e => updateRun(run.id, 'weight', parseFloat(e.target.value))} />
            </Field> */}
          </div>
        </div>
      ))}
      </div>
    </SectionBox>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// EXAMPLES SYSTEM — Meeting §1: replace system selector with Examples button
// Examples named by reaction type (not proprietary system name)
// Each auto-populates ALL fields on click; includes reference citation
// ═══════════════════════════════════════════════════════════════════════════


// ─── Reaction system metadata ─────────────────────────────────────────────
export const SYSTEM_META = {
  meoac: {
    key: 'meoac',
    displayName: 'Methyl Acetate',
    shortName: 'MeOAc',
    color: '#8b55db',
    equation: 'HOAc + MeOH ⇌ MeOAc + H₂O',
    equationLatex: 'HOAc + MeOH ⇌ MeOAc + H₂O',
    // [FIX — Madhura feedback, 9-Sep-26] "Write full name, not symbols" +
    // "Esterification/hydrolysis reaction (in text underneath)" — mirrors
    // the identical fix in HelpExamples.jsx SYSTEM_META.
    reactionTypeLabel: 'Esterification / hydrolysis reaction',
    reference: 'Song et al., Ind. Eng. Chem. Res. 1998, 37, 1917–1928',
    doi: '10.1021/ie9708790',
  },
  dme: {
    key: 'dme',
    displayName: 'Dimethyl Ether',
    shortName: 'DME',
    color: '#0ab5e2',
    equation: '2 MeOH ⇌ DME + H₂O',
    equationLatex: '2 MeOH ⇌ DME + H₂O',
    reactionTypeLabel: 'Methanol dehydration reaction',
    reference: 'Song et al., Ind. Eng. Chem. Res. 1998, 37, 1917–1928',
    doi: '10.1021/ie9708790',
  },
  acetic_acid: {
    key: 'acetic_acid',
    displayName: 'Acetic Acid',
    shortName: 'AA',
    color: '#E65100',
    equation: 'C₂H₆ + O₂ → Products (3 parallel rxns)',
    equationLatex: 'C₂H₆ + O₂ → AA / ET / CO₂  (3 parallel)',
    reactionTypeLabel: 'Ethane oxidation reaction',
    // [FIX] Was internal/proprietary plant data — replaced with a public,
    // peer-reviewed source for the same C2H6 -> C2H4 / CH3COOH / COx
    // parallel-reaction network over a packed-bed (PFR) MoV-type catalyst.
    // Mirrors the identical fix in HelpExamples.jsx SYSTEM_META.
    reference: 'Rahman, F.; Loughlin, K.F.; Al-Saleh, M.A.; Saeed, M.R.; Tukur, N.M.; Hossain, M.M.; Karim, K.; Mamedov, A. Appl. Catal. A: Gen. 2010, 375, 17–25',
    doi: '10.1016/j.apcata.2009.11.026',
  },
  // Generic user-defined system — species/stoichiometry come entirely from
  // Components & Stoichiometry (rxn1..3 Reactants/Products), not from a
  // fixed preset. Power-law only, batch reactor only, in this pass — see
  // /api/run/generic-fitting and generic_kinetics_objective.py.
  custom: {
    key: 'custom',
    displayName: 'Custom Reaction System',
    shortName: 'Custom',
    color: '#546E7A',
    equation: 'Defined in Components & Stoichiometry below',
    equationLatex: '',
    reference: null,
    doi: null,
  },
};

// ─── Rate equation strings per model type ─────────────────────────────────
// ─── Unit conversion helpers ──────────────────────────────────────────────
// Pressure: value-per-1-atm for each unit (so value_in_unit = value_atm * factor)
const PRESSURE_PER_ATM = { atm: 1, bar: 1.01325, psi: 14.6959, kPa: 101.325, mmHg: 760 };
const convertPressure = (value, fromUnit, toUnit) => {
  const num = parseFloat(value);
  if (!Number.isFinite(num) || fromUnit === toUnit) return value;
  const atm = num / PRESSURE_PER_ATM[fromUnit];
  const converted = atm * PRESSURE_PER_ATM[toUnit];
  // Trim to a sane number of decimals per unit magnitude instead of long floats
  const decimals = toUnit === 'mmHg' || toUnit === 'kPa' ? 2 : 4;
  return String(Number(converted.toFixed(decimals)));
};

// Catalyst weight: grams-per-1-unit (so value_in_g = value_in_unit * factor)
const WEIGHT_G_PER_UNIT = { g: 1, kg: 1000 };
const convertWeight = (value, fromUnit, toUnit) => {
  const num = parseFloat(value);
  if (!Number.isFinite(num) || fromUnit === toUnit) return value;
  const grams = num * WEIGHT_G_PER_UNIT[fromUnit];
  const converted = grams / WEIGHT_G_PER_UNIT[toUnit];
  return String(Number(converted.toFixed(4)));
};

export const MODEL_EQUATIONS = {
  meoac: {
    // ── Reversible ────────────────────────────────────────────────────────
    'single_rev_pl':     'r = kf · xHOAc^a · xMeOH^b · (1 − Q/Keq)',
    'single_rev_lhhw':   'r = [kf · xHOAc · xMeOH − kb · xMeOAc · xH₂O] / (1 + ΣKᵢxᵢ)²',
    'global_rev_pl':     'r = Af·exp(−Ea/RT) · xHOAc^a · xMeOH^b · (1 − Q/Keq)',
    'global_rev_lhhw':   'r = [kf · xHOAc · xMeOH − kb · xMeOAc · xH₂O] / (1 + ΣKᵢxᵢ)²   shared Ea',
    // ── Irreversible (forward only) ────────────────────────────────────────
    'single_irrev_pl':   'r = kf · xHOAc^a · xMeOH^b',
    'single_irrev_lhhw': 'r = kf · xHOAc · xMeOH / (1 + ΣKᵢxᵢ)²',
    'global_irrev_pl':   'r = Af·exp(−Ea/RT) · xHOAc^a · xMeOH^b   shared Ea',
    'global_irrev_lhhw': 'r = kf · xHOAc · xMeOH / (1 + ΣKᵢxᵢ)²   shared Ea',
    'multi_irrev_pl':    'N/A — MeOAc is a single-reaction system',
    'multi_irrev_lhhw':  'N/A — MeOAc is a single-reaction system',
  },
  dme: {
    // ── Reversible ────────────────────────────────────────────────────────
    'single_rev_pl':     'r = kf · xMeOH^a · (1 − Q/Keq)',
    'single_rev_lhhw':   'r = [kf · xMeOH² − kb · xDME · xH₂O] / (1 + ΣKᵢxᵢ)²',
    'global_rev_pl':     'r = Af·exp(−Ea/RT) · xMeOH^a · (1 − Q/Keq)   shared Ea',
    'global_rev_lhhw':   'r = [kf · xMeOH² − kb · xDME · xH₂O] / (1 + ΣKᵢxᵢ)²   shared Ea',
    // ── Irreversible (forward only) ────────────────────────────────────────
    'single_irrev_pl':   'r = kf · xMeOH^a',
    'single_irrev_lhhw': 'r = kf · xMeOH² / (1 + ΣKᵢxᵢ)²',
    'global_irrev_pl':   'r = Af·exp(−Ea/RT) · xMeOH^a   shared Ea',
    'global_irrev_lhhw': 'r = kf · xMeOH² / (1 + ΣKᵢxᵢ)²   shared Ea',
    'multi_irrev_pl':    'N/A — DME is a single-reaction system',
    'multi_irrev_lhhw':  'N/A — DME is a single-reaction system',
  },
  acetic_acid: {
    // ── Reversible — not applicable ────────────────────────────────────────
    'single_rev_pl':     'N/A — AA system is inherently irreversible (3 parallel rxns)',
    'single_rev_lhhw':   'N/A — AA system is inherently irreversible (3 parallel rxns)',
    'global_rev_pl':     'N/A — AA system is irreversible',
    'global_rev_lhhw':   'N/A — AA system is irreversible',
    'single_irrev_pl':   'N/A — AA system has 3 parallel reactions (use Multi-Rxn below)',
    'single_irrev_lhhw': 'N/A — AA system has 3 parallel reactions (use Multi-Rxn below)',
    'global_irrev_pl':   'N/A — AA system has 3 parallel reactions (use Multi-Rxn below)',
    'global_irrev_lhhw': 'N/A — AA system has 3 parallel reactions (use Multi-Rxn below)',
    // ── Irreversible parallel — the applicable forms ───────────────────────
    'multi_irrev_pl':    'rᵢ = Af_i·exp(−Ea_i/RT) · xA^aᵢ · xB^bᵢ   [i = 1, 2, 3]',
    'multi_irrev_lhhw':  'rᵢ = kf_i · xA^aᵢ · xB^bᵢ / (1 + ΣKⱼxⱼ)²   [i = 1, 2, 3]',
    'global_multi_irrev_pl':   'rᵢ = Af_i·exp(−Ea_i/RT) · xA^aᵢ · xB^bᵢ   shared Ea   [i=1,2,3]',
    'global_multi_irrev_lhhw': 'rᵢ = kf_i · xA^aᵢ · xB^bᵢ / (1 + ΣKⱼxⱼ)²   shared Ea   [i=1,2,3]',
  },
};

// ─── Built-in Initialize with Example presets ─────────────────────────────
// The preset list intentionally contains only the two approved Song et al.
// Batch systems (MeOAc and DME), with 8 model/scope variants each. The former
// synthetic Acetic Acid PFR sample is retired; user-supplied AA/PFR fitting
// remains supported through the general Data Fitting input path.
// ──────────────────────────────────────────────────────────────────────────────
const buildExamples = () => [

  // PFR setup presets: chemistry and model settings only. No preset injects data.
  // The 8-card matrix is 2 dataset scopes × 2 kinetic models × 2 order modes.
  // Titles use the same naming pattern as Batch; order mode is described consistently below.
  {
    id: 'acetic_pfr_single_pl_fixed',
    system: 'acetic_acid', modelKey: 'multi_irrev_pl',
    cardLabel: 'Single Data Set — Parallel Irreversible Power Law Model',
    description: 'Reaction orders fixed at 1. Upload a CSV or enter observations manually.',
    rxn1Reactants: 'C2H6 + 0.5 O2', rxn1Products: 'ET + H2O',
    rxn2Reactants: 'C2H6 + 1.5 O2', rxn2Products: 'AA + H2O',
    rxn3Reactants: 'C2H6 + 3.5 O2', rxn3Products: '2 CO2 + 3 H2O',
    inerts: 'N2', reactorMode: 'pfr', reactionPhase: 'gas',
    concentrationBasis: 'mole_fraction', reversibility: 'irreversible',
    modelType: 'powerlaw', exponentMode: 'fixed_1', keqMode: 'regress',
    fitScope: 'single', reactionTempC: '', reactionPressure: '240', pressureUnit: 'psi',
    wCatG: '24.0', vRxL: '0.25', rateBasis: 'cat_weight',
  },
  {
    id: 'acetic_pfr_single_pl_regress',
    system: 'acetic_acid', modelKey: 'multi_irrev_pl',
    cardLabel: 'Single Data Set — Parallel Irreversible Power Law Model',
    description: 'Reaction orders regressed from the data. Upload a CSV or enter observations manually.',
    rxn1Reactants: 'C2H6 + 0.5 O2', rxn1Products: 'ET + H2O',
    rxn2Reactants: 'C2H6 + 1.5 O2', rxn2Products: 'AA + H2O',
    rxn3Reactants: 'C2H6 + 3.5 O2', rxn3Products: '2 CO2 + 3 H2O',
    inerts: 'N2', reactorMode: 'pfr', reactionPhase: 'gas',
    concentrationBasis: 'mole_fraction', reversibility: 'irreversible',
    modelType: 'powerlaw', exponentMode: 'regress', keqMode: 'regress',
    fitScope: 'single', reactionTempC: '', reactionPressure: '240', pressureUnit: 'psi',
    wCatG: '24.0', vRxL: '0.25', rateBasis: 'cat_weight',
  },
  {
    id: 'acetic_pfr_single_lhhw_fixed',
    system: 'acetic_acid', modelKey: 'multi_irrev_lhhw',
    cardLabel: 'Single Data Set — Parallel Irreversible LHHW Model',
    description: 'Reaction orders fixed at 1. Upload a CSV or enter observations manually.',
    rxn1Reactants: 'C2H6 + 0.5 O2', rxn1Products: 'ET + H2O',
    rxn2Reactants: 'C2H6 + 1.5 O2', rxn2Products: 'AA + H2O',
    rxn3Reactants: 'C2H6 + 3.5 O2', rxn3Products: '2 CO2 + 3 H2O',
    inerts: 'N2', reactorMode: 'pfr', reactionPhase: 'gas',
    concentrationBasis: 'mole_fraction', reversibility: 'irreversible',
    modelType: 'lhhw', exponentMode: 'fixed_1', keqMode: 'regress',
    fitScope: 'single', reactionTempC: '', reactionPressure: '240', pressureUnit: 'psi',
    wCatG: '24.0', vRxL: '0.25', rateBasis: 'cat_weight',
  },
  {
    id: 'acetic_pfr_single_lhhw_regress',
    system: 'acetic_acid', modelKey: 'multi_irrev_lhhw',
    cardLabel: 'Single Data Set — Parallel Irreversible LHHW Model',
    description: 'Reaction orders regressed from the data. Upload a CSV or enter observations manually.',
    rxn1Reactants: 'C2H6 + 0.5 O2', rxn1Products: 'ET + H2O',
    rxn2Reactants: 'C2H6 + 1.5 O2', rxn2Products: 'AA + H2O',
    rxn3Reactants: 'C2H6 + 3.5 O2', rxn3Products: '2 CO2 + 3 H2O',
    inerts: 'N2', reactorMode: 'pfr', reactionPhase: 'gas',
    concentrationBasis: 'mole_fraction', reversibility: 'irreversible',
    modelType: 'lhhw', exponentMode: 'regress', keqMode: 'regress',
    fitScope: 'single', reactionTempC: '', reactionPressure: '240', pressureUnit: 'psi',
    wCatG: '24.0', vRxL: '0.25', rateBasis: 'cat_weight',
  },
  {
    id: 'acetic_pfr_multi_pl_fixed',
    system: 'acetic_acid', modelKey: 'global_multi_irrev_pl',
    cardLabel: 'Multiple Data Sets — Parallel Irreversible Power Law Model',
    description: 'Reaction orders fixed at 1. Fit separate user-supplied PFR datasets; upload each CSV or enter its observations manually.',
    rxn1Reactants: 'C2H6 + 0.5 O2', rxn1Products: 'ET + H2O',
    rxn2Reactants: 'C2H6 + 1.5 O2', rxn2Products: 'AA + H2O',
    rxn3Reactants: 'C2H6 + 3.5 O2', rxn3Products: '2 CO2 + 3 H2O',
    inerts: 'N2', reactorMode: 'pfr', reactionPhase: 'gas',
    concentrationBasis: 'mole_fraction', reversibility: 'irreversible',
    modelType: 'powerlaw', exponentMode: 'fixed_1', keqMode: 'regress',
    fitScope: 'multiple', reactionTempC: '', reactionPressure: '240', pressureUnit: 'psi',
    wCatG: '24.0', vRxL: '0.25', rateBasis: 'cat_weight',
  },
  {
    id: 'acetic_pfr_multi_pl_regress',
    system: 'acetic_acid', modelKey: 'global_multi_irrev_pl',
    cardLabel: 'Multiple Data Sets — Parallel Irreversible Power Law Model',
    description: 'Reaction orders regressed from the supplied datasets. Upload each CSV or enter its observations manually.',
    rxn1Reactants: 'C2H6 + 0.5 O2', rxn1Products: 'ET + H2O',
    rxn2Reactants: 'C2H6 + 1.5 O2', rxn2Products: 'AA + H2O',
    rxn3Reactants: 'C2H6 + 3.5 O2', rxn3Products: '2 CO2 + 3 H2O',
    inerts: 'N2', reactorMode: 'pfr', reactionPhase: 'gas',
    concentrationBasis: 'mole_fraction', reversibility: 'irreversible',
    modelType: 'powerlaw', exponentMode: 'regress', keqMode: 'regress',
    fitScope: 'multiple', reactionTempC: '', reactionPressure: '240', pressureUnit: 'psi',
    wCatG: '24.0', vRxL: '0.25', rateBasis: 'cat_weight',
  },
  {
    id: 'acetic_pfr_multi_lhhw_fixed',
    system: 'acetic_acid', modelKey: 'global_multi_irrev_lhhw',
    cardLabel: 'Multiple Data Sets — Parallel Irreversible LHHW Model',
    description: 'Reaction orders fixed at 1. Fit separate user-supplied PFR datasets; upload each CSV or enter its observations manually.',
    rxn1Reactants: 'C2H6 + 0.5 O2', rxn1Products: 'ET + H2O',
    rxn2Reactants: 'C2H6 + 1.5 O2', rxn2Products: 'AA + H2O',
    rxn3Reactants: 'C2H6 + 3.5 O2', rxn3Products: '2 CO2 + 3 H2O',
    inerts: 'N2', reactorMode: 'pfr', reactionPhase: 'gas',
    concentrationBasis: 'mole_fraction', reversibility: 'irreversible',
    modelType: 'lhhw', exponentMode: 'fixed_1', keqMode: 'regress',
    fitScope: 'multiple', reactionTempC: '', reactionPressure: '240', pressureUnit: 'psi',
    wCatG: '24.0', vRxL: '0.25', rateBasis: 'cat_weight',
  },
  {
    id: 'acetic_pfr_multi_lhhw_regress',
    system: 'acetic_acid', modelKey: 'global_multi_irrev_lhhw',
    cardLabel: 'Multiple Data Sets — Parallel Irreversible LHHW Model',
    description: 'Reaction orders regressed from the supplied datasets. Upload each CSV or enter its observations manually.',
    rxn1Reactants: 'C2H6 + 0.5 O2', rxn1Products: 'ET + H2O',
    rxn2Reactants: 'C2H6 + 1.5 O2', rxn2Products: 'AA + H2O',
    rxn3Reactants: 'C2H6 + 3.5 O2', rxn3Products: '2 CO2 + 3 H2O',
    inerts: 'N2', reactorMode: 'pfr', reactionPhase: 'gas',
    concentrationBasis: 'mole_fraction', reversibility: 'irreversible',
    modelType: 'lhhw', exponentMode: 'regress', keqMode: 'regress',
    fitScope: 'multiple', reactionTempC: '', reactionPressure: '240', pressureUnit: 'psi',
    wCatG: '24.0', vRxL: '0.25', rateBasis: 'cat_weight',
  },

  // ╔══════════════════════════════════════════════════════════════════════╗
  // ║  METHYL ACETATE  (MeOAc)  —  HOAc + MeOH ⇌ MeOAc + H₂O           ║
  // ╚══════════════════════════════════════════════════════════════════════╝
  {
    id: 'meoac_single_rev_pl',
    system: 'meoac', modelKey: 'single_rev_pl',
    label: 'Single Data Set — Reversible Power Law Model',
    description: 'Power Law model for temperature-dependent rate constants regressed using a single data set.',
    rxn1Reactants: 'HOAc + MeOH', rxn1Products: 'MeOAc + H₂O',
    rxn2Reactants: '', rxn2Products: '', rxn3Reactants: '', rxn3Products: '', inerts: '',
    reactorMode: 'batch', reactionPhase: 'liquid', concentrationBasis: 'mole_fraction',
    reversibility: 'reversible', modelType: 'powerlaw', exponentMode: 'fixed_1', keqMode: 'regress',
    fitScope: 'single', nMixMol: '5.61', wCatG: '24.0', reactionTempC: '40',
    reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },
  {
    id: 'meoac_single_rev_lhhw',
    system: 'meoac', modelKey: 'single_rev_lhhw',
    label: 'Single Data Set — Reversible LHHW Model',
    description: 'Surface-mechanism LHHW model for temperature-dependent rate constants regressed using a single data set.',
    rxn1Reactants: 'HOAc + MeOH', rxn1Products: 'MeOAc + H₂O',
    rxn2Reactants: '', rxn2Products: '', rxn3Reactants: '', rxn3Products: '', inerts: '',
    reactorMode: 'batch', reactionPhase: 'liquid', concentrationBasis: 'mole_fraction',
    reversibility: 'reversible', modelType: 'lhhw', exponentMode: 'fixed_1', keqMode: 'regress',
    fitScope: 'single', nMixMol: '5.61', wCatG: '24.0', reactionTempC: '40',
    reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },
  {
    id: 'meoac_global_rev_pl',
    system: 'meoac', modelKey: 'global_rev_pl',
    label: 'Multi-Dataset Fit — Reversible Power Law Model',
    description: 'Power Law model for temperature-dependent rate constants regressed using multiple data sets.',
    rxn1Reactants: 'HOAc + MeOH', rxn1Products: 'MeOAc + H₂O',
    rxn2Reactants: '', rxn2Products: '', rxn3Reactants: '', rxn3Products: '', inerts: '',
    reactorMode: 'batch', reactionPhase: 'liquid', concentrationBasis: 'mole_fraction',
    reversibility: 'reversible', modelType: 'powerlaw', exponentMode: 'fixed_1', keqMode: 'regress',
    fitScope: 'multiple', nMixMol: '5.61', wCatG: '24.0', reactionTempC: '',
    reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },
  {
    id: 'meoac_global_rev_lhhw',
    system: 'meoac', modelKey: 'global_rev_lhhw',
    label: 'Multi-Dataset Fit — Reversible LHHW Model',
    description: 'Surface-mechanism LHHW model for temperature-dependent rate constants regressed using multiple data sets.',
    rxn1Reactants: 'HOAc + MeOH', rxn1Products: 'MeOAc + H₂O',
    rxn2Reactants: '', rxn2Products: '', rxn3Reactants: '', rxn3Products: '', inerts: '',
    reactorMode: 'batch', reactionPhase: 'liquid', concentrationBasis: 'mole_fraction',
    reversibility: 'reversible', modelType: 'lhhw', exponentMode: 'fixed_1', keqMode: 'regress',
    fitScope: 'multiple', nMixMol: '5.61', wCatG: '24.0', reactionTempC: '',
    reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },
  // ── MeOAc IRREVERSIBLE (forward-only) ─────────────────────────────────
  {
    id: 'meoac_single_irrev_pl',
    system: 'meoac', modelKey: 'single_irrev_pl',
    label: 'Single Data Set — Irreversible Power Law Model',
    description: 'Power Law model for temperature-dependent rate constants regressed using a single data set.',
    rxn1Reactants: 'HOAc + MeOH', rxn1Products: 'MeOAc + H₂O',
    rxn2Reactants: '', rxn2Products: '', rxn3Reactants: '', rxn3Products: '', inerts: '',
    reactorMode: 'batch', reactionPhase: 'liquid', concentrationBasis: 'mole_fraction',
    reversibility: 'irreversible', modelType: 'powerlaw', exponentMode: 'regress', keqMode: 'regress',
    fitScope: 'single', nMixMol: '5.61', wCatG: '24.0', reactionTempC: '40',
    reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },
  {
    id: 'meoac_single_irrev_lhhw',
    system: 'meoac', modelKey: 'single_irrev_lhhw',
    label: 'Single Data Set — Irreversible LHHW Model',
    description: 'Surface-mechanism LHHW model for temperature-dependent rate constants regressed using a single data set.',
    rxn1Reactants: 'HOAc + MeOH', rxn1Products: 'MeOAc + H₂O',
    rxn2Reactants: '', rxn2Products: '', rxn3Reactants: '', rxn3Products: '', inerts: '',
    reactorMode: 'batch', reactionPhase: 'liquid', concentrationBasis: 'mole_fraction',
    reversibility: 'irreversible', modelType: 'lhhw', exponentMode: 'fixed_1', keqMode: 'regress',
    fitScope: 'single', nMixMol: '5.61', wCatG: '24.0', reactionTempC: '40',
    reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },
  // [ADD — Madhura feedback, 9-Sep-26] "Why are there no example cases for
  // Multi-Dataset Fit — Irreversible Power Law and LHHW? There should be 8
  // cases for each example: Power Law, LHHW, Reversible, Irreversible."
  // MODEL_EQUATIONS/MODEL_KEY_META already had global_irrev_pl/lhhw entries
  // but no EXAMPLES card ever used them — added below to complete the
  // 2(single/multi) x 2(PL/LHHW) x 2(rev/irrev) = 8-case matrix.
  {
    id: 'meoac_global_irrev_pl',
    system: 'meoac', modelKey: 'global_irrev_pl',
    label: 'Multi-Dataset Fit — Irreversible Power Law Model',
    description: 'Power Law model for temperature-dependent rate constants regressed using multiple data sets.',
    rxn1Reactants: 'HOAc + MeOH', rxn1Products: 'MeOAc + H₂O',
    rxn2Reactants: '', rxn2Products: '', rxn3Reactants: '', rxn3Products: '', inerts: '',
    reactorMode: 'batch', reactionPhase: 'liquid', concentrationBasis: 'mole_fraction',
    reversibility: 'irreversible', modelType: 'powerlaw', exponentMode: 'regress', keqMode: 'regress',
    fitScope: 'multiple', nMixMol: '5.61', wCatG: '24.0', reactionTempC: '',
    reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },
  {
    id: 'meoac_global_irrev_lhhw',
    system: 'meoac', modelKey: 'global_irrev_lhhw',
    label: 'Multi-Dataset Fit — Irreversible LHHW Model',
    description: 'Surface-mechanism LHHW model for temperature-dependent rate constants regressed using multiple data sets.',
    rxn1Reactants: 'HOAc + MeOH', rxn1Products: 'MeOAc + H₂O',
    rxn2Reactants: '', rxn2Products: '', rxn3Reactants: '', rxn3Products: '', inerts: '',
    reactorMode: 'batch', reactionPhase: 'liquid', concentrationBasis: 'mole_fraction',
    reversibility: 'irreversible', modelType: 'lhhw', exponentMode: 'fixed_1', keqMode: 'regress',
    fitScope: 'multiple', nMixMol: '5.61', wCatG: '24.0', reactionTempC: '',
    reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },

  // ╔══════════════════════════════════════════════════════════════════════╗
  // ║  DIMETHYL ETHER  (DME)  —  2 MeOH ⇌ DME + H₂O                     ║
  // ╚══════════════════════════════════════════════════════════════════════╝
  {
    id: 'dme_single_rev_pl',
    system: 'dme', modelKey: 'single_rev_pl',
    label: 'Single Data Set — Reversible Power Law Model',
    description: 'Power Law model for temperature-dependent rate constants regressed using a single data set.',
    rxn1Reactants: '2 MeOH', rxn1Products: 'DME + H₂O',
    rxn2Reactants: '', rxn2Products: '', rxn3Reactants: '', rxn3Products: '', inerts: '',
    reactorMode: 'batch', reactionPhase: 'vapor', concentrationBasis: 'mole_fraction',
    reversibility: 'reversible', modelType: 'powerlaw', exponentMode: 'fixed_1', keqMode: 'regress',
    fitScope: 'single', nMixMol: '10.47', wCatG: '72.0', reactionTempC: '85',
    reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },
  {
    id: 'dme_single_rev_lhhw',
    system: 'dme', modelKey: 'single_rev_lhhw',
    label: 'Single Data Set — Reversible LHHW Model',
    description: 'Surface-mechanism LHHW model for temperature-dependent rate constants regressed using a single data set.',
    rxn1Reactants: '2 MeOH', rxn1Products: 'DME + H₂O',
    rxn2Reactants: '', rxn2Products: '', rxn3Reactants: '', rxn3Products: '', inerts: '',
    reactorMode: 'batch', reactionPhase: 'vapor', concentrationBasis: 'mole_fraction',
    reversibility: 'reversible', modelType: 'lhhw', exponentMode: 'fixed_1', keqMode: 'regress',
    fitScope: 'single', nMixMol: '10.47', wCatG: '72.0', reactionTempC: '85',
    reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },
  {
    id: 'dme_global_rev_pl',
    system: 'dme', modelKey: 'global_rev_pl',
    label: 'Multi-Dataset Fit — Reversible Power Law Model',
    description: 'Power Law model for temperature-dependent rate constants regressed using multiple data sets.',
    rxn1Reactants: '2 MeOH', rxn1Products: 'DME + H₂O',
    rxn2Reactants: '', rxn2Products: '', rxn3Reactants: '', rxn3Products: '', inerts: '',
    reactorMode: 'batch', reactionPhase: 'vapor', concentrationBasis: 'mole_fraction',
    reversibility: 'reversible', modelType: 'powerlaw', exponentMode: 'fixed_1', keqMode: 'regress',
    fitScope: 'multiple', nMixMol: '10.47', wCatG: '72.0', reactionTempC: '',
    reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },
  {
    id: 'dme_global_rev_lhhw',
    system: 'dme', modelKey: 'global_rev_lhhw',
    label: 'Multi-Dataset Fit — Reversible LHHW Model',
    description: 'Surface-mechanism LHHW model for temperature-dependent rate constants regressed using multiple data sets.',
    rxn1Reactants: '2 MeOH', rxn1Products: 'DME + H₂O',
    rxn2Reactants: '', rxn2Products: '', rxn3Reactants: '', rxn3Products: '', inerts: '',
    reactorMode: 'batch', reactionPhase: 'vapor', concentrationBasis: 'mole_fraction',
    reversibility: 'reversible', modelType: 'lhhw', exponentMode: 'fixed_1', keqMode: 'regress',
    fitScope: 'multiple', nMixMol: '10.47', wCatG: '72.0', reactionTempC: '',
    reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },
  // ── DME IRREVERSIBLE (forward-only) ───────────────────────────────────
  {
    id: 'dme_single_irrev_pl',
    system: 'dme', modelKey: 'single_irrev_pl',
    label: 'Single Data Set — Irreversible Power Law Model',
    description: 'Power Law model for temperature-dependent rate constants regressed using a single data set.',
    rxn1Reactants: '2 MeOH', rxn1Products: 'DME + H₂O',
    rxn2Reactants: '', rxn2Products: '', rxn3Reactants: '', rxn3Products: '', inerts: '',
    reactorMode: 'batch', reactionPhase: 'vapor', concentrationBasis: 'mole_fraction',
    reversibility: 'irreversible', modelType: 'powerlaw', exponentMode: 'regress', keqMode: 'regress',
    fitScope: 'single', nMixMol: '10.47', wCatG: '72.0', reactionTempC: '85',
    reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },
  {
    id: 'dme_single_irrev_lhhw',
    system: 'dme', modelKey: 'single_irrev_lhhw',
    label: 'Single Data Set — Irreversible LHHW Model',
    description: 'Surface-mechanism LHHW model for temperature-dependent rate constants regressed using a single data set.',
    rxn1Reactants: '2 MeOH', rxn1Products: 'DME + H₂O',
    rxn2Reactants: '', rxn2Products: '', rxn3Reactants: '', rxn3Products: '', inerts: '',
    reactorMode: 'batch', reactionPhase: 'vapor', concentrationBasis: 'mole_fraction',
    reversibility: 'irreversible', modelType: 'lhhw', exponentMode: 'fixed_1', keqMode: 'regress',
    fitScope: 'single', nMixMol: '10.47', wCatG: '72.0', reactionTempC: '85',
    reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },
  // [ADD — Madhura feedback, 9-Sep-26] same 8-case-matrix completion as
  // meoac above — "make the same changes for the DME example."
  {
    id: 'dme_global_irrev_pl',
    system: 'dme', modelKey: 'global_irrev_pl',
    label: 'Multi-Dataset Fit — Irreversible Power Law Model',
    description: 'Power Law model for temperature-dependent rate constants regressed using multiple data sets.',
    rxn1Reactants: '2 MeOH', rxn1Products: 'DME + H₂O',
    rxn2Reactants: '', rxn2Products: '', rxn3Reactants: '', rxn3Products: '', inerts: '',
    reactorMode: 'batch', reactionPhase: 'vapor', concentrationBasis: 'mole_fraction',
    reversibility: 'irreversible', modelType: 'powerlaw', exponentMode: 'regress', keqMode: 'regress',
    fitScope: 'multiple', nMixMol: '10.47', wCatG: '72.0', reactionTempC: '',
    reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },
  {
    id: 'dme_global_irrev_lhhw',
    system: 'dme', modelKey: 'global_irrev_lhhw',
    label: 'Multi-Dataset Fit — Irreversible LHHW Model',
    description: 'Surface-mechanism LHHW model for temperature-dependent rate constants regressed using multiple data sets.',
    rxn1Reactants: '2 MeOH', rxn1Products: 'DME + H₂O',
    rxn2Reactants: '', rxn2Products: '', rxn3Reactants: '', rxn3Products: '', inerts: '',
    reactorMode: 'batch', reactionPhase: 'vapor', concentrationBasis: 'mole_fraction',
    reversibility: 'irreversible', modelType: 'lhhw', exponentMode: 'fixed_1', keqMode: 'regress',
    fitScope: 'multiple', nMixMol: '10.47', wCatG: '72.0', reactionTempC: '',
    reactionPressure: '1', pressureUnit: 'atm', rateBasis: 'cat_weight',
  },


];

export const EXAMPLES = buildExamples();

// ─── Model key display metadata ───────────────────────────────────────────
export const MODEL_KEY_META = {
  // ── Reversible ────────────────────────────────────────────────────────
  single_rev_pl:         { label: 'Single Data Set — Reversible Power Law Model',                  icon: '⇌', tag: 'Single Data Set',  tagColor: '#4a90d9' },
  single_rev_lhhw:       { label: 'Single Data Set — Reversible LHHW Model',                       icon: '⇌', tag: 'Single Data Set',  tagColor: '#8b55db' },
  global_rev_pl:         { label: 'Multi-Dataset Fit — Reversible Power Law Model',      icon: '⇌', tag: 'Multi-Dataset Fit',  tagColor: '#9e4c6e' },
  global_rev_lhhw:       { label: 'Multi-Dataset Fit — Reversible LHHW Model',          icon: '⇌', tag: 'Multi-Dataset Fit',  tagColor: '#9e4c6e' },
  // ── Irreversible (single rxn, forward only) ───────────────────────────
  single_irrev_pl:       { label: 'Single Data Set — Irreversible Power Law Model',                 icon: '→', tag: 'Single Data Set',  tagColor: '#2e7d32' },
  single_irrev_lhhw:     { label: 'Single Data Set — Irreversible LHHW Model',                     icon: '→', tag: 'Single Data Set',  tagColor: '#2e7d32' },
  global_irrev_pl:       { label: 'Multi-Dataset Fit — Irreversible Power Law',    icon: '→', tag: 'Multi-Dataset Fit',  tagColor: '#1b5e20' },
  global_irrev_lhhw:     { label: 'Multi-Dataset Fit — Irreversible LHHW',        icon: '→', tag: 'Multi-Dataset Fit',  tagColor: '#1b5e20' },
  // ── Multi-reaction parallel irreversible (AA) ─────────────────────────
  multi_irrev_pl:        { label: 'Multi-Reaction Parallel System — Irreversible Power Law',  icon: '→', tag: 'Multi-Rxn',   tagColor: '#E65100' },
  multi_irrev_lhhw:      { label: 'Multi-Reaction Parallel System — Irreversible LHHW',      icon: '→', tag: 'Multi-Rxn',   tagColor: '#E65100' },
  global_multi_irrev_pl:    { label: 'Multi-Dataset Fit — Irreversible Power Law',         icon: '→', tag: 'Multi-Dataset Fit', tagColor: '#bf360c' },
  global_multi_irrev_lhhw:  { label: 'Multi-Dataset Fit — Irreversible LHHW',             icon: '→', tag: 'Multi-Dataset Fit', tagColor: '#bf360c' },
};

// Built-in presets include the verified Song Batch examples and the legacy
// Acetic Acid PFR dataset. The PFR preset is deliberately limited to that
// recognizable historical header signature; custom PFR files still use the
// generic species-key/name/formula header mapper.
const REACTOR_GROUPS = [
  { key: 'batch', label: 'Batch Examples', systems: ['meoac', 'dme'] },
  { key: 'pfr', label: 'PFR Examples', systems: ['acetic_acid'] },
];

const ExamplesModal = ({ show, onClose, onSelect }) => {
  const [activeGroupKey, setActiveGroupKey] = React.useState('batch');
  const [activeSystem, setActiveSystem] = React.useState('meoac');
  if (!show) return null;

  const activeGroup = REACTOR_GROUPS.find(g => g.key === activeGroupKey) || REACTOR_GROUPS[0];
  const sysMeta    = SYSTEM_META[activeSystem];

  // [FIX] Acetic Acid is no longer gated behind privileged roles — every
  // example in every reactor group is fully visible and selectable.
  const sysExamples = EXAMPLES.filter(e => e.system === activeSystem);

  const selectGroup = (gKey) => {
    const group = REACTOR_GROUPS.find(g => g.key === gKey);
    setActiveGroupKey(gKey);
    if (group) setActiveSystem(group.systems[0]);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(61,31,48,0.55)', zIndex: 9990,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, animation: 'fadeIn 0.15s ease' }}>
      <div style={{ background: `var(--color-surface)`, border: `1px solid var(--color-border)`,
        borderTop: `3px solid ${sysMeta.color}`, borderRadius: 10, width: '100%', maxWidth: 720,
        maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        boxShadow: `0 24px 64px ${sysMeta.color}22` }}>

        {/* ── Header ── */}
        <div style={{ padding: '16px 22px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: `var(--color-text)`, fontFamily: `var(--font-ui)` }}>
                Initialize with Examples
              </div>
              <div style={{ fontSize: 12, color: `var(--color-text-muted)`, fontFamily: `var(--font-ui)`, marginTop: 2 }}>
                Choose a model setup to prefill chemistry and conditions. Data points stay blank until you upload a CSV or enter them manually.
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: `var(--color-text-muted)`,
              fontSize: 20, cursor: 'pointer', padding: '0 4px', borderRadius: 4, lineHeight: 1 }}>✕</button>
          </div>

          {/* ── Reactor-type tabs (Batch vs PFR — client requirement) ── */}
          <div style={{ display: 'flex', gap: 0, borderBottom: `2px solid var(--color-border)` }}>
            {REACTOR_GROUPS.map(g => {
              const active = activeGroupKey === g.key;
              return (
                <button key={g.key} onClick={() => selectGroup(g.key)} style={{
                  padding: '9px 20px', fontSize: 13, fontWeight: active ? 700 : 500,
                  fontFamily: `var(--font-ui)`, cursor: 'pointer',
                  background: active ? `var(--color-surface)` : 'transparent',
                  color: active ? `var(--color-text)` : `var(--color-text-muted)`,
                  border: 'none', borderBottom: active ? `2px solid var(--color-accent)` : '2px solid transparent',
                  marginBottom: '-2px', transition: 'all 0.15s', letterSpacing: '0.01em',
                }}>
                  {g.label}
                </button>
              );
            })}
          </div>

          {/* ── System sub-tabs — shown when the active reactor group has
              more than one chemical system ── */}
          {activeGroup.systems.length > 1 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              {activeGroup.systems.map(sKey => {
                const sm = SYSTEM_META[sKey];
                const active = activeSystem === sKey;
                return (
                  <button key={sKey} onClick={() => setActiveSystem(sKey)} style={{
                    padding: '4px 12px', fontSize: 12, fontWeight: active ? 700 : 500,
                    fontFamily: `var(--font-ui)`, cursor: 'pointer', borderRadius: 14,
                    background: active ? sm.color + '1a' : 'transparent',
                    color: active ? sm.color : `var(--color-text-muted)`,
                    border: `1px solid ${active ? sm.color + '55' : `var(--color-border)`}`,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    {sm.displayName}
                    <span style={{ fontSize: 11, fontWeight: 700, fontFamily: `var(--font-ui)`,
                      background: sm.color + '15', color: sm.color,
                      border: `1px solid ${sm.color}30`, borderRadius: 3, padding: '0 4px' }}>
                      {sm.shortName}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── System equation strip ── */}
        <div style={{ padding: '10px 22px 8px',
          background: sysMeta.color + '0d',
          borderBottom: `1px solid ${sysMeta.color}22`,
          flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* [FIX — Madhura feedback, 9-Sep-26] "Write full name, not
                symbols, here" — was "{shortName} Reaction" (e.g. "MeOAc
                Reaction"); now the full system name, matching the
                identical fix in HelpExamples.jsx. */}
            <span style={{ fontSize: 12, fontWeight: 700,
              color: sysMeta.color,
              fontFamily: `var(--font-ui)`, textTransform: 'uppercase', letterSpacing: '0.08em', flexShrink: 0 }}>
              {sysMeta.displayName} System
            </span>
            <div style={{ background: `var(--color-code-bg)`,
              border: `1px solid ${sysMeta.color}33`,
              borderRadius: 4, padding: '3px 12px', fontSize: 13, fontFamily: `var(--font-ui)`,
              color: sysMeta.color, fontWeight: 600, flex: 1 }}>
              {sysMeta.equation}
            </div>
            {activeGroupKey !== 'pfr' && (sysMeta.reference || sysMeta.doi) && (
            <div style={{ fontSize: 13, color: `var(--color-text-dim)`, fontFamily: `var(--font-ui)`,
              flexShrink: 0, maxWidth: 200, textAlign: 'right', lineHeight: 1.4 }}>
              📚 {sysMeta.reference}
              {sysMeta.doi && <><br/><span style={{ color: sysMeta.color }}>DOI: {sysMeta.doi}</span></>}
            </div>
            )}
          </div>
          {/* [ADD — Madhura feedback] reaction-type descriptor "in text
              underneath" instead of folded into the heading above. */}
          {sysMeta.reactionTypeLabel && (
            <div style={{ fontSize: 12, color: `var(--color-text-dim)`, fontFamily: `var(--font-ui)`,
              marginTop: 6, fontStyle: 'italic' }}>
              {sysMeta.reactionTypeLabel}
            </div>
          )}
        </div>

        {/* ── Example cards list ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 22px 18px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sysExamples.map(ex => {
              const mm      = MODEL_KEY_META[ex.modelKey];
              const cardLabel = ex.cardLabel || mm?.label;
              const eq      = MODEL_EQUATIONS[ex.system]?.[ex.modelKey];
              const isNA    = ex.na;
              const disabled = isNA;

              return (
                <div key={ex.id}
                  onClick={() => { if (!disabled) { onSelect(ex); onClose(); } }}
                  style={{
                    border: `1px solid ${isNA ? `var(--color-border)` : sysMeta.color + '55'}`,
                    borderLeft: `3px solid ${isNA ? `var(--color-border)` : sysMeta.color}`,
                    borderRadius: 6, padding: '10px 14px',
                    background: isNA ? `var(--color-shell)` : `var(--color-card)`,
                    cursor: disabled ? 'default' : 'pointer',
                    opacity: isNA ? 0.45 : 1,
                    transition: 'all 0.12s', position: 'relative',
                  }}
                  onMouseEnter={e => { if (!disabled) { e.currentTarget.style.background = sysMeta.color + '0f'; } }}
                  onMouseLeave={e => { if (!disabled) { e.currentTarget.style.background = `var(--color-card)`; } }}>

                  {/* ── Card header row ── */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 14, flexShrink: 0 }}>{mm?.icon}</span>
                      <span style={{ fontSize: 13, fontWeight: 700,
                        color: isNA ? `var(--color-text-dim)` : `var(--color-text)`,
                        fontFamily: `var(--font-ui)` }}>
                        {cardLabel}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                      {isNA && (
                        <span style={{ background: `var(--color-warning-dim)`, color: `var(--color-warning-dark)`,
                          border: `1px solid var(--color-warning-dark)33`, borderRadius: 3,
                          fontSize: 12, fontWeight: 700, padding: '1px 6px', letterSpacing: '0.06em',
                          fontFamily: `var(--font-ui)`, textTransform: 'uppercase' }}>N/A</span>
                      )}
                      <span style={{ background: mm?.tagColor + '22', color: mm?.tagColor,
                        border: `1px solid ${mm?.tagColor}44`, borderRadius: 3,
                        fontSize: 12, fontWeight: 700, padding: '1px 6px', letterSpacing: '0.06em',
                        fontFamily: `var(--font-ui)`, textTransform: 'uppercase' }}>
                        {mm?.tag}
                      </span>
                      <span style={{ background: ex.modelType === 'lhhw' ? '#8b55db22' : '#4a90d922',
                        color: ex.modelType === 'lhhw' ? '#8b55db' : '#4a90d9',
                        border: `1px solid ${ex.modelType === 'lhhw' ? '#8b55db' : '#4a90d9'}44`,
                        borderRadius: 3, fontSize: 12, fontWeight: 700, padding: '1px 6px',
                        letterSpacing: '0.06em', fontFamily: `var(--font-ui)`, textTransform: 'uppercase' }}>
                        {ex.modelType === 'lhhw' ? 'LHHW' : 'Power Law'}
                      </span>
                    </div>
                  </div>

                  {/* ── Description ── */}
                  <div style={{ fontSize: 12, color: `var(--color-text-muted)`, fontFamily: `var(--font-ui)`,
                    lineHeight: 1.5, marginBottom: (eq && !isNA) ? 8 : 0 }}>
                    {ex.description}
                  </div>

                  {/* ── Rate equation preview ── */}
                  {eq && !isNA && (
                    <div style={{ background: `var(--color-code-bg)`, border: `1px solid ${sysMeta.color}22`,
                      borderRadius: 4, padding: '5px 10px', fontSize: 12, fontFamily: `var(--font-ui)`,
                      color: sysMeta.color, fontWeight: 600, marginTop: 6, lineHeight: 1.6 }}>
                      <span style={{ fontSize: 12, color: `var(--color-text-dim)`, fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: 8 }}>Rate:</span>
                      {eq}
                    </div>
                  )}

                  {/* ── Conditions strip ── */}
                  {!isNA && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                      {[
                        ex.reactorMode.toUpperCase(),
                        ex.reactionPhase,
                        ex.fitScope === 'multiple' ? 'Multiple Data Sets' : 'Single Data Set',
                        ex.reversibility === 'reversible' ? '⇌ Reversible' : '→ Irreversible',
                        ex.reactionTempC ? `T = ${ex.reactionTempC}°C` : null,
                      ].filter(Boolean).map((tag, i) => (
                        <span key={i} style={{ fontSize: 13, fontFamily: `var(--font-ui)`, color: `var(--color-text-dim)`,
                          background: `var(--color-panel)`, borderRadius: 3, padding: '2px 7px',
                          border: `1px solid var(--color-border-light)` }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PANEL 1 — INPUT  (split into Data Input & Model Input tabs — Madhura feedback #2)
// ═══════════════════════════════════════════════════════════════════════════
const PanelInput = ({
  reactionSystem,
  reactorMode, setReactorMode, concentrationBasis, setConcentrationBasis,
  reactionPhase, setReactionPhase, reactionPressure, setReactionPressure,
  pressureUnit, setPressureUnit,
  file, dataPreview, autoDetect, tempWarning, moleFractionWarnings, normalizedCsvBlob,
  dataSourceType, // 'csv' | 'manual' | null — gates the auto-detect/preview block to CSV uploads only
  onFileChange, onRemoveFile, onManualEntry, onManualEntryForRun,
  nMixMol, setNMixMol, wCatG, setWCatG, vRxL, setVRxL, rateBasis, setRateBasis,
  fitScope, setFitScope, globalRuns, setGlobalRuns,
  reversibility, setReversibility,
  modelType, setModelType,
  exponentMode, setExponentMode,
  keqMode, setKeqMode, keqUserValues, setKeqUserValues,
  customLhhwKiValues, setCustomLhhwKiValues,
  reactionTempC, setReactionTempC,
  catWtUnit, setCatWtUnit,
  speciesWeights, setSpeciesWeights, selectedPreset, setSelectedPreset,
  reactionOrders, setReactionOrders,
  onSubmit, loading, fittingProgress, onReinitialize,
  onResetAll,       // full in-place Reset (see handleResetAll) — replaces the old window.location.reload()
  onStop, progressFraction = 0, estimatedRuntimeSec = 0,   // Run Fitting UX: stop control + real progress
  runMeta,          // set while a fit is running — lets this banner show even right after navigating back mid-run
  excludedRows,     // Jun-25 2026: for exclusion badge in Run Fitting header
  onShowExamples,   // GAP 2 FIX: Examples button handler
  exampleLoadTick,  // [FIX] bumps on every example load — forces reaction-row count resync
  rxn1Reactants, setRxn1Reactants, rxn1Products, setRxn1Products,
  rxn2Reactants, setRxn2Reactants, rxn2Products, setRxn2Products,
  rxn3Reactants, setRxn3Reactants, rxn3Products, setRxn3Products,
  rxn4Reactants, setRxn4Reactants, rxn4Products, setRxn4Products,
  rxn5Reactants, setRxn5Reactants, rxn5Products, setRxn5Products,
  rxn6Reactants, setRxn6Reactants, rxn6Products, setRxn6Products,
  rxn7Reactants, setRxn7Reactants, rxn7Products, setRxn7Products,
  rxn8Reactants, setRxn8Reactants, rxn8Products, setRxn8Products,
  rxn9Reactants, setRxn9Reactants, rxn9Products, setRxn9Products,
  rxn10Reactants, setRxn10Reactants, rxn10Products, setRxn10Products,
  inertComponents, setInertComponents,
  registeredComponents, setRegisteredComponents,
  speciesIdentityMap = {}, setSpeciesIdentityMap,
  selectedDataSets,  // 18-Aug-2026 ADD — see runsSelectedCount below
}) => {
  // speciesIdentityMap is supplied by the parent and defaults to an empty map.

  // Sub-tab state: 'data' | 'model'
  const [inputTab, setInputTab] = useState('data');

  // Controls the themed Reset confirmation modal (replaces window.confirm()).
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Generic/custom systems support up to ten reactions. AA's dedicated
  // literature model remains its own fixed three-reaction system.
  const reactionEntries = [
    { label: 'Reaction 1', reactants: rxn1Reactants, setReactants: setRxn1Reactants, products: rxn1Products, setProducts: setRxn1Products },
    { label: 'Reaction 2', reactants: rxn2Reactants, setReactants: setRxn2Reactants, products: rxn2Products, setProducts: setRxn2Products },
    { label: 'Reaction 3', reactants: rxn3Reactants, setReactants: setRxn3Reactants, products: rxn3Products, setProducts: setRxn3Products },
    { label: 'Reaction 4', reactants: rxn4Reactants, setReactants: setRxn4Reactants, products: rxn4Products, setProducts: setRxn4Products },
    { label: 'Reaction 5', reactants: rxn5Reactants, setReactants: setRxn5Reactants, products: rxn5Products, setProducts: setRxn5Products },
    { label: 'Reaction 6', reactants: rxn6Reactants, setReactants: setRxn6Reactants, products: rxn6Products, setProducts: setRxn6Products },
    { label: 'Reaction 7', reactants: rxn7Reactants, setReactants: setRxn7Reactants, products: rxn7Products, setProducts: setRxn7Products },
    { label: 'Reaction 8', reactants: rxn8Reactants, setReactants: setRxn8Reactants, products: rxn8Products, setProducts: setRxn8Products },
    { label: 'Reaction 9', reactants: rxn9Reactants, setReactants: setRxn9Reactants, products: rxn9Products, setProducts: setRxn9Products },
    { label: 'Reaction 10', reactants: rxn10Reactants, setReactants: setRxn10Reactants, products: rxn10Products, setProducts: setRxn10Products },
  ];
  const inputReactionRows = reactionEntries.map((rxn, index) => ({ ...rxn, index: index + 1 }));
  const inferredReactionCount = Math.max(1, ...reactionEntries
    .map((rxn, idx) => (String(rxn.reactants || '').trim() || String(rxn.products || '').trim()) ? idx + 1 : 0));
  const [meoacDmeReactionCount, setMeoacDmeReactionCount] = useState(inferredReactionCount);
  // 23-Oct-2026: which reactions have their component table folded away (index -> true).
  const [collapsedRxnNames, setCollapsedRxnNames] = useState({});
  useEffect(() => {
    if (inferredReactionCount > meoacDmeReactionCount) setMeoacDmeReactionCount(inferredReactionCount);
    // Deliberately only auto-grow on ordinary edits; clearing/removing the
    // last row must not collapse a row while the user is still entering it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rxn1Reactants, rxn1Products, rxn2Reactants, rxn2Products,
    rxn3Reactants, rxn3Products, rxn4Reactants, rxn4Products,
    rxn5Reactants, rxn5Products, rxn6Reactants, rxn6Products,
    rxn7Reactants, rxn7Products, rxn8Reactants, rxn8Products,
    rxn9Reactants, rxn9Products, rxn10Reactants, rxn10Products,
  ]);

  // [FIX] On every "Initialize with Example" selection, hard-reset the
  // visible reaction-row count to match exactly what that example defines
  // (1 for MeOAc/DME single-reaction examples, 3 for the AA preset), instead
  // of only ever growing. Runs only when exampleLoadTick changes (i.e. on
  // an explicit example load), so it never interferes with a user manually
  // adding/typing into additional reactions via "+ Add Reaction".
  useEffect(() => {
    if (exampleLoadTick === undefined) return;
    setCollapsedRxnNames({});   // a freshly loaded example starts with every table open
    setMeoacDmeReactionCount(Math.max(1, ...reactionEntries
      .map((rxn, idx) => (String(rxn.reactants || '').trim() || String(rxn.products || '').trim()) ? idx + 1 : 0)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exampleLoadTick]);

  // Per-system species list — drives weights panel + form submission
  // ── [FIX] AA keys use storage format (C2H6, O2, AA, ET, CO2, H2O, N2) to match
  //    the root SYSTEM_SPECIES_MAP and speciesWeights state. Display labels are
  //    shown via the Field label prop below, not encoded in the key itself.
  //    This ensures Model Input weights and Optimize "Tune Species Weights" read
  //    from the same keys — fixing the "weights not preserved after cool start" bug.
  const SYSTEM_SPECIES = {
    meoac:       ['MeOAc', 'HOAc', 'MeOH', 'H2O'],
    dme:         ['DME', 'MeOH', 'H2O'],
    acetic_acid: ['C2H6', 'O2', 'AA', 'ET', 'CO2', 'H2O', 'N2'],
  };
  // [FIX] Derive species from whatever is actually typed into Reaction 1–10 +
  // Inerts, rather than only ever recognizing the 3 built-in preset species
  // lists. Falls back to the preset (or MeOAc) only when nothing's been
  // typed yet. AA keeps its fixed list — it's a privileged, fixed-species
  // system, not something users redefine via Components & Stoichiometry.
  // Song 1998 supplies the MeOAc and DME reaction systems. The PFR AA
  // three-reaction network is from Madhura's dataset + aa_kinetics_objective.py,
  // not from the Song paper.
  const activeSpecies = reactionSystem === 'acetic_acid'
    ? SYSTEM_SPECIES.acetic_acid
    : deriveSpeciesFromReactions(
        inputReactionRows,
        inertComponents,
        SYSTEM_SPECIES[reactionSystem] || SYSTEM_SPECIES.meoac,
        registeredComponents,
        speciesIdentityMap,
      );

  // Inert species always carry a fixed zero weight, so they're excluded
  // from the Objective Function Weights box list entirely (Madhura,
  // 17-Jul-2026 call) rather than shown as a disabled/zeroed box.
  const inertSpeciesKeys = reactionSystem === 'acetic_acid'
    ? ['N2']
    : (inertComponents || '').split(/[,+]/).map(s => s.trim()).filter(Boolean);
  const weightSpeciesToShow = activeSpecies.filter(sp => !inertSpeciesKeys.includes(sp));

  // NOTE: an earlier `weightPresets` constant used to live here (focusPrimary/
  // balanced/highPrimary dicts per reaction system). It's been removed as dead
  // code — actual preset weights are computed inline from `reactionSystem` /
  // `weightSpeciesToShow` in the "Objective Function Weights" ToggleSwitch
  // onChange handler below (see productFocusWeights / balancedWeights).

  // Fitting Scope simplified to 2 options (Madhura, follow-up call 8-Jul-2026, item A):
  // "Single data set" vs "Multiple data sets". A single CSV can already contain rows at
  // multiple temperatures, so there's no separate "Global" option any more — for the AA
  // system, whether shared Ea/Af (multi-temperature) treatment is needed is now detected
  // automatically from the uploaded/entered CSV at submit time (see detectAAMultiTemp()).
  // "Multiple data sets" = 2+ separately-uploaded CSV files (AAMultiCsvManager for AA,
  // GlobalRunManager for MeOAc/DME).
  const isAAMultiCsv = reactionSystem === 'acetic_acid' && fitScope === 'multiple';
  // 18-Aug-2026 FIX (client-reported bug) — the Run button used to always
  // read globalRuns.length ("Run Multi-3 Dataset Fit") even after some runs
  // were unchecked under Optimise → "Data Sets for Regression". Since
  // clicking this exact button actually submits only the SELECTED runs (see
  // handleSubmit's own identical filter), the label was silently promising
  // one number and delivering another — exactly what produced the "labelled
  // 3, ran 2" mismatch. Mirrors handleSubmit's `run.id ?? idx` derivation
  // exactly so the count always matches what's really about to be
  // submitted.
  const runsSelectedCount = (!selectedDataSets || selectedDataSets.length === 0)
    ? globalRuns.length
    : globalRuns.filter((r, idx) => selectedDataSets.includes(r.id ?? idx)).length;
  // 18-Aug-2026 FIX (client-reported bug, round 2) — MeOAc/DME multi-run
  // previously only required runsSelectedCount > 0, so excluding down to
  // just 1 selected dataset still let the fit run (screenshot: 1 of 3
  // checked, "Running optimization…" proceeded anyway). A "multi-dataset
  // fit" with only 1 dataset selected doesn't make sense — require >=2
  // selected for EVERY multi-dataset scope now, matching AA's existing
  // rule, so the button disables upfront the same way in both cases.
  const canRun = fitScope === 'multiple'
    ? (isAAMultiCsv ? globalRuns.length >= 2 : globalRuns.length > 0)
        && globalRuns.every(r => r.file) && runsSelectedCount >= 2
    : !!file;                                                 // Single data set: one CSV / manual entry



  // ── Sub-tab bar styles ────────────────────────────────────────────────────
  const tabBarStyle = {
    display: 'flex',
    gap: 0,
    borderBottom: `2px solid ${C.border}`,
    marginBottom: 12,
    background: C.panel,
    flexShrink: 0,
  };
  const tabStyle = (active) => ({
    padding: '10px 22px',
    fontSize: 13,                          // feedback 1b: bigger tab font
    fontWeight: active ? 700 : 500,
    fontFamily: font.ui,
    cursor: 'pointer',
    background: active ? C.surface : 'transparent',
    color: active ? C.accent : C.textMuted,
    borderBottom: active ? `2px solid ${C.accent}` : '2px solid transparent',
    marginBottom: '-2px',
    transition: 'all 0.15s',
    border: 'none',
    outline: 'none',
    letterSpacing: '0.01em',
  });

  return (
    <>
    <ResetConfirmModal
      show={showResetConfirm}
      onCancel={() => setShowResetConfirm(false)}
      onConfirm={() => {
        // 13-Aug-2026 FIX — same real-cancellation gap as Stop/Reinitialize:
        // a page reload abandons the in-flight request client-side but was
        // never telling the backend, so a fit running when Reset was
        // clicked kept going server-side. Stop it first, then reload.
        // Close the confirmation first. Reset used to end in a page reload,
        // which destroyed the modal as a side effect; now that Reset happens
        // in place the modal has to be dismissed explicitly (and doing it
        // first means it still closes even if a later step throws).
        setShowResetConfirm(false);
        if (loading && onStop) onStop();
        // FIX (Reset still restored previous fit-run selections): a page
        // reload re-mounts useInputSession, which immediately RESTORES the
        // backend-saved session (every field, selected data sets, excluded
        // rows, the last result) — so Reset appeared to do nothing. Reset
        // now clears the state in place (handleResetAll) so nothing is
        // reloaded from the saved session. The reload stays only as a
        // fallback if the handler isn't supplied.
        if (onResetAll) onResetAll();
        else window.location.reload();
      }}
    />
    <PanelShell
      title="Input"
      subtitle="Configure system · upload data · set run parameters"
      icon={<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="0.5" y="0.5" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="1.2"/><path d="M3 4h8M3 7h5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>}
      hideHeader
    >
      {/* ── "Initialize with Example" + Run Fitting — same row ──────────────
          Sagar feedback (Jun 10): Run Fitting doesn't belong crowded next to
          the panel title; moved down here, right-aligned, next to the
          "Initialize with Example" row where there was empty space.        */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        padding: '7px 0 4px 0', marginBottom: 2,
        borderBottom: `1px dashed ${C.borderLight}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flexWrap: 'wrap' }}>
          <Btn variant="secondary" onClick={onShowExamples}
            style={{ fontSize: 12, padding: '5px 14px', flexShrink: 0 }}>
            ✦ Initialize with Examples
          </Btn>
          {/* "New Custom System" button removed (03-Aug-2026 feedback) — the
              Input tab now opens directly in the blank custom-system state
              by default (see reactionSystem's initial useState above), so
              there's no separate entry point needed. "Initialize with Example" (above) remains the only way to pre-populate one of
              the 3 built-in reference systems; everything else — including
              the results/Graph/Optimize/Appendix panels — already reads
              off the same `reactionSystem` state, so a custom run and an
              example run share one implementation, not two. */}
          <span style={{ fontSize: 13, color: C.textDim, fontFamily: font.ui }}>
            Pre-populate all fields from a reference system, then upload your data and run.
          </span>
          {/* Distinct from "Initialize with Example" above (Madhura, 17-Jul-2026
              call — "Initialize with Example" and "examples" are two different
              things): this opens the locked, read-only reference library under
              Help in a new tab, so it never touches or resets your current
              in-progress work here. */}
          <a href="/examples" target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 12, color: C.accent, fontFamily: font.ui, textDecoration: 'none',
              display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            📖 View Examples (reference)
          </a>
        </div>
        <div className="df-fit-actions" style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
          {/* Exclusion badge — shown when user has flagged rows in ResidualTable */}
          {excludedRows?.size > 0 && (
            <div style={{
              background: `${cssVar('--color-warning-dark')}22`,
              border: `1px solid ${cssVar('--color-warning-dark')}55`,
              borderRadius: 4, padding: '3px 9px',
              fontSize: 10, fontFamily: font.ui,
              color: cssVar('--color-warning-dark'), fontWeight: 700,
            }}>
              ⚠ {excludedRows.size} row{excludedRows.size > 1 ? 's' : ''} excluded
            </div>
          )}
          {/* 13-Aug-2026 REMOVED (client requirement — Progress Indicator gap):
              "The 'Est. ~24s' estimate is misleading — actual runs often take
              significantly longer." Agreed to drop the static estimate for
              now; a future version could derive a real estimate from
              historical run data (reaction type, reversibility, dataset
              count). preRunEstimateSec/estimatedRuntimeSec are kept internally
              — they still drive the fallback progress bar's rate of advance
              for the rare case a run has no real backend progress yet — but
              are no longer shown to the user as a concrete number. */}
          {/* Reset — clears an initialised example (or any in-progress entry)
              and starts blank, without the user having to hit the browser's
              reload button. Disabled mid-run; use Stop to cancel a run. */}
          <Btn variant="secondary" disabled={loading} onClick={() => setShowResetConfirm(true)}
            className="df-fit-btn"
            style={{ fontSize: 12, padding: '7px 12px' }} title="Clear all fields (including uploaded data) and start a new system">
            ↺ Reset
          </Btn>
          {/* Reinitialize — client requirement (Run Controls gap): "Reset" is
              a full restart that also wipes uploaded data, which is ambiguous/
              too destructive when someone just wants a clean optimization pass
              on the SAME data. Reinitialize restores model/optimization inputs
              (weights, exponent mode, iteration settings, initial guesses) to
              defaults without touching the uploaded file/CSV or reaction system. */}
          <Btn variant="secondary" disabled={loading} onClick={onReinitialize}
            className="df-fit-btn"
            style={{ fontSize: 12, padding: '7px 12px' }}
            title="Reset model/optimization inputs to defaults — keeps your uploaded data">
            ⟲ Reinitialize
          </Btn>
          {/* Stop/Abort — cancels the in-flight fitting request. Only shown
              (enabled) while a run is actually in progress. */}
          <Btn variant="secondary" disabled={!loading} onClick={onStop}
            className="df-fit-btn"
            style={{
              fontSize: 12, padding: '7px 12px',
              ...(loading ? { borderColor: C.error, color: C.error } : {}),
            }}
            title="Cancel the fit currently running">
            ■ Stop
          </Btn>
          <Btn variant="run" onClick={onSubmit} disabled={loading || !canRun}
            className="df-fit-btn df-fit-btn-run"
            style={{ fontSize: 12, padding: '7px 16px' }}>
            {loading
            ? <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <div style={{ width: 12, height: 12, border: `2px solid #fff4`,
                  borderTop: '2px solid #ffffff', borderRadius: '50%',
                  animation: 'spin 0.7s linear infinite' }} />
                {fittingProgress || 'Running…'}
              </span>
            : `▶  ${
                isAAMultiCsv
                  ? `Run Multi-${Math.max(1, runsSelectedCount)} CSV Fit`
                  : fitScope === 'multiple'
                    ? `Run Multi-${Math.max(1, runsSelectedCount)} Dataset Fit`
                    : 'Run Fitting'
              }`}
          </Btn>
        </div>
      </div>

      {/* ── "Still running" banner — reappears whenever this panel is shown
          (including right after navigating back from Help/Training/
          Dashboard/Contact) while a fit kicked off earlier is still going.
          The run itself lives in fittingSessionStore.js, not in this
          component, so it was never stopped by leaving the page — this is
          just making that visible again. Deliberately does NOT name the
          reaction system: runMeta is a snapshot of whatever was submitted,
          and naming it here risked showing a stale/unrelated system if an
          older run were still finishing — a short, system-agnostic message
          is correct for every case, built-in example or custom system
          alike. Uses the app's standard warning/notice color tokens
          (C.warning / C.warningDim) instead of an ad-hoc translucent tint
          for proper contrast on the dark theme. ─────────────────────────── */}
      {loading && runMeta && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: C.warningDim, border: `1px solid ${C.warning}`,
          borderRadius: 6, padding: '9px 12px', marginBottom: 10,
          fontSize: 12.5, fontFamily: font.ui, color: C.text, lineHeight: 1.5,
        }}>
          <div style={{ width: 11, height: 11, border: `2px solid ${C.warning}55`,
            borderTop: `2px solid ${C.warning}`, borderRadius: '50%',
            animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />
          <span>
            A fit is still running in the background -it kept going while you were away.
            {' '}Use <strong style={{ color: C.warning }}>Stop</strong> above to cancel it.
          </span>
        </div>
      )}

      {/* ── Freeze scope (client requirement): while a fit is running, lock
          the Data Fitting / Input fields below so nothing can be edited
          mid-run — but leave the Reset/Stop/Run Fitting row above untouched
          (Stop must stay clickable), and this wrapper only ever covers this
          Input panel's own content, never the Sidebar/Navbar, so Help/
          Training and the rest of the app remain fully usable. ─────────── */}
      <div style={{
        position: 'relative',
        opacity: loading ? 0.55 : 1,
        pointerEvents: loading ? 'none' : 'auto',
        transition: 'opacity 0.15s ease',
        filter: loading ? 'grayscale(0.2)' : 'none',
      }} aria-disabled={loading}>
        {loading && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 5, cursor: 'not-allowed' }}
            title="Inputs are locked while a fit is running — use Stop above to cancel." />
        )}

      {/* ── Sub-tab navigation (Data Input / Model Input) ─────────────────── */}
      <div style={tabBarStyle}>
        <button style={tabStyle(inputTab === 'data')} onClick={() => setInputTab('data')}>
          📂  Data Input
        </button>
        <button style={tabStyle(inputTab === 'model')} onClick={() => setInputTab('model')}>
          ⚙️  Model Input
        </button>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          TAB 1 — DATA INPUT
          Reaction System, Reactor & Phase, Feed Parameters, Data Sets
          ══════════════════════════════════════════════════════════════════ */}
      {inputTab === 'data' && reactionSystem === 'acetic_acid' ? (
        /* ── AA: 3-column layout — Stoichiometry | Data Sets & Upload | Reaction Conditions (GAP 7 FIX) ── */
        <ThreeCol ratio="1fr 1fr 1fr" gap={12}
          a={
            <>
              {/* GAP 1 FIX: RadioGroup "Select system" removed from here.
                  System selection now happens exclusively via the Examples modal (GAP 2).
                  Data Input starts directly from Components & Stoichiometry (GAP 7). */}
              <SectionBox title="Components & Stoichiometry"
                info="Enter reactants and products for each reaction (e.g. C2H6 + O2 -> AA + H2O). Coefficients and element balance are checked automatically. This is a multi-reaction parallel system — 3 reactions with shared feed (C2H6, O2, AA, ET, CO2, H2O, N2)."
                style={{ overflow: 'visible' }}>
                {/* [FIX] Was previously shown with a generic 'internal build'
                    label to avoid naming the (formerly proprietary) system —
                    no longer needed now that AA uses published literature
                    data; species/reaction names are shown normally. */}
                {[
                  { label: 'Reaction 1', rKey: 0 },
                  { label: 'Reaction 2', rKey: 1 },
                  { label: 'Reaction 3', rKey: 2 },
                ].map((rxn, ri) => {
                  const rVals = [
                    { reactants: rxn1Reactants, setReactants: setRxn1Reactants, products: rxn1Products, setProducts: setRxn1Products },
                    { reactants: rxn2Reactants, setReactants: setRxn2Reactants, products: rxn2Products, setProducts: setRxn2Products },
                    { reactants: rxn3Reactants, setReactants: setRxn3Reactants, products: rxn3Products, setProducts: setRxn3Products },
                  ][ri];
                  return (
                  <div key={ri} style={{ border: `1px solid ${C.borderLight}`, borderRadius: 5,
                    padding: '8px 10px', marginBottom: 6, background: C.shell, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, textTransform: 'uppercase',
                      letterSpacing: '0.07em', fontFamily: font.ui, marginBottom: 5 }}>{rxn.label}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, minWidth: 0 }}>
                      {/* FIX (Madhura, 17-Jul-2026 call — "you have to specify
                          at least one reactant and one product... just put
                          that red asterisk"): required, unlike Inert Components below. */}
                      <Field label="Reactants" required><SpeciesAutocompleteInput value={rVals.reactants} onChange={e => rVals.setReactants(e.target.value)} style={{ fontSize: 13, width: '100%', boxSizing: 'border-box' }} placeholder="e.g. C₂H₆ + O₂" registeredComponents={registeredComponents} identityScope={`r${ri + 1}.reactants`} speciesIdentityMap={speciesIdentityMap} onSpeciesIdentityChange={setSpeciesIdentityMap} /></Field>
                      <Field label="Products" required><SpeciesAutocompleteInput value={rVals.products} onChange={e => rVals.setProducts(e.target.value)} style={{ fontSize: 13, width: '100%', boxSizing: 'border-box' }} placeholder="e.g. AA + H₂O" registeredComponents={registeredComponents} identityScope={`r${ri + 1}.products`} speciesIdentityMap={speciesIdentityMap} onSpeciesIdentityChange={setSpeciesIdentityMap} /></Field>
                    </div>
                    <InlineBalanceBadge
                      reactants={rVals.reactants}
                      products={rVals.products}
                      reactantScope={`r${ri + 1}.reactants`}
                      productScope={`r${ri + 1}.products`}
                      registeredComponents={registeredComponents}
                      speciesIdentityMap={speciesIdentityMap}
                    />
                    <ReactionSpeciesNames
                      reactants={rVals.reactants}
                      products={rVals.products}
                      reactantScope={`r${ri + 1}.reactants`}
                      productScope={`r${ri + 1}.products`}
                      registeredComponents={registeredComponents}
                      speciesIdentityMap={speciesIdentityMap}
                      collapsed={!!collapsedRxnNames[ri]}
                      onToggleCollapsed={() => setCollapsedRxnNames(prev => ({ ...prev, [ri]: !prev[ri] }))}
                    />
                  </div>
                  );
                })}
                <Field label="Inert Components" sub="optional - e.g. N₂, Ar">
                  <InertComponentsInput value={inertComponents} onChange={e => setInertComponents(e.target.value)} />
                </Field>
              </SectionBox>
            </>
          }
          b={
            <>
              <SectionBox title="Choice of Data Sets">
                <RadioGroup label="Fitting Scope" value={fitScope} onChange={setFitScope}
                  options={[
                    { value: 'single',   label: 'Single data set' },
                    { value: 'multiple', label: 'Multiple data sets' },
                  ]} inline={false} />
              </SectionBox>

              {/* "Multiple data sets" for AA = 2+ separately-uploaded CSVs (Madhura,
                  24-Jun-2026 call, item i). "Single data set" = one CSV, which may
                  itself contain rows at one temperature or several — if it does, the
                  fit automatically shares Ea/Af across them (detected at submit time). */}
              {isAAMultiCsv ? (
                <AAMultiCsvManager globalRuns={globalRuns} setGlobalRuns={setGlobalRuns}
                  reactionSystem={reactionSystem} onManualEntryForRun={onManualEntryForRun} />
              ) : (
                <SectionBox title="Experimental Data"
                  info="A single CSV can contain rows at one temperature or several — if it includes more than one temperature, the fit automatically shares Ea/Af across them.">
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 7,
                      background: C.accent, border: 'none', borderRadius: 6, padding: '8px 14px',
                      cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 600, fontFamily: font.ui,
                      boxShadow: '0 2px 8px var(--color-accent-glow)' }}>
                      <svg width="13" height="13" viewBox="0 0 12 12" fill="none"><path d="M2 10h8M6 2v6M3.5 4.5L6 2L8.5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      Upload CSV
                      <input type="file" accept={CSV_UPLOAD_ACCEPT} style={{ display: 'none' }} onChange={onFileChange} />
                    </label>
                    <Btn variant="secondary" onClick={onManualEntry} style={{ fontSize: 13 }}>✏  Enter Manually</Btn>
                    <Btn variant="ghost" onClick={() => downloadCsvTemplate(getManualEntryConfig(reactionSystem, activeSpecies, reactorMode), reactionSystem)} style={{ fontSize: 13 }}>⬇  Download Template</Btn>
                  </div>
                  {/* Client feedback (30-Jul-2026 call): remove the
                      auto-detected system banner and the uploaded-data
                      preview table entirely — for CSV upload AND manual
                      entry. Once a file is present, show only a minimal
                      confirmation; no experimental-data preview, no
                      auto-detect / temperature / mole-fraction messaging. */}
                  {file ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.success, fontSize: 12,
                      border: `1px dashed ${C.border}`, borderRadius: 5, fontFamily: font.ui, background: C.panel, padding: '6px 6px 6px 12px' }}>
                      <span style={{ flex: 1, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                        ✓ {dataSourceType === 'manual' ? 'Manual data entered' : (file?.name || 'File uploaded')}
                      </span>
                      {/* Client request: remove a wrongly uploaded data set without resetting the whole
                          system. Same idea as the per-row delete in Manual Data Entry (no separate button). */}
                      {onRemoveFile && (
                        <button onClick={onRemoveFile}
                          title={dataSourceType === 'manual' ? 'Remove the manually entered data' : 'Remove this uploaded file'}
                          aria-label="Remove data set"
                          style={removeXButtonStyle}>{'\u00D7'}</button>
                      )}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', color: C.textDim, fontSize: 12,
                      border: `1px dashed ${C.border}`, borderRadius: 5, fontFamily: font.ui, background: C.panel }}>
                      Upload a CSV (single or multi-temperature) or enter data manually
                    </div>
                  )}
                </SectionBox>
              )}
            </>
          }
          c={
            <>
              {/* FIX (Madhura, 17-Jul-2026 call, item 1): this panel used to be
                  gated on `fitScope === 'single'`, so Reactor/Phase/Concentration
                  Basis/Rate Basis/Catalyst Weight/Reactor Volume/Initial Charge
                  simply vanished the moment "Multiple data sets" was selected.
                  Per her direction — "reactor, reaction phase, concentration
                  basis, rate basis... are common for whether it is multiple or
                  single data sets... they should not be hidden" — those fields
                  now always render. Only Temperature / Pressure / Residence Time
                  are scoped to Single data set, since for Multiple those vary
                  per dataset and are captured in the Multi-CSV Manager instead. */}
              <SectionBox title="Reaction Conditions" badge="AA System" badgeColor="#E65100" collapsible>
                <TwoCol gap={8}
                  left={<RadioGroup label="Reactor" value={reactorMode} onChange={setReactorMode} toggle compact
                    options={[{ value: 'batch', label: 'Batch' }, { value: 'pfr', label: 'PFR' }]} />}
                  right={<RadioGroup label="Phase" value={reactionPhase} onChange={setReactionPhase} toggle compact
                    options={[{ value: 'liquid', label: 'Liquid' }, { value: 'vapor', label: 'Vapor' }]} />}
                />
                <RadioGroup label="Concentration Basis" value={concentrationBasis} onChange={setConcentrationBasis} toggle compact
                  options={[
                    { value: 'mole_fraction', label: 'Mole Fraction' },
                    { value: 'mass_fraction', label: 'Mass Fraction' },
                  ]} />
                <RadioGroup label="Rate Basis" value={rateBasis} onChange={setRateBasis} toggle compact
                  info={rateBasis === 'volume'
                    ? 'Rate-constant units for this basis: kmol/(m³·s)'
                    : 'Rate-constant units for this basis: kmol/(kg_cat·s)'}
                  options={[
                    { value: 'cat_weight', label: 'Catalyst weight' },
                    { value: 'volume',     label: 'Reaction volume' },
                  ]} />
                {fitScope === 'single' ? (
                  <>
                    {/* Action item #10 (call tracker, 02-Sep) — layout ask:
                        "Reactor Volume + Catalyst Weight side-by-side." */}
                    <TwoCol gap={8}
                      left={<Field label="Catalyst Weight" required>
                        <Stepper value={wCatG} onChange={e => setWCatG(e.target.value)} step={0.1} min={0} unit="g" />
                      </Field>}
                      right={<Field label="Reactor Volume" required
                        info="Bulk volume occupied by the catalyst bed - not the full physical vessel volume.">
                        <Stepper value={vRxL} onChange={e => setVRxL(e.target.value)} step={0.01} min={0} unit="L" />
                      </Field>}
                    />
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: C.textDim, fontFamily: font.ui, marginTop: 2, marginBottom: 4 }}>
                    Catalyst Weight and Reactor Volume are entered per dataset in the
                    Multiple Data Sets Manager, since these can differ between the
                    data sets you're fitting together.
                  </div>
                )}
                {/* REMOVED "Initial Charge" for AA (Madhura, 17-Jul-2026 call, item 2 —
                    reactor volume / initial moles discussion): traced through handleSubmit
                    and aa_kinetics_objective.py — the mixture amount for AA is always
                    derived per row from pressure, temperature, and reactor volume via the
                    ideal-gas law (n = PV/RT); nothing in the AA submission path ever reads
                    a user-entered Initial Charge. Showing it as a required field here was
                    asking for a number that gets silently discarded. (MeOAc/DME genuinely
                    use a user-entered Initial Charge — see that branch below — this
                    removal is AA-specific.) */}

                {/* FIX (Madhura, 17-Jul-2026 call, item 3 — "on the right side
                    only keep catalyst weight, reactor volume, and initial
                    moles; keep temperature, pressure, residence time in the
                    data table... don't repeat labels that are already there"):
                    for a single AA data set, every row already carries its
                    own P / T (°C) / τ (s) (see MANUAL_ENTRY_CONFIG.acetic_acid),
                    so a PFR run must not duplicate those as single fixed
                    values here — they're entered per-row via CSV/Manual Entry
                    instead. Batch rows only carry Time (min) per row, so
                    Temperature and Pressure (which are constant for the
                    whole batch run) still belong here — only Batch Time is
                    dropped, since time is already the per-row variable in
                    the table. */}
                {fitScope === 'single' && reactorMode === 'batch' && (
                  <>
                    {/* Action item #10 (call tracker, 02-Sep) — layout ask:
                        "Temperature + Pressure side-by-side." */}
                    <TwoCol gap={8}
                      left={<TemperatureSlider value={reactionTempC} onChange={e => setReactionTempC(e.target.value)} min={0} max={1000} />}
                      right={
                        <Field label="Reaction Pressure">
                          <div style={{ display: 'flex', gap: 4 }}>
                            <Stepper value={reactionPressure} onChange={e => setReactionPressure(e.target.value)} step={0.01} min={0} style={{ flex: 1, minWidth: 0 }} />
                            <Select value={pressureUnit} onChange={e => {
                              const newUnit = e.target.value;
                              setReactionPressure(convertPressure(reactionPressure, pressureUnit, newUnit));
                              setPressureUnit(newUnit);
                            }} style={{ width: 58, flexShrink: 0 }}>
                              {['atm','bar','psi','kPa','mmHg'].map(u => <option key={u}>{u}</option>)}
                            </Select>
                          </div>
                        </Field>
                      }
                    />
                  </>
                )}
              </SectionBox>

            </>
          }
        />
      ) : inputTab === 'data' && (
        /* ── MeOAc / DME: 3-column layout — Stoichiometry | Data Sets & Upload | Reaction Conditions (GAP 1,7 FIX) ── */
        <ThreeCol ratio="1fr 1fr 1fr" gap={12}
          a={
            <>
              {/* GAP 1 FIX: RadioGroup "Select system" removed.
                  GAP 7 FIX: Data Input now starts directly from Components & Stoichiometry. */}
              <SectionBox title="Components & Stoichiometry"
                info="Enter reactants and products for each reaction (e.g., Reactant A + Reactant B). Coefficients and element balances are checked automatically."
                style={{ overflow: 'visible' }}>
                {reactionEntries.slice(0, meoacDmeReactionCount).map((rxn, ri) => (
                  <div key={ri} style={{ border: `1px solid ${C.borderLight}`, borderRadius: 5,
                    padding: '8px 10px', marginBottom: 6, background: C.shell, minWidth: 0, position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted,
                        textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: font.ui }}>{rxn.label}</div>
                      {ri === meoacDmeReactionCount - 1 && meoacDmeReactionCount > 1 && (
                        <Btn variant="ghost" style={{ fontSize: 11, padding: '2px 7px' }}
                          onClick={() => {
                            const removedReactionNumber = ri + 1;
                            rxn.setReactants('');
                            rxn.setProducts('');
                            if (setSpeciesIdentityMap) {
                              setSpeciesIdentityMap(prev => Object.fromEntries(
                                Object.entries(prev || {}).filter(([key]) =>
                                  !key.startsWith(`r${removedReactionNumber}.reactants::`)
                                  && !key.startsWith(`r${removedReactionNumber}.products::`)
                                )
                              ));
                            }
                            setCollapsedRxnNames(prev => { const next = { ...prev }; delete next[ri]; return next; });
                            setMeoacDmeReactionCount(c => c - 1);
                          }}>✕ Remove</Btn>
                      )}
                    </div>
                    {/* FIX (Madhura, 17-Jul-2026 call — "you have to specify
                        at least one reactant and one product... just put
                        that red asterisk"): only Reaction 1 is mandatory —
                        Reactions 2–10 are optional "+ Add Reaction" rows for custom systems. */}
                    <TwoCol ratio="1fr 1fr" gap={6}
                      left={<Field label="Reactants" required={ri === 0}><SpeciesAutocompleteInput value={rxn.reactants} onChange={e => rxn.setReactants(e.target.value)} style={{ fontSize: 13 }} placeholder="Reactant A + Reactant B" registeredComponents={registeredComponents} identityScope={`r${ri + 1}.reactants`} speciesIdentityMap={speciesIdentityMap} onSpeciesIdentityChange={setSpeciesIdentityMap} /></Field>}
                      right={<Field label="Products" required={ri === 0}><SpeciesAutocompleteInput value={rxn.products} onChange={e => rxn.setProducts(e.target.value)} style={{ fontSize: 13 }} placeholder="Product C + Product D" registeredComponents={registeredComponents} identityScope={`r${ri + 1}.products`} speciesIdentityMap={speciesIdentityMap} onSpeciesIdentityChange={setSpeciesIdentityMap} /></Field>}
                    />
                    <InlineBalanceBadge
                      reactants={rxn.reactants}
                      products={rxn.products}
                      reactantScope={`r${ri + 1}.reactants`}
                      productScope={`r${ri + 1}.products`}
                      registeredComponents={registeredComponents}
                      speciesIdentityMap={speciesIdentityMap}
                    />
                    {/* Action item #2 — editable component names right under
                        the equation (see ReactionSpeciesNames above). */}
                    <ReactionSpeciesNames
                      reactants={rxn.reactants}
                      products={rxn.products}
                      reactantScope={`r${ri + 1}.reactants`}
                      productScope={`r${ri + 1}.products`}
                      registeredComponents={registeredComponents}
                      speciesIdentityMap={speciesIdentityMap}
                      collapsed={!!collapsedRxnNames[ri]}
                      onToggleCollapsed={() => setCollapsedRxnNames(prev => ({ ...prev, [ri]: !prev[ri] }))}
                    />
                  </div>
                ))}
                {meoacDmeReactionCount < 10 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    <Btn variant="ghost" style={{ fontSize: 13 }}
                      onClick={() => {
                        // Fold the component tables of the reactions already entered so the
                        // new reaction row is visible without scrolling (23-Oct-2026).
                        setCollapsedRxnNames(prev => {
                          const next = { ...prev };
                          for (let i = 0; i < meoacDmeReactionCount; i += 1) next[i] = true;
                          return next;
                        });
                        setMeoacDmeReactionCount(c => Math.min(10, c + 1));
                      }}>
                      + Add Reaction
                    </Btn>
                    <InfoTooltip text="Use '+ Add Reaction' for systems with more than one reaction." size={13} />
                  </div>
                )}
                <Field label="Inert Components" sub="optional - e.g. N₂, Ar">
                  <InertComponentsInput value={inertComponents} onChange={e => setInertComponents(e.target.value)} />
                </Field>
              </SectionBox>
            </>
          }
          b={
            <>
              <SectionBox title="Choice of Data Sets">
                <RadioGroup label="Fitting Scope" value={fitScope} onChange={setFitScope}
                  options={[
                    { value: 'single',   label: 'Single data set' },
                    { value: 'multiple', label: 'Multiple data sets' },
                  ]} inline={false} />
              </SectionBox>

              {fitScope === 'single' ? (
                <SectionBox title="Experimental Data"
                  info="Verify that all data aligns correctly with the respective component headings.">
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 7,
                      background: C.accent, border: 'none', borderRadius: 6, padding: '8px 14px',
                      cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 600, fontFamily: font.ui,
                      boxShadow: '0 2px 8px var(--color-accent-glow)' }}>
                      <svg width="13" height="13" viewBox="0 0 12 12" fill="none"><path d="M2 10h8M6 2v6M3.5 4.5L6 2L8.5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      Upload CSV
                      <input type="file" accept={CSV_UPLOAD_ACCEPT} style={{ display: 'none' }} onChange={onFileChange} />
                    </label>
                    <Btn variant="secondary" onClick={onManualEntry} style={{ fontSize: 13 }}>✏  Enter Manually</Btn>
                    <Btn variant="ghost" onClick={() => downloadCsvTemplate(getManualEntryConfig(reactionSystem, activeSpecies, reactorMode), reactionSystem)} style={{ fontSize: 13 }}>⬇  Download Template</Btn>
                  </div>
                  {/* Client feedback (30-Jul-2026 call): remove the
                      auto-detected system banner and the uploaded-data
                      preview table entirely — for CSV upload AND manual
                      entry. Once a file is present, show only a minimal
                      confirmation; no experimental-data preview, no
                      auto-detect / temperature / mole-fraction messaging. */}
                  {file ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.success, fontSize: 12,
                      border: `1px dashed ${C.border}`, borderRadius: 5, fontFamily: font.ui, background: C.panel, padding: '6px 6px 6px 12px' }}>
                      <span style={{ flex: 1, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                        ✓ {dataSourceType === 'manual' ? 'Manual data entered' : (file?.name || 'File uploaded')}
                      </span>
                      {/* Client request: remove a wrongly uploaded data set without resetting the whole
                          system. Same idea as the per-row delete in Manual Data Entry (no separate button). */}
                      {onRemoveFile && (
                        <button onClick={onRemoveFile}
                          title={dataSourceType === 'manual' ? 'Remove the manually entered data' : 'Remove this uploaded file'}
                          aria-label="Remove data set"
                          style={removeXButtonStyle}>{'\u00D7'}</button>
                      )}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center',color: C.textDim, fontSize: 12,
                      border: `1px dashed ${C.border}`, borderRadius: 5, fontFamily: font.ui, background: C.panel }}>
                      No file loaded -upload a CSV or enter data manually
                    </div>
                  )}
                </SectionBox>
              ) : (
                <GlobalRunManager reactionSystem={reactionSystem} activeSpecies={activeSpecies} reactorMode={reactorMode} globalRuns={globalRuns} setGlobalRuns={setGlobalRuns}
                  onManualEntryForRun={onManualEntryForRun} />
              )}
            </>
          }
          c={
            <>
              {/* FIX (Madhura, 17-Jul-2026 call, item 1): panel no longer
                  disappears when "Multiple data sets" is selected — see the
                  matching fix and rationale in the AA layout above. */}
              <SectionBox title="Reaction Conditions" badgeColor={C.textMuted} collapsible>
                <TwoCol gap={8}
                  left={<RadioGroup label="Reactor" value={reactorMode} onChange={setReactorMode} toggle compact
                    options={[{ value: 'batch', label: 'Batch' }, { value: 'pfr', label: 'PFR' }]} />}
                  right={<RadioGroup label="Reaction Phase" value={reactionPhase} onChange={setReactionPhase} toggle compact
                    options={[{ value: 'liquid', label: 'Liquid' }, { value: 'vapor', label: 'Vapor' }]} />}
                />

                <RadioGroup label="Concentration Basis" value={concentrationBasis} onChange={setConcentrationBasis} toggle compact
                  options={[
                    { value: 'mole_fraction', label: 'Mole Fraction' },
                    { value: 'mass_fraction', label: 'Mass Fraction' },
                  ]} />

                {/* Rate Basis — informational only now; Catalyst weight AND Reactor
                    volume are both collected together below (residence-time
                    calc for PFR needs both), so this no longer switches which
                    field is shown. An (i) tooltip surfaces the corresponding
                    rate-constant units for the selected basis (Aspen-style). */}
                <RadioGroup label="Rate Basis" value={rateBasis} onChange={setRateBasis} toggle compact
                  info={rateBasis === 'volume'
                    ? 'Rate-constant units for this basis: kmol/(m³·s)'
                    : 'Rate-constant units for this basis: kmol/(kg_cat·s)'}
                  options={[
                    { value: 'cat_weight', label: 'Catalyst weight' },
                    { value: 'volume',     label: 'Reaction volume' },
                  ]} />

                {fitScope === 'single' ? (
                  <>
                    {/* Action item #10 (call tracker, 02-Sep) — layout ask:
                        "Reactor Volume + Catalyst Weight side-by-side."
                        Catalyst Weight keeps its own g/kg unit picker
                        (compact, inline with its stepper) so the pairing
                        doesn't lose that control. */}
                    <TwoCol gap={8}
                      left={
                        <Field label="Catalyst Weight" required>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <Stepper value={wCatG} onChange={e => setWCatG(e.target.value)} step={0.1} min={0} style={{ flex: 1, minWidth: 0 }} />
                            <Select value={catWtUnit} onChange={e => {
                              const newUnit = e.target.value;
                              setWCatG(convertWeight(wCatG, catWtUnit, newUnit));
                              setCatWtUnit(newUnit);
                            }} style={{ width: 58, flexShrink: 0 }}>
                              {['g','kg'].map(u => <option key={u}>{u}</option>)}
                            </Select>
                          </div>
                        </Field>
                      }
                      right={
                        <Field label="Reactor Volume" required
                          info={reactorMode === 'pfr'
                            ? 'Bulk volume occupied by the catalyst bed. For PFR this also feeds the ideal-gas n_mix calculation (n = P·V/(R·T)) used to scale the rate constant, so it\'s required here regardless of Rate Basis.'
                            : 'Bulk volume occupied by the catalyst bed - not the full physical vessel volume.'}>
                          <Stepper value={vRxL} onChange={e => setVRxL(e.target.value)} step={0.01} min={0} unit="L" />
                        </Field>
                      }
                    />
                  </>
                ) : null}

                {/* FIX (Madhura, 17-Jul-2026 call, item 3) — same rationale as
                    the AA layout above: for a single data set, PFR rows
                    already carry their own P / T (°C) / τ (s) per row (see
                    buildDynamicManualEntryConfig, reactorMode==='pfr'), so
                    those must not be duplicated as single fixed values here.
                    Batch rows only carry Time (min) per row, so Temperature
                    and Pressure (constant for the whole batch run) still
                    belong here — only Batch Time is dropped. */}
                {fitScope === 'single' && reactorMode === 'batch' && (
                  <>
                    {/* [FIX] Layout ask (screenshot feedback): Temperature
                        should sit on its own full-width row rather than be
                        squeezed next to Reaction Pressure (that pairing was
                        also what caused the label/manual-entry-box overlap
                        into "REACTION PRESSURE" — see TemperatureSlider
                        fix above). Reaction Pressure now pairs with Initial
                        Charge instead, as the last row in this section. */}
                    <Field label="" style={{ marginTop: 2, marginBottom: 5 }}>
                      <TemperatureSlider value={reactionTempC} onChange={e => setReactionTempC(e.target.value)} min={0} max={1000} />
                    </Field>

                    {/* GAP FIX ("Initial charge / initial moles field should
                        not appear at all for PFR — it's a Batch-only
                        concept", 30-Jul-2026 walkthrough): for PFR, n_mix is
                        now derived per row from the ideal gas law
                        (n = P·V/(R·T), same approach already used and
                        Madhura-confirmed for the AA system — see
                        pfr_scale_factor_row in generic_kinetics_objective.py)
                        instead of being a user-entered scalar, so this field
                        is genuinely batch-only. Paired with Reaction
                        Pressure per the screenshot layout ask. */}
                    <TwoCol gap={8}
                      left={
                        <Field label="Reaction Pressure">
                          <div style={{ display: 'flex', gap: 4 }}>
                            <Stepper value={reactionPressure} onChange={e => setReactionPressure(e.target.value)} step={0.01} min={0} style={{ flex: 1, minWidth: 0 }} />
                            <Select value={pressureUnit} onChange={e => {
                              const newUnit = e.target.value;
                              setReactionPressure(convertPressure(reactionPressure, pressureUnit, newUnit));
                              setPressureUnit(newUnit);
                            }} style={{ width: 58, flexShrink: 0 }}>
                              {['atm','bar','psi','kPa','mmHg'].map(u => <option key={u}>{u}</option>)}
                            </Select>
                          </div>
                        </Field>
                      }
                      right={
                        <Field label="Initial Charge" required
                          info="Total moles initially charged into the batch reactor.">
                          <Stepper value={nMixMol} onChange={e => setNMixMol(e.target.value)} step={0.01} min={0} unit="mol" />
                        </Field>
                      }
                    />
                  </>
                )}

                {fitScope === 'single' && reactorMode === 'pfr' && (
                  <TwoCol gap={6}
                    left={<Field label="Reaction Pressure"
                      // [FIX] Label shortened from "Reaction Pressure (fallback)" —
                      // that longer string didn't fit this column's half-width next
                      // to the Unit dropdown and truncated to "REACTION PRESSURE
                      // (..." (ellipsis from the Label component's overflow
                      // handling), which looked broken/shrunk even though the
                      // input itself was sized correctly. The "fallback" nuance
                      // now lives in the tooltip instead, where there's room for
                      // it, matching how the Reactor Volume field above explains
                      // its own dual purpose via its info tooltip rather than its
                      // label text.
                      //
                      // 24-Aug-2026 FIX (Madhura doc, items 1 & 6 — she entered
                      // this value in atm, but the fitting engine always treated
                      // Pressure numbers as psi, so the run silently used a value
                      // ~15x too low, degenerated numerically, and hung for 10+
                      // minutes before a 504). The Unit dropdown below used to let
                      // you pick atm/bar/psi/kPa/mmHg here, but psi was the ONLY
                      // unit the backend actually treated it as no matter what you
                      // picked. Rather than leave that trap in place, Pressure is
                      // now always psi throughout Manual Data Entry / CSV upload —
                      // matching the Acetic Acid system's existing convention —
                      // with no unit picker to get out of sync.
                      info="Used only for CSV rows that don't have their own Pressure value. Always in psi — matches the units the fitting engine uses, so there's no unit-mismatch risk.">
                      <Stepper value={reactionPressure} onChange={e => setReactionPressure(e.target.value)} step={0.01} min={0} unit="psi" />
                    </Field>}
                    right={<Field label="Unit"><Select value="psi" disabled>
                      <option>psi</option>
                    </Select></Field>}
                  />
                )}
              </SectionBox>
            </>
          }
        />
      )}

      {/* ══════════════════════════════════════════════════════════════════
          TAB 2 — MODEL INPUT  (fully PDF-aligned: reversibility toggle,
          conditional section visibility, correct defaults)
          ══════════════════════════════════════════════════════════════════ */}
      {inputTab === 'model' && (() => {
        // ── Derived branch flags ──────────────────────────────────────────
        const isReversible   = reversibility === 'reversible';
        const isIrreversible = reversibility === 'irreversible';
        const isPowerlaw     = modelType === 'powerlaw';
        const isLHHW         = modelType === 'lhhw';

        // Compute fitMode string sent to backend based on new selectors.
        // AA (acetic_acid) is always irreversible: map exponentMode directly
        // to the two supported AA search spaces rather than MeOAc legacy names.
//        const isAA = reactionSystem === 'acetic_acid';

        // fitMode sync is handled by a useEffect in DataFittingPage —
        // never derive or set fitMode inside render (causes stale-state bugs
        // when the Model tab is not active during submit).

        // ── PDF: Keq section label per branch ────────────────────────────
        // Show Keq section only for reversible branches; hide for irreversible
        const showKeqSection    = isReversible;
        // Show LHHW adsorption params only when LHHW is selected
        const showLHHWSection   = isLHHW;
        // Exponents always shown (all 4 branches)
        // Weights always shown on right

        // LHHW_SPECIES lookup
        // Custom systems have no literature adsorption table, so their Ki
        // species list is derived live from whatever the user actually
        // typed into Components & Stoichiometry — same parser
        // (deriveSpeciesFromReactions) the CSV column list and species
        // weights already use, so all three stay in sync automatically.
        const isCustomSystem = reactionSystem === 'custom';
        const customAllSpecies = isCustomSystem
          ? deriveSpeciesFromReactions(inputReactionRows, inertComponents, [], registeredComponents, speciesIdentityMap)
          : [];
        const customReactantSpecies = isCustomSystem
          ? deriveSpeciesFromReactions(inputReactionRows.map(r => ({ ...r, products: '' })), '', [], registeredComponents, speciesIdentityMap)
          : [];

        const LHHW_SPECIES = reactionSystem === 'acetic_acid'
          ? [
              { name: 'C₂H₆', key: 'C2H6', def: '0.10' },
              { name: 'O₂',   key: 'O2',   def: '0.05' },
              { name: 'AA',   key: 'AA',   def: '3.00' },
              { name: 'ET',   key: 'ET',   def: '0.20' },
              { name: 'CO₂',  key: 'CO2',  def: '0.10' },
              { name: 'H₂O',  key: 'H2O',  def: '10.00' },
            ]
          : reactionSystem === 'dme'
          ? [
              { name: 'MeOH', key: 'MeOH', def: '4.95'  },
              { name: 'H₂O',  key: 'H2O',  def: '10.50' },
              { name: 'DME',  key: 'DME',  def: '0.82'  },
            ]
          : isCustomSystem
          ? customAllSpecies.map(sp => ({ name: sp, key: sp, def: '1.00' }))
          : [
              { name: 'HOAc',  key: 'HOAc',  def: '3.18'  },
              { name: 'MeOH',  key: 'MeOH',  def: '4.95'  },
              { name: 'MeOAc', key: 'MeOAc', def: '0.82'  },
              { name: 'H₂O',   key: 'H2O',   def: '10.50' },
            ];

        // For irreversible LHHW, show only reactant adsorption species (PDF spec)
        const LHHW_REACTANTS = reactionSystem === 'acetic_acid'
          ? [
              { name: 'C₂H₆', key: 'C2H6', def: '0.10' },
              { name: 'O₂',   key: 'O2',   def: '0.05' },
            ]
          : reactionSystem === 'dme'
          ? [{ name: 'MeOH', key: 'MeOH', def: '4.95' }]
          : isCustomSystem
          ? customReactantSpecies.map(sp => ({ name: sp, key: sp, def: '1.00' }))
          : [
              { name: 'HOAc', key: 'HOAc', def: '3.18' },
              { name: 'MeOH', key: 'MeOH', def: '4.95' },
            ];

        const lhhwSpeciesToShow = isIrreversible ? LHHW_REACTANTS : LHHW_SPECIES;
        const lhhwAlertMsg = isCustomSystem || reactionSystem === 'acetic_acid'
          ? 'Enter your own catalyst-specific Kᵢ values, or leave a species blank to default to Kᵢ = 1 (no adsorption preference assumed).'
          : isIrreversible
          ? 'Reactant adsorption constants Kᵢ (irreversible). Defaults from Song 1998, Table 5.'
          : 'Adsorption constants Kᵢ per component. Defaults from Song 1998, Table 5.';

        // ── Regressed-parameters-per-branch summary (PDF spec) ──────────────
        // Client feedback: this box ("Regresses: Af, Ab, Ea_f, Ea_b for each
        // reaction" etc., shown under Rate Law) is no longer wanted in the
        // UI. Logic kept here (unused) rather than deleted outright, in case
        // it's needed again — e.g. surfaced in a tooltip or the Appendix
        // instead of an always-visible box.
        // const branchSummary =
        //   isReversible && isPowerlaw && keqMode === 'regress'
        //     ? 'Regresses: Af, Ab, Ea_f, Ea_b for each reaction'
        //     : isReversible && isPowerlaw && keqMode === 'user_specified'
        //     ? 'Regresses: Af, Ea_f, Keq (user-specified) for each reaction'
        //     : isIrreversible && isPowerlaw
        //     ? 'Regresses: Af, Ea_f for each reaction'
        //     : isReversible && isLHHW && keqMode === 'regress'
        //     ? 'Regresses: Af, Ea_f, Keq (adsorption parameters always user-specified)'
        //     : isReversible && isLHHW && keqMode === 'user_specified'
        //     ? 'Regresses: Af, Ea_f, Keq (user-specified); adsorption parameters always user-specified'
        //     : isIrreversible && isLHHW
        //     ? 'Regresses: Af, Ea_f (reactant adsorption parameters always user-specified)'
        //     : '';

        // ── Rate equation (typeset) per branch ────────────────────────────
        // Species names are parsed live from whatever the user has entered
        // as Reaction 1's reactants/products — for EVERY reaction system,
        // not just AA — so changing the reactants (e.g. CH₄ + O₂, or a
        // custom MeOAc-style pair) updates the typeset equation immediately.
        const parseSpeciesNames = (str) => (str || '')
          .split('+')
          .map(s => s.trim().replace(/^[\d.]+\s*/, ''))
          .filter(Boolean);
        const [aaSpA, aaSpB] = (() => {
          const names = parseSpeciesNames(rxn1Reactants);
          return [names[0] || 'A', names[1] || 'B'];
        })();
        const [aaPrA, aaPrB] = (() => {
          const names = parseSpeciesNames(rxn1Products);
          return [names[0] || 'P', names[1] || 'Q'];
        })();
        // Wrap a (possibly unicode-subscripted, e.g. "C₂H₆") species name for
        // KaTeX \text{} — KaTeX renders these unicode characters natively.
        const spTex = (name) => `\\text{${String(name || '?').replace(/\\/g, '')}}`;

        const ratePreviewLatex =
          isIrreversible && isPowerlaw
            ? (reactionSystem === 'acetic_acid'
                ? `r_i = A_{f,i} \\cdot \\exp\\!\\left(-\\dfrac{E_{a,i}}{RT}\\right) \\cdot x_{${spTex(aaSpA)}}^{\\,a_i} \\cdot x_{${spTex(aaSpB)}}^{\\,b_i} \\quad [i=1,2,3]`
                : `r = A_f \\cdot \\exp\\!\\left(-\\dfrac{E_{a,f}}{RT}\\right) \\cdot x_{${spTex(aaSpA)}}^{\\,a} \\cdot x_{${spTex(aaSpB)}}^{\\,b}`)
            : isIrreversible && isLHHW
            ? (reactionSystem === 'acetic_acid'
                ? `r_i = k_{f,i} \\cdot \\dfrac{x_{${spTex(aaSpA)}}^{\\,a_i} \\cdot x_{${spTex(aaSpB)}}^{\\,b_i}}{\\left(1+\\sum_j K_j x_j\\right)^{2}} \\quad [i=1,2,3]`
                : `r = k_f \\cdot \\dfrac{x_{${spTex(aaSpA)}}^{\\,a} \\cdot x_{${spTex(aaSpB)}}^{\\,b}}{\\left(1+\\sum_i K_i x_i\\right)^{2}}`)
            : isReversible && isPowerlaw
            ? (reactionSystem === 'acetic_acid'
                ? `r_i = A_{f,i} \\cdot \\exp\\!\\left(-\\dfrac{E_{a,i}}{RT}\\right) \\cdot x_{${spTex(aaSpA)}}^{\\,a_i} \\cdot x_{${spTex(aaSpB)}}^{\\,b_i} \\quad [i=1,2,3]`
                : `r = k_f \\cdot x_{${spTex(aaSpA)}}^{\\,a} \\cdot x_{${spTex(aaSpB)}}^{\\,b} \\cdot \\left(1-\\dfrac{Q}{K_{eq}}\\right)`)
            : /* reversible LHHW */
              (reactionSystem === 'acetic_acid'
                ? `r_i = k_{f,i} \\cdot \\dfrac{x_{${spTex(aaSpA)}} \\cdot x_{${spTex(aaSpB)}} - \\dfrac{\\text{products}}{K_{eq}}}{\\left(1+\\sum_j K_j x_j\\right)^{2}}`
                : `r = k_f \\cdot \\dfrac{x_{${spTex(aaSpA)}} \\cdot x_{${spTex(aaSpB)}} - \\dfrac{x_{${spTex(aaPrA)}} \\cdot x_{${spTex(aaPrB)}}}{K_{eq}}}{\\left(1+\\sum_i K_i x_i\\right)^{2}}`);

        // ── Build each Model Input section as a standalone block, then deal
        //    them round-robin into 3 equal-width columns. This keeps column
        //    heights balanced regardless of which branch (reversible/LHHW/…)
        //    is active, instead of a fixed left/right split that leaves a
        //    tall empty gap on one side. ─────────────────────────────────
        const blockReactionType = (
          <SectionBox title="Reaction Type" key="reactionType">
            <ToggleSwitch
              value={reversibility} onChange={setReversibility}
              options={[
                { value: 'reversible',   label: 'Reversible',   icon: '⇌',
                  desc: 'Forward and backward rates — includes Keq term' },
                { value: 'irreversible', label: 'Irreversible', icon: '→',
                  desc: 'Forward rate only — no equilibrium constant' },
              ]}
            />
          </SectionBox>
        );

        const blockRateLaw = (
          <SectionBox title="Rate Law" key="rateLaw">
            <ToggleSwitch
              value={modelType} onChange={setModelType}
              options={[
                { value: 'powerlaw', label: 'Power Law',  icon: '⚡',
                  desc: 'Standard power-law kinetics with Arrhenius temperature dependence' },
                { value: 'lhhw',     label: 'LHHW Model', icon: '⚗',
                  desc: 'Langmuir-Hinshelwood-Hougen-Watson with adsorption terms' },
              ]}
            />
          </SectionBox>
        );

        const blockExponents = (
          <SectionBox title="Reaction-Order Exponents" key="exponents">
            <RadioGroup label="Exponent treatment" value={exponentMode} onChange={setExponentMode}
              inline={false} toggle compact
              options={[
                { value: 'fixed_1',        label: 'Fixed at 1',   desc: 'Unit exponents (a = b = 1)' },
                { value: 'regress',        label: 'Regress',      desc: 'Regress exponents from data' },
                { value: 'user_specified', label: 'User-Specified', desc: 'Start from user-specified initial values' },
              ]}
            />
            {exponentMode === 'user_specified' && (() => {
              // Component-name-driven labels (Madhura, 17-Jul-2026 call):
              // lowercase "a" for reactant exponents, "b" for product
              // exponents, labeled with the actual species typed into
              // Reaction 1–10 — e.g. a(C₂H₆) — instead of hard-coded
              // system names. Irreversible reactions have no reverse term,
              // so both exponent boxes are reactant ("a…") exponents there.
              const rxnPairs = inputReactionRows.map(r => [r.reactants, r.products]);
              const rowsToShow = reactionSystem === 'acetic_acid'
                ? reactionOrders.slice(0, 3)
                : reactionOrders.slice(0, meoacDmeReactionCount);
              return (
                <div style={{ marginTop: 6 }}>
                  <Label>Initial exponent guesses (per reaction)</Label>
                  {rowsToShow.map((ro, i) => {
                    const [reactantsStr, productsStr] = rxnPairs[i] || ['', ''];
                    const reactantSpecies = Object.keys(parseStoicSide(reactantsStr || ''));
                    const productSpecies  = Object.keys(parseStoicSide(productsStr || ''));
                    const aLabel = reactantSpecies[0] ? `a(${speciesDisplayName(reactantSpecies[0], registeredComponents)})` : 'a';
                    const bLabel = isIrreversible
                      ? (reactantSpecies[1] ? `a(${speciesDisplayName(reactantSpecies[1], registeredComponents)})` : 'a (2nd reactant)')
                      : (productSpecies[0] ? `b(${speciesDisplayName(productSpecies[0], registeredComponents)})` : 'b');
                    return (
                      <div key={i}>
                        {rowsToShow.length > 1 && (
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#E65100',
                            textTransform: 'uppercase', letterSpacing: '0.07em',
                            fontFamily: font.ui, marginBottom: 4, marginTop: i > 0 ? 8 : 0 }}>
                            Reaction {i + 1}
                          </div>
                        )}
                        {/* Client: for a REVERSIBLE reaction the user must be able to enter an exponent for EACH
                            reactant (forward) and EACH product (backward) - only the first of each was offered.
                            Values are kept per species in reactant_exponents / product_exponents; `a` / `b` still
                            mirror the first reactant / product so everything that already reads {a, b} keeps
                            working. Irreversible reactions keep their original two boxes. */}
                        {(() => {
                          const DEF_REACTANT_EXP = 1.0, DEF_PRODUCT_EXP = 0.5;
                          const readExp = (list, idx, first, dflt) =>
                            (Array.isArray(list) && list[idx] !== undefined) ? list[idx] : (idx === 0 && first !== undefined ? first : dflt);
                          const writeExp = (side, idx, count, raw) => {
                            const val = parseFloat(raw);
                            setReactionOrders(prev => {
                              const u = [...prev];
                              const cur = u[i] || {};
                              const key = side === 'fwd' ? 'reactant_exponents' : 'product_exponents';
                              const first = side === 'fwd' ? cur.a : cur.b;
                              const dflt = side === 'fwd' ? DEF_REACTANT_EXP : DEF_PRODUCT_EXP;
                              const arr = Array.from({ length: count }, (_, k) => readExp(cur[key], k, first, dflt));
                              arr[idx] = val;
                              u[i] = { ...cur, [key]: arr, ...(idx === 0 ? (side === 'fwd' ? { a: val } : { b: val }) : {}) };
                              return u;
                            });
                          };
                          const colHead = (txt) => (
                            <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, marginBottom: 4,
                              textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: font.ui }}>{txt}</div>
                          );
                          if (isIrreversible) {
                            return (
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                <div>
                                  {colHead('Forward Exponents')}
                                  <Field label={aLabel}>
                                    <Input type="number" value={ro.a}
                                      onChange={e => setReactionOrders(prev => { const u = [...prev]; u[i] = { ...u[i], a: parseFloat(e.target.value) }; return u; })} step="0.1" />
                                  </Field>
                                </div>
                                <div>
                                  {colHead('Forward Exponents')}
                                  <Field label={bLabel}>
                                    <Input type="number" value={ro.b}
                                      onChange={e => setReactionOrders(prev => { const u = [...prev]; u[i] = { ...u[i], b: parseFloat(e.target.value) }; return u; })} step="0.1" />
                                  </Field>
                                </div>
                              </div>
                            );
                          }
                          // Reversible: one box per reactant (forward) and per product (backward)
                          const nR = Math.max(reactantSpecies.length, 1);
                          const nP = Math.max(productSpecies.length, 1);
                          return (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                              <div>
                                {colHead('Forward Exponents')}
                                {Array.from({ length: nR }, (_, k) => (
                                  <Field key={`fwd-${k}`}
                                    label={reactantSpecies[k] ? `a(${speciesDisplayName(reactantSpecies[k], registeredComponents)})` : (nR > 1 ? `a${k + 1}` : 'a')}>
                                    <Input type="number" value={readExp(ro.reactant_exponents, k, ro.a, DEF_REACTANT_EXP)}
                                      onChange={e => writeExp('fwd', k, nR, e.target.value)} step="0.1" />
                                  </Field>
                                ))}
                              </div>
                              <div>
                                {colHead('Backward Exponents')}
                                {Array.from({ length: nP }, (_, k) => (
                                  <Field key={`rev-${k}`}
                                    label={productSpecies[k] ? `b(${speciesDisplayName(productSpecies[k], registeredComponents)})` : (nP > 1 ? `b${k + 1}` : 'b')}>
                                    <Input type="number" value={readExp(ro.product_exponents, k, ro.b, DEF_PRODUCT_EXP)}
                                      onChange={e => writeExp('rev', k, nP, e.target.value)} step="0.1" />
                                  </Field>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </SectionBox>
        );

        const blockKeq = showKeqSection && (
          <SectionBox title="Equilibrium Constant (Keq)" key="keq">
            <RadioGroup label="Keq treatment" value={keqMode} onChange={setKeqMode}
              inline={false}
              options={[
                { value: 'regress',        label: 'Regress from data' },
                { value: 'user_specified', label: 'User-Specified (enter below)' },
              ]}
            />
            {keqMode === 'user_specified' && (() => {
              // Number of reactions scales with however many reactions are
              // actually defined: fixed 3-reaction network for the AA
              // system, otherwise the live meoacDmeReactionCount (1-10) from
              // Data Input. Generic "Reaction N" naming — no hard-coded
              // system/species names (Madhura, 17-Jul-2026 call).
              const rxnLabels = reactionSystem === 'acetic_acid'
                ? ['Reaction 1', 'Reaction 2', 'Reaction 3']
                : Array.from({ length: meoacDmeReactionCount }, (_, i) => `Reaction ${i + 1}`);
              return (
                <div style={{ marginTop: 6 }}>
                  {rxnLabels.map((rxnLabel, ri) => (
                    <div key={ri} style={{ marginBottom: 8, padding: '8px 10px',
                      border: `1px solid ${C.borderLight}`, borderRadius: 5, background: C.shell }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted,
                        textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6,
                        fontFamily: font.ui }}>{rxnLabel}</div>
                      <TwoCol ratio="1fr 1fr" gap={6}
                        left={<Field label="Keq X"><Input type="number" placeholder="X"
                          value={keqUserValues[ri]?.X ?? ''} step="0.01"
                          onChange={e => setKeqUserValues(prev => { const u = [...prev]; u[ri] = { ...(u[ri]||{}), X: e.target.value }; return u; })} /></Field>}
                        right={<Field label="Keq Y"><Input type="number" placeholder="Y"
                          value={keqUserValues[ri]?.Y ?? ''} step="0.01"
                          onChange={e => setKeqUserValues(prev => { const u = [...prev]; u[ri] = { ...(u[ri]||{}), Y: e.target.value }; return u; })} /></Field>}
                      />
                      <div style={{ fontSize: 13, color: C.textDim, fontFamily: font.ui, marginTop: 2 }}>
                        ln(Keq) = X + Y/T(K)
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            {/* Keq preview from literature — only meaningful for the two
                built-in systems with a published Song 1998 correlation.
                A custom system has no literature Keq, so it must not fall
                through to this (previously showed the MeOAc line by
                default for anything that wasn't 'acetic_acid'). */}
            {(reactionSystem === 'meoac' || reactionSystem === 'dme') && (
              <div style={{ marginTop: 8, background: C.codeBg, border: `1px solid ${C.borderLight}`,
                borderRadius: 5, padding: '7px 10px', fontFamily: font.ui, fontSize: 12, color: C.toggle }}>
                {reactionSystem === 'dme'
                  ? 'ln(Keq) = 0.7633 + 1239.76 / T(K)   [DME - Song 1998]'
                  : 'ln(Keq) = 0.83983 + 782.98 / T(K)   [MeOAc - Song 1998]'}
              </div>
            )}
          </SectionBox>
        );

        const blockLHHW = showLHHWSection && (
          <SectionBox title={isIrreversible ? 'LHHW Adsorption Parameters (Reactants)' : 'LHHW Adsorption Parameters'} badge="LHHW" badgeColor={C.success} key="lhhw">
            <Alert type="info" style={{ marginBottom: 6 }}>
              {lhhwAlertMsg}
            </Alert>
            {isCustomSystem && lhhwSpeciesToShow.length === 0 && (
              <div style={{ fontSize: 12, color: C.textDim, fontFamily: font.ui }}>
                Define your reactants/products in Components &amp; Stoichiometry first —
                adsorption constants will appear here per species.
              </div>
            )}
            {/* Compact horizontal grid — wraps as many species per row as fit,
                so 4-6 adsorption constants sit on one line instead of stacking. */}
            <div style={{ display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 6 }}>
              {lhhwSpeciesToShow.map(({ name, key, def }) => (
                <Field key={key} style={{ minWidth: 0 }} required={!isCustomSystem}
                  label={`K(${name})`}
                  info={key === lhhwSpeciesToShow[0].key
                    ? (isCustomSystem
                        ? 'Adsorption constants can never be regressed — blank defaults to Kᵢ = 1 (no adsorption preference assumed).'
                        : 'Adsorption/absorption constants can never be regressed — they are catalyst- and component-specific and must always be user-specified.')
                    : undefined}>
                  {/* BUG FIX ("User specified" k-value mode unlocks fields
                      inconsistently, 30-Jul-2026 walkthrough): this field was
                      only ever wired to state (customLhhwKiValues/
                      setCustomLhhwKiValues) on the isCustomSystem branch — the
                      built-in MeOAc/DME branch rendered a bare, uncontrolled
                      <Input> with no value/onChange at all, so typing an
                      adsorption constant here did nothing and was silently
                      discarded. The backend has always supported this
                      (kinetics_objective_methyl_acetate / _dme read
                      user_inputs['lhhw_ki_values'] to override the Song 1998
                      defaults — see openmkm_api.py), so only the "custom
                      system" fields ever actually unlocked; built-in systems'
                      "user-specified" K fields looked editable but weren't.
                      Both branches now read/write the same keyed-by-species
                      state — safe since species names are unique within
                      whichever system is active. */}
                  <Input type="number" step="0.01" min="0" placeholder={def}
                    value={customLhhwKiValues[key] ?? ''}
                    onChange={(e) => setCustomLhhwKiValues(prev => ({ ...prev, [key]: e.target.value }))}
                    style={{ fontSize: 12, padding: '4px 6px' }} />
                </Field>
              ))}
            </div>
          </SectionBox>
        );

        // De-emphasized per meeting feedback: the rate-equation preview was
        // crowding the main Model Input flow without adding proportional
        // value next to the input requirements. Collapsed by default here
        // as an interim fix; team to decide whether it moves fully into
        // an Appendix / hover reference instead.
        const blockRatePreview = (
          <SectionBox title="Rate Equation Preview (reference)" key="ratePreview" collapsible defaultOpen={false}>
            <div style={{ marginBottom: 10 }}>
              <Label>Rate Equation</Label>
              {/* No horizontal scroll here on purpose (client feedback: the
                  native browser scrollbar with its left/right arrow buttons
                  was unwanted UI clutter, and hiding it cosmetically via
                  ::-webkit-scrollbar wasn't reliable across browsers e.g.
                  Edge). overflow removed entirely; 'safe center' plus a
                  slightly smaller katex size keeps the equation inside the
                  box without needing to scroll. */}
              <div style={{ background: C.codeBg, border: `1px solid ${C.borderLight}`, borderRadius: 5,
                padding: '10px 10px', display: 'flex', justifyContent: 'safe center' }}>
                <BlockMath math={ratePreviewLatex} />
              </div>
            </div>
            <div>
              <Label>Arrhenius</Label>
              <div style={{ background: C.codeBg, border: `1px solid ${C.borderLight}`, borderRadius: 5,
                padding: '7px 10px', display: 'flex', justifyContent: 'safe center' }}>
                <BlockMath math={String.raw`k_f = A_f \exp\!\left(-\dfrac{E_{a,f}}{RT}\right) \quad [\mathrm{kmol/(kg_{cat}\cdot s)}]`} />
              </div>
            </div>
          </SectionBox>
        );
        // Species names (x_A, x_B, product names for reversible branches) are
        // pulled live from Reaction 1's Reactants/Products fields on the
        // "Data Input" tab, for every reaction system — see `ratePreviewLatex`
        // above. Editing those fields updates this equation immediately.

        const blockWeights = (
          <SectionBox title="Objective Function Weights" key="weights"
            // [FIX] Header badge removed — it duplicated the ToggleSwitch's own
            // active-pill highlight below (and only ever resolved for the
            // 'balanced' preset key since weightPresets has no matching
            // 'userSpecified'/'productFocus' entries), so it showed a
            // spurious green "BALANCED" label with no counterpart for the
            // other two options. The toggle itself is the single source of
            // truth for which preset is active now.
            // [FIX 5-Aug-2026, Madhura interim-walkthrough follow-up]: she asked for
            // an "i" button here explaining what these weights actually do, in
            // plain language, adapted to how this program uses them — not just a
            // generic textbook definition. Kept short on purpose (her direction:
            // "some very simple sentence").
            info={<ul style={{ margin: 0, paddingLeft: 16 }}>
              <li>Each weight scales how much that species' error counts toward the loss the optimizer minimizes — a higher weight makes the fit work harder to match that species, at the cost of the others.</li>
              <li style={{ marginTop: 6 }}>The same weights are also applied when computing the R² shown after a run — a heavily-weighted species pulls the reported R² toward its own fit quality too.</li>
            </ul>}>            {/* Order: Balanced (fixed at 1, default) | User Specified | Product Focus (reactants 1, products 2) */}
            <div style={{ marginBottom: 10 }}>
              <ToggleSwitch
                value={selectedPreset}
                onChange={(key) => {
                  setSelectedPreset(key);
                  if (key !== 'userSpecified') {
                    // Apply preset weights.
                    // BUG FIX (22-Jul-2026, cross-checked per Madhura/Sagar report):
                    // the final branch of each ternary below used to be an
                    // unconditional fallback to the hardcoded MeOAc dict — so
                    // for reactionSystem === 'custom' (propane combustion,
                    // AA+ethylene combustion, or ANY user-typed system) this
                    // silently loaded {MeOAc, HOAc, MeOH, H2O} into
                    // speciesWeights. The per-species input boxes still
                    // *looked* right afterwards only because they read
                    // `speciesWeights[sp] ?? 1` — any custom species name not
                    // present in that MeOAc dict just displayed the '?? 1'
                    // default, masking the mismatch, while 'H2O' (present in
                    // both) kept showing its real, wrong stored value. The
                    // stale MeOAc-keyed object was then exactly what got sent
                    // to the backend in the species_weights field on Run
                    // Fitting — matching the {'MeOAc':.., 'HOAc':.., 'MeOH':..,
                    // 'H2O':..} weights dict printed in the terminal for both
                    // the propane and AA+ethylene custom runs.
                    // Fix: derive weights generically from whatever species
                    // the user actually typed into Components & Stoichiometry
                    // — products get weight 2, everything else gets weight 1
                    // — instead of falling back to any hardcoded system.
                    const customProductSpecies = new Set(deriveSpeciesFromReactions(
                      inputReactionRows.map(r => ({ ...r, reactants: '' })),
                      '', [], registeredComponents, speciesIdentityMap,
                    ));
                    const customProductFocusWeights = Object.fromEntries(
                      weightSpeciesToShow.map(sp => [sp, customProductSpecies.has(sp) ? 2 : 1])
                    );
                    const customBalancedWeights = Object.fromEntries(
                      weightSpeciesToShow.map(sp => [sp, 1])
                    );
                    // FIX (23-Oct-2026): MeOAc and DME boxes are labelled with the
                    // species names derived from the typed reactions (e.g. "Ethanoic
                    // acid", "Methyl ethanoate"), not the short keys MeOAc / HOAc /
                    // MeOH. The old hardcoded MeOAc/DME dicts therefore never matched a
                    // box, and every box kept falling back to 1 after clicking Product
                    // Focus. Only AA keeps fixed keys (its species list is fixed).
                    const productFocusWeights = reactionSystem === 'acetic_acid'
                      ? { C2H6: 1, O2: 1, AA: 2, ET: 2, CO2: 2, H2O: 2, N2: 0 }
                      : customProductFocusWeights;
                    const balancedWeights = reactionSystem === 'acetic_acid'
                      ? { C2H6: 1, O2: 1, AA: 1, ET: 1, CO2: 1, H2O: 1, N2: 0 }
                      : customBalancedWeights;
                    setSpeciesWeights(key === 'productFocus' ? productFocusWeights : balancedWeights);
                  }
                }}
                options={[
                  { value: 'balanced',      label: 'Balanced',        desc: 'All components equal (fixed at 1, default)' },
                  { value: 'userSpecified', label: 'User-Specified',  desc: 'Enter values below' },
                  { value: 'productFocus',  label: 'Product Focus', desc: 'Products ×2, Reactants ×1' },
                ]}
              />
            </div>

            {/* Per-species weight inputs — always shown (editable when userSpecified, editable preview otherwise).
                3 boxes per row (PDF/Madhura feedback) to reduce panel height/scrolling;
                inert species (fixed zero weight) are excluded from the list. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 7 }}>
              {weightSpeciesToShow.map(sp => (
                <Field key={sp} label={`w(${sp})`}>
                  <Input type="number" value={speciesWeights[sp] ?? 1}
                    onChange={e => {
                      setSelectedPreset('userSpecified');
                      setSpeciesWeights(w => ({ ...w, [sp]: parseFloat(e.target.value) || 0 }));
                    }}
                    step="0.5" min="0" max="20"
                    style={{ opacity: selectedPreset === 'userSpecified' ? 1 : 0.8 }} />
                </Field>
              ))}
            </div>
          </SectionBox>
        );

        // Fixed column groups — each block always lands in the same column
        // regardless of which optional blocks (Keq, LHHW) are hidden for the
        // current Reversible/Irreversible × Power-Law/LHHW combination. Using
        // a position-based round-robin here previously caused blocks like
        // "Rate Equation Preview" to jump columns (e.g. shifting from the
        // middle column into the left column) whenever an earlier optional
        // block such as Equilibrium Constant was filtered out.
        const cols = [
          [blockReactionType, blockKeq].filter(Boolean),
          [blockRateLaw, blockLHHW, blockRatePreview].filter(Boolean),
          [blockExponents, blockWeights].filter(Boolean),
        ];

        return (
          <ThreeCol ratio="1fr 1fr 1fr" gap={12}
            a={<>{cols[0]}</>}
            b={<>{cols[1]}</>}
            c={<>{cols[2]}</>}
          />
        );
      })()}
      </div>
    </PanelShell>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PANEL 2 — GRAPHS / DATA FITTING
// ═══════════════════════════════════════════════════════════════════════════

// CUSTOM MULTI-DATASET BATCH — composition profile is the primary view.
// Each dataset gets its own tab and time axis; experimental series are
// distinct from the model line and no dataset is treated as a continuation
// of another dataset.
const CUSTOM_MULTI_PROFILE_COLORS = ['#2563eb', '#d97706', '#16a34a', '#dc2626', '#7c3aed', '#0891b2'];

// ── Smooth model curve (monotone cubic / PCHIP) ──────────────────────────
// The backend returns the model prediction only at the experimental time
// points, so drawing straight segments between them gives a polyline.
// This does what Excel's "smoothed line" option does, but with a
// shape-preserving (Fritsch–Carlson) cubic: the curve passes exactly through
// every model point, is C1-smooth, and never overshoots between points
// (a concentration can't dip below zero or bulge past its own data).
const smoothCurve = (points, targetSamples = 90) => {
  const p = (points || [])
    .filter(q => Number.isFinite(q?.x) && Number.isFinite(q?.y))
    .sort((a, b) => a.x - b.x)
    .filter((q, i, arr) => i === 0 || q.x > arr[i - 1].x);   // drop duplicate x
  const n = p.length;
  if (n < 3) return p;                                       // 1–2 points: nothing to smooth

  const h = [], d = [];
  for (let i = 0; i < n - 1; i += 1) {
    h[i] = p[i + 1].x - p[i].x;
    d[i] = (p[i + 1].y - p[i].y) / h[i];
  }
  const m = new Array(n);
  for (let i = 1; i < n - 1; i += 1) {
    if (d[i - 1] * d[i] <= 0) m[i] = 0;                      // local extremum / flat -> zero slope
    else {
      const w1 = 2 * h[i] + h[i - 1];
      const w2 = h[i] + 2 * h[i - 1];
      m[i] = (w1 + w2) / (w1 / d[i - 1] + w2 / d[i]);        // weighted harmonic mean
    }
  }
  const endSlope = (h0, h1, d0, d1) => {                     // shape-preserving 3-point end slope
    let s = ((2 * h0 + h1) * d0 - h0 * d1) / (h0 + h1);
    if (Math.sign(s) !== Math.sign(d0)) s = 0;
    else if (Math.sign(d0) !== Math.sign(d1) && Math.abs(s) > 3 * Math.abs(d0)) s = 3 * d0;
    return s;
  };
  m[0] = endSlope(h[0], h[1], d[0], d[1]);
  m[n - 1] = endSlope(h[n - 2], h[n - 3], d[n - 2], d[n - 3]);

  const perSeg = Math.max(4, Math.ceil(targetSamples / (n - 1)));
  const out = [];
  for (let i = 0; i < n - 1; i += 1) {
    for (let s = 0; s < perSeg; s += 1) {
      const t = s / perSeg;
      const t2 = t * t, t3 = t2 * t;
      out.push({
        x: p[i].x + t * h[i],
        y: (2 * t3 - 3 * t2 + 1) * p[i].y + (t3 - 2 * t2 + t) * h[i] * m[i]
         + (-2 * t3 + 3 * t2) * p[i + 1].y + (t3 - t2) * h[i] * m[i + 1],
      });
    }
  }
  out.push(p[n - 1]);
  return out;
};

const ALL_COMPONENTS_KEY = '__all_components__';

// One composition-vs-time chart for ONE component, all data sets overlaid.
// colour = data set · line = smoothed model fit · dots = experimental data.
const CompositionProfileChart = ({ seriesList, fullName, height = 340, compact = false }) => {
  const tMax = Math.max(0, ...seriesList.flatMap(s => s.rows.map(r => r.time)));
  const rows = seriesList.flatMap(s => ([
    ...smoothCurve(s.rows.map(r => ({ x: r.time, y: r.model }))
      .filter(q => Number.isFinite(q.y))).map(q => ({ time: q.x, [`model_${s.index}`]: q.y })),
    ...s.rows.filter(r => Number.isFinite(r.experimental))
      .map(r => ({ time: r.time, [`experimental_${s.index}`]: r.experimental })),
  ])).sort((a, b) => a.time - b.time);
  const fmtTick = v => (Number.isFinite(v) ? Number(v.toPrecision(3)).toString() : v);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={rows} margin={{ top: 8, right: 14, left: compact ? 0 : 6, bottom: compact ? 14 : 22 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis type="number" dataKey="time" domain={[0, tMax || 'auto']} tickFormatter={fmtTick}
          tick={{ fontSize: 11 }}
          label={{ value: 'Time (min)', position: 'insideBottom', offset: compact ? -8 : -12, fontSize: 11 }} />
        <YAxis type="number" domain={[0, 'auto']} tickFormatter={fmtTick} tick={{ fontSize: 11 }} width={compact ? 38 : 48}
          label={compact ? undefined : { value: `${fullName} (composition)`, angle: -90, position: 'insideLeft', fontSize: 11 }} />
        <Tooltip
          labelFormatter={value => `Time: ${Number(value).toFixed(3)} min`}
          formatter={(value, name) => [Number.isFinite(Number(value)) ? Number(value).toFixed(5) : value, name]} />
        {seriesList.map(s => (
          <Line key={`model-${s.index}`} name={`${s.label} — model`} type="linear"
            dataKey={`model_${s.index}`} stroke={s.color} strokeWidth={2}
            strokeDasharray={s.index === 0 ? undefined : `${5 + (s.index % 3) * 2} 3`}
            dot={false} activeDot={false} connectNulls isAnimationActive={false} />
        ))}
        {seriesList.map(s => (
          <Scatter key={`data-${s.index}`} name={`${s.label} — experimental`}
            dataKey={`experimental_${s.index}`} fill={s.color} isAnimationActive={false}
            shape={p => <circle cx={p.cx} cy={p.cy} r={3.5} fill={s.color} stroke="#fff" strokeWidth={0.75} />} />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

// Always-visible legend that explains every mark on the chart(s).
const CompositionProfileLegend = ({ seriesList }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 11.5,
    color: C.textMid, fontFamily: font.ui, padding: '6px 10px',
    border: `1px solid ${C.border}`, borderRadius: 6, background: C.panel }}>
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <svg width="26" height="10"><path d="M1 8 C 9 8, 12 2, 25 2" stroke="#6b7280" strokeWidth="2" fill="none" /></svg>
        <strong>Line</strong> = model fit (smoothed)
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <svg width="12" height="12"><circle cx="6" cy="6" r="3.5" fill="#6b7280" stroke="#fff" strokeWidth="0.75" /></svg>
        <strong>Dots</strong> = experimental data
      </span>
      <span style={{ color: C.textDim }}>Colour identifies the data set:</span>
    </div>
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
      {seriesList.map(s => (
        <span key={`legend-${s.index}`} title={s.sourceLabel || undefined}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width="34" height="12">
            <line x1="1" y1="6" x2="25" y2="6" stroke={s.color} strokeWidth="2"
              strokeDasharray={s.index === 0 ? undefined : '5 3'} />
            <circle cx="29" cy="6" r="3.5" fill={s.color} stroke="#fff" strokeWidth="0.75" />
          </svg>
          {s.label}
          {Number.isFinite(Number(s.temperature)) ? ` · ${s.temperature} °C` : ''}
          {s.index > 0 ? ' (dashed line)' : ''}
        </span>
      ))}
    </div>
  </div>
);

const CustomMultiBatchProfileViewer = ({ result, registeredComponents = [] }) => {
  // All rows come from the completed backend response. Do not reconstruct a
  // curve from source/example input or join independent datasets into one ODE
  // trajectory.
  const datasets = useMemo(() => {
    const isValidDs = ds => Array.isArray(ds?.time_min) && ds?.species && typeof ds.species === 'object';
    const rawTimeSeriesCandidates = [
      result?.predicted_timeseries,
      result?.metrics?.predicted_timeseries,
      result?.global_metrics?.predicted_timeseries,
      result?.metrics?.global_metrics?.predicted_timeseries,
    ];
    const rawTimeSeries = rawTimeSeriesCandidates.find(candidate =>
      Array.isArray(candidate) && candidate.some(isValidDs)
    ) || [];
    return rawTimeSeries.filter(isValidDs);
  }, [result]);
  const speciesList = useMemo(
    () => Array.from(new Set(datasets.flatMap(ds => Object.keys(ds?.species || {})))),
    [datasets]
  );
  // 'All components' (grid of every species) is the default view; the
  // individual-component chips stay available next to it.
  const [species, setSpecies] = useState(ALL_COMPONENTS_KEY);

  useEffect(() => {
    if (speciesList.length === 0) return;
    if (speciesList.length === 1) { if (species !== speciesList[0]) setSpecies(speciesList[0]); return; }
    if (species !== ALL_COMPONENTS_KEY && !speciesList.includes(species)) setSpecies(ALL_COMPONENTS_KEY);
  }, [speciesList, species]);

  if (datasets.length === 0 || speciesList.length === 0) {
    const rawPlot = result?.main_plot_base64
      ?? result?.metrics?.main_plot_base64
      ?? result?.global_metrics?.main_plot_base64
      ?? '';
    const plotSrc = typeof rawPlot === 'string' && rawPlot
      ? (rawPlot.startsWith('data:image/') ? rawPlot : `data:image/png;base64,${rawPlot}`)
      : '';
    const runPlots = result?.per_run_plots || result?.metrics?.per_run_plots || {};
    const runPlotEntries = Object.entries(runPlots).filter(([, value]) => typeof value === 'string' && value);
    if (plotSrc || runPlotEntries.length > 0) {
      return (
        <div style={{ minHeight: 300, display: 'flex', flexDirection: 'column', gap: 10,
          color: C.textDim, fontFamily: font.ui }}>
          <div style={{ fontSize: 12 }}>
            Backend-generated fit plot shown because this response has no structured per-dataset series.
          </div>
          {plotSrc && <img src={plotSrc} alt="Backend-generated multi-dataset fit"
            style={{ width: '100%', height: 'auto', maxHeight: 680, objectFit: 'contain',
              background: '#fff', border: `1px solid ${C.border}`, borderRadius: 6 }} />}
          {!plotSrc && runPlotEntries.map(([name, value]) => {
            const src = value.startsWith('data:image/') ? value : `data:image/png;base64,${value}`;
            return <img key={name} src={src} alt={`Backend fit plot ${name}`}
              style={{ width: '100%', height: 'auto', maxHeight: 560, objectFit: 'contain',
                background: '#fff', border: `1px solid ${C.border}`, borderRadius: 6 }} />;
          })}
        </div>
      );
    }
    return (
      <div style={{ minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: C.textDim, fontFamily: font.ui, border: `1px dashed ${C.border}`, borderRadius: 6,
        padding: 18, textAlign: 'center' }}>
        The backend returned neither structured composition-profile data nor a fitted plot image.
        Check the fitting response in the backend log; no source/example data is substituted.
      </div>
    );
  }

  const isAll = species === ALL_COMPONENTS_KEY;
  // Build the per-dataset rows for one species. Each dataset keeps its own
  // time axis; nothing is joined across datasets.
  const buildSeries = (sp) => datasets.map((ds, index) => {
    const pair = ds?.species?.[sp] || {};
    const times = Array.isArray(ds?.time_min) ? ds.time_min : [];
    const n = Math.max(times.length, Array.isArray(pair.exp) ? pair.exp.length : 0,
      Array.isArray(pair.pred) ? pair.pred.length : 0);
    const rows = Array.from({ length: n }, (_, i) => ({
      time: Number(times[i]),
      experimental: pair.exp?.[i] == null ? null : Number(pair.exp[i]),
      model: pair.pred?.[i] == null ? null : Number(pair.pred[i]),
    })).filter(row => Number.isFinite(row.time)
      && (Number.isFinite(row.experimental) || Number.isFinite(row.model)));
    return {
      index,
      label: `Data Set ${index + 1}`,
      sourceLabel: ds?.source_label || ds?.dataset_label || '',
      color: CUSTOM_MULTI_PROFILE_COLORS[index % CUSTOM_MULTI_PROFILE_COLORS.length],
      temperature: ds?.T_C,
      rows,
    };
  });
  const legendSeries = buildSeries(isAll ? speciesList[0] : species);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: '100%', minHeight: 0 }}>
      <div style={{ fontSize: 12, color: C.textMuted, fontFamily: font.ui }}>
        Composition Profile — all data sets · {isAll ? 'all components' : speciesDisplayName(species, registeredComponents)}
      </div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', flexShrink: 0 }}>
        {speciesList.length > 1 && (
          <Chip active={isAll} onClick={() => setSpecies(ALL_COMPONENTS_KEY)}>All components</Chip>
        )}
        {speciesList.map(sp => (
          <Chip key={sp} active={sp === species} onClick={() => setSpecies(sp)}>
            {speciesDisplayName(sp, registeredComponents)}
          </Chip>
        ))}
      </div>
      <div style={{ flexShrink: 0 }}><CompositionProfileLegend seriesList={legendSeries} /></div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {isAll ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 10 }}>
            {speciesList.map(sp => {
              const name = speciesDisplayName(sp, registeredComponents);
              return (
                <div key={sp} style={{ border: `1px solid ${C.border}`, borderRadius: 6, background: '#fff',
                  padding: '6px 4px 0' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#111827', fontFamily: font.ui,
                    padding: '0 8px 2px' }}>{name}</div>
                  <CompositionProfileChart seriesList={buildSeries(sp)} fullName={name} height={210} compact />
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, background: '#fff', padding: '6px 4px 0' }}>
            <CompositionProfileChart seriesList={buildSeries(species)}
              fullName={speciesDisplayName(species, registeredComponents)} height={340} />
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// MULTI-DATASET PARITY (steady-state / PFR and any non-batch multi-dataset fit)
// Client: "In the graphs, if multiple data sets are used, the composition profile
// view or parity view should show 2 data sets in 2 separate colors." Batch
// multi-dataset fits already get a per-dataset colour composition profile
// (CustomMultiBatchProfileViewer above). Parity-style results used to fall back to
// ONE backend picture with every data set mixed together; this draws the same
// observed-vs-model data from the structured series instead, one colour per data
// set (same palette as the profile view). Returns null - so the original backend
// image is kept - unless the response really holds 2+ data sets.
// ---------------------------------------------------------------------------
const buildMultiDatasetParity = (result) => {
  const ts = getAppendixPredictedTimeseries(result);
  if (!Array.isArray(ts) || ts.length === 0) return null;
  const lastOf = (v) => (Array.isArray(v) ? v[v.length - 1] : v);
  const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
  const datasets = [];   // [{ label, species: { sp: [{ x, y }] } }]

  if (ts[0]?.time_min && ts[0]?.species) {
    // one block per data set (batch-shaped): every (exp, model) pair is a parity point
    ts.forEach((ds, i) => {
      const species = {};
      Object.entries(ds?.species || {}).forEach(([sp, v]) => {
        const n = Math.max(v?.exp?.length || 0, v?.pred?.length || 0);
        const pts = [];
        for (let k = 0; k < n; k++) {
          const x = num(v?.exp?.[k]); const y = num(v?.pred?.[k]);
          if (x != null && y != null) pts.push({ x, y });
        }
        if (pts.length) species[sp] = pts;
      });
      datasets.push({ label: `Data Set ${i + 1}`, species });
    });
  } else {
    // flat rows (steady-state): grouped by data set, one point per run
    const groups = new Map();
    ts.forEach((row) => {
      const k = row?.dataset_label ?? row?.source_label ?? '__single__';
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(row);
    });
    let i = 0;
    groups.forEach((rows) => {
      const species = {};
      rows.forEach((row) => {
        Object.entries(row?.species || {}).forEach(([sp, v]) => {
          const x = num(v?.exp_end); const y = num(lastOf(v?.pred));
          if (x == null || y == null) return;
          (species[sp] = species[sp] || []).push({ x, y });
        });
      });
      datasets.push({ label: `Data Set ${i + 1}`, species });
      i += 1;
    });
  }
  const usable = datasets.filter(d => Object.keys(d.species).length > 0);
  return usable.length >= 2 ? usable : null;
};

const MultiDatasetParityViewer = ({ datasets, registeredComponents = [] }) => {
  const speciesList = useMemo(
    () => Array.from(new Set(datasets.flatMap(d => Object.keys(d.species)))),
    [datasets]
  );
  const [species, setSpecies] = useState('');
  useEffect(() => {
    if (!speciesList.includes(species)) setSpecies(speciesList[0] || '');
  }, [speciesList, species]);

  const fullName = speciesDisplayName(species, registeredComponents);
  const series = datasets.map((d, index) => ({
    index, label: d.label, pts: d.species[species] || [],
    color: CUSTOM_MULTI_PROFILE_COLORS[index % CUSTOM_MULTI_PROFILE_COLORS.length],
  }));
  const all = series.flatMap(s => s.pts);
  const hi = all.length ? Math.max(...all.map(p => Math.max(p.x, p.y))) : 1;
  const lo = all.length ? Math.min(0, ...all.map(p => Math.min(p.x, p.y))) : 0;
  const pad = (hi - lo) * 0.05 || 0.05;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: '100%', minHeight: 0 }}>
      <div style={{ fontSize: 12, color: C.textMuted, fontFamily: font.ui }}>
        Parity (Experimental vs Model) {'\u2014'} all data sets {'\u2014'} {fullName}
      </div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', flexShrink: 0 }}>
        {speciesList.map(sp => (
          <Chip key={sp} active={sp === species} onClick={() => setSpecies(sp)}>
            {speciesDisplayName(sp, registeredComponents)}
          </Chip>
        ))}
      </div>
      <div style={{ flex: 1, minHeight: 340, border: `1px solid ${C.border}`, borderRadius: 6, background: '#fff' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart margin={{ top: 18, right: 22, left: 10, bottom: 18 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" dataKey="x" name="Experimental" domain={[lo - pad, hi + pad]}
              tickFormatter={v => Number(v).toFixed(2)}
              label={{ value: `Experimental ${fullName}`, position: 'insideBottom', offset: -8 }} />
            <YAxis type="number" dataKey="y" name="Model" domain={[lo - pad, hi + pad]}
              tickFormatter={v => Number(v).toFixed(2)}
              label={{ value: `Model ${fullName}`, angle: -90, position: 'insideLeft' }} />
            <Tooltip formatter={(v) => (Number.isFinite(Number(v)) ? Number(v).toFixed(4) : v)} />
            <ReferenceLine segment={[{ x: lo - pad, y: lo - pad }, { x: hi + pad, y: hi + pad }]}
              stroke="#888" strokeDasharray="4 3" ifOverflow="extendDomain" />
            {series.map(s => (
              <Scatter key={`parity-${s.index}`} name={s.label} data={s.pts} dataKey="y"
                fill={s.color} isAnimationActive={false} />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11, color: C.textDim, fontFamily: font.ui }}>
        {series.map(s => (
          <span key={`parity-legend-${s.index}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: s.color }} />
            {s.label}
          </span>
        ))}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ display: 'inline-block', width: 14, borderTop: '2px dashed #888' }} />
          y = x (perfect fit)
        </span>
      </div>
    </div>
  );
};

const PanelGraph = ({ result, activePlot, setActivePlot, loading, registeredComponents = [] }) => {
  const icon = <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="0.5" y="0.5" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="1.2"/><path d="M2.5 9.5L5 6.5L7.5 8L10.5 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>;

  if (!result && !loading) return (
    <PanelShell title="Graph / Data Fitting" subtitle="Run fitting to see plots" icon={icon}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '70%', flexDirection: 'column', gap: 14, color: C.textDim }}>
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <rect x="3" y="3" width="42" height="42" rx="6" stroke={C.border} strokeWidth="2"/>
          <path d="M9 33L17 21L24 27L33 15L39 21" stroke={C.borderMid} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <div style={{ fontSize: 12, fontFamily: font.ui, textAlign: 'center', lineHeight: 1.6 }}>
          No results yet.<br/>
          <span style={{ color: C.textMuted }}>Go to </span><span style={{ color: C.accent }}>Input</span>
          <span style={{ color: C.textMuted }}> and run fitting.</span>
        </div>
      </div>
    </PanelShell>
  );

  const allPlots = {};
  if (result) {
    // ── Primary parity/overview plot (returned by ALL backends) ────────────
    // AA backend returns: result.main_plot_base64
    // MeOAc/DME backends return: result.main_plot_base64 OR result.metrics.main_plot_base64
    const mainImg = result.main_plot_base64 || result.metrics?.main_plot_base64;
    if (mainImg) allPlots['Overview'] = mainImg;

    // ── AA: additional per-species base64 plots ───────────────────────────
    // Backend may embed them in result.metrics.species_plots, result.metrics.plots,
    // or result.plots directly
    const aaSpeciesPlots = result.metrics?.species_plots
      || result.metrics?.plots
      || result.plots
      || {};
    Object.entries(aaSpeciesPlots).forEach(([k, v]) => { if (v && typeof v === 'string') allPlots[k] = v; });

    // ── AA / MeOAc/DME timeseries base64 plots ───────────────────────────
    const tsPlots = result.timeseries_plots || result.metrics?.timeseries_plots || {};
    Object.entries(tsPlots).forEach(([k, v]) => { if (v && typeof v === 'string') allPlots[k] = v; });

    // ── MeOAc/DME individual species plots ───────────────────────────────
    const indiv = result.individual_plots || result.metrics?.individual_plots || {};
    Object.entries(indiv).forEach(([k, v]) => { if (v && typeof v === 'string') allPlots[k] = v; });

    // ── Global per-run plots ──────────────────────────────────────────────
    const perRun = result.per_run_plots || {};
    Object.entries(perRun).forEach(([k, v]) => { if (v && typeof v === 'string') allPlots[k] = v; });
  }

  // Batch multi-dataset fits must default to composition profiles, not the
  // backend parity overview. The generic endpoint already returns one
  // time-series block per dataset; MeOAc/DME global endpoints now return the
  // same observed/predicted payload as part of this A04 fix.
  const isMultiDatasetBatchProfile =
    (result?.data_type === 'custom_multi_dataset'
      && String(result?.reactor_mode || '').toLowerCase() === 'batch')
    || ['methyl_acetate_global', 'dme_global'].includes(result?.data_type);

  // Parity-style multi-dataset results (PFR / steady-state etc.): one colour per data set.
  const multiParityDatasets = !isMultiDatasetBatchProfile && result ? buildMultiDatasetParity(result) : null;

  // Merge r2_summary (MeOAc/DME) + r2_per_species (AA) + per_species_r2
  // (custom/generic-system endpoint — /api/run/generic-fitting returns this
  // key instead, both for batch and PFR) into one map. BUG FIX (Graph tab,
  // 30-Jul-2026 call): per_species_r2 was previously never read here, so a
  // completed custom-system run had a real R² per species sitting in the
  // response but the "Fit Quality — R²" sidebar box simply never rendered
  // for it (r2SingleRows.length stayed 0) — the only R² a custom result
  // could show was whatever was baked into the plot image itself.
  const r2Summary = {
    ...(result?.metrics?.r2_summary     || {}),
    ...(result?.metrics?.r2_per_species || {}),
    ...(result?.metrics?.per_species_r2 || {}),
  };
  const r2SingleRows = Object.entries(r2Summary).map(([sp, val]) => [sp, val]);
  const globalPerRun = result?.global_metrics?.per_run_results || [];

  // Multi-dataset feedback: display exactly one overall R² per dataset.
  // Prefer backend per-run overall_r2 and fall back to the explicit
  // per_dataset summary. Do not average across datasets or display Wcat/n_mix.
  const datasetR2Rows = (() => {
    const rows = globalPerRun.map((run, i) => ({
      label: `Data Set ${run?.run_index != null ? Number(run.run_index) + 1 : i + 1}`,
      r2: Number(run?.overall_r2 ?? run?.final_r2),
    })).filter(row => Number.isFinite(row.r2));
    if (rows.length) return rows;

    const dsRows = Array.isArray(result?.global_metrics?.r2_summary?.per_dataset)
      ? result.global_metrics.r2_summary.per_dataset : [];
    return dsRows.map((ds, i) => ({
      label: `Data Set ${i + 1}`,
      r2: Number(ds?.overall_r2 ?? ds?.final_r2),
    })).filter(row => Number.isFinite(row.r2));
  })();
  const isMultiDatasetResult = datasetR2Rows.length > 1 || Number(result?.global_metrics?.n_runs) > 1;

  // ── AA Multi-CSV Global: combined (pooled) R² + per-dataset breakdown ──────
  // Madhura, 24-Jun-2026 follow-up call, item ii: "R² summary shows both
  // overall and per-CSV metrics."  result.global_metrics.r2_summary is a
  // {combined, per_dataset} object here (distinct from the plain-object
  // r2Summary map used by MeOAc/DME/AA-single, above).
  // (The combined / per-dataset rows are read where they are rendered below.)

  return (
    <PanelShell title="Experimental Data vs. Model Fit"
      subtitle="Parity plots and composition profiles"
      icon={icon} scrollable={!loading}
      actions={
        result ? (
          <Btn variant="ghost" style={{ fontSize: 12 }} onClick={() => window.print()}>🖨 Print</Btn>
        ) : null
      }
    >
      {/* Print-only rule: hide everything on the page except #graph-print-section */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #graph-print-section, #graph-print-section * { visibility: visible; }
          #graph-print-section { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
          height: '100%', flexDirection: 'column', gap: 14 }}>
          <div style={{ position: 'relative', width: 48, height: 48 }}>
            <div style={{ position: 'absolute', inset: 0, border: `3px solid ${C.border}`, borderRadius: '50%' }} />
            <div style={{ position: 'absolute', inset: 0, border: `3px solid transparent`,
              borderTop: `3px solid ${C.accent}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          </div>
          <div style={{ color: C.textMuted, fontSize: 13, fontFamily: font.ui }}>Optimizing kinetic parameters…</div>
        </div>
      ) : (
        // FIX: no more fixed height:'100%' + overflow:hidden here — that was
        // capping this section to the viewport and silently clipping whatever
        // didn't fit (only visible before by zooming the whole browser out).
        // Now the grid sizes to its natural content height and the PanelShell's
        // own body (scrollable=true above) scrolls the whole section as one
        // normal page, so every R² block is reachable via the standard scrollbar.
        <div id="graph-print-section" style={{ display: 'grid', gridTemplateColumns: '1.5fr 0.5fr', gap: 12,
          alignItems: 'start' }}>
          {/* Client feedback round 1: "Graph shown is too big, reduce size —
              currently doesn't even fit in the window." Round 2 (this fix):
              the previous clamp (280–480px tall, 640px wide max) was too
              SHORT relative to the plot's own aspect ratio. object-fit:
              'contain' always preserves the image's aspect ratio, so when the
              box is much wider than it is tall, it shrinks the image down to
              fit the short height and leaves the box's extra width empty —
              that's why the chart rendered small inside a big empty frame.
              Taller clamp + a wider cap gives 'contain' more vertical room to
              scale the image up before it runs out of height. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6,
            height: 'clamp(420px, 68vh, 720px)', maxWidth: 760, width: '100%' }}>
            {isMultiDatasetBatchProfile
              ? <CustomMultiBatchProfileViewer result={result} registeredComponents={registeredComponents} />
              : (multiParityDatasets
                ? <MultiDatasetParityViewer datasets={multiParityDatasets} registeredComponents={registeredComponents} />
                : <PlotViewer plots={allPlots} activePlot={activePlot} setActivePlot={setActivePlot} registeredComponents={registeredComponents} />)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
            {result && (
              <>
                {/* ── AA system: show Optimization Info in sidebar ── */}
                {(() => {
                  const isAASidebar = result.data_type === 'acetic_acid'
                    || result.metrics?.data_type === 'acetic_acid';
                  if (!isAASidebar) return null;
                  return (
                    <>
                      {/* {(result.metrics?.bo_stop_reason || result.metrics?.final_r2 != null) && (
                        <SectionBox title="AA — Optimizer Info" badge="AA" badgeColor="#E65100">
                          <div style={{ fontSize: 12, fontFamily: font.ui, lineHeight: 1.9 }}>
                            {[
                              ['Stop reason', result.metrics.bo_stop_reason],
                              ['Iters run',   result.metrics.bo_iters_run],
                              ['Final R²',   typeof result.metrics.final_r2 === 'number' ? result.metrics.final_r2.toFixed(4) : null],
                              ['Loss',        typeof result.metrics.loss === 'number' ? result.metrics.loss.toFixed(6) : null],
                              ['Mode',        result.metrics.mode],
                            ].map(([k, v]) => v != null ? (
                              <div key={k} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: C.textMuted }}>{k}</span>
                                <span style={{ color: C.text, maxWidth: 100, textAlign: 'right', wordBreak: 'break-all' }}>{String(v)}</span>
                              </div>
                            ) : null)}
                          </div>
                        </SectionBox>
                      )}
                      {result.metrics?.r2_per_species && Object.keys(result.metrics.r2_per_species).length > 0 && (
                        <SectionBox title="R² per Species" badge="AA" badgeColor="#E65100">
                          {Object.entries(result.metrics.r2_per_species).map(([sp, r2v]) => {
                            const v   = typeof r2v === 'number' ? r2v : parseFloat(r2v) || 0;
                            const col = v > 0.95 ? C.success : v > 0.8 ? cssVar('--color-warning-dark') : C.error;
                            const spLabel = speciesDisplayName(sp, registeredComponents);
                            return (
                              <div key={sp} style={{ marginBottom: 6 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2,
                                  fontSize: 12, fontFamily: font.ui }}>
                                  <span style={{ color: C.text }}>{sp}</span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <span style={{ color: col, fontWeight: 700 }}>{v.toFixed(4)}</span>
                                    <Badge color={col}>{v > 0.95 ? 'EXC' : v > 0.8 ? 'GOOD' : 'POOR'}</Badge>
                                  </div>
                                </div>
                                <div style={{ background: C.border, borderRadius: 3, height: 3 }}>
                                  <div style={{ width: `${Math.max(0,Math.min(100,v*100))}%`,
                                    height: '100%', background: col, borderRadius: 3 }} />
                                </div>
                              </div>
                            );
                          })}
                        </SectionBox>
                      )} */}
                    </>
                  );
                })()}

                {isMultiDatasetResult && datasetR2Rows.length > 0 ? (
                  <SectionBox title="R² by Data Set" badge={`${datasetR2Rows.length} datasets`} badgeColor={C.accent}>
                    {datasetR2Rows.map((row, i) => {
                      const r2v = row.r2;
                      const col = r2v > 0.95 ? C.success : r2v > 0.8 ? cssVar('--color-warning-dark') : C.error;
                      const pct = Math.max(0, Math.min(100, r2v * 100));
                      return (
                        <div key={`${row.label}-${i}`} style={{ marginBottom: 10 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3,
                            fontSize: 12, fontFamily: font.ui }}>
                            <span style={{ color: C.text }}>{row.label}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              <span style={{ color: col, fontWeight: 700 }}>R² {r2v.toFixed(4)}</span>
                              <Badge color={col}>{r2v > 0.95 ? 'EXC' : r2v > 0.8 ? 'GOOD' : 'POOR'}</Badge>
                            </div>
                          </div>
                          <div style={{ background: C.border, borderRadius: 3, height: 4 }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: col, borderRadius: 3 }} />
                          </div>
                        </div>
                      );
                    })}
                  </SectionBox>
                ) : (
                  r2SingleRows.length > 0 && (
                    <SectionBox title="Fit Quality — R²">
                      {r2SingleRows.map(([sp, r2]) => {
                        const r2v = typeof r2 === 'number' ? r2 : parseFloat(r2) || 0;
                        const col = r2v > 0.95 ? C.success : r2v > 0.8 ? cssVar('--color-warning-dark') : C.error;
                        const label = speciesDisplayName(sp, registeredComponents);
                        return (
                          <div key={sp} style={{ marginBottom: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3,
                              fontSize: 12, fontFamily: font.ui }}>
                              <span style={{ color: C.text }}>{label}</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <span style={{ color: col, fontWeight: 700 }}>{r2v.toFixed(4)}</span>
                                <Badge color={col}>{r2v > 0.95 ? 'EXC' : r2v > 0.8 ? 'GOOD' : 'POOR'}</Badge>
                              </div>
                            </div>
                            <div style={{ background: C.border, borderRadius: 3, height: 4 }}>
                              <div style={{ width: `${Math.max(0, Math.min(100, r2v * 100))}%`,
                                height: '100%', background: col, borderRadius: 3 }} />
                            </div>
                          </div>
                        );
                      })}
                    </SectionBox>
                  )
                )}

                {/* Client feedback: "Equilibrium check tab has Keq parameters;
                    it should not be in Graphs, and Song result shouldn't show
                    either. No need for ratio to be displayed." — removed from
                    here entirely. Keq is now shown, where applicable, as an
                    "Equilibrium Constant" table in the Parameters tab instead. */}
              </>
            )}
          </div>
        </div>
      )}
    </PanelShell>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PANEL 3 — OUTPUTS
// ═══════════════════════════════════════════════════════════════════════════
const PanelOutputs = ({ result, fitMode, reactionSystem: rxSysProp, excludedRows, onToggleRow, onResetExcluded,
  rateBasis = 'cat_weight', reversibility = 'reversible', keqMode = 'regress',
  keqUserValues = [], rxn1Reactants = '', rxn1Products = '', loading = false, registeredComponents = [] }) => {
  // ⚠ Hook must be declared before any early return (Rules of Hooks)
  const [copiedParams, setCopiedParams] = useState(false);

  const icon = <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="0.5" y="0.5" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="1.2"/><path d="M3 4.5h8M3 7h5.5M3 9.5h3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>;

  // ── Shared helpers for the succinct parameter tables (client requirement) ──
  // "Units for rate constant will change according to Rate Basis."
  const rateConstUnits = rateBasis === 'volume' ? 'kmol/(m³·s)' : 'kmol/(kg·s)';
  const isReversibleSys = reversibility !== 'irreversible';

  // Split a reaction side ("HOAc + MeOH", "2 MeOH", "C2H6 + 3.5 O2") into
  // individual component tokens, stripping any leading stoichiometric coefficient.
  const splitSpeciesTokens = (side) => (side || '')
    .split('+')
    .map(s => parseStoicTermText(s).species)
    .filter(Boolean);

  // Parse a full equation string ("HOAc + MeOH ⇌ MeOAc + H₂O", "C2H6 + O2 -> CO2 + H2O")
  // into { reactants: [...], products: [...] } component tokens.
  const parseEquationSpecies = (eqStr) => {
    const sides = (eqStr || '').split(/⇌|→|->|=/);
    return {
      reactants: splitSpeciesTokens(sides[0]),
      products: splitSpeciesTokens(sides[1]),
    };
  };

  // Client feedback (Labels & Naming, 30-Jul-2026): "Exponent/label text must
  // always use the component name or formula, never internal shortcut codes,
  // for any user-entered system." splitSpeciesTokens/parseEquationSpecies
  // above return whatever the user (or a built-in system) literally typed —
  // which for MeOAc/DME/AA includes internal shorthand like 'HOAc'/'MeOAc'/
  // 'MeOH' that isn't a real chemical formula or a name. Every exponent
  // table/export below resolves through this instead of using the raw token
  // or a raw subscript-formatting helper alone (see toSubscriptFormula near
  // the top of the file for the shared formatter).
  const nameOf = sp => speciesDisplayName(sp, registeredComponents);

  if (!result) return (
    <PanelShell title="Output" subtitle="Kinetic Data Prepared for Reactor Modeling" icon={icon}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '70%', flexDirection: 'column', gap: 12, color: C.textDim }}>
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
          <rect x="3" y="3" width="34" height="34" rx="5" stroke={C.border} strokeWidth="2"/>
          <path d="M9 13h22M9 20h15M9 27h10" stroke={C.borderMid} strokeWidth="2" strokeLinecap="round"/>
        </svg>
        <span style={{ fontSize: 13, fontFamily: font.ui }}>Run fitting to see output parameters</span>
      </div>
    </PanelShell>
  );

  // ── Detect AA multi-reaction system (check result payload AND the prop) ──
  const isAA        = result.data_type === 'acetic_acid'
                   || result.metrics?.data_type === 'acetic_acid'
                   || rxSysProp === 'acetic_acid';
  // BUG FIX (24-Jul-2026): a completed custom-system run (see
  // /api/run/generic-fitting, generic_kinetics_objective.py) already returns
  // `reaction_params` at the top level in exactly the same shape AA does
  // ({reaction_index, equation, Af, Ea_kJ_mol, kf_at_T_ref, ...} per
  // reaction, plus a generic `exponents` dict instead of AA's fixed
  // a_C2H6/b_O2 fields). But this panel only ever checked `isAA` before
  // switching into the per-reaction rendering block below — for
  // reactionSystem === 'custom' it fell through to the single-reaction
  // MeOAc-shaped fields (fr.Af_aspen, asp.a_acoh, ...), which don't exist on
  // a custom result, so every stat tile showed '—' even though the BO run
  // completed and the terminal printed a successful optimum (propane /
  // AA+ethylene custom runs). `isCustom` lets the same per-reaction block
  // below serve custom systems too.
  const isCustom    = rxSysProp === 'custom'
                   || result.data_type === 'custom'
                   || result.reaction_system === 'custom';
  const isMultiRxn  = isAA || isCustom;
  const aaRxnParams = result.metrics?.reaction_params
                   || result.reaction_params
                   || [];   // [{reaction_index, equation, Af, Ea_kJ_mol, a_C2H6, b_O2, kf_at_T_ref, rate_units}]

  const fr   = result.fit_results?.[0] || {};
  const asp  = result.aspen_ready || result.metrics?.aspen_params || fr.aspen_ready || {};
  const kp   = result.global_metrics?.kinetic_params || {};
  const aspG = result.global_metrics?.aspen_params_ref_run0 || {};
  const isGlobal = !!result.global_metrics;

  // For AA system: aggregate Af/Ea from primary reaction (Rxn 1 = AA production)
  const aaRxn1 = aaRxnParams[0] || {};

  const a_acoh  = fr.a_acoh  ?? kp.a_acoh  ?? asp.a_acoh  ?? '—';
  const a_meoh  = fr.a_meoh  ?? kp.a_meoh  ?? asp.a_meoh  ?? '—';
  const b_meoac = fr.b_meoac ?? kp.b_meoac ?? asp.b_meoac ?? '—';
  const b_h2o   = fr.b_h2o   ?? kp.b_h2o   ?? asp.b_h2o   ?? '—';

  // Stat tiles — prefer AA params when isAA
  const Af_aspen = isAA ? aaRxn1.Af           : (fr.Af_aspen ?? aspG.Af_aspen ?? asp.Af_aspen);
  const Ea_f_kJ  = isAA ? aaRxn1.Ea_kJ_mol   : (fr.Ea_f ?? aspG.Ea_f_kJ ?? asp.Ea_f);
  const Ab_aspen = isAA ? (aaRxnParams[1]?.Af)           : (fr.Ab_internal ?? aspG.Ab_aspen ?? asp.Ab_aspen);
  const Ea_b_kJ  = isAA ? (aaRxnParams[1]?.Ea_kJ_mol)   : (fr.Ea_b ?? aspG.Ea_b_kJ ?? asp.Ea_b);

  const fmt  = (v, dp = 4) => v != null ? (typeof v === 'number' ? v.toFixed(dp) : v) : '—';
  const fmtE = v => v != null ? (typeof v === 'number' ? v.toExponential(3) : v) : '—';

  const exportCSV = () => {
    let rows;
    if (isAA) {
      const anyReverse = aaRxnParams.some(r => r.Ab != null || r.Ea_rev_kJ_mol != null);
      rows = [anyReverse
        ? ['Reaction', 'Forward Rate Constant (Af)', 'Forward Activation Energy (Eaf)', 'Backward Rate Constant (Ab)', 'Backward Activation Energy (Eab)', 'Units']
        : ['Reaction', 'Forward Rate Constant (Af)', 'Forward Activation Energy (Eaf)', 'Units']];
      aaRxnParams.forEach(rxn => {
        const row = [`Rxn ${rxn.reaction_index ?? ''}`,
          rxn.Af != null ? rxn.Af.toExponential(4) : '—',
          rxn.Ea_kJ_mol != null ? rxn.Ea_kJ_mol.toFixed(4) : '—'];
        if (anyReverse) row.push(
          rxn.Ab != null ? rxn.Ab.toExponential(4) : '—',
          rxn.Ea_rev_kJ_mol != null ? rxn.Ea_rev_kJ_mol.toFixed(4) : '—');
        row.push(rxn.rate_units || rateConstUnits);
        rows.push(row);
      });
      rows.push([]);
      rows.push(['Reaction', 'Reactant 1 exponent', 'Reactant 2 exponent']);
      aaRxnParams.forEach(rxn => {
        const { reactants } = parseEquationSpecies(rxn.equation);
        rows.push([`Rxn ${rxn.reaction_index ?? ''}`,
          `${reactants[0] ? nameOf(reactants[0]) : 'Reactant 1'}: ${rxn.a_C2H6 != null ? rxn.a_C2H6.toFixed(4) : '—'}`,
          `${reactants[1] ? nameOf(reactants[1]) : 'Reactant 2'}: ${rxn.b_O2 != null ? rxn.b_O2.toFixed(4) : '—'}`]);
      });
    } else if (isCustom) {
      // Custom systems have an arbitrary, user-typed species set, so there's
      // no fixed a_<sp>/b_<sp> column pair like AA — exponents are exported
      // as one "sp=value" string per reaction instead. Rate-constant columns
      // mirror the same succinct AA format above (client feedback applies
      // equally here since custom systems share the same multi-reaction panel).
      const anyReverse = aaRxnParams.some(r => r.Ab != null || r.Ea_rev_kJ_mol != null);
      rows = [anyReverse
        ? ['Reaction', 'Forward Rate Constant (Af)', 'Forward Activation Energy (Eaf)', 'Backward Rate Constant (Ab)', 'Backward Activation Energy (Eab)', 'Units']
        : ['Reaction', 'Forward Rate Constant (Af)', 'Forward Activation Energy (Eaf)', 'Units']];
      aaRxnParams.forEach(rxn => {
        const row = [`Rxn ${rxn.reaction_index ?? ''}`,
          rxn.Af != null ? rxn.Af.toExponential(4) : '—',
          rxn.Ea_kJ_mol != null ? rxn.Ea_kJ_mol.toFixed(4) : '—'];
        if (anyReverse) row.push(
          rxn.Ab != null ? rxn.Ab.toExponential(4) : '—',
          rxn.Ea_rev_kJ_mol != null ? rxn.Ea_rev_kJ_mol.toFixed(4) : '—');
        row.push(rxn.rate_units || rateConstUnits);
        rows.push(row);
      });
      rows.push([]);
      rows.push(['Reaction', 'Exponents']);
      aaRxnParams.forEach(rxn => {
        const expStr = rxn.exponents && Object.keys(rxn.exponents).length > 0
          ? Object.entries(rxn.exponents).map(([sp, v]) => `${nameOf(sp)}=${Number(v).toFixed(3)}`).join('; ')
          : 'fixed @ 1';
        rows.push([`Rxn ${rxn.reaction_index ?? ''}`, expStr]);
      });
    } else {
      const reactants = splitSpeciesTokens(rxn1Reactants);
      const products  = splitSpeciesTokens(rxn1Products);
      rows = isReversibleSys
        ? [['Reaction', 'Forward Rate Constant (Af)', 'Forward Activation Energy (Eaf)', 'Backward Rate Constant (Ab)', 'Backward Activation Energy (Eab)', 'Units'],
           ['1', fmtE(Af_aspen), fmt(Ea_f_kJ, 4), fmtE(Ab_aspen), fmt(Ea_b_kJ, 4), rateConstUnits]]
        : [['Reaction', 'Forward Rate Constant (Af)', 'Forward Activation Energy (Eaf)', 'Units'],
           ['1', fmtE(Af_aspen), fmt(Ea_f_kJ, 4), rateConstUnits]];
      rows.push([]);
      rows.push(['Reaction', ...[reactants[0], reactants[1], products[0], products[1]].filter(Boolean).map(nameOf)]);
      const expVals = [a_acoh, a_meoh, b_meoac, b_h2o];
      const names = [reactants[0], reactants[1], products[0], products[1]];
      rows.push(['1', ...names.map((n, i) => n ? fmt(typeof expVals[i] === 'number' ? expVals[i] : parseFloat(expVals[i]), 4) : null).filter((v, i) => names[i])]);
    }
    const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url;
    a.download = isAA ? 'aa_kinetics_params.csv' : isCustom ? 'custom_kinetics_params.csv' : 'fitted_params.csv'; a.click();
  };

  const exportExcel = () => {
    const b64 = result.excel_export;
    if (!b64) { alert('Excel export not available for this result.'); return; }
    const byteArr = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([byteArr], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    const a = document.createElement('a'); a.href = url;
    a.download = isGlobal ? 'global_parity.xlsx' : 'parity_export.xlsx'; a.click();
  };

  // Client feedback: "Copy/Download/Print results... should be available only
  // in the format shown in the table [on this page]." — Copy mirrors the same
  // succinct Rate Constant / Exponents tables rendered on screen.
  //
  // Client feedback (Data Entry & Import/Export, 30-Jul-2026): "Table
  // copy-paste into Excel doesn't split into Name / Value / Unit columns" —
  // the previous version joined everything with '\n' only, so pasting into
  // Excel dropped a whole "Forward Rate Constant (Af) = 1.23e0 kg/s" string
  // into a single cell. Tab (`\t`) is the delimiter Excel/Sheets recognize
  // for "split into columns on paste" — every row below is
  // Name<TAB>Value<TAB>Unit, so a paste lands as 3 real columns.
  const handleCopyParams = () => {
    const rows = [['Name', 'Value', 'Unit']]; // header row — becomes the Excel column headers on paste
    const rxnTag = (idx, total) => total > 1 ? `Rxn ${idx} — ` : '';

    if (isAA || isCustom) {
      const anyReverse = aaRxnParams.some(r => r.Ab != null || r.Ea_rev_kJ_mol != null);
      const total = aaRxnParams.length;
      aaRxnParams.forEach(rxn => {
        const tag = rxnTag(rxn.reaction_index ?? '', total);
        rows.push([`${tag}Equation`, rxn.equation ?? '', '']);
        rows.push([`${tag}Forward Rate Constant (Af)`, fmtE(rxn.Af), rxn.rate_units || rateConstUnits]);
        rows.push([`${tag}Forward Activation Energy (Eaf)`, fmt(rxn.Ea_kJ_mol, 4), 'kJ/mol']);
        if (anyReverse) {
          rows.push([`${tag}Backward Rate Constant (Ab)`, fmtE(rxn.Ab), rxn.rate_units || rateConstUnits]);
          rows.push([`${tag}Backward Activation Energy (Eab)`, fmt(rxn.Ea_rev_kJ_mol, 4), 'kJ/mol']);
        }
        if (isAA) {
          const { reactants } = parseEquationSpecies(rxn.equation);
          rows.push([`${tag}${reactants[0] ? nameOf(reactants[0]) : 'Reactant 1'} exponent`, fmt(rxn.a_C2H6, 4), '']);
          rows.push([`${tag}${reactants[1] ? nameOf(reactants[1]) : 'Reactant 2'} exponent`, fmt(rxn.b_O2, 4), '']);
        } else if (rxn.exponents && Object.keys(rxn.exponents).length > 0) {
          Object.entries(rxn.exponents).forEach(([sp, v]) =>
            rows.push([`${tag}${nameOf(sp)} exponent`, Number(v).toFixed(3), '']));
        } else {
          rows.push([`${tag}Exponents`, 'fixed @ 1', '']);
        }
      });
    } else {
      const reactants = splitSpeciesTokens(rxn1Reactants);
      const products  = splitSpeciesTokens(rxn1Products);
      rows.push(['Forward Rate Constant (Af)', fmtE(Af_aspen), rateConstUnits]);
      rows.push(['Forward Activation Energy (Eaf)', fmt(Ea_f_kJ, 4), 'kJ/mol']);
      if (isReversibleSys) {
        rows.push(['Backward Rate Constant (Ab)', fmtE(Ab_aspen), rateConstUnits]);
        rows.push(['Backward Activation Energy (Eab)', fmt(Ea_b_kJ, 4), 'kJ/mol']);
      }
      [[reactants[0], a_acoh], [reactants[1], a_meoh], [products[0], b_meoac], [products[1], b_h2o]]
        .filter(([name]) => name)
        .forEach(([name, val]) =>
          rows.push([`${nameOf(name)} exponent`, fmt(typeof val === 'number' ? val : parseFloat(val), 4), '']));
    }

    const tsv = rows.map(r => r.map(c => String(c ?? '').replace(/\t/g, ' ')).join('\t')).join('\n');
    navigator.clipboard.writeText(tsv).then(() => {
      setCopiedParams(true); setTimeout(() => setCopiedParams(false), 2000);
    });
  };

  // ── AA SYSTEM: dedicated multi-reaction output panel ─────────────────────
  // The AA response nests parameters under result.metrics.reaction_params
  // (array of 3 per-reaction dicts with keys Af, Ea_kJ_mol, a_C2H6, b_O2)
  // and result.metrics.aspen_params (nested {reaction_1:{...},...}).
  // The MeOAc field paths (fr.Af_aspen, asp.Ea_f, fr.a_acoh, etc.) are all
  // undefined for AA, which is why the standard panel shows '—' everywhere.
  // Alias rxSysProp back to reactionSystem so all downstream JSX keeps working
  const reactionSystem = rxSysProp;

  // Show the per-reaction panel whenever the system is AA OR a custom
  // user-typed system (see isMultiRxn note above), even if params are
  // still loading/empty. NOTE: keep this gated on isMultiRxn (not isAA) —
  // custom systems are multi-reaction too and rely on this same panel.
  if (isMultiRxn) {
    return (
      <PanelShell title="Output" subtitle="Kinetic Data Prepared for Reactor Modeling" icon={icon}
        actions={
          <div style={{ display: 'flex', gap: 6 }}>
            {result.excel_export && (
              <Btn variant="ghost" style={{ fontSize: 12 }} onClick={exportExcel}>⬇ Excel</Btn>
            )}
            <Btn variant="ghost" style={{ fontSize: 12 }} onClick={handleCopyParams}>
              {copiedParams ? '✓ Copied' : '📋 Copy'}
            </Btn>
            <Btn variant="ghost" style={{ fontSize: 12 }} onClick={() => window.print()}>🖨 Print</Btn>
          </div>
        }
      >
        {/* Print-only rule: hide everything on the page except #output-multirxn-print-section */}
        <style>{`
          @media print {
            body * { visibility: hidden; }
            #output-multirxn-print-section, #output-multirxn-print-section * { visibility: visible; }
            #output-multirxn-print-section { position: absolute; left: 0; top: 0; width: 100%; }
          }
        `}</style>
        <div id="output-multirxn-print-section">
        {/* ── Fallback when params not yet populated. Distinguishes "a fit is
            actively running" from "nothing has been submitted yet" — both
            leave aaRxnParams empty, but they read very differently to the
            user, especially right after navigating back to a run that's
            still going (see the "still running" banner on the Input tab). */}
        {aaRxnParams.length === 0 && (
          <div style={{ padding: '20px 0', textAlign: 'center', color: C.textDim,
            fontSize: 13, fontFamily: font.ui }}>
            {loading
              ? 'Fitting is still in progress — kinetic parameters will appear here as soon as it finishes.'
              : 'Run fitting to see per-reaction kinetic parameters.'}
          </div>
        )}
        {/* ── Forward/Backward Rate Constant & Activation Energy — succinct table ──
            Client spec: no kf@T/kb@T (not directly used); succinct table;
            units follow Rate Basis; irreversible → forward-only columns;
            single reaction → 1 row. Applies to both AA and custom systems —
            both share the same aaRxnParams shape (Af/Ea/Ab/Ea_rev/rate_units). */}
        {aaRxnParams.length > 0 && (() => {
          const anyReverse = aaRxnParams.some(r => r.Ab != null || r.Ea_rev_kJ_mol != null);
          const headerRows = anyReverse ? [
            [{ label: 'Reaction', rowSpan: 3 }, { label: 'Forward Reaction', colSpan: 2 }, { label: 'Backward Reaction', colSpan: 2 }],
            [{ label: 'Rate Constant (Af)' }, { label: 'Activation Energy (Eaf)' }, { label: 'Rate Constant (Ab)' }, { label: 'Activation Energy (Eab)' }],
            [{ label: rateConstUnits }, { label: 'kJ/mol' }, { label: rateConstUnits }, { label: 'kJ/mol' }],
          ] : [
            [{ label: 'Reaction', rowSpan: 3 }, { label: 'Forward Reaction', colSpan: 2 }],
            [{ label: 'Rate Constant (Af)' }, { label: 'Activation Energy (Eaf)' }],
            [{ label: rateConstUnits }, { label: 'kJ/mol' }],
          ];
          const bodyRows = aaRxnParams.map(rxn => {
            const row = [`${rxn.reaction_index ?? ''}`, fmtE(rxn.Af), fmt(rxn.Ea_kJ_mol, 4)];
            if (anyReverse) row.push(fmtE(rxn.Ab), fmt(rxn.Ea_rev_kJ_mol, 4));
            return row;
          });
          return (
            <SectionBox title="Rate Constant & Activation Energy">
              <MultiHeaderTable headerRows={headerRows} bodyRows={bodyRows} />
            </SectionBox>
          );
        })()}

        {/* ── Equilibrium constant table (only if applicable) ──────────────
            Action item #6 (call tracker, 10-Sep): "K is currently shown as
            a single number, but K is temperature-dependent (derived from
            kf/kb); the results table needs to express K as a function of
            temperature, matching the format already used for rate
            constants." The Rate Constant table above shows the two
            Arrhenius parameters (Af, Eaf) rather than a single kf-at-T —
            this does the same for Keq: the two ln(Keq)=X+Y/T(K)
            coefficients (keq_X, keq_Y — X=ln(Af/Ab), Y=(Ea_b-Ea_f)/R, same
            convention as Madhura's Equation_for_Keq.docx derivation).
            18-Sep-2026 EDIT (Sept-17 call, item #14): the single-value
            "K_eq @ T (info)" column was dropped — Madhura called it
            redundant, since a user who has the X/Y expression can already
            compute K at any T themselves, same as the tool doesn't do this
            for the rate constants either. Backend still computes
            Keq_at_T_ref (harmless, may be useful elsewhere) — only the
            display column is gone; the `!= null` filters below just use
            it as an existence check for "this reaction has a Keq to show".
            Source text is also simplified per the same call (item #14/
            Madhura's live follow-up): only "Calculated from kf/kb" or
            "User-specified" — no equation-name mention at all. */}
        {(() => {
          const keqRows = aaRxnParams.map((r, i) => {
            const user = keqUserValues?.[i] || {};
            const x = r.keq_X ?? (user.X !== '' && user.X != null ? Number(user.X) : null);
            const y = r.keq_Y ?? (user.Y !== '' && user.Y != null ? Number(user.Y) : null);
            const hasData = r.Keq_at_T_ref != null || Number.isFinite(Number(x)) || Number.isFinite(Number(y));
            if (!hasData) return null;
            return [
              `${r.reaction_index ?? i + 1}`,
              Number.isFinite(Number(x)) ? fmt(Number(x), 4) : '—',
              Number.isFinite(Number(y)) ? fmt(Number(y), 2) : '—',
              r.keq_source || (keqMode === 'user_specified' ? 'User-specified' : 'Calculated from kf/kb'),
            ];
          }).filter(Boolean);
          return keqRows.length > 0 ? (
            <SectionBox title="Equilibrium Constant">
              <MultiHeaderTable
                headerRows={[[
                  { label: 'Reaction' },
                  { label: 'ln(Keq) Intercept (X)' },
                  { label: 'ln(Keq) Slope (Y)' },
                  { label: 'Source' },
                ]]}
                bodyRows={keqRows}
              />
            </SectionBox>
          ) : null;
        })()}


        {/* ── Table of exponents — AA uses named reactant columns (component
            names, not shortcut var names); custom systems have an arbitrary,
            user-typed species set, so there's no fixed column pair like AA —
            each reaction lists its own "species = exponent" pairs instead. ── */}
        {aaRxnParams.length > 0 && (isAA ? (() => {
          const parsed = aaRxnParams.map(rxn => {
            const { reactants } = parseEquationSpecies(rxn.equation);
            return {
              label: `${rxn.reaction_index ?? ''}`,
              r1: { name: reactants[0] ? nameOf(reactants[0]) : 'Reactant 1', value: rxn.a_C2H6 },
              r2: { name: reactants[1] ? nameOf(reactants[1]) : 'Reactant 2', value: rxn.b_O2 },
            };
          });
          const sameR1 = parsed.every(p => p.r1.name === parsed[0].r1.name);
          const sameR2 = parsed.every(p => p.r2.name === parsed[0].r2.name);
          const h1 = sameR1 ? parsed[0].r1.name : 'Reactant 1';
          const h2 = sameR2 ? parsed[0].r2.name : 'Reactant 2';
          const cell = (c, sameCol) => c.value == null ? '—'
            : (sameCol ? fmt(c.value, 4) : `${c.name}: ${fmt(c.value, 4)}`);
          return (
            <SectionBox title="Reaction-Order Exponents">
              <MultiHeaderTable
                headerRows={[[{ label: 'Reaction' }, { label: h1 }, { label: h2 }]]}
                bodyRows={parsed.map(p => [p.label, cell(p.r1, sameR1), cell(p.r2, sameR2)])}
              />
            </SectionBox>
          );
        })() : (
          <SectionBox title="Reaction-Order Exponents">
            <MultiHeaderTable
              headerRows={[[{ label: 'Reaction' }, { label: 'Forward Exponents' }, { label: 'Backward Exponents' }]]}
              bodyRows={aaRxnParams.map(rxn => {
                const fwd = rxn.exponents && Object.keys(rxn.exponents).length > 0
                  ? Object.entries(rxn.exponents).map(([sp, v]) => `${nameOf(sp)}=${Number(v).toFixed(3)}`).join('; ')
                  : 'fixed @ 1';
                // GAP FIX (KinetiQ work-items review, 07-Aug-2026): this table
                // used to show only one merged "Exponents" column, which for a
                // reversible reaction actually mixed forward (reactant-side)
                // and backward (product-side) numbers together under a single
                // undifferentiated header — reverse_exponents (now surfaced by
                // the API, see openmkm_api.py) lets us split them properly.
                // Irreversible reactions have no backward rate at all, so
                // that column reads "—" rather than "fixed @ 1" (there's no
                // reverse exponent to be fixed).
                const bwd = !rxn.reversible ? '—'
                  : (rxn.reverse_exponents && Object.keys(rxn.reverse_exponents).length > 0
                      ? Object.entries(rxn.reverse_exponents).map(([sp, v]) => `${nameOf(sp)}=${Number(v).toFixed(3)}`).join('; ')
                      : 'fixed @ 1');
                return [` ${rxn.reaction_index ?? ''}`, fwd, bwd];
              })}
            />
          </SectionBox>
        ))}

        {/* GAP FIX (KinetiQ work-items review, 07-Aug-2026): MeOAc/DME show
            an "Expressions used" panel with the literal rate law + Arrhenius
            expression being fit; custom/generic systems had no equivalent
            at all, even though it's arguably more useful there since the
            rate law isn't a fixed textbook one — it depends entirely on
            what the user typed (species, stoichiometry, powerlaw vs LHHW,
            reversible or not). Built per-reaction from that reaction's own
            fitted metadata rather than looked up from a table. AA has its
            own dedicated equations elsewhere, so this is scoped to the
            generic/custom branch only. */}
        {!isAA && aaRxnParams.length > 0 && (
          <SectionBox title="Expressions used">
            {aaRxnParams.map((rxn, idx) => {
              const { reactants, products } = parseEquationSpecies(rxn.equation);
              const speciesTex = (sp) => `\\mathrm{${nameOf(sp).replace(/\s+/g, '\\,')}}`;
              const term = (sp, expMap) => {
                const e = expMap && expMap[sp] != null ? Number(expMap[sp]) : 1;
                const base = `C_{${speciesTex(sp)}}`;
                return Math.abs(e - 1) < 1e-9 ? base : `${base}^{${e.toFixed(3).replace(/\.?0+$/, '')}}`;
              };
              const fwdTerms = reactants.map(sp => term(sp, rxn.exponents)).join('\\,');
              const bwdTerms = rxn.reversible ? products.map(sp => term(sp, rxn.reverse_exponents)).join('\\,') : null;
              const numerator = `k_f\\,${fwdTerms}` + (bwdTerms ? ` - k_b\\,${bwdTerms}` : '');
              let mainLatex;
              if (rxn.model_type === 'lhhw') {
                const denomSpecies = rxn.reversible ? [...reactants, ...products] : reactants;
                const denomTerms = denomSpecies.map(sp => `K_{${speciesTex(sp)}}\\,C_{${speciesTex(sp)}}`).join(' + ');
                mainLatex = `r = \\dfrac{${numerator}}{\\left(1 + ${denomTerms}\\right)^{2}}`;
              } else {
                mainLatex = `r = ${numerator}`;
              }
              const arrLatex = rxn.reversible
                ? String.raw`k_f = A_f \exp\!\left(-\dfrac{E_{a,f}}{RT}\right), \quad k_b = A_b \exp\!\left(-\dfrac{E_{a,b}}{RT}\right)`
                : String.raw`k_f = A_f \exp\!\left(-\dfrac{E_{a,f}}{RT}\right)`;
              // Keq(T) expression — client feedback: the Keq parameters (X, Y)
              // were shown in the Equilibrium Constant table but the matching
              // expression was missing from "Expressions used". Same X/Y as that
              // table: ln(Keq) = X + Y/T(K), X = ln(Af/Ab), Y = (Ea,b - Ea,f)/R.
              const keqUser = keqUserValues?.[idx] || {};
              const keqX = rxn.keq_X ?? (keqUser.X !== '' && keqUser.X != null ? Number(keqUser.X) : null);
              const keqY = rxn.keq_Y ?? (keqUser.Y !== '' && keqUser.Y != null ? Number(keqUser.Y) : null);
              const hasKeqXY = Number.isFinite(Number(keqX)) && keqX !== null && Number.isFinite(Number(keqY)) && keqY !== null;
              const showKeq = !!rxn.reversible || hasKeqXY;
              const keqLatex = hasKeqXY
                ? String.raw`\ln K_{eq} = X + \dfrac{Y}{T} = ${Number(keqX).toFixed(4)} ${Number(keqY) < 0 ? '-' : '+'} \dfrac{${Math.abs(Number(keqY)).toFixed(2)}}{T}, \quad K_{eq} = \dfrac{k_f}{k_b}`
                : String.raw`\ln K_{eq} = X + \dfrac{Y}{T}, \quad K_{eq} = \dfrac{k_f}{k_b}`;
              const eqLabel = (txt) => (
                <div style={{ fontSize: 13, fontWeight: 700, color: C.accent, textTransform: 'uppercase',
                  letterSpacing: '0.1em', fontFamily: font.ui, marginBottom: 4, marginTop: idx === 0 ? 0 : 8 }}>{txt}</div>
              );
              const EqBlock = ({ latex }) => (
                <div style={{ background: C.codeBg, border: `1px solid ${C.accent}33`,
                  borderRadius: 5, padding: '10px 12px', overflowX: 'auto',
                  display: 'flex', justifyContent: 'safe center' }}>
                  <BlockMath math={latex} />
                </div>
              );
              return (
                <div key={idx} style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: '10px 12px',
                  marginBottom: idx < aaRxnParams.length - 1 ? 8 : 0 }}>
                  {aaRxnParams.length > 1 && (
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, fontFamily: font.ui }}>
                      Reaction {rxn.reaction_index ?? idx + 1}
                    </div>
                  )}
                  {eqLabel('Expression for reaction rate')}
                  <EqBlock latex={mainLatex} />
                  {eqLabel('Arrhenius expression')}
                  <EqBlock latex={arrLatex} />
                  {showKeq && (
                    <>
                      {eqLabel('Equilibrium constant as a function of Temperature')}
                      <EqBlock latex={keqLatex} />
                    </>
                  )}
                  {rxn.model_type === 'lhhw' && (
                    <div style={{ fontSize: 12, color: C.textDim, marginTop: 6, fontFamily: font.ui }}>
                      Denominator uses a squared dual-site adsorption term (same convention as MeOAc/DME).
                      K values come from the LHHW Adsorption Parameters panel — species not entered
                      there default to K = 1 (no adsorption preference assumed).
                    </div>
                  )}
                </div>
              );
            })}
          </SectionBox>
        )}

        {/* R² per Species / Optimization Info removed from this tab per
            client feedback (05-Aug-2026 screenshot review) — not to be
            displayed here. */}

        {/* ── Per-Row Residuals + Outlier Exclusion ─────────────────────────
            Lives here (Output/Parameters tab) per the original screenshot —
            NOT in the Graph tab. */}
        {result && (
          <ResidualTable
            evpRows={result.metrics?.exp_vs_pred_rows}
            excludedRows={excludedRows || new Set()}
            onToggleRow={onToggleRow || (() => {})}
            onResetExcluded={onResetExcluded || (() => {})}
            weightsUsed={result.metrics?.weights_used}
          />
        )}
        </div>
      </PanelShell>
    );
  }
  // ── End AA block — MeOAc / DME rendering continues below unchanged ────────

  return (
    <PanelShell title="Output" subtitle="Kinetic Data Prepared for Reactor Modeling" icon={icon}
      actions={
        <div style={{ display: 'flex', gap: 6 }}>
          {result.excel_export && (
            <Btn variant="ghost" style={{ fontSize: 12 }} onClick={exportExcel}>⬇ Excel</Btn>
          )}
          <Btn variant="ghost" style={{ fontSize: 12 }} onClick={exportCSV}>⬇ CSV</Btn>
          <Btn variant="ghost" style={{ fontSize: 12 }} onClick={handleCopyParams}>
            {copiedParams ? '✓ Copied' : '📋 Copy'}
          </Btn>
          <Btn variant="ghost" style={{ fontSize: 12 }} onClick={() => window.print()}>🖨 Print</Btn>
        </div>
      }
    >
      {/* Print-only rule: hide everything on the page except #output-print-section */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #output-print-section, #output-print-section * { visibility: visible; }
          #output-print-section { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
      <div id="output-print-section">
      {/* Client feedback: "No need to display kf@T, kb@T, it is not directly
          used. Display Forward Rate Constant, Forward Activation Energy, etc
          in a succinct table format... Units for rate constant will change
          according to Rate Basis. For an Irreversible reaction, only forward
          reaction cells should show. For a single reaction, only 1 row." —
          MeOAc/DME are always a single reaction, so this is always exactly 1 row. */}
      <SectionBox title="Rate Constant & Activation Energy">
        <MultiHeaderTable
          headerRows={isReversibleSys ? [
            [{ label: 'Reaction', rowSpan: 3 }, { label: 'Forward Reaction', colSpan: 2 }, { label: 'Backward Reaction', colSpan: 2 }],
            [{ label: 'Rate Constant (Af)' }, { label: 'Activation Energy (Eaf)' }, { label: 'Rate Constant (Ab)' }, { label: 'Activation Energy (Eab)' }],
            [{ label: rateConstUnits }, { label: 'kJ/mol' }, { label: rateConstUnits }, { label: 'kJ/mol' }],
          ] : [
            [{ label: 'Reaction', rowSpan: 3 }, { label: 'Forward Reaction', colSpan: 2 }],
            [{ label: 'Rate Constant (Af)' }, { label: 'Activation Energy (Eaf)' }],
            [{ label: rateConstUnits }, { label: 'kJ/mol' }],
          ]}
          bodyRows={[
            isReversibleSys
              ? ['1', fmtE(Af_aspen), fmt(Ea_f_kJ, 4), fmtE(Ab_aspen), fmt(Ea_b_kJ, 4)]
              : ['1', fmtE(Af_aspen), fmt(Ea_f_kJ, 4)],
          ]}
        />
      </SectionBox>

      {/* ── Equilibrium constant — shown here (Parameters), not in Graphs ──
          Action item #6: same "K as a function of T, not a single number"
          fix as the Custom/AA table above. boa_kinetics_objective.py's
          compute_aspen_params already builds a keq_expression string
          ("ln(Keq) = X + Y/T(K)") for the regressed-kb case and keq_source
          for it — this surfaces the expression instead of only the single
          K_eq-at-T number. keq_literature mode (Song 1998) doesn't have a
          fitted keq_expression (Keq there isn't derived from a fitted
          kb — it IS the literature relationship), so that case falls back
          to the literature ln(Keq) line already shown as static reference
          text elsewhere on this tab (Rate Equation Preview), reproduced
          here as the expression so this table stays consistent for every
          mode rather than only for kb_free/lhhw.
          18-Sep-2026 EDIT (Sept-17 call, item #14): dropped the single-
          value "K_eq @ T (info)" column — same reasoning as the Custom/AA
          table above (redundant once the expression is shown). Source
          text simplified to exactly "Calculated from kf/kb" or
          "User-specified" — no equation-name mention. */}
      {(() => {
        const keqFit = result.fit_results?.[0]?.Keq_fit ?? result.aspen_ready?.Keq_fit;
        if (keqFit == null && keqMode !== 'user_specified') return null;
        const fittedExpr = result.fit_results?.[0]?.keq_expression ?? result.aspen_ready?.keq_expression;
        const fittedSource = result.fit_results?.[0]?.keq_source ?? result.aspen_ready?.keq_source;
        // fittedExpr (when present) is "<kf expr> | ln(Keq) = X + Y/T(K)" —
        // this table is Keq-only, so split off just the ln(Keq) half.
        const userKeq = keqUserValues?.[0] || {};
        const userX = userKeq.X !== '' && userKeq.X != null ? Number(userKeq.X) : null;
        const userY = userKeq.Y !== '' && userKeq.Y != null ? Number(userKeq.Y) : null;
        const userExpr = Number.isFinite(userX) && Number.isFinite(userY)
          ? `ln(Keq) = ${fmt(userX, 6)} + ${fmt(userY, 6)}/T(K)`
          : null;
        const keqExpr = fittedExpr
          ? (fittedExpr.includes('ln(Keq)') ? `ln(Keq) ${fittedExpr.split('ln(Keq)')[1]}` : fittedExpr)
          : (keqMode === 'user_specified' && userExpr
              ? userExpr
              : (reactionSystem === 'dme'
                  ? 'ln(Keq) = 0.7633 + 1239.76/T(K)   [DME - Song 1998]'
                  : 'ln(Keq) = 0.83983 + 782.98/T(K)   [MeOAc - Song 1998]'));
        return (
          <SectionBox title="Equilibrium Constant">
            <MultiHeaderTable
              headerRows={[[{ label: 'Reaction' }, { label: 'Equilibrium Expression' }, { label: 'Source' }]]}
              bodyRows={[[
                '1',
                keqExpr,
                fittedSource || (keqMode === 'user_specified' ? 'User-specified' : 'Calculated from kf/kb'),
              ]]}
            />
          </SectionBox>
        );
      })()}


      <TwoCol ratio="1fr 1fr" gap={12}
        left={
          <>
            {/* Client feedback: "Remove light labels MeOAc, reverse, etc.
                Reactant 1, Product 1 etc should be component names that
                adapt for each system. What do Ab Aspen, Ea_b mean? Those
                labels are not required." Single-reaction system, so the
                headers can directly be the real component names. */}
            {(() => {
              const reactants = splitSpeciesTokens(rxn1Reactants);
              const products  = splitSpeciesTokens(rxn1Products);
              // Action item #8 (call tracker, 02-Sep): "Forward Reaction" /
              // "Backward Reaction" exponent-group headers are already
              // correct for CUSTOM systems (see the isMultiRxn table above,
              // headerRows 'Forward Exponents' / 'Backward Exponents') but
              // were missing here for the Example systems (MeOAc/DME) —
              // this table used a single flat header row with no forward/
              // backward grouping at all. Extended the same grouping
              // convention to this table; irreversible reactions (DME
              // forward-only, or MeOAc run in forward-only mode) drop the
              // Backward group entirely, matching the Rate Constant table's
              // own isReversibleSys-gated header just above.
              const fwdCols = [
                { name: reactants[0] ? nameOf(reactants[0]) : null, value: a_acoh },
                { name: reactants[1] ? nameOf(reactants[1]) : null, value: a_meoh },
              ].filter(c => c.name);
              const bwdCols = [
                { name: products[0] ? nameOf(products[0]) : null, value: b_meoac },
                { name: products[1] ? nameOf(products[1]) : null, value: b_h2o },
              ].filter(c => c.name);
              const showBwd = isReversibleSys && bwdCols.length > 0;
              const cols = showBwd ? [...fwdCols, ...bwdCols] : fwdCols;
              const headerRows = showBwd ? [
                [{ label: 'Reaction', rowSpan: 2 }, { label: 'Forward Reaction', colSpan: fwdCols.length }, { label: 'Backward Reaction', colSpan: bwdCols.length }],
                [...fwdCols.map(c => ({ label: c.name })), ...bwdCols.map(c => ({ label: c.name }))],
              ] : [
                [{ label: 'Reaction', rowSpan: 2 }, { label: 'Forward Reaction', colSpan: fwdCols.length }],
                [...fwdCols.map(c => ({ label: c.name }))],
              ];
              return (
                <SectionBox title="Reaction-Order Exponents">
                  <MultiHeaderTable
                    headerRows={headerRows}
                    bodyRows={[['1', ...cols.map(c => c.value != null && c.value !== '—' ? fmt(typeof c.value === 'number' ? c.value : parseFloat(c.value), 4) : '—')]]}
                  />
                </SectionBox>
              );
            })()}

            {/* ── Fitting Mode Summary + Rate Equation ─────────────────── */}
            {fitMode && (() => {
              const isDme = reactionSystem === 'dme';

              // ── Per-mode metadata ────────────────────────────────────────
              const modeMap = {
                keq_literature: {
                  label: 'Keq Literature',
                  tag: 'Case 0',
                  desc: 'Fitted: Af, Ea, a_acoh, a_meoh',
                  color: cssVar('--color-mode-keq'),
                  detail: 'Uses equilibrium constant from Song 1998 literature. Forward rate parameters and concentration exponents are regressed; reverse rate is derived from Keq.',
                },
                optimize_kb: {
                  label: 'Optimize kb',
                  tag: 'Case 1',
                  desc: 'Fitted: Af, Ea, Ab, Ea_b, all exponents',
                  color: cssVar('--color-mode-kb'),
                  detail: 'Both forward and backward pre-exponential factors and activation energies are independently optimized along with all reaction-order exponents.',
                },
                unit_exponents: {
                  label: 'Unit Exponents',
                  tag: 'Case 2',
                  desc: 'Fixed exponents = 1, fit Af / Ab / Ea',
                  color: cssVar('--color-mode-unit'),
                  detail: 'All concentration exponents are fixed at 1 (elementary-step assumption). Only the Arrhenius pre-exponential factors and activation energies are fitted.',
                },
                lhhw: {
                  label: 'LHHW Model',
                  tag: 'Case 3',
                  desc: 'LHHW denominator + Song 1998 K values',
                  color: cssVar('--color-mode-lhhw'),
                  detail: 'Langmuir–Hinshelwood–Hougen–Watson rate form with adsorption equilibrium constants from Song 1998. Surface-reaction limited kinetics.',
                },
              };

              // ── Rate equations (LaTeX) — indexed by [fitMode][system] ───
              // MeOAc system:  HOAc + MeOH ⇌ MeOAc + H₂O
              // DME   system:  2 MeOH ⇌ DME + H₂O
              const rateEquations = {
                keq_literature: {
                  // Case 0 — reverse derived from Keq_lit
                  meoac: {
                    main:   String.raw`r = k_f \, C_{\mathrm{HOAc}}^{a} \, C_{\mathrm{MeOH}}^{b} - \dfrac{k_f}{K_{\mathrm{eq}}^{\mathrm{lit}}} \, C_{\mathrm{MeOAc}} \, C_{\mathrm{H_2O}}`,
                    kf:     String.raw`k_f = A_f \exp\!\left(-\dfrac{E_{a,f}}{RT}\right)`,
                    keq:    String.raw`K_{\mathrm{eq}}^{\mathrm{lit}} = X \cdot \exp\!\left(\dfrac{Y}{T}\right) \quad \text{(Song 1998)}`,
                    legend: [
                      { sym: 'a,\\,b',              def: 'fitted forward exponents (HOAc, MeOH)' },
                      { sym: 'A_f,\\,E_{a,f}',      def: 'fitted pre-exponential & activation energy' },
                      { sym: 'K_{\\mathrm{eq}}^{\\mathrm{lit}}', def: 'literature equilibrium constant (Song 1998)' },
                    ],
                  },
                  dme: {
                    main:   String.raw`r = k_f \, C_{\mathrm{MeOH}}^{a} - \dfrac{k_f}{K_{\mathrm{eq}}^{\mathrm{lit}}} \, C_{\mathrm{DME}} \, C_{\mathrm{H_2O}}`,
                    kf:     String.raw`k_f = A_f \exp\!\left(-\dfrac{E_{a,f}}{RT}\right)`,
                    keq:    String.raw`K_{\mathrm{eq}}^{\mathrm{lit}} = X \cdot \exp\!\left(\dfrac{Y}{T}\right) \quad \text{(Song 1998)}`,
                    legend: [
                      { sym: 'a',                   def: 'fitted forward exponent (MeOH)' },
                      { sym: 'A_f,\\,E_{a,f}',      def: 'fitted pre-exponential & activation energy' },
                      { sym: 'K_{\\mathrm{eq}}^{\\mathrm{lit}}', def: 'literature equilibrium constant (Song 1998)' },
                    ],
                  },
                },

                optimize_kb: {
                  // Case 1 — independent forward & backward Arrhenius
                  meoac: {
                    main:   String.raw`r = k_f \, C_{\mathrm{HOAc}}^{a} \, C_{\mathrm{MeOH}}^{b} - k_b \, C_{\mathrm{MeOAc}}^{c} \, C_{\mathrm{H_2O}}^{d}`,
                    kf:     String.raw`k_f = A_f \exp\!\left(-\dfrac{E_{a,f}}{RT}\right), \quad k_b = A_b \exp\!\left(-\dfrac{E_{a,b}}{RT}\right)`,
                    legend: [
                      { sym: 'a,\\,b,\\,c,\\,d',        def: 'all fitted concentration exponents' },
                      { sym: 'A_f,\\,E_{a,f}',          def: 'forward pre-exponential & activation energy' },
                      { sym: 'A_b,\\,E_{a,b}',          def: 'backward pre-exponential & activation energy' },
                    ],
                  },
                  dme: {
                    main:   String.raw`r = k_f \, C_{\mathrm{MeOH}}^{a} - k_b \, C_{\mathrm{DME}}^{c} \, C_{\mathrm{H_2O}}^{d}`,
                    kf:     String.raw`k_f = A_f \exp\!\left(-\dfrac{E_{a,f}}{RT}\right), \quad k_b = A_b \exp\!\left(-\dfrac{E_{a,b}}{RT}\right)`,
                    legend: [
                      { sym: 'a,\\,c,\\,d',             def: 'all fitted concentration exponents' },
                      { sym: 'A_f,\\,E_{a,f}',          def: 'forward pre-exponential & activation energy' },
                      { sym: 'A_b,\\,E_{a,b}',          def: 'backward pre-exponential & activation energy' },
                    ],
                  },
                },

                unit_exponents: {
                  // Case 2 — all exponents = 1, elementary-step form
                  meoac: {
                    main:   String.raw`r = k_f \, C_{\mathrm{HOAc}} \, C_{\mathrm{MeOH}} - k_b \, C_{\mathrm{MeOAc}} \, C_{\mathrm{H_2O}}`,
                    kf:     String.raw`k_f = A_f \exp\!\left(-\dfrac{E_{a,f}}{RT}\right), \quad k_b = A_b \exp\!\left(-\dfrac{E_{a,b}}{RT}\right)`,
                    note:   'All concentration exponents are fixed at 1 (elementary-step / unit-order assumption).',
                    legend: [
                      { sym: 'A_f,\\,E_{a,f}',  def: 'fitted forward Arrhenius parameters' },
                      { sym: 'A_b,\\,E_{a,b}',  def: 'fitted backward Arrhenius parameters' },
                    ],
                  },
                  dme: {
                    main:   String.raw`r = k_f \, C_{\mathrm{MeOH}}^{2} - k_b \, C_{\mathrm{DME}} \, C_{\mathrm{H_2O}}`,
                    kf:     String.raw`k_f = A_f \exp\!\left(-\dfrac{E_{a,f}}{RT}\right), \quad k_b = A_b \exp\!\left(-\dfrac{E_{a,b}}{RT}\right)`,
                    note:   'DME stoichiometry: exponent on MeOH = 2 (stoichiometric), all others fixed at 1.',
                    legend: [
                      { sym: 'A_f,\\,E_{a,f}',  def: 'fitted forward Arrhenius parameters' },
                      { sym: 'A_b,\\,E_{a,b}',  def: 'fitted backward Arrhenius parameters' },
                    ],
                  },
                },

                lhhw: {
                  // Case 3 — LHHW with Song 1998 adsorption constants
                  meoac: {
                    main:   String.raw`r = \dfrac{k_f \, C_{\mathrm{HOAc}} \, C_{\mathrm{MeOH}} - k_b \, C_{\mathrm{MeOAc}} \, C_{\mathrm{H_2O}}}{\left(1 + K_{\mathrm{HOAc}}\,C_{\mathrm{HOAc}} + K_{\mathrm{MeOH}}\,C_{\mathrm{MeOH}} + K_{\mathrm{MeOAc}}\,C_{\mathrm{MeOAc}} + K_{\mathrm{H_2O}}\,C_{\mathrm{H_2O}}\right)^{2}}`,
                    kf:     String.raw`k_f = A_f \exp\!\left(-\dfrac{E_{a,f}}{RT}\right), \quad k_b = \dfrac{k_f}{K_{\mathrm{eq}}^{\mathrm{lit}}}`,
                    keq:    String.raw`K_i = K_i^0 \exp\!\left(\dfrac{-\Delta H_{\mathrm{ads},i}}{RT}\right) \quad \text{(Song 1998 adsorption constants)}`,
                    note:   'Denominator squared reflects dual-site Langmuir–Hinshelwood mechanism. Adsorption equilibrium constants Kᵢ taken from Song 1998.',
                    legend: [
                      { sym: 'A_f,\\,E_{a,f}',          def: 'fitted forward Arrhenius parameters' },
                      { sym: 'K_{\\mathrm{eq}}^{\\mathrm{lit}}', def: 'equilibrium constant from Song 1998' },
                      { sym: 'K_i',                      def: 'adsorption equilibrium constants (Song 1998)' },
                    ],
                  },
                  dme: {
                    main:   String.raw`r = \dfrac{k_f \, C_{\mathrm{MeOH}}^{2} - k_b \, C_{\mathrm{DME}} \, C_{\mathrm{H_2O}}}{\left(1 + K_{\mathrm{MeOH}}\,C_{\mathrm{MeOH}} + K_{\mathrm{DME}}\,C_{\mathrm{DME}} + K_{\mathrm{H_2O}}\,C_{\mathrm{H_2O}}\right)^{2}}`,
                    kf:     String.raw`k_f = A_f \exp\!\left(-\dfrac{E_{a,f}}{RT}\right), \quad k_b = \dfrac{k_f}{K_{\mathrm{eq}}^{\mathrm{lit}}}`,
                    keq:    String.raw`K_i = K_i^0 \exp\!\left(\dfrac{-\Delta H_{\mathrm{ads},i}}{RT}\right) \quad \text{(Song 1998 adsorption constants)}`,
                    note:   'Denominator squared reflects dual-site Langmuir–Hinshelwood mechanism. Adsorption equilibrium constants Kᵢ taken from Song 1998.',
                    legend: [
                      { sym: 'A_f,\\,E_{a,f}',          def: 'fitted forward Arrhenius parameters' },
                      { sym: 'K_{\\mathrm{eq}}^{\\mathrm{lit}}', def: 'equilibrium constant from Song 1998' },
                      { sym: 'K_i',                      def: 'adsorption equilibrium constants (Song 1998)' },
                    ],
                  },
                },
              };

              const m   = modeMap[fitMode];
              const eq  = rateEquations[fitMode]?.[isDme ? 'dme' : 'meoac'];
              if (!m) return null;

              // Shared sub-section label style
              const eqLabel = (txt) => (
                <div style={{ fontSize: 13, fontWeight: 700, color: m.color, textTransform: 'uppercase',
                  letterSpacing: '0.1em', fontFamily: font.ui, marginBottom: 4, marginTop: 8 }}>{txt}</div>
              );

              // KaTeX block wrapper — dark-themed code bg
              const EqBlock = ({ latex }) => (
                <div style={{ background: C.codeBg, border: `1px solid ${m.color}33`,
                  borderRadius: 5, padding: '10px 12px', overflowX: 'auto',
                  display: 'flex', justifyContent: 'safe center' }}>
                  <BlockMath math={latex} />
                </div>
              );

              return (
                // Client feedback: "All the text in the Fitting Mode, MeOAc,
                // Case 0, etc should go away. Title should be Expressions used"
                // with: (a) Expression for reaction rate, (b) Arrhenius
                // expression, (c) Equilibrium constant as a function of
                // Temperature. The mode label, DME/MeOAc badge, Case-N badge,
                // short descriptor, and prose description are all removed —
                // only the equations themselves remain.
                <SectionBox title="Expressions used">
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: '10px 12px' }}>
                    {eq && (
                      <>
                        {eqLabel('Expression for reaction rate')}
                        <EqBlock latex={eq.main} />

                        {eq.kf && (
                          <>
                            {eqLabel('Arrhenius expression')}
                            <EqBlock latex={eq.kf} />
                          </>
                        )}

                        {eq.keq && (
                          <>
                            {eqLabel('Equilibrium constant as a function of Temperature')}
                            <EqBlock latex={eq.keq} />
                          </>
                        )}
                      </>
                    )}
                  </div>
                </SectionBox>
              );
            })()}

            {/* Shankar, 31-Aug: this left-column "Reaction Rate Constants"
                card duplicated the right-column "Rate Constants" card added
                for item #18 — same kf/kb expressions shown twice on the
                same screen. Removed; its one non-duplicated bit (Aspen
                Rate Basis / Units) moved onto the surviving right-column
                card below so nothing is lost. */}

            {isGlobal && (
              <SectionBox title="Global Fit Summary">
                <div style={{ fontSize: 12, fontFamily: font.ui, lineHeight: 2 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: C.textMuted }}>Runs fitted</span>
                    <span style={{ color: C.text }}>{result.global_metrics.n_runs}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: C.textMuted }}>Temperatures</span>
                    <span style={{ color: C.text }}>{(result.global_metrics.temperatures_C || []).map(t => `${t}°C`).join(', ')}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: C.textMuted }}>Global loss</span>
                    <span style={{ color: C.text }}>{result.global_metrics.global_loss?.toExponential(4)}</span>
                  </div>
                </div>
              </SectionBox>

            )}
          </>
        }
        right={
          <>
            {/* Client feedback: "Only parameters should be in Parameters.
                Parity data vs model table can be shown in Appendix." The
                Predicted-Timeseries and Exp-vs-Predicted tables that used to
                live here have moved to the Appendix tab (see PanelAppendix).
                This branch is MeOAc/DME only (reactionSystem is never
                'acetic_acid' here — AA has its own return above). */}
            {/* CLIENT FEEDBACK (Madhura, 26-Aug doc): "Engine-side scaling
                info isn't useful to Madhura." The ODE Scale Info card
                (n_mix / W_cat / W/n scale factor — internal solver
                bookkeeping) is removed and this panel space now shows the
                kf/kb rate expression instead. Per her low-priority nit,
                shown as "kf = ..." rather than the bare expression. This
                is now the ONLY kf/kb card on the page — the old duplicate
                left-column "Reaction Rate Constants" card was removed
                (Shankar, 31-Aug) once this one made it redundant. */}
            {(asp.kf_expression || asp.kb_expression) && (
              <SectionBox title="Rate Constants">
                <div style={{ fontSize: 12, fontFamily: font.ui, lineHeight: 1.8, color: C.textMid }}>
                  {asp.kf_expression && (
                    <div style={{ marginBottom: 8 }}>
                      {/* ACTION ITEM #5 (10-Sep-2026 call): the shared <Label>
                          component applies textTransform:'uppercase' via CSS —
                          right for ordinary field labels, but it silently
                          rendered these two as "KF"/"KB", contradicting the
                          lowercase-k-for-rate-constant / capital-K-for-
                          equilibrium-constant convention Madhura called out on
                          screen. Swapped for a plain span that mirrors Label's
                          visual style (size/weight/color/spacing) minus the
                          uppercase transform, so "kf"/"kb" render as typed. */}
                      <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.01em',
                        color: C.textMuted, marginBottom: 4, fontFamily: font.ui }}>kf</div>
                      <CodeBlock>{`kf = ${asp.kf_expression}`}</CodeBlock>
                    </div>
                  )}
                  {asp.kb_expression && (
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.01em',
                        color: C.textMuted, marginBottom: 4, fontFamily: font.ui }}>kb</div>
                      <CodeBlock>{`kb = ${asp.kb_expression}`}</CodeBlock>
                    </div>
                  )}
                  {asp.rate_basis_aspen_label && (
                    <div style={{ marginTop: 8, fontSize: 12, color: C.textDim, fontFamily: font.ui }}>
                      Aspen Rate Basis: <strong style={{ color: C.textMid }}>{asp.rate_basis_aspen_label}</strong>
                      {asp.rate_units_aspen && <> · Units: <strong style={{ color: C.textMid }}>{asp.rate_units_aspen}</strong></>}
                    </div>
                  )}
                </div>
              </SectionBox>
            )}
          </>
        }
      />
      </div>
    </PanelShell>
  );
};


// ═══════════════════════════════════════════════════════════════════════════
// SAVE THIS FIT — lives at the top of the Optimize tab (Madhura, 20-Aug:
// "Move Save this fit to the top of Optimize").
//
// The actual Compare Fits UI (selection list, Compare Selected, the
// graphical overlay) has moved OUT of this tab into its own sidebar page —
// see CompareFitsPage.jsx / the "Compare Fits" nav item in Sidebar.js.
// Comparing is a browse-your-history action, not a step in a single
// fitting run, and it doesn't need `result` from a run in progress the
// way saving does — splitting it out also means it isn't lost/reset by
// anything that resets this page's run state.
//
// Reads/writes fitRecordStore.js via useFitRecords() only for the saved
// count badge and inline save errors — same store the new page reads.
// ═══════════════════════════════════════════════════════════════════════════
const CompareFitsPanel = ({ result, buildSaveFitPayload }) => {
  const { fits, error } = useFitRecords();
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!result || !buildSaveFitPayload) return;
    setSaving(true);
    try {
      const payload = await buildSaveFitPayload();
      if (label.trim()) payload.label = label.trim();
      await FitRecords.saveFitRecord(payload);
      setLabel('');
    } catch (e) {
      // Save failures are shown inline via the store's `error` field below.
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionBox title="Save This Fit" badge={`${fits.length} saved`} badgeColor={C.textMuted}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <Input placeholder={result ? 'Label (optional) — e.g. "LHHW, regressed exponents"' : 'Run a fit first'}
          value={label} disabled={!result || saving}
          onChange={(e) => setLabel(e.target.value)} style={{ flex: 1, fontSize: 12 }} />
        <Btn variant="secondary" disabled={!result || saving} onClick={handleSave} style={{ fontSize: 12, padding: '6px 12px' }}>
          {saving ? 'Saving…' : '💾 Save this fit'}
        </Btn>
      </div>

      {error && <Alert type="warning" style={{ marginBottom: 8 }}>{error}</Alert>}

      <div style={{ fontSize: 12, color: C.textDim, fontFamily: font.ui, display: 'flex',
        alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span>
          {fits.length === 0
            ? 'No saved fits yet for this reaction system. Save this one, then run again with different settings to build up something to compare.'
            : `${fits.length} saved fit${fits.length === 1 ? '' : 's'} for this reaction system.`}
        </span>
        <a href="/compare-fits" style={{ color: C.accent, fontFamily: font.ui, fontSize: 12,
          textDecoration: 'none', flexShrink: 0, fontWeight: 600 }}>
          Compare Fits →
        </a>
      </div>
    </SectionBox>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
// PANEL 4 — OPTIMIZATION / IMPROVE FIT
// ═══════════════════════════════════════════════════════════════════════════
const PanelOptimize = ({
  result, reactionSystem, fitMode, setFitMode,
  exponentMode, setExponentMode, customParamCounts,
  speciesWeights, setSpeciesWeights, activeSpecies,
  useInitialGuess, setUseInitialGuess, initialGuesses, setInitialGuesses,
  maxIterations, setMaxIterations,
  r2Target, setR2Target,
  warmStart, setWarmStart,
  selectedDataSets, setSelectedDataSets,
  globalRuns, fitScope, // fitScope: gates "Data Sets for Regression" — see below
  onSubmit, loading, onStop, onReinitialize, fittingProgress,
  buildSaveFitPayload, // Compare Fits — assembles the POST /fit-records
                        // body from current result + form state; owned by
                        // the parent (DataFittingPage) since it needs
                        // fields (dataset_selection, optimizer_config, etc.)
                        // this panel doesn't otherwise track.
  registeredComponents = [], // BUG FIX (22-Sep-2026, Madhura call): Fit
                              // Diagnostics was showing raw formulae
                              // (C2H4, C2H4O2...) instead of chemical
                              // names — this panel never received
                              // registeredComponents at all before, unlike
                              // PanelGraph's identical R² list.
}) => {
  const icon = <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2"/><circle cx="7" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.2"/><path d="M7 1v2M7 11v2M1 7h2M11 7h2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>;

  if (!result) return (
    <PanelShell title="Optimization / Improve Fit" subtitle="Diagnose and tune parameters" icon={icon}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '70%',
        flexDirection: 'column', gap: 12, color: C.textDim }}>
        <span style={{ fontSize: 13, fontFamily: font.ui }}>Complete an initial fitting run first</span>
      </div>
    </PanelShell>
  );

  // BUG FIX (Fit Diagnostics panel, 22-Sep-2026): this previously read only
  // result.metrics?.r2_summary — the key MeOAc/DME use. AA returns its R²
  // under metrics.r2_per_species and custom/generic systems under
  // metrics.per_species_r2 (see openmkm_api.py's run_aa_fitting /
  // run_generic_fitting), so for those two systems r2Rows was always empty
  // — the "Fit Diagnostics" SectionBox rendered its header/legend with
  // nothing underneath, and `worst` stayed undefined. Same three-key merge
  // the Graph tab's "Fit Quality — R²" box already uses (see the
  // 30-Jul-2026 BUG FIX comment on r2Summary in PanelGraph) — kept as a
  // single object spread here since, unlike the Graph tab, this panel has
  // no other reason to read result.metrics beyond this one map.
  const optimizeDatasetR2Rows = (() => {
    const perRun = result?.global_metrics?.per_run_results || [];
    const rows = perRun.map((run, i) => ({
      label: `Data Set ${run?.run_index != null ? Number(run.run_index) + 1 : i + 1}`,
      r2: Number(run?.overall_r2 ?? run?.final_r2),
    })).filter(row => Number.isFinite(row.r2));
    if (rows.length) return rows;
    const dsRows = Array.isArray(result?.global_metrics?.r2_summary?.per_dataset)
      ? result.global_metrics.r2_summary.per_dataset : [];
    return dsRows.map((ds, i) => ({
      label: `Data Set ${i + 1}`,
      r2: Number(ds?.overall_r2 ?? ds?.final_r2),
    })).filter(row => Number.isFinite(row.r2));
  })();
  const isOptimizeMultiDataset = optimizeDatasetR2Rows.length > 1 || Number(result?.global_metrics?.n_runs) > 1;

  const r2SummaryMerged = {
    ...(result.metrics?.r2_summary     || {}),
    ...(result.metrics?.r2_per_species || {}),
    ...(result.metrics?.per_species_r2 || {}),
  };

  // BUG FIX (24-Sep-2026 — "Empty Fit Diagnostics — Optimize Page" for the
  // multi-dataset scenario): the three multi-dataset endpoints
  // (run_meoac_global_fitting, run_dme_global_fitting,
  // run_aa_multi_csv_global_fitting) return NO top-level result.metrics at
  // all — every R² lives under result.global_metrics instead, in two
  // different per-run/per-CSV shapes, which is why r2SummaryMerged above is
  // always empty for these. Reuses exactly the same two data sources the
  // Graph tab's own per-run/per-CSV breakdown already reads correctly
  // (globalPerRun / multiCsvR2 there) rather than reading anything new.
  //
  // AA multi-CSV already reports one pooled "Combined R² — All CSVs" per
  // species (global_metrics.r2_summary.combined) — the natural single
  // number for this panel, used as-is.
  //
  // MeOAc/DME global has no such pooled number (global_metrics.
  // per_run_results is only ever per-run, one 40°C/45°C/50°C-style entry
  // per temperature) — this takes the WORST (minimum) R² any run reported
  // for that species, since that's the number that should actually decide
  // whether to go tune that species' weight, matching this panel's purpose
  // (contrast with the Graph tab's per-run breakdown, which deliberately
  // keeps every run visible rather than collapsing them).
  const multiCsvCombined = result.global_metrics?.r2_summary;
  if (multiCsvCombined && Array.isArray(multiCsvCombined.combined)) {
    multiCsvCombined.combined.slice(1).forEach(([sp, v]) => {
      r2SummaryMerged[sp] = typeof v === 'number' ? v : parseFloat(v) || 0;
    });
  }
  (result.global_metrics?.per_run_results || []).forEach(run => {
    Object.entries(run.r2_per_species || {}).forEach(([sp, v]) => {
      const val = typeof v === 'number' ? v : parseFloat(v) || 0;
      if (!(sp in r2SummaryMerged) || val < r2SummaryMerged[sp]) r2SummaryMerged[sp] = val;
    });
  });

  const r2Rows = isOptimizeMultiDataset
    ? []
    : Object.entries(r2SummaryMerged)
      .map(([sp, v]) => ({ sp, r2: typeof v === 'number' ? v : parseFloat(v) || 0 }))
      .sort((a, b) => a.r2 - b.r2);

  const worst = r2Rows[0];

  // NOTE: the old mode-color map and the "Change Fitting Mode" card that
  // used it were removed from this panel — see the Species Weights /
  // Exponent Treatment de-duplication note further down.

  return (
    <PanelShell title="Optimization / Improve Fit"
      subtitle="Diagnose fit quality · tune weights · tune initial parameter guesses · re-run"
      icon={icon}
      actions={
        // Client requirement (Run Controls gap): Stop / Rerun Fitting /
        // Reinitialize must remain visible and functional from the Optimise
        // tab itself, not just Input — so a user tuning weights/settings
        // here doesn't have to switch tabs to cancel, retry, or reset inputs.
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Btn variant="secondary" disabled={loading} onClick={onReinitialize} style={{ fontSize: 13 }}
            title="Reset model/optimization inputs to defaults — keeps your uploaded data">
            ⟲ Reinitialize
          </Btn>
          <Btn variant="secondary" disabled={!loading} onClick={onStop} style={{ fontSize: 13,
            ...(loading ? { borderColor: C.error, color: C.error } : {}) }}
            title="Cancel the fit currently running">
            ■ Stop
          </Btn>
          <Btn variant="run" onClick={onSubmit} disabled={loading} style={{ fontSize: 13 }}>
            {loading ? `⟳  ${fittingProgress || 'Re-fitting…'}` : '↻  Re-run Fitting'}
          </Btn>
        </div>
      }
    >
      <TwoCol ratio="0.85fr 1.15fr" gap={12}
        left={
          <>
            {/* Action item #12 (call tracker, 02-Sep) / Sep-2 call transcript:
                Madhura/Suresh asked for an info-icon legend next to "Fit
                Diagnostics" spelling out the Poor/Good/Excellent R² ranges.
                Thresholds below (<80 Poor, 80–95 Good, >95 Excellent) are
                exactly what's already driving the POOR/GOOD/EXC badge
                colors a few lines down (r2 > 0.95 / > 0.8) — this legend
                just makes those cutoffs visible instead of only implicit
                in color. These are the numbers Shankar and Madhura landed
                on live on the call, but the tracker still flags them as
                NOT YET FINAL — confirm with Sagar before treating this
                copy as permanent. */}
            <SectionBox title="Fit Diagnostics"
              info="R² quality: below 0.80 is Poor, 0.80–0.95 is Good, above 0.95 is Excellent.">
              {isOptimizeMultiDataset
                ? optimizeDatasetR2Rows.map((row, i) => {
                    const r2 = row.r2;
                    const col = r2 > 0.95 ? C.success : r2 > 0.8 ? cssVar('--color-warning-dark') : C.error;
                    const pct = Math.max(0, Math.min(100, r2 * 100));
                    return (
                      <div key={`${row.label}-${i}`} style={{ marginBottom: 11 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3,
                          fontSize: 12, fontFamily: font.ui }}>
                          <span style={{ color: C.text }}>{row.label}</span>
                          <span style={{ color: col, fontWeight: 700 }}>R² {r2.toFixed(4)}</span>
                        </div>
                        <div style={{ background: C.border, borderRadius: 3, height: 5, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: col, borderRadius: 3 }} />
                        </div>
                      </div>
                    );
                  })
                : r2Rows.map(({ sp, r2 }) => {
                    const col = r2 > 0.95 ? C.success : r2 > 0.8 ? cssVar('--color-warning-dark') : C.error;
                    const pct = Math.max(0, Math.min(100, r2 * 100));
                    return (
                      <div key={sp} style={{ marginBottom: 11 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3,
                          fontSize: 12, fontFamily: font.ui }}>
                          <span style={{ color: C.text }}>{speciesDisplayName(sp, registeredComponents)}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ color: col, fontWeight: 700 }}>R² {r2.toFixed(4)}</span>
                            <Badge color={col}>{r2 > 0.95 ? 'EXC' : r2 > 0.8 ? 'GOOD' : 'POOR'}</Badge>
                          </div>
                        </div>
                        <div style={{ background: C.border, borderRadius: 3, height: 5, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: col,
                            borderRadius: 3, transition: 'width 0.6s ease', boxShadow: `0 0 6px ${col}88` }} />
                        </div>
                      </div>
                    );
                  })}
            </SectionBox>
            
            {/* ACTION ITEM #8 (10-Sep-2026 call): Madhura — the generic
                "Suggestions for Fit Improvement" reference below is fine,
                but this run-specific "Optimizer Suggestion" is too
                system-dependent to generalize reliably right now; hide it
                and revisit once more case studies establish clearer
                patterns. Per the call: keep the code (don't delete) for
                future reinstatement — gated behind SHOW_OPTIMIZER_SUGGESTION
                below rather than removed. */}
            {SHOW_OPTIMIZER_SUGGESTION && worst && (
              // CLIENT FEEDBACK (Madhura, 26-Aug doc): "There is a lot of
              // jargon here from a coding perspective." "optimize_kb" is a
              // stale internal mode name (removed from the UI entirely —
              // see the Change Fitting Mode removal above) and meant
              // nothing to her. Reworded to the same Model Input
              // terminology ("Species Weights" / "Regress Exponents")
              // used everywhere else in this tab.
              <SectionBox title="Optimizer Suggestion">
                <Alert type="warning">
                  Worst: <strong>{speciesDisplayName(worst.sp, registeredComponents)}</strong> (R²={worst.r2.toFixed(3)})<br/>
                  {reactionSystem === 'acetic_acid'
                    ? <>Try increasing w({speciesDisplayName(worst.sp, registeredComponents)}), raising Max Iterations to 300+, and switching to <strong>Regress Exponents</strong> mode with warm start.</>
                    : <>Try tuning Species Weights (increase w({speciesDisplayName(worst.sp, registeredComponents)})) or switching Exponent Treatment to <strong>Regress Exponents</strong> in Model Input.</>
                  }
                </Alert>
              </SectionBox>
            )}

            {/* ── Suggestions for Fit Improvement (client doc, Sept-2026 —
                "Fit Diagnostics and Suggestions are missing!"): a general,
                always-available reference of every knob a user can turn to
                improve a fit, split the same way the app itself is split —
                Model Input vs. Fit Optimization — so this doubles as a quick
                index of where each option lives. This is deliberately static
                (unlike the run-specific "Optimizer Suggestion" above, which
                only appears once a result exists and calls out the worst
                species) so it's useful before a first run too. ───────────── */}
            <SectionBox title="Suggestions for Fit Improvement">
              <div style={{ fontSize: 12.5, color: C.textMid, fontFamily: font.ui, marginBottom: 8, lineHeight: 1.5 }}>
                To improve the fit, the following parameters are available to the user:
              </div>
              <DataTable rows={[
                ['Model Input', 'Fit Optimization'],
                ['Reaction Type — Reversible/Irreversible', 'R² Target'],
                ['Rate Law — Power Law/LHHW', 'Iterations'],
                ['Fixed or Regressed Exponents', 'Initial parameter values'],
                ['Species Weights', ''],
              ]} compact />
            </SectionBox>


          </>
        }
        right={
          <>
            {/* Save this fit / Compare Fits — moved to the top of the
                Optimize tab per request, so it's visible without scrolling
                past every settings panel first. */}
            <CompareFitsPanel result={result} buildSaveFitPayload={buildSaveFitPayload} />

            {/* Optimizer Settings — maps to PDF page 5: R², Iterations, Warm Start */}
            <SectionBox title="Optimizer Settings">
              <TwoCol ratio="1fr 1fr" gap={8}
                left={
                  <Field label="R² target" sub="Lever 1 — stop when R² ≥">
                    <Input type="number"
                      value={r2Target}
                      onChange={e => setR2Target(parseFloat(e.target.value) || 0.90)}
                      step="0.01" min="0.1" max="0.999" />
                  </Field>
                }
                right={
                  <Field label="Max Iterations" sub="Lever 2 — hard cap">
                    <Input type="number" value={maxIterations}
                      onChange={e => setMaxIterations(parseInt(e.target.value) || 100)}
                      step="10" min="10" max="2000" />
                  </Field>
                }
              />
            </SectionBox>

            {/* ── User-Defined Initial Parameters — PDF page 5 ───────────────── */}
            {/* Shankar, 8-Sep: moved from the left column to the right column
                (below Optimizer Settings) per Madhura's screenshot — this section
                used to sit at the bottom of the left column, forcing a scroll past
                Fit Diagnostics / Optimizer Suggestion / Suggestions for Fit
                Improvement to reach it. The right column had unused space below
                Optimizer Settings (Data Sets for Regression only renders for
                fitScope === 'multiple'), so this now fills that space instead. No
                logic changed — same startingPoint/useInitialGuess state and JSX,
                only its position in the two-column layout. */}
            {/* CLIENT FEEDBACK (Madhura, 26-Aug doc): "There is a lot of
                jargon here from a coding perspective, which is not needed.
                Remove 'Current BO starting point. Random LHD sampling
                across parameter bounds (default)'." Replaced the old
                transparency box + two independent checkboxes with the
                three plain-language, mutually-exclusive options from her
                doc. This is a relabel, not new logic — `startingPoint`
                below is just a derived view of the same warmStart /
                useInitialGuess booleans the rest of this component (and
                handleSubmit) already reads, so nothing downstream changes. */}
            <SectionBox title="User-Defined Initial Parameters">
              {(() => {
                const startingPoint = warmStart ? 'auto' : useInitialGuess ? 'manual' : 'random';
                const setStartingPoint = mode => {
                  setWarmStart(mode === 'auto');
                  setUseInitialGuess(mode === 'manual');
                };
                const options = [
                  { value: 'random', label: 'Random parameter values',
                    desc: 'Default — sampled across the parameter bounds' },
                  { value: 'manual', label: 'Manual initial values',
                    desc: 'Enter starting Af / Eaf / Ab / Eab below' },
                  { value: 'auto',   label: 'Auto-seed from current best-fit parameters',
                    desc: 'Start from the last run\u2019s best-fit values' },
                ];
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                    {options.map(opt => (
                      <label key={opt.value} onClick={() => setStartingPoint(opt.value)}
                        style={{ display: 'flex', alignItems: 'flex-start', gap: 8,
                          padding: '7px 10px', borderRadius: 5, cursor: 'pointer',
                          border: `1px solid ${startingPoint === opt.value ? C.accent : C.borderLight}`,
                          background: startingPoint === opt.value ? C.accentDim : C.shell }}>
                        <input type="radio" checked={startingPoint === opt.value}
                          onChange={() => setStartingPoint(opt.value)}
                          style={{ accentColor: C.accent, marginTop: 2 }} />
                        <span style={{ fontFamily: font.ui }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{opt.label}</span>
                          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 1 }}>{opt.desc}</div>
                        </span>
                      </label>
                    ))}
                  </div>
                );
              })()}

              {/* Auto-fill from last results — only relevant once "Manual
                  initial values" is selected. */}
              {useInitialGuess && result && (() => {
                const fr   = result.fit_results?.[0] || {};
                const asp  = result.aspen_ready || result.metrics?.aspen_params || fr.aspen_ready || {};
                const aspG = result.global_metrics?.aspen_params_ref_run0 || {};
                const Af_v  = fr.Af_aspen ?? aspG.Af_aspen ?? asp.Af_aspen;
                const Ea_v  = fr.Ea_f     ?? aspG.Ea_f_kJ  ?? asp.Ea_f;
                const Ab_v  = fr.Ab_internal ?? aspG.Ab_aspen ?? asp.Ab_aspen;
                const Eab_v = fr.Ea_b     ?? aspG.Ea_b_kJ  ?? asp.Ea_b;
                if (!Af_v && !Ea_v) return null;
                return (
                  <div style={{ marginBottom: 8 }}>
                    <Btn variant="ghost" style={{ fontSize: 12, padding: '3px 10px', color: C.accent }}
                      onClick={() => setInitialGuesses([{
                        A:   Af_v  != null ? String(parseFloat(Af_v.toFixed(4)))  : '',
                        Ea:  Ea_v  != null ? String(parseFloat(Ea_v.toFixed(2)))  : '',
                        Ab:  Ab_v  != null ? String(parseFloat(Ab_v.toFixed(4)))  : '',
                        Eab: Eab_v != null ? String(parseFloat(Eab_v.toFixed(2))) : '',
                      }])}>
                      ↓ Auto-fill from Previous Run
                    </Btn>
                  </div>
                );
              })()}

              {/* Per-reaction fields */}
              {useInitialGuess && (
                <>
                  {initialGuesses.map((g, i) => (
                    <div key={i} style={{ marginBottom: 10, border: `1px solid ${C.borderLight}`,
                      borderRadius: 5, padding: '8px 10px', background: C.shell }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 6,
                        textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: font.ui }}>
                        Reaction {i + 1}
                      </div>
                      {/* Powerlaw equation preview */}
                      <div style={{ fontFamily: font.ui, fontSize: 12, color: C.textMuted,
                        marginBottom: 8, background: C.card, borderRadius: 4, padding: '4px 8px' }}>
                        kf = Af · exp(−Eaf / RT)&nbsp;&nbsp;|&nbsp;&nbsp;kb = Ab · exp(−Eab / RT)
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                        <Field label="Af (kmol/kg·s)">
                          <Input type="number" value={g.A ?? ''}
                            placeholder="e.g. 5000"
                            onChange={e => setInitialGuesses(prev => {
                              const u = [...prev]; u[i] = { ...u[i], A: e.target.value }; return u;
                            })} step="any" />
                        </Field>
                        <Field label="Ab (kmol/kg·s)">
                          <Input type="number" value={g.Ab ?? ''}
                            placeholder="e.g. 8000"
                            onChange={e => setInitialGuesses(prev => {
                              const u = [...prev]; u[i] = { ...u[i], Ab: e.target.value }; return u;
                            })} step="any" />
                        </Field>
                        <Field label="Eaf (kJ/mol)">
                          <Input type="number" value={g.Ea ?? ''}
                            placeholder="e.g. 50"
                            onChange={e => setInitialGuesses(prev => {
                              const u = [...prev]; u[i] = { ...u[i], Ea: e.target.value }; return u;
                            })} step="1" />
                        </Field>
                        <Field label="Eab (kJ/mol)">
                          <Input type="number" value={g.Eab ?? ''}
                            placeholder="e.g. 63"
                            onChange={e => setInitialGuesses(prev => {
                              const u = [...prev]; u[i] = { ...u[i], Eab: e.target.value }; return u;
                            })} step="1" />
                        </Field>
                      </div>
                    </div>
                  ))}
                  <Btn variant="ghost" style={{ fontSize: 12, padding: '3px 8px' }}
                    onClick={() => setInitialGuesses(prev => [
                      ...prev, { A: '', Ea: '', Ab: '', Eab: '' }
                    ])}>
                    + Add Reaction
                  </Btn>
                </>
              )}
            </SectionBox>

            {/* CLIENT FEEDBACK (Madhura, 26-Aug doc): "The Data Set
                selection option should not appear if Single Data Set is
                chosen." Gated on the same `fitScope` state Model Input
                already uses to decide single- vs multi-dataset mode —
                nothing else about this card changes. */}
            {fitScope === 'multiple' && (
              <SectionBox title="Data Sets for Regression" badge="select subsets" badgeColor={C.textMuted}>
                <div style={{ fontSize: 12, color: C.textDim, fontFamily: font.ui, marginBottom: 8 }}>
                  Check which data sets to include in the re-fit.
                </div>
                {(globalRuns && globalRuns.length > 0
                  ? globalRuns
                  : [{ id: 'single', fileName: 'Current CSV', T_C: null }]
                ).map((run, idx) => {
                  const dsId = run.id ?? idx;
                  const allIds = (globalRuns && globalRuns.length > 0 ? globalRuns : [{ id: 'single' }]).map(r => r.id ?? 0);
                  const isSelected = selectedDataSets.length === 0 || selectedDataSets.includes(dsId);
                  return (
                    <label key={dsId} style={{ display: 'flex', alignItems: 'center', gap: 8,
                      padding: '6px 8px', borderRadius: 4, cursor: 'pointer',
                      background: isSelected ? C.accentDim : 'transparent',
                      border: `1px solid ${isSelected ? C.accent + '44' : C.borderLight}`,
                      marginBottom: 4, fontSize: 13, fontFamily: font.ui, color: C.text }}>
                      <input type="checkbox" checked={isSelected}
                        style={{ accentColor: C.accent }}
                        onChange={e => {
                          setSelectedDataSets(prev => {
                            const base = prev.length === 0 ? allIds : prev;
                            return e.target.checked
                              ? [...new Set([...base, dsId])]
                              : base.filter(x => x !== dsId);
                          });
                        }} />
                      {run.fileName || `Data Set ${idx + 1}`}
                      {run.T_C != null && <Badge color={C.textMuted}>{run.T_C}°C</Badge>}
                    </label>
                  );
                })}
              </SectionBox>
            )}

            {/* CLIENT FEEDBACK (Madhura, 26-Aug doc): "Species weights and
                regress/fixed exponents already exist in Model Input. It is
                repeated in Optimize. Remove from here." Both the "Tune
                Species Weights" and "Change Fitting Mode" cards that used
                to live here are removed — Model Input's own Species
                Weights and Exponent Treatment controls (which already
                write to the same speciesWeights / exponentMode / fitMode
                state) are now the single, unduplicated source for both.
                This also resolves the "Exponent Treatment control not
                synced between Optimize and Model Input" bug (item #2) by
                construction — there's only one control left to go out of
                sync with. */}

          </>
        }
      />
    </PanelShell>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PANEL 5 — DATA ELEMENTS / APPENDIX
// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
// ANTOINE PARAMETERS — DB-backed search panel (search by name or formula)
// ═══════════════════════════════════════════════════════════════════════════
// eslint-disable-next-line no-unused-vars -- kept for later; no longer rendered in the Appendix
const AntoineParametersPanel = () => {
  const [query, setQuery]               = useState('');
  const [suggestions, setSuggestions]   = useState([]);   // dropdown list
  const [selected, setSelected]         = useState(null);  // chosen compound
  const [allResults, setAllResults]     = useState([]);    // all rows for chosen compound
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);

  // Read auth token the same way the parent DataFittingPage does
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '';
  const authHeaders = token ? { Authorization: token } : {};

  // ── Fetch suggestions as user types (debounced 300 ms) ──────────────────
  useEffect(() => {
    const q = query.trim();
    if (!q) { setSuggestions([]); setShowDropdown(false); return; }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setError('');
        const res = await fetch(
          `${API_BASE}/antoine_parameters/search?q=${encodeURIComponent(q)}&limit=30`,
          { headers: authHeaders }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();   // { results: [...], total_count: N }
        const list = data.results || [];
        setSuggestions(list);
        setShowDropdown(list.length > 0);
      } catch (e) {
        setError('Could not reach antoine_parameters API: ' + e.message);
        setSuggestions([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── When user picks a suggestion, fetch all parameter rows for that compound
  const handleSelect = async (row) => {
    setQuery(`${row.species_name} (${row.formula})`);
    setShowDropdown(false);
    setSelected(row);
    setAllResults([]);
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE}/antoine_parameters/search?name=${encodeURIComponent(row.species_name)}&formula=${encodeURIComponent(row.formula)}&limit=50`,
        { headers: authHeaders }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAllResults(data.results || []);
    } catch (e) {
      setError('Failed to load parameter rows: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setSelected(null);
    setAllResults([]);
    setSuggestions([]);
    setShowDropdown(false);
    setError('');
  };

  return (
    <SectionBox title="Antoine Parameters" badge="DB" badgeColor={C.purple}
      info="Search the antoine_parameters table by compound name or molecular formula. Select a result to view all available parameter sets.">
      {/* ── Search input + clear ── */}
      <div style={{ position: 'relative', marginBottom: 8 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Input
              placeholder="e.g. Methanol, C2H6O, Benzene, CH4O …"
              value={query}
              onChange={e => { setQuery(e.target.value); setSelected(null); setAllResults([]); }}
              onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 180)}
              style={{ width: '100%', paddingRight: 28 }}
            />
            {query && (
              <button
                onClick={clearSearch}
                style={{
                  position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: C.textMuted, fontSize: 14, lineHeight: 1, padding: 0,
                }}
                title="Clear"
              >✕</button>
            )}
          </div>
        </div>

        {/* ── Scrollable dropdown ── */}
        {showDropdown && suggestions.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: 5, boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
            maxHeight: 260, overflowY: 'auto', marginTop: 2,
          }}>
            {suggestions.map((s, i) => (
              <div
                key={i}
                onMouseDown={() => handleSelect(s)}
                style={{
                  padding: '7px 12px', cursor: 'pointer', display: 'flex',
                  justifyContent: 'space-between', alignItems: 'center',
                  borderBottom: `1px solid ${C.border}22`,
                  background: i % 2 ? C.surface : C.card,
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = C.accentDim}
                onMouseLeave={e => e.currentTarget.style.background = i % 2 ? C.surface : C.card}
              >
                <span style={{ fontSize: 13, color: C.text, fontFamily: font.ui }}>{s.species_name}</span>
                <span style={{
                  fontSize: 13, color: C.purple, fontFamily: font.mono,
                  background: `${C.purple}18`, borderRadius: 3, padding: '1px 6px',
                }}>{s.formula}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <Alert type="error" style={{ marginBottom: 8 }}>{error}</Alert>}

      {/* ── Loading ── */}
      {loading && (
        <div style={{ padding: '12px 0', textAlign: 'center', color: C.textMuted,
          fontSize: 12, fontFamily: font.ui }}>
          Loading parameters…
        </div>
      )}

      {/* ── Results table ── */}
      {!loading && allResults.length > 0 && (
        <div>
          {/* Compound header */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
            padding: '7px 12px', borderRadius: 5,
            background: `${C.purple}18`, border: `1px solid ${C.purple}44`,
          }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: C.text, fontFamily: font.ui }}>
              {selected?.species_name}
            </span>
            <span style={{
              fontSize: 12, color: C.purple, fontFamily: font.mono,
              background: `${C.purple}28`, borderRadius: 3, padding: '1px 8px',
            }}>{selected?.formula}</span>
            <span style={{ fontSize: 13, color: C.textMuted, marginLeft: 'auto', fontFamily: font.ui }}>
              {allResults.length} parameter set{allResults.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Antoine equation reminder */}
          <div style={{
            fontSize: 13, color: C.textDim, fontFamily: font.ui,
            marginBottom: 8, padding: '4px 8px',
            borderLeft: `3px solid ${C.purple}`,
          }}>
            Antoine equation: <code style={{ fontFamily: font.mono }}>log₁₀(P) = A − B / (T + C)</code>
            &nbsp;· P in mmHg · T in K
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: font.ui }}>
              <thead>
                <tr style={{ background: C.panel }}>
                  {['#', 'T Range (K)', 'A', 'B', 'C', 'CAS', 'Reference'].map(h => (
                    <th key={h} style={{
                      padding: '5px 8px', color: C.textMuted,
                      borderBottom: `1px solid ${C.border}`,
                      textAlign: h === '#' ? 'center' : 'left',
                      fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.07em',
                      whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allResults.map((row, i) => (
                  <tr key={row.id ?? i} style={{
                    background: i % 2 ? C.card : C.surface,
                    borderBottom: `1px solid ${C.border}18`,
                  }}>
                    <td style={{ padding: '4px 8px', color: C.textMuted, textAlign: 'center', fontFamily: font.mono }}>{i + 1}</td>
                    <td style={{ padding: '4px 8px', color: C.cyan, fontFamily: font.mono, whiteSpace: 'nowrap' }}>
                      {row.temperature_range}
                    </td>
                    <td style={{ padding: '4px 8px', color: C.accent, fontFamily: font.mono }}>{row.A}</td>
                    <td style={{ padding: '4px 8px', color: C.accent, fontFamily: font.mono }}>{row.B}</td>
                    <td style={{ padding: '4px 8px', color: C.accent, fontFamily: font.mono }}>{row.C}</td>
                    <td style={{ padding: '4px 8px', color: C.textDim, fontSize: 13 }}>{row.cas_number ?? '—'}</td>
                    <td style={{
                      padding: '4px 8px', color: C.textMid, fontSize: 13,
                      maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} title={row.reference}>{row.reference ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Empty state after search but no compound selected ── */}
      {!loading && !selected && !error && query.trim() && suggestions.length === 0 && (
        <div style={{ padding: '10px 0', color: C.textDim, fontSize: 12, fontFamily: font.ui }}>
          No compounds found for <em>"{query}"</em>. Try a different name or formula.
        </div>
      )}
    </SectionBox>
  );
};

const appendixValue = (value, suffix = '', fallback = '—') => {
  if (value === null || value === undefined || value === '') return fallback;
  return `${value}${suffix}`;
};

const appendixFileLabel = (file, fallback = 'Current data set') => {
  const name = typeof file?.name === 'string' ? file.name.trim() : '';
  return name || fallback;
};

const appendixSourceLabel = (source, fileName = '') => {
  if (source === 'manual') return 'Manual entry';
  if (source === 'csv') return 'CSV upload';
  if (/^manual[_-]/i.test(String(fileName || ''))) return 'Manual entry';
  return 'CSV upload';
};

const countAppendixSpeciesColumns = (header = [], reactorMode = 'batch') => {
  if (!Array.isArray(header) || !header.length) return null;
  if (String(reactorMode).toLowerCase() !== 'pfr') return Math.max(header.length - 1, 0);
  const norm = value => String(value ?? '').toLowerCase().replace(/[\\s_()\\-]+/g, '');
  const ids = new Set(header.map(norm));
  // The retired Acetic Acid PFR template has a distinctive set of paired
  // inlet/outlet aliases plus auxiliary flow columns. Count its species pairs,
  // not every column in the CSV.
  const legacyPairs = [
    ['ethane', 'eout'], ['o2', 'o2out'], ['n2', 'n2out'],
    ['waterin', 'waterout'], ['aain', 'aaout'], ['etin', 'etout'], ['co2in', 'co2out'],
  ];
  if (ids.has('ethane') && ids.has('eout') && ids.has('aaout') && ids.has('etout') && ids.has('co2out')) {
    return legacyPairs.filter(([inlet, outlet]) => ids.has(inlet) && ids.has(outlet)).length;
  }
  const identities = new Set();
  header.forEach(value => {
    const id = norm(value);
    const stripped = id.replace(/(inlet|outlet|in|out)$/i, '');
    if (stripped && stripped !== id && !['t','tau','temperature','pressure'].includes(stripped)) identities.add(stripped);
  });
  return identities.size || null;
};

const buildAppendixDatasetRows = ({
  fitScope = 'single',
  reactorMode = 'batch',
  reactionSystem = 'custom',
  result = null,
  file = null,
  globalRuns = [],
  aaGlobalRuns = [],
  dataSourceType = null,
  dataPreview = null,
  reactionTempC = '',
  reactionPressure = '',
  pressureUnit = 'atm',
  nMixMol = '',
  wCatG = '',
  catWtUnit = 'g',
  vRxL = '',
  rateBasis = 'cat_weight',
}) => {
  const sourceFor = run => appendixSourceLabel(
    run?.dataSourceType || run?.source,
    run?.fileName || run?.file?.name || ''
  );

  if (fitScope === 'multiple') {
    const runs = Array.isArray(globalRuns) && globalRuns.length > 0
      ? globalRuns
      : (Array.isArray(aaGlobalRuns) && aaGlobalRuns.length > 0 ? aaGlobalRuns : []);

    // After a completed multi-dataset fit, prefer the persisted run metadata
    // but fall back to backend result metadata when the page was restored from
    // a result without rebuilding the in-memory GlobalRunManager objects.
    const backendRuns = Array.isArray(result?.global_metrics?.per_run_results)
      ? result.global_metrics.per_run_results
      : [];
    const resolvedSeries = getAppendixPredictedTimeseries(result);
    const resultRows = backendRuns.length > 0 ? backendRuns : (Array.isArray(resolvedSeries)
      ? Array.from(new Map(resolvedSeries
          .filter(r => r && r.dataset_label != null)
          .map(r => [String(r.dataset_index ?? r.dataset_label), r]))).values()
      : []);

    if (runs.length > 0) {
      return runs.map((run, i) => {
        const rr = backendRuns[i] || {};
        const rowCount = run?.dataPreview?.length != null
          ? Math.max(run.dataPreview.length - 1, 0)
          : (rr.n_rows ?? null);
        const pfrTemp = run?.T_C ?? rr.T_C;
        const pfrPressure = run?.P_psi ?? rr.P_psi;
        return {
          id: run?.id ?? rr.run_index ?? i,
          datasetIndex: i,
          label: run?.label || rr.label || appendixFileLabel(run?.file, `Data Set ${i + 1}`),
          fileName: run?.fileName || run?.file?.name || '',
          source: sourceFor(run),
          reactor: reactorMode.toUpperCase(),
          temperature: reactorMode === 'pfr'
            ? appendixValue(pfrTemp, ' °C', 'Per CSV row')
            : appendixValue(run?.T_C ?? rr.T_C, ' °C'),
          pressure: reactorMode === 'pfr'
            ? appendixValue(pfrPressure, ' psi', 'Per CSV row')
            : appendixValue(run?.P_atm, ' atm'),
          initialCharge: reactorMode === 'batch'
            ? appendixValue(run?.n_mix_mol ?? rr.n_mix_mol, ' mol')
            : 'Derived per row',
          catalystWeight: appendixValue(run?.W_cat_g ?? rr.W_cat_g, ` ${catWtUnit}`),
          reactorVolume: appendixValue(run?.V_rx_L ?? rr.V_rx_L, ' L'),
          rateBasis: rateBasis === 'volume' ? 'Reactor volume' : 'Catalyst weight',
          rowCount,
          speciesColumnCount: Array.isArray(run?.dataPreview?.[0])
            ? Math.max(run.dataPreview[0].length - (reactorMode === 'pfr' ? 3 : 1), 0)
            : null,
          overallR2: rr.overall_r2 ?? null,
        };
      });
    }

    if (resultRows.length > 0) {
      return resultRows.map((rr, i) => ({
        id: rr?.dataset_index ?? rr?.run_index ?? i,
        datasetIndex: rr?.dataset_index ?? rr?.run_index ?? i,
        label: rr?.label || `Data Set ${i + 1}`,
        fileName: '',
        source: 'Fitting result',
        reactor: String(reactorMode).toUpperCase(),
        temperature: reactorMode === 'pfr'
          ? appendixValue(rr?.T_C, ' °C', 'Per CSV row')
          : appendixValue(rr?.T_C, ' °C'),
        pressure: reactorMode === 'pfr'
          ? appendixValue(rr?.P_psi, ' psi', 'Per CSV row')
          : '—',
        initialCharge: reactorMode === 'batch' ? appendixValue(rr?.n_mix_mol, ' mol') : 'Derived per row',
        catalystWeight: appendixValue(rr?.W_cat_g, ` ${catWtUnit}`),
        reactorVolume: appendixValue(rr?.V_rx_L, ' L'),
        rateBasis: rateBasis === 'volume' ? 'Reactor volume' : 'Catalyst weight',
        rowCount: rr?.n_rows ?? null,
        speciesColumnCount: null,
        overallR2: rr?.overall_r2 ?? null,
      }));
    }

    return [];
  }

  return [{
    id: 'single',
    label: dataSourceType === 'manual' ? 'Manual entry' : appendixFileLabel(file, 'Current data set'),
    fileName: file?.name || '',
    source: appendixSourceLabel(dataSourceType, file?.name || ''),
    reactor: reactorMode.toUpperCase(),
    temperature: reactorMode === 'pfr' ? 'Per CSV row' : appendixValue(reactionTempC, ' °C'),
    pressure: reactorMode === 'pfr' ? 'Per CSV row' : appendixValue(reactionPressure, ` ${pressureUnit}`),
    initialCharge: reactorMode === 'batch' ? appendixValue(nMixMol, ' mol') : 'Derived per row',
    catalystWeight: appendixValue(wCatG, ` ${catWtUnit}`),
    reactorVolume: appendixValue(vRxL, ' L'),
    rateBasis: rateBasis === 'volume' ? 'Reactor volume' : 'Catalyst weight',
    rowCount: file?.exampleRowCount ?? (Array.isArray(dataPreview) ? Math.max(dataPreview.length - 1, 0) : null),
    speciesColumnCount: Array.isArray(dataPreview?.[0])
      ? countAppendixSpeciesColumns(dataPreview[0], reactorMode)
      : null,
  }];
};

const appendixNumeric = value => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const appendixLabel = (species, registeredComponents = []) => {
  const label = speciesDisplayName(species, registeredComponents);
  return label || String(species ?? '');
};

// Normalizes all result shapes currently returned by the fitting endpoints.
// Generic Batch/MeOAc/DME typically return:
//   {time_min, species: {sp: {exp: [...], pred: [...]}}}
// Generic/PFR multi-row paths may return:
//   [{row_index, species: {sp: {pred: [...], exp_end}}}, ...]
// Older endpoints may still expose:
//   metrics.y_i_exp / metrics.y_i_pred
const getAppendixPredictedTimeseries = (result) =>
  result?.predicted_timeseries
  ?? result?.metrics?.predicted_timeseries
  ?? result?.global_metrics?.predicted_timeseries
  ?? result?.metrics?.global_metrics?.predicted_timeseries
  ?? result?.result?.predicted_timeseries
  ?? null;

// What "Save this fit" stores for Compare Fits: the run's prediction series from
// wherever the result carries it, or - for older endpoints that only return
// metrics.y_i_exp / y_i_pred (per-species arrays, no time axis) - a series built
// from those, so the Compare overlay still has something to plot.
const getSavablePredictedTimeseries = (result) => {
  const ts = getAppendixPredictedTimeseries(result);
  if (ts) return ts;
  const yExp = result?.metrics?.y_i_exp;
  if (yExp && typeof yExp === 'object') {
    const yPred = result?.metrics?.y_i_pred || {};
    return { species: Object.fromEntries(Object.keys(yExp).map((sp) => [sp, { exp: yExp[sp], pred: yPred[sp] || [] }])) };
  }
  // Helps pin down run types that return no series at all.
  // eslint-disable-next-line no-console
  console.warn('[Save fit] no predicted_timeseries found on the result; keys:', Object.keys(result || {}), 'metrics keys:', Object.keys(result?.metrics || {}));
  return null;
};

// eslint-disable-next-line no-unused-vars
const buildAppendixComparisonRows = (result, registeredComponents = []) => {
  const ts = getAppendixPredictedTimeseries(result);

  if (ts && !Array.isArray(ts) && ts.species && typeof ts.species === 'object') {
    const speciesList = Object.keys(ts.species);
    if (!speciesList.length) return [];
    const time = Array.isArray(ts.time_min) ? ts.time_min : (Array.isArray(ts.time) ? ts.time : []);
    const n = Math.max(
      time.length,
      ...speciesList.map(sp => {
        const s = ts.species?.[sp] || {};
        return Math.max(Array.isArray(s.exp) ? s.exp.length : 0, Array.isArray(s.pred) ? s.pred.length : 0);
      })
    );
    if (!n) return [];

    const header = ['#'];
    if (time.length) header.push('Time (min)');
    speciesList.forEach(sp => {
      const label = appendixLabel(sp, registeredComponents);
      header.push(`${label} (experimental)`, `${label} (fitted)`);
    });

    return [
      header,
      ...Array.from({ length: n }, (_, i) => {
        const row = [i + 1];
        if (time.length) row.push(appendixNumeric(time[i]) ?? time[i] ?? '—');
        speciesList.forEach(sp => {
          const values = ts.species?.[sp] || {};
          row.push(
            appendixNumeric(values.exp?.[i]) ?? values.exp?.[i] ?? '—',
            appendixNumeric(values.pred?.[i]) ?? values.pred?.[i] ?? '—',
          );
        });
        return row;
      }),
    ];
  }

  // Custom multi-Batch: an array of independent time-series blocks. Keep the
  // dataset identity on every row so different temperatures/scale factors are
  // never presented as one continuous trajectory.
  if (Array.isArray(ts) && ts.length && ts[0]?.time_min && ts[0]?.species) {
    const speciesList = Array.from(new Set(ts.flatMap(ds => Object.keys(ds?.species || {}))));
    if (!speciesList.length) return [];
    const header = ['Data set', '#', 'Time (min)'];
    speciesList.forEach(sp => {
      const label = appendixLabel(sp, registeredComponents);
      header.push(`${label} (experimental)`, `${label} (fitted)`);
    });
    const rows = [];
    ts.forEach((ds, dsIdx) => {
      const times = Array.isArray(ds?.time_min) ? ds.time_min : [];
      const n = Math.max(times.length, ...speciesList.map(sp => {
        const pair = ds?.species?.[sp] || {};
        return Math.max(Array.isArray(pair.exp) ? pair.exp.length : 0, Array.isArray(pair.pred) ? pair.pred.length : 0);
      }));
      for (let i = 0; i < n; i += 1) {
        const row = [ds?.dataset_label || `Data Set ${dsIdx + 1}`, i + 1,
          appendixNumeric(times[i]) ?? times[i] ?? '—'];
        speciesList.forEach(sp => {
          const pair = ds?.species?.[sp] || {};
          row.push(
            appendixNumeric(pair.exp?.[i]) ?? pair.exp?.[i] ?? '—',
            appendixNumeric(pair.pred?.[i]) ?? pair.pred?.[i] ?? '—',
          );
        });
        rows.push(row);
      }
    });
    return [header, ...rows];
  }

  if (Array.isArray(ts) && ts.length) {
    const speciesList = Array.from(new Set(ts.flatMap(row => Object.keys(row?.species || {}))));
    if (!speciesList.length) return [];

    const hasDatasetLabels = ts.some(row => row?.dataset_label != null);
    const isPfrRows = ts.some(row => row?.tau_s != null || row?.Pressure_psi != null);
    const header = [];
    if (hasDatasetLabels) header.push('Data set');
    header.push('Data point');
    if (isPfrRows) header.push('Temperature (°C)', 'Pressure (psi)', 'τ (s)');
    speciesList.forEach(sp => {
      const label = appendixLabel(sp, registeredComponents);
      header.push(`${label} (experimental)`, `${label} (fitted)`);
    });

    return [
      header,
      ...ts.map((row, i) => {
        const out = [];
        if (hasDatasetLabels) out.push(row?.dataset_label || 'Data Set');
        out.push(row?.row_index ?? i + 1);
        if (isPfrRows) {
          out.push(
            appendixNumeric(row?.T_C) ?? row?.T_C ?? '—',
            appendixNumeric(row?.Pressure_psi) ?? row?.Pressure_psi ?? '—',
            appendixNumeric(row?.tau_s) ?? row?.tau_s ?? '—',
          );
        }
        speciesList.forEach(sp => {
          const pair = row?.species?.[sp] || {};
          const pred = Array.isArray(pair.pred) ? pair.pred[pair.pred.length - 1] : pair.pred;
          out.push(
            appendixNumeric(pair.exp_end) ?? pair.exp_end ?? '—',
            appendixNumeric(pred) ?? pred ?? '—',
          );
        });
        return out;
      }),
    ];
  }

  const exp_ = result?.metrics?.y_i_exp;
  const pred_ = result?.metrics?.y_i_pred || {};
  if (exp_ && typeof exp_ === 'object') {
    const speciesList = Object.keys(exp_);
    const n = Math.max(...speciesList.map(sp => Array.isArray(exp_[sp]) ? exp_[sp].length : 0), 0);
    if (!speciesList.length || !n) return [];
    const header = ['#'];
    speciesList.forEach(sp => {
      const label = appendixLabel(sp, registeredComponents);
      header.push(`${label} (experimental)`, `${label} (fitted)`);
    });
    return [
      header,
      ...Array.from({ length: n }, (_, i) => [
        i + 1,
        ...speciesList.flatMap(sp => [
          appendixNumeric(exp_[sp]?.[i]) ?? exp_[sp]?.[i] ?? '—',
          appendixNumeric(pred_[sp]?.[i]) ?? pred_[sp]?.[i] ?? '—',
        ]),
      ]),
    ];
  }

  return [];
};

// ───────────────────────────────────────────────────────────────────────────
// Appendix > Fitted vs Experimental (client spec):
//  - NO run-number (#) column; the first column is the time metric
//    (Time (min) for Batch, residence time τ (s) for PFR), then the
//    experimental and Fitted values for each component.
//  - ONE table per uploaded data set.
//  - Model values come from the same parity/time-series data the plots use
//    (Batch: pred[i] aligned with time[i]; PFR: exp_end and the final pred value).
//  - Two-level header (component name over Experimental | Fitted) so the table
//    is narrow enough to read without scrolling sideways.
// buildAppendixComparisonRows (the earlier flat single-table builder) is kept below, unused.
// ───────────────────────────────────────────────────────────────────────────
const SHOW_PREDICTED_TIMESERIES_TABLE = false;   // client: hidden for now (kept for 2.0 notes: user-defined run time / run to equilibrium)

const appendixCell = (v) => appendixNumeric(v) ?? v ?? '—';

// Client: model (fitted) values were showing 9-10+ digits. Round them to 4 decimals
// everywhere they are shown - the table, Copy Results and Print Report all read this
// same table data. Non-zero values too small to show at 4 decimals (|v| < 5e-5, which
// would round to a misleading 0.0000) are kept in scientific notation, 4 decimals.
// Experimental columns are the user's own uploaded numbers and are left as uploaded.
const appendixModelCell = (v) => {
  const n = appendixNumeric(v);
  if (n == null) return appendixCell(v);
  if (n !== 0 && Math.abs(n) < 5e-5) return n.toExponential(4);
  return n.toFixed(4);
};

const buildAppendixFitTables = (result, registeredComponents = []) => {
  const ts = getAppendixPredictedTimeseries(result);
  const labelOf = (sp) => appendixLabel(sp, registeredComponents);
  const tables = [];

  // One time-series block: { time, species: { sp: { exp: [...], pred: [...] } } }
  const fromSeries = (key, title, time, speciesMap) => {
    const speciesList = Object.keys(speciesMap || {});
    if (!speciesList.length) return;
    const t = Array.isArray(time) ? time : [];
    const n = Math.max(t.length, ...speciesList.map(sp => {
      const v = speciesMap[sp] || {};
      return Math.max(Array.isArray(v.exp) ? v.exp.length : 0, Array.isArray(v.pred) ? v.pred.length : 0);
    }), 0);
    if (!n) return;
    tables.push({
      key, title,
      leadHeaders: t.length ? ['Time (min)'] : [],
      species: speciesList.map(labelOf),
      rows: Array.from({ length: n }, (_, i) => ({
        lead: t.length ? [appendixCell(t[i])] : [],
        pairs: speciesList.map(sp => {
          const v = speciesMap[sp] || {};
          return [appendixCell(v.exp?.[i]), appendixModelCell(v.pred?.[i])];
        }),
      })),
    });
  };

  if (ts && !Array.isArray(ts) && ts.species && typeof ts.species === 'object') {
    // Single Batch data set
    fromSeries('single', null, ts.time_min ?? ts.time, ts.species);
  } else if (Array.isArray(ts) && ts.length && ts[0]?.time_min && ts[0]?.species) {
    // Multiple Batch: one table per data set (each keeps its own time axis)
    ts.forEach((ds, i) => fromSeries(`ds-${i}`, ds?.dataset_label || `Data Set ${i + 1}`, ds?.time_min, ds?.species));
  } else if (Array.isArray(ts) && ts.length) {
    // PFR / steady-state rows: one table per data set; residence time first
    const groups = new Map();
    ts.forEach((row) => {
      const k = row?.dataset_label ?? '__single__';
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(row);
    });
    groups.forEach((rows, k) => {
      const speciesList = Array.from(new Set(rows.flatMap(r => Object.keys(r?.species || {}))));
      if (!speciesList.length) return;
      const cols = [];   // [header, getter] - τ first, then the other columns the uploaded file carries
      if (rows.some(r => r?.tau_s != null)) cols.push(['τ (s)', r => r?.tau_s]);
      if (rows.some(r => r?.T_C != null)) cols.push(['Temperature (°C)', r => r?.T_C]);
      if (rows.some(r => r?.Pressure_psi != null)) cols.push(['Pressure (psi)', r => r?.Pressure_psi]);
      tables.push({
        key: String(k),
        title: k === '__single__' ? null : String(k),
        leadHeaders: cols.map(c => c[0]),
        species: speciesList.map(labelOf),
        rows: rows.map(r => ({
          lead: cols.map(c => appendixCell(c[1](r))),
          pairs: speciesList.map(sp => {
            const pair = r?.species?.[sp] || {};
            const pred = Array.isArray(pair.pred) ? pair.pred[pair.pred.length - 1] : pair.pred;
            return [appendixCell(pair.exp_end), appendixModelCell(pred)];
          }),
        })),
      });
    });
  } else {
    // Older endpoints: metrics.y_i_exp / y_i_pred (per-species arrays, no time axis)
    const e = result?.metrics?.y_i_exp;
    const p = result?.metrics?.y_i_pred || {};
    if (e && typeof e === 'object') {
      fromSeries('legacy', null, null, Object.fromEntries(Object.keys(e).map(sp => [sp, { exp: e[sp], pred: p[sp] || [] }])));
    }
  }
  return tables;
};

// flat rows (header + data) for Copy Results
const flattenAppendixFitTable = (t) => [
  [...t.leadHeaders, ...t.species.flatMap(l => [`${l} (Experimental)`, `${l} (Fitted)`])],
  ...t.rows.map(r => [...r.lead, ...r.pairs.flat()]),
];

const AppendixFitTables = ({ tables }) => (
  <>
    <style>{`
      .df-fit-block { margin: 0 0 18px; }
      .df-fit-title { font-size: 12px; font-weight: 600; letter-spacing: 0.04em; margin: 0 0 6px; color: var(--color-text); }
      .df-fit-scroll { width: 100%; max-height: min(58vh, 620px); overflow: auto; border-radius: var(--radius-md); }
      .df-fit-table { width: 100%; border-collapse: collapse; font-size: 12px; font-variant-numeric: tabular-nums; }
      .df-fit-table th, .df-fit-table td { padding: 4px 8px; border-bottom: 1px solid var(--color-border); text-align: center; white-space: nowrap; }
      .df-fit-table td { color: var(--color-text); }
      .df-fit-table th { position: sticky; z-index: 2; background: var(--color-panel); color: var(--color-text-muted); font-weight: 600; }
      .df-fit-h1 th { top: 0; height: 26px; text-align: center; }
      .df-fit-h2 th { top: 26px; font-size: 11px; font-weight: 500; }
      .df-fit-table th.df-fit-lead, .df-fit-table td.df-fit-lead { text-align: center; }
      .df-fit-table th.df-fit-sp, .df-fit-table td.df-fit-first { border-left: 1px solid var(--color-border); }
      /* Client: vertical line between every column (before each Experimental AND each Fitted column,
         and after the Time column), so the pairs are easy to follow down the table. */
      .df-fit-table td + td, .df-fit-table .df-fit-h2 th { border-left: 1px solid var(--color-border-strong, var(--color-border)); }
    `}</style>
    {tables.map(t => (
      <div key={t.key} className="df-fit-block">
        {t.title && <div className="df-fit-title">{t.title}</div>}
        <div className="df-fit-scroll">
          <table className="df-fit-table">
            <thead>
              <tr className="df-fit-h1">
                {t.leadHeaders.map(h => <th key={h} rowSpan={2} className="df-fit-lead">{h}</th>)}
                {t.species.map((l, i) => <th key={`${l}-${i}`} colSpan={2} className="df-fit-sp">{l}</th>)}
              </tr>
              <tr className="df-fit-h2">
                {t.species.map((l, i) => (
                  <React.Fragment key={`${l}-${i}`}><th>Experimental</th><th>Fitted</th></React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {t.rows.map((r, ri) => (
                <tr key={ri}>
                  {r.lead.map((v, i) => <td key={`l${i}`} className="df-fit-lead">{v}</td>)}
                  {r.pairs.flat().map((v, i) => <td key={`p${i}`} className={i % 2 === 0 ? 'df-fit-first' : undefined}>{v}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ))}
  </>
);

const AppendixDatasetCard = ({ label, value, emphasis = false }) => (
  <div className="df-appendix-summary-card">
    <div className="df-appendix-summary-label">{label}</div>
    <div className={`df-appendix-summary-value${emphasis ? ' df-appendix-summary-value--emphasis' : ''}`} title={String(value ?? '')}>
      {value ?? '—'}
    </div>
  </div>
);

const AppendixDataSetSummary = ({ datasetRows, selectedDatasetId, setSelectedDatasetId }) => {
  if (!datasetRows.length) {
    return <div className="df-appendix-empty">No data-set metadata is available for this result.</div>;
  }
  const selected = datasetRows.find(row => String(row.id) === String(selectedDatasetId)) || datasetRows[0];
  const many = datasetRows.length > 1;

  return (
    <>
      <div className="df-appendix-summary-toolbar">
        <div className="df-appendix-summary-selector-wrap">
          <label className="df-appendix-summary-selector-label" htmlFor="appendix-dataset-select">Data set</label>
          <select
            id="appendix-dataset-select"
            className="df-appendix-summary-selector"
            value={String(selected.id)}
            onChange={e => setSelectedDatasetId(e.target.value)}
          >
            {datasetRows.map((row, i) => (
              <option key={row.id ?? i} value={String(row.id)}>
                {many ? `${i + 1}. ${row.label}` : row.label}
              </option>
            ))}
          </select>
        </div>
        <div className="df-appendix-summary-meta">
          <span className="df-appendix-source-badge">{selected.source}</span>
          {selected.rowCount != null && <span>{selected.rowCount} data row{selected.rowCount === 1 ? '' : 's'}</span>}
          {selected.speciesColumnCount != null && <span>{selected.speciesColumnCount} species column{selected.speciesColumnCount === 1 ? '' : 's'}</span>}
        </div>
      </div>

      <div className="df-appendix-summary-grid">
        <AppendixDatasetCard label="Reactor" value={selected.reactor} emphasis />
        <AppendixDatasetCard label="Temperature" value={selected.temperature} />
        <AppendixDatasetCard label="Pressure" value={selected.pressure} />
        <AppendixDatasetCard label="Initial charge" value={selected.initialCharge} />
        <AppendixDatasetCard label="Catalyst weight" value={selected.catalystWeight} />
        <AppendixDatasetCard label="Reactor volume" value={selected.reactorVolume} />
      </div>

      <div className="df-appendix-summary-foot">
        <span><strong>Data source:</strong> {selected.source}</span>
        <span><strong>Rate basis:</strong> {selected.rateBasis}</span>
        {selected.fileName && <span className="df-appendix-filename" title={selected.fileName}><strong>File:</strong> {selected.fileName}</span>}
      </div>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PANEL 5 — DATA ELEMENTS / APPENDIX
// ═══════════════════════════════════════════════════════════════════════════
// Client refinement (Feedback - Optimize+Appendix, 26-Aug-2026): keep the
// Appendix focused on user-facing result data only:
//   1) data-set summary; 2) fitted-vs-experimental values;
//   3) predicted timeseries; 4) registered components.
// No literature/engine-side reference blocks are rendered here.
// ═══════════════════════════════════════════════════════════════════════════
const PanelAppendix = ({
  result, reactionSystem,
  registeredComponents, setRegisteredComponents,
  aaGlobalRuns, setAaGlobalRuns,
  rxn1Reactants = '', rxn1Products = '',
  rxn2Reactants = '', rxn2Products = '',
  rxn3Reactants = '', rxn3Products = '',
  reactionRows = [],
  speciesIdentityMap = {},
  // Data-set summary inputs — shared by Single + Multiple + Custom/Example.
  reactorMode = 'batch',
  fitScope = 'single',
  file = null,
  dataPreview = null,
  dataSourceType = null,
  globalRuns = [],
  reactionTempC = '',
  reactionPressure = '',
  pressureUnit = 'atm',
  nMixMol = '',
  wCatG = '',
  catWtUnit = 'g',
  vRxL = '',
  rateBasis = 'cat_weight',
}) => {
  const icon = <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M2 2h7l3 3v7a1 1 0 01-1 1H2a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M9 2v3h3M4 7h6M4 9.5h5M4 12h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>;
  const [copied, setCopied] = useState(false);
  const [selectedDatasetId, setSelectedDatasetId] = useState('single');

  const datasetRows = useMemo(() => buildAppendixDatasetRows({
    fitScope,
    reactorMode,
    reactionSystem,
    result,
    file,
    globalRuns,
    aaGlobalRuns,
    dataSourceType,
    dataPreview,
    reactionTempC,
    reactionPressure,
    pressureUnit,
    nMixMol,
    wCatG,
    catWtUnit,
    vRxL,
    rateBasis,
  }), [
    fitScope, reactorMode, reactionSystem, result, file, globalRuns, aaGlobalRuns,
    dataSourceType, dataPreview, reactionTempC, reactionPressure, pressureUnit,
    nMixMol, wCatG, catWtUnit, vRxL, rateBasis,
  ]);

  useEffect(() => {
    if (!datasetRows.some(row => String(row.id) === String(selectedDatasetId))) {
      setSelectedDatasetId(datasetRows[0]?.id ?? 'single');
    }
  }, [datasetRows, selectedDatasetId]);

  const fitTables = useMemo(
    () => buildAppendixFitTables(result, registeredComponents),
    [result, registeredComponents]
  );
  const fitRowCount = fitTables.reduce((n, t) => n + t.rows.length, 0);

  const predictedRows = useMemo(() => {
    // Hidden for now (client): identical to Fitted vs Experimental for Batch. The
    // function is intentionally kept - flip SHOW_PREDICTED_TIMESERIES_TABLE to bring it back.
    if (!SHOW_PREDICTED_TIMESERIES_TABLE) return [];
    // Appendix keeps experimental + fitted columns (original behaviour);
    // the series is resolved through getAppendixPredictedTimeseries so it also
    // works when the backend nests predicted_timeseries under metrics/global_metrics.
    const ts = getAppendixPredictedTimeseries(result);
    if (!ts) return [];

    if (!Array.isArray(ts) && ts.species && typeof ts.species === 'object') {
      const speciesList = Object.keys(ts.species);
      const times = Array.isArray(ts.time_min) ? ts.time_min : (Array.isArray(ts.time) ? ts.time : []);
      const header = ['#'];
      if (times.length) header.push('Time (min)');
      speciesList.forEach(sp => {
        const label = appendixLabel(sp, registeredComponents);
        header.push(`${label} (experimental)`, `${label} (fitted)`);
      });
      const n = Math.max(times.length, ...speciesList.map(sp => {
        const s = ts.species?.[sp] || {};
        return Math.max(Array.isArray(s.exp) ? s.exp.length : 0, Array.isArray(s.pred) ? s.pred.length : 0);
      }), 0);
      return [header, ...Array.from({ length: n }, (_, i) => [
        i + 1,
        ...(times.length ? [appendixNumeric(times[i]) ?? times[i] ?? '—'] : []),
        ...speciesList.flatMap(sp => {
          const s = ts.species?.[sp] || {};
          return [
            appendixNumeric(s.exp?.[i]) ?? s.exp?.[i] ?? '—',
            appendixNumeric(s.pred?.[i]) ?? s.pred?.[i] ?? '—',
          ];
        }),
      ])];
    }

    // Custom multiple Batch: one independent time-series block per dataset.
    if (Array.isArray(ts) && ts.length && ts.some(ds => ds?.time_min && ds?.species)) {
      const speciesList = Array.from(new Set(ts.flatMap(ds => Object.keys(ds?.species || {}))));
      const header = ['Data set', '#', 'Time (min)'];
      speciesList.forEach(sp => {
        const label = appendixLabel(sp, registeredComponents);
        header.push(`${label} (experimental)`, `${label} (fitted)`);
      });
      const rows = [];
      ts.forEach((ds, dsIdx) => {
        const times = Array.isArray(ds?.time_min) ? ds.time_min : [];
        const n = Math.max(times.length, ...speciesList.map(sp => {
          const pair = ds?.species?.[sp] || {};
          return Math.max(Array.isArray(pair.exp) ? pair.exp.length : 0, Array.isArray(pair.pred) ? pair.pred.length : 0);
        }), 0);
        for (let i = 0; i < n; i += 1) {
          rows.push([
            ds?.dataset_label || `Data Set ${dsIdx + 1}`,
            i + 1,
            appendixNumeric(times[i]) ?? times[i] ?? '—',
            ...speciesList.flatMap(sp => {
              const pair = ds?.species?.[sp] || {};
              return [
                appendixNumeric(pair.exp?.[i]) ?? pair.exp?.[i] ?? '—',
                appendixNumeric(pair.pred?.[i]) ?? pair.pred?.[i] ?? '—',
              ];
            }),
          ]);
        }
      });
      return [header, ...rows];
    }

    // PFR (including Custom Multiple PFR): one row per steady-state datapoint.
    if (Array.isArray(ts) && ts.length) {
      const speciesList = Array.from(new Set(ts.flatMap(row => Object.keys(row?.species || {}))));
      const datasetAware = ts.some(row => row?.dataset_label != null);
      const pfr = ts.some(row => row?.tau_s != null || row?.Pressure_psi != null);
      const header = [];
      if (datasetAware) header.push('Data set');
      header.push('Data point');
      if (pfr) header.push('Temperature (°C)', 'Pressure (psi)', 'τ (s)');
      // PFR: model values only - this table must carry NO experimental values.
      speciesList.forEach(sp => {
        const label = appendixLabel(sp, registeredComponents);
        header.push(`${label} (Fitted)`);
      });
      const rows = ts.map((row, i) => {
        const out = [];
        if (datasetAware) out.push(row?.dataset_label || 'Data Set');
        out.push(row?.row_index ?? i + 1);
        if (pfr) out.push(
          appendixNumeric(row?.T_C) ?? row?.T_C ?? '—',
          appendixNumeric(row?.Pressure_psi) ?? row?.Pressure_psi ?? '—',
          appendixNumeric(row?.tau_s) ?? row?.tau_s ?? '—',
        );
        speciesList.forEach(sp => {
          const pair = row?.species?.[sp] || {};
          const pred = Array.isArray(pair.pred) ? pair.pred[pair.pred.length - 1] : pair.pred;
          out.push(appendixNumeric(pred) ?? pred ?? '—');
        });
        return out;
      });
      return [header, ...rows];
    }

    return [];
  }, [result, registeredComponents]);

  // BUG FIX (client: "Print Report prints the whole page, we want the report only"):
  // the old handler called window.print() on the whole app (sidebar, navbar, tabs),
  // and its print-only CSS never applied because it was written with literal "\n"
  // characters. The report section is now copied into a hidden frame and ONLY that
  // is printed - white background, dark text, no sidebar/navbar/buttons, and no
  // scroll-box clipping, so long tables run across pages.
  const handlePrintReport = () => {
    const node = document.getElementById('appendix-print-section');
    if (!node) { window.print(); return; }

    const clone = node.cloneNode(true);
    clone.querySelectorAll('button, [data-no-print]').forEach(el => el.remove());   // controls are not part of a report

    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(frame);

    const printedOn = new Date().toLocaleString();
    const doc = frame.contentWindow.document;
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8">
      <title>KinetiQ - Appendix Report</title>
      <style>
        @page { margin: 14mm; }
        html, body { background: #fff !important; color: #111 !important; height: auto !important; overflow: visible !important;
          font-family: Inter, Arial, Helvetica, sans-serif; margin: 0; }
        h1 { font-size: 18px; margin: 0 0 2px; }
        .meta { font-size: 11px; color: #555; margin: 0 0 14px; }
        #report, #report * { color: #111 !important; background: transparent !important; box-shadow: none !important;
          border-color: #aaa !important; text-shadow: none !important; overflow: visible !important; max-height: none !important; }
        #report table { width: 100% !important; border-collapse: collapse; }
        #report th, #report td { border: 1px solid #bbb !important; padding: 4px 8px; }
        #report .df-fit-table th, #report .df-fit-table td { text-align: center !important; }
        #report tr { page-break-inside: avoid; }
        #report thead { display: table-header-group; }
      </style></head><body>
      <h1>KinetiQ - Data Elements / Appendix</h1>
      <p class="meta">Printed ${printedOn}</p>
      <div id="report">${clone.innerHTML}</div></body></html>`);
    doc.close();

    let printed = false;
    const go = () => {
      if (printed) return;
      printed = true;
      try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch (e) { window.print(); }
      setTimeout(() => { if (frame.parentNode) frame.parentNode.removeChild(frame); }, 1500);
    };
    frame.onload = go;
    setTimeout(go, 400);   // fallback when the frame's load event doesn't fire
  };

  // BUG FIX (client report: "Results pasted from the Appendix only pasted
  // partial results"): the copy handler exports exactly what the Appendix
  // renders (data-set summary + fitted-vs-experimental + predicted timeseries),
  // tab-separated so it pastes cleanly into Excel. "Copy/Download/Print...
  // should be available only in the format shown in the table."
  const handleCopyModelFit = () => {
    if (!result) return;
    const blocks = [];
    const selectedDataset = datasetRows.find(row => String(row.id) === String(selectedDatasetId)) || datasetRows[0];

    if (selectedDataset) {
      // BUG FIX ("r.join is not a function" when clicking Copy Results): the title
      // string used to sit INSIDE the array of rows that was mapped with r.join('\t'),
      // so the title itself (a string, not a row) crashed the handler. The title is
      // now added separately, like the other two blocks below.
      const summaryRows = [
        ['Field', 'Value'],
        ['Data set', selectedDataset.label],
        ['Source', selectedDataset.source],
        ['Reactor', selectedDataset.reactor],
        ['Temperature', selectedDataset.temperature],
        ['Pressure', selectedDataset.pressure],
        ['Initial charge', selectedDataset.initialCharge],
        ['Catalyst weight', selectedDataset.catalystWeight],
        ['Reactor volume', selectedDataset.reactorVolume],
        ['Rate basis', selectedDataset.rateBasis],
      ];
      blocks.push(['Data Set Summary', summaryRows.map(r => r.join('\t')).join('\n')].join('\n'));
    }
    fitTables.forEach((t) => {
      blocks.push([
        `Fitted vs Experimental Values${t.title ? ` - ${t.title}` : ''}`,
        flattenAppendixFitTable(t).map(r => r.join('\t')).join('\n'),
      ].join('\n'));
    });
    if (predictedRows.length > 0) {
      blocks.push(['Predicted Timeseries', predictedRows.map(r => r.join('\t')).join('\n')].join('\n'));
    }

    const text = blocks.join('\n\n');
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
    const fallbackCopy = () => {   // older browsers / non-secure pages without the Clipboard API
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { if (document.execCommand('copy')) done(); } catch (e) { /* ignore */ }
      document.body.removeChild(ta);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  };

  if (!result) {
    return (
      <PanelShell
        title="Data Elements / Appendix"
        subtitle="Run data and fitted vs experimental values"
        icon={icon}
      >
        <div style={{ minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center',
          textAlign: 'center', color: C.textDim, fontSize: 13, fontFamily: font.ui }}>
          No results yet. Go to Input and run fitting.
        </div>
      </PanelShell>
    );
  }

  return (
    <PanelShell
      title="Data Elements / Appendix"
      subtitle="Run data and fitted vs experimental values"
      icon={icon}
      actions={
        <div style={{ display: 'flex', gap: 6 }}>
          <Btn variant="ghost" style={{ fontSize: 12 }} onClick={handlePrintReport}>🖨 Print Report</Btn>
          <Btn variant="ghost" style={{ fontSize: 12 }} onClick={handleCopyModelFit}>
            {copied ? '✓ Copied' : '📋 Copy Results'}
          </Btn>
        </div>
      }
    >
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #appendix-print-section, #appendix-print-section * { visibility: visible; }
          #appendix-print-section { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>

      <div id="appendix-print-section">
        <SectionBox
          title="Data Set Summary"
          info="Shows the actual input conditions for the selected data set. Single and multiple Batch/PFR runs use their own dataset metadata; PFR rows retain their per-row temperature, pressure, and residence-time information."
        >
          <AppendixDataSetSummary
            datasetRows={datasetRows}
            selectedDatasetId={selectedDatasetId}
            setSelectedDatasetId={setSelectedDatasetId}
          />
        </SectionBox>

        <SectionBox
          title={`Fitted vs Experimental Values${fitRowCount ? ` · ${fitRowCount} rows` : ''}${fitTables.length > 1 ? ` · ${fitTables.length} data sets` : ''}`}
          info="Shows the experimental values used by the completed fit beside the fitted/model values. Multiple datasets remain separated by dataset label, so Batch time series and PFR steady-state rows are not merged into a false single trajectory."
        >
          {fitTables.length > 0 ? (
            <AppendixFitTables tables={fitTables} />
          ) : (
            <div className="df-appendix-empty">
              The completed result did not return paired experimental and fitted values for this run.
            </div>
          )}
        </SectionBox>

        {/* Predicted Timeseries: hidden for now (client). Re-enable with SHOW_PREDICTED_TIMESERIES_TABLE. */}
        {SHOW_PREDICTED_TIMESERIES_TABLE && predictedRows.length > 1 && (
          <SectionBox
            title="Predicted Timeseries"
            info="Complete model-prediction table. For multiple Batch datasets each dataset retains its own time axis; PFR rows carry model values only (no experimental values)."
          >
            <div className="df-appendix-comparison-scroll">
              <DataTable rows={predictedRows} compact />
            </div>
          </SectionBox>
        )}

        {/* The Appendix is intentionally result-driven. Static MeOAc/DME run
            tables, literature rate equations, adsorption constants, Aspen/export
            documentation, and reference boilerplate do not belong here. */}
      {/* ── Req 2 & 3: Component Registry + Stoichiometry Validator (full-width below) ── */}
      <div style={{ padding: '0 0 12px 0' }}>
        {/* Antoine Parameters search removed from the Appendix (client). The AntoineParametersPanel
            component is kept in this file, just not rendered here. */}
        {/* Component Registry removed from the Appendix (client): component search is done on the
            Input tab. The ComponentRegistry component is kept in this file, just not rendered here.
            */}
        <StoichiometryValidator
          reactions={(reactionRows.length ? reactionRows : [
            { reactants: rxn1Reactants, products: rxn1Products, index: 1 },
            { reactants: rxn2Reactants, products: rxn2Products, index: 2 },
            { reactants: rxn3Reactants, products: rxn3Products, index: 3 },
          ]).filter(r => String(r.reactants || '').trim() || String(r.products || '').trim())}
          registeredComponents={registeredComponents}
          speciesIdentityMap={speciesIdentityMap}
        />
        {/* ── Req 4: AA multi-CSV global fitting panel ─────────────── */}
        {reactionSystem === 'acetic_acid' && (
          <AAGlobalRunsPanel aaGlobalRuns={aaGlobalRuns} setAaGlobalRuns={setAaGlobalRuns} />
        )}
      </div>
      </div>
    </PanelShell>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPARE FIT — our own embedded tab (client spec):
//   "Users should be able to select multiple saved fits (via checkboxes)
//    and compare them ... allow removing clear outliers. Comparison should
//    be shown two ways: Graphically — overlay of fit vs. data (primary
//    decision-making view) ... In a summary table — descriptive 'key
//    features' of each fit ... Actual parameter values do not need to be
//    shown here."
//
// Reuses the shared selection state (FitRecords.selectedFitUuids /
// toggleFitSelected, capped at 4) that CompareFitsPanel's "Save this fit"
// already sits next to in Optimize — one selection, one source of truth.
//
// DATA SHAPE: fit_records_api.py stores each saved run's prediction data
// inside metrics_json.predicted_timeseries, in one of two shapes depending
// on reaction system (see save_fit_record / DataFittingPage's
// buildSaveFitPayload):
//   Shape A (single time series — generic powerlaw/lhhw, MeOAc, DME):
//     { time_min: [...], species: { sp: { exp: [...], pred: [...] } } }
//   Shape B (per-datapoint, multi-run — AA, steady-state PFR):
//     [ { row_index, species: { sp: { pred: [...], y0, exp_end } } }, ... ]
// normalizeSeries() below turns either into one common shape so the
// renderer doesn't need to know which reaction system produced it.
// ═══════════════════════════════════════════════════════════════════════════
const COMPARE_COLORS = ['#3b82f6', '#f59e0b', '#22c55e', '#ec4899'];

function normalizeSeries(predicted_timeseries) {
  if (!predicted_timeseries) return null;
  // Custom multi-Batch: flatten independent dataset time-series blocks into
  // one comparable time-series stream while preserving dataset_label on each
  // point. The renderer can still overlay the fit, but no dataset is treated
  // as a continuation of another dataset's time axis.
  if (Array.isArray(predicted_timeseries) && predicted_timeseries.length
      && predicted_timeseries[0]?.time_min && predicted_timeseries[0]?.species) {
    const speciesList = Array.from(new Set(predicted_timeseries.flatMap(ds => Object.keys(ds?.species || {}))));
    return {
      kind: 'timeseries',
      speciesList,
      forSpecies: (sp) => predicted_timeseries.flatMap((ds, dsIdx) => {
        const pair = ds?.species?.[sp];
        const times = Array.isArray(ds?.time_min) ? ds.time_min : [];
        if (!pair) return [];
        const n = Math.max(times.length, Array.isArray(pair.exp) ? pair.exp.length : 0, Array.isArray(pair.pred) ? pair.pred.length : 0);
        return Array.from({ length: n }, (_, i) => ({
          time: times[i], exp: pair.exp?.[i], pred: pair.pred?.[i],
          datasetLabel: ds?.dataset_label || `Data Set ${dsIdx + 1}`,
        })).filter(pt => pt.exp != null || pt.pred != null);
      }),
    };
  }
  if (Array.isArray(predicted_timeseries)) {
    if (predicted_timeseries.length === 0) return null;
    const speciesList = Array.from(new Set(predicted_timeseries.flatMap(row => Object.keys(row.species || {}))));
    return {
      kind: 'parity',
      speciesList,
      forSpecies: (sp) => predicted_timeseries
        .filter(row => row.species && row.species[sp])
        .map((row, i) => ({
          index: i + 1,
          observed: row.species[sp].exp_end,
          predicted: Array.isArray(row.species[sp].pred) ? row.species[sp].pred[row.species[sp].pred.length - 1] : null,
        }))
        .filter(pt => pt.observed != null && pt.predicted != null),
    };
  }
  if (predicted_timeseries.species) {
    const speciesList = Object.keys(predicted_timeseries.species);
    if (speciesList.length === 0) return null;
    return {
      kind: 'timeseries',
      speciesList,
      forSpecies: (sp) => {
        const s = predicted_timeseries.species[sp];
        if (!s) return null;
        const t = predicted_timeseries.time_min || [];
        return t.map((time, i) => ({ time, exp: s.exp?.[i], pred: s.pred?.[i] }));
      },
    };
  }
  return null;
}

// BUG FIX (Compare Fit summary table — "Overall R²" and per-species R²
// permanently blank for Acetic Acid fits saved before the overall_r2/
// final_r2 naming-mismatch fix): that earlier fix only changes what gets
// WRITTEN at save time — it can't recover R² for fits already sitting in
// the database whose metrics_json genuinely has neither key. But
// predicted_timeseries (observed vs. predicted per point) is already
// saved for these older fits — that's the same data driving the overlay
// chart above, which is why the chart works retroactively but R² didn't.
// Compute R² client-side from that same data as a display-time fallback,
// so this fixes already-saved fits too, with no re-save and no backend
// migration needed. Standard R² = 1 − SS_res/SS_tot.
function computeR2FromPairs(pairs) {
  const clean = pairs.filter(([o, p]) => typeof o === 'number' && typeof p === 'number' && !Number.isNaN(o) && !Number.isNaN(p));
  if (clean.length < 2) return null;
  const mean = clean.reduce((s, [o]) => s + o, 0) / clean.length;
  const ssTot = clean.reduce((s, [o]) => s + (o - mean) ** 2, 0);
  if (ssTot === 0) return null;
  const ssRes = clean.reduce((s, [o, p]) => s + (o - p) ** 2, 0);
  return 1 - ssRes / ssTot;
}

// Pulls {observed, predicted} pairs for one species out of an already-
// normalized series (works for both the parity and timeseries shapes).
function pairsForSpecies(norm, sp) {
  if (!norm) return [];
  const pts = norm.forSpecies(sp) || [];
  return norm.kind === 'parity'
    ? pts.map(p => [p.observed, p.predicted])
    : pts.map(p => [p.exp, p.pred]);
}

// Client feedback (Fit Diagnostics, 2-Sep call): "R² can mathematically be
// negative when a fit performs worse than predicting the mean of the data.
// Confirm this can happen in the UI and present it clearly (e.g. a 'Poor'
// label alongside the number) rather than as a bare, confusing negative
// number." Confirmed — negative R² does show up in practice (e.g. a
// reversible MeOAc kb_free run that timed out mid-search can return R² well
// below 0). Every colored per-species R² box elsewhere in this file (Fit
// Diagnostics sidebar, Fit Quality panel, AA/Combined/Per-CSV/Global-per-run
// R² boxes) already pairs the number with a POOR/GOOD/EXC badge — the
// Compare Fit summary table below was the one place still showing a
// completely bare, uncolored number. Shared here so the table uses the
// exact same thresholds/labels as those boxes instead of a fourth
// definition of "poor."
function renderR2Cell(r2v, computed = false) {
  const col = r2v > 0.95 ? C.success : r2v > 0.8 ? cssVar('--color-warning-dark') : C.error;
  const label = r2v > 0.95 ? 'EXC' : r2v > 0.8 ? 'GOOD' : 'POOR';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <span style={{ color: col, fontWeight: 700 }}>
        {r2v.toFixed(4)}{computed ? ' (computed)' : ''}
      </span>
      <Badge color={col} style={{ fontSize: 10, padding: '0 4px' }}>{label}</Badge>
    </div>
  );
}

const PanelCompare = ({ registeredComponents = [] }) => {
  const icon = <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2">
    <path d="M2 9.5c1.5-5 3-6 4.5-6M2 9.5h10" opacity="0.45"/>
    <path d="M2 3.5c1.5 5 3 6 4.5 6M10 3.5v6" />
  </svg>;

  const { fits, selectedFitUuids } = useFitRecords();
  const [detailByUuid, setDetailByUuid] = useState({});
  const [detailLoading, setDetailLoading] = useState({});
  const [activeSpecies, setActiveSpecies] = useState(null);

  const selected = selectedFitUuids
    .map(u => fits.find(f => f.fit_uuid === u))
    .filter(Boolean);

  useEffect(() => {
    selectedFitUuids.forEach(u => {
      if (detailByUuid[u] || detailLoading[u]) return;
      setDetailLoading(s => ({ ...s, [u]: true }));
      FitRecords.fetchFitDetail(u).then(d => {
        setDetailByUuid(s => ({ ...s, [u]: d }));
        setDetailLoading(s => ({ ...s, [u]: false }));
      });
    });
  }, [selectedFitUuids]); // eslint-disable-line react-hooks/exhaustive-deps

  const normByUuid = {};
  selectedFitUuids.forEach(u => {
    normByUuid[u] = normalizeSeries(detailByUuid[u]?.metrics?.predicted_timeseries);
  });

  const speciesOptions = Array.from(new Set(
    selectedFitUuids.flatMap(u => normByUuid[u]?.speciesList || [])
  ));
  const chartKind = selectedFitUuids.map(u => normByUuid[u]?.kind).find(Boolean) || 'timeseries';

  useEffect(() => {
    if (!activeSpecies && speciesOptions.length) setActiveSpecies(speciesOptions[0]);
    if (activeSpecies && !speciesOptions.includes(activeSpecies)) setActiveSpecies(speciesOptions[0] || null);
  }, [speciesOptions.join(','), activeSpecies]); // eslint-disable-line react-hooks/exhaustive-deps

  const chartData = (() => {
    if (!activeSpecies) return [];
    let maxLen = 0;
    const perFit = {};
    selectedFitUuids.forEach(u => {
      const pts = normByUuid[u]?.forSpecies?.(activeSpecies) || [];
      perFit[u] = pts;
      maxLen = Math.max(maxLen, pts.length);
    });
    const rows = [];
    for (let i = 0; i < maxLen; i++) {
      const row = { x: i + 1 };
      selectedFitUuids.forEach(u => {
        const pt = perFit[u]?.[i];
        if (!pt) return;
        if (chartKind === 'parity') {
          row[`${u}_observed`] = pt.observed;
          row[`${u}_predicted`] = pt.predicted;
        } else {
          row.x = pt.time ?? row.x;
          if (typeof pt.exp === 'number') row[`${u}_exp`] = pt.exp;
          if (typeof pt.pred === 'number') row[`${u}_pred`] = pt.pred;
        }
      });
      rows.push(row);
    }
    return rows;
  })();

  const speciesLabel = activeSpecies ? speciesDisplayName(activeSpecies, registeredComponents) : '';

  return (
    <PanelShell title="Compare Fit" subtitle="Select saved fits to compare visually" icon={icon}>
      <div className="cf-root">

        {/* ── Fit picker ─────────────────────────────────────────────── */}
        <SectionBox title="Select fits to compare" badge={`${selectedFitUuids.length}/4`}>
          {fits.length === 0 ? (
            <div className="cf-empty-note">
              No saved fits yet. Run a fit, then use <strong>Save this fit</strong> in the
              Optimize tab to start building a comparison set.
            </div>
          ) : (
            <div className="cf-picker-list">
              {fits.map(f => {
                const isSel = selectedFitUuids.includes(f.fit_uuid);
                const colorIdx = selectedFitUuids.indexOf(f.fit_uuid);
                return (
                  <label key={f.fit_uuid} className={`cf-pick-row${isSel ? ' cf-pick-row--sel' : ''}`}>
                    <input type="checkbox" checked={isSel}
                      disabled={!isSel && selectedFitUuids.length >= 4}
                      onChange={() => FitRecords.toggleFitSelected(f.fit_uuid)} />
                    {isSel && <span className="cf-color-dot" style={{ background: COMPARE_COLORS[colorIdx % COMPARE_COLORS.length] }} />}
                    <span className="cf-pick-label">{f.label}</span>
                    <span className="cf-pick-model">{f.model_type === 'lhhw' ? 'LHHW' : 'Power Law'}</span>
                    {f.is_active && <span className="cf-pick-active">ACTIVE</span>}
                  </label>
                );
              })}
            </div>
          )}
          {selectedFitUuids.length > 0 && (
            <div className="cf-clear-row">
              <button className="cf-clear-btn" onClick={() => FitRecords.clearSelection()}>Clear selection</button>
            </div>
          )}
        </SectionBox>

        {selected.length === 0 ? (
          <div className="cf-select-prompt">
            Select 2 or more saved fits above to compare them here.
          </div>
        ) : (
          <>
            {/* ── Graphical overlay — the primary view ────────────────── */}
            <SectionBox title="Fit vs. data — overlay" badge={speciesLabel || undefined}>
              {speciesOptions.length === 0 ? (
                <div className="cf-empty-note">
                  None of the selected fits have plotting data saved (they
                  were likely saved before this feature was added) —
                  overlay isn't available for these.
                </div>
              ) : (
                <>
                  {(() => {
                    const missing = selected.filter(f => !normByUuid[f.fit_uuid]);
                    if (missing.length === 0) return null;
                    return (
                      <div className="cf-warning">
                        No overlay data saved for {missing.map(f => f.label).join(', ')} —
                        {missing.length === 1 ? ' it was' : ' they were'} likely saved before this
                        feature was added.
                      </div>
                    );
                  })()}
                  {speciesOptions.length > 1 && (
                    <div className="cf-species-tabs">
                      {speciesOptions.map(sp => (
                        <button key={sp} onClick={() => setActiveSpecies(sp)}
                          className={`cf-species-tab${sp === activeSpecies ? ' cf-species-tab--active' : ''}`}>
                          {speciesDisplayName(sp, registeredComponents)}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="cf-chart-wrap">
                    <ResponsiveContainer>
                      <ComposedChart data={chartData} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                        <XAxis dataKey="x" type="number" tick={{ fontSize: 11, fill: C.textDim }}
                          label={{
                            value: chartKind === 'parity' ? 'Measured' : 'Time (min)',
                            position: 'insideBottom', offset: -4, fontSize: 11, fill: C.textDim,
                          }} />
                        <YAxis domain={chartKind === 'parity' ? [0, 'auto'] : ['auto', 'auto']} tick={{ fontSize: 11, fill: C.textDim }}
                          label={chartKind === 'parity'
                            ? { value: 'Predicted', angle: -90, position: 'insideLeft', fontSize: 11, fill: C.textDim }
                            // ADD (client feedback — Compare Fit overlay chart): X-axis already
                            // labels "Time (min)" for the timeseries case; Y-axis had no label
                            // at all. Mirror it with a "Composition" label here.
                            : { value: 'Composition', angle: -90, position: 'insideLeft', fontSize: 11, fill: C.textDim }} />
                        <Tooltip contentStyle={{ background: C.surface, border: `1px solid ${C.border}`, fontSize: 12 }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {chartKind === 'parity' && (
                          <ReferenceLine segment={[{ x: 0, y: 0 }, { x: 1, y: 1 }]}
                            stroke={C.textDim} strokeDasharray="3 3" ifOverflow="extendDomain" />
                        )}
                        {selected.map((f, i) => {
                          const color = COMPARE_COLORS[i % COMPARE_COLORS.length];
                          if (!normByUuid[f.fit_uuid]) return null;
                          return chartKind === 'parity' ? (
                            <Scatter key={`${f.fit_uuid}_parity`}
                              // BUG FIX: this Scatter gets its own LOCAL
                              // `data` array shaped {x, y} (observed vs
                              // predicted), separate from the chart-level
                              // `chartData` the XAxis reads from — but
                              // dataKey was set to `${f.fit_uuid}_predicted`,
                              // a property that doesn't exist on {x, y}
                              // objects. Recharts uses dataKey to pull each
                              // point's Y value; since that key was never
                              // present, every point's Y resolved to
                              // undefined and nothing drew — while the
                              // X-axis domain still looked real, because
                              // Recharts computes axis domains generically
                              // from every series' data using the axis's
                              // own "x" field, independent of whether the
                              // series' *own* dataKey resolves. Only
                              // AA/parity-mode fits hit this path — the
                              // timeseries Line/Scatter below reads
                              // straight from chart-level chartData and
                              // was never affected.
                              dataKey="y"
                              data={chartData.map(r => ({ x: r[`${f.fit_uuid}_observed`], y: r[`${f.fit_uuid}_predicted`] })).filter(r => r.x != null)}
                              name={f.label} fill={color} shape="circle" />
                          ) : [
                            <Line key={`${f.fit_uuid}_pred`} type="monotone" dataKey={`${f.fit_uuid}_pred`}
                              name={`${f.label} (model)`} stroke={color} strokeWidth={2} dot={false} connectNulls />,
                            <Scatter key={`${f.fit_uuid}_exp`} dataKey={`${f.fit_uuid}_exp`}
                              name={`${f.label} (data)`} fill={color} shape="circle" />,
                          ];
                        })}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  {chartKind === 'parity' && (
                    <div className="cf-footnote">
                      Parity plot (steady-state runs) — each point is one experimental run;
                      closer to the dashed y=x line means a better fit.
                    </div>
                  )}
                </>
              )}
            </SectionBox>

            {/* ── Descriptive summary table — key features + R² (overall
                   and per-species). Raw fitted parameters intentionally
                   excluded per client spec. ─────────────────────────── */}
            <SectionBox title="Summary — what each run represents">
              <div className="cf-table-wrap">
                <table className="cf-table">
                  <thead>
                    <tr>
                      <th></th>
                      {selected.map((f, i) => (
                        <th key={f.fit_uuid} style={{ color: COMPARE_COLORS[i % COMPARE_COLORS.length] }}>
                          {f.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Model', f => f.model_type === 'lhhw' ? 'LHHW' : 'Power Law'],
                      ['Reversibility', f => f.reversibility === 'irreversible' ? 'Irreversible' : 'Reversible'],
                      ['Exponents', f => {
                        const em = detailByUuid[f.fit_uuid]?.exponent_mode;
                        if (!em) return '…';
                        return em === 'fixed_1' ? 'Fixed at 1' : em === 'user_specified' ? 'User-specified' : 'Regressed';
                      }],
                      ['Overall R²', f => {
                        const saved = detailByUuid[f.fit_uuid]?.metrics?.overall_r2;
                        if (typeof saved === 'number') return renderR2Cell(saved);
                        // Fallback for fits saved before overall_r2/final_r2
                        // was captured — pool every species' observed/
                        // predicted pairs from the same predicted_timeseries
                        // already powering the overlay chart above.
                        const norm = normByUuid[f.fit_uuid];
                        if (!norm) return '…';
                        const pooled = norm.speciesList.flatMap(sp => pairsForSpecies(norm, sp));
                        const computed = computeR2FromPairs(pooled);
                        return typeof computed === 'number' ? renderR2Cell(computed, true) : '…';
                      }],
                    ].map(([rowLabel, get]) => (
                      <tr key={rowLabel}>
                        <td className="cf-td-label">{rowLabel}</td>
                        {selected.map(f => <td key={f.fit_uuid}>{get(f)}</td>)}
                      </tr>
                    ))}
                    {Array.from(new Set(
                      selected.flatMap(f => [
                        ...Object.keys(detailByUuid[f.fit_uuid]?.metrics?.per_species_r2 || {}),
                        ...(normByUuid[f.fit_uuid]?.speciesList || []),
                      ])
                    )).map(sp => (
                      <tr key={`r2-${sp}`}>
                        <td className="cf-td-label">R² — {speciesDisplayName(sp, registeredComponents)}</td>
                        {selected.map(f => {
                          const saved = detailByUuid[f.fit_uuid]?.metrics?.per_species_r2?.[sp];
                          if (typeof saved === 'number') return <td key={f.fit_uuid}>{renderR2Cell(saved)}</td>;
                          const computed = computeR2FromPairs(pairsForSpecies(normByUuid[f.fit_uuid], sp));
                          return <td key={f.fit_uuid}>{typeof computed === 'number' ? renderR2Cell(computed, true) : '—'}</td>;
                        })}
                      </tr>
                    ))}
                    {[
                      ['Status', f => f.status === 'converged' ? 'Converged' : f.status === 'stopped' ? 'Stopped early' : 'Failed'],
                      ['Saved', f => f.created_at ? new Date(f.created_at).toLocaleString() : '—'],
                      ['Active fit', f => f.is_active ? '✓' : ''],
                    ].map(([rowLabel, get]) => (
                      <tr key={rowLabel}>
                        <td className="cf-td-label">{rowLabel}</td>
                        {selected.map(f => <td key={f.fit_uuid}>{get(f)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="cf-footnote">
                Raw fitted parameters aren't shown here — visual fit-to-data
                comparison above is what drives model selection.
              </div>
            </SectionBox>
          </>
        )}
      </div>
    </PanelShell>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MANUAL ENTRY — column/field configuration per reaction system
// ─────────────────────────────────────────────────────────────────────────
// Each entry's `csvHeader` matches the corresponding CSV-upload template
// exactly — verified column-for-column against the real upload files
// (aa-experimental_data.csv, S2-02_Run2_DME.csv, S1-01_Run2_MethylAcetate.csv)
// so manual entry and CSV upload produce data in an identical, consistent
// shape (Action item #5). `templateRows` holds a couple of representative
// rows from those same files, used to generate a downloadable "starter" CSV
// that exactly matches both the manual-entry table and the CSV upload format.
// ═══════════════════════════════════════════════════════════════════════════
const MANUAL_ENTRY_CONFIG = {
  meoac: {
    label: 'Methyl Acetate (Batch)',
    type: 'timeseries', // one row per sampling time within a single batch run
    csvHeader:   ['Time (min)', 'x-MeOAc', 'x-MeOH', 'x-H2O', 'x-HOAc'],
    displayCols: ['Time (min)', 'x-MeOAc', 'x-MeOH', 'x-H2O', 'x-HOAc'],
    fields:            ['time', 'c1', 'c2', 'c3', 'c4'],
    compositionFields: ['c1', 'c2', 'c3', 'c4'],
    // Representative rows from S1-01_Run2_MethylAcetate.csv
    templateRows: [
      ['1.33', '0.0057', '0.5142', '0.216',  '0.2641'],
      ['15',   '0.0511', '0.4677', '0.256',  '0.2251'],
      ['30',   '0.0851', '0.4361', '0.2897', '0.1891'],
    ],
  },
  dme: {
    label: 'DME (Batch)',
    type: 'timeseries',
    csvHeader:   ['Time (min)', 'x-MeOH', 'x-H2O', 'x-DME'],
    displayCols: ['Time (min)', 'x-MeOH', 'x-H2O', 'x-DME'],
    fields:            ['time', 'c1', 'c2', 'c3'],
    compositionFields: ['c1', 'c2', 'c3'],
    // Representative rows from S2-02_Run2_DME.csv
    templateRows: [
      ['6',     '0.9487', '0.0513', '0.0000'],
      ['120.5', '0.9433', '0.0520', '0.0047'],
      ['240',   '0.9223', '0.0661', '0.0116'],
    ],
  },
  acetic_acid: {
    label: 'Acetic Acid (PFR)',
    type: 'pfr',

    // Generic user-supplied Acetic Acid / ethane-oxidation PFR schema. No
    // client-provided or synthetic example rows are bundled in this template.
    // Optional feed/outlet flow columns remain outside this composition grid.
    csvHeader: [
      'Pressure (psi)', 'T', 'tau',
      'Ethane', 'O2', 'N2', 'WaterIn', 'AA_in', 'ET_in', 'CO2_in',
      'EOUT', 'N2OUT', 'O2OUT', 'AAOUT', 'ETOUT', 'CO2OUT', 'WaterOut'
    ],
    displayCols: [
      'P (psi)', 'T (°C)', 'τ (s)',
      'C₂H₆ in', 'O₂ in', 'N₂ in', 'H₂O in', 'AA in', 'ET in', 'CO₂ in',
      'C₂H₆ out', 'N₂ out', 'O₂ out', 'AA out', 'ET out', 'CO₂ out', 'H₂O out'
    ],
    fields: [
      'Pressure', 'T', 'tau',
      'Ethane', 'O2', 'N2', 'WaterIn', 'AA_in', 'ET_in', 'CO2_in',
      'EOUT', 'N2OUT', 'O2OUT', 'AAOUT', 'ETOUT', 'CO2OUT', 'WaterOut'
    ],
    inletFields:  ['Ethane', 'O2', 'N2', 'WaterIn', 'AA_in', 'ET_in', 'CO2_in'],
    outletFields: ['EOUT', 'N2OUT', 'O2OUT', 'AAOUT', 'ETOUT', 'CO2OUT', 'WaterOut'],

    // No sample values are preloaded for this user-defined PFR schema.
    // This prevents retired synthetic/proprietary example rows from being reused.
    templateRows: [],
  },
};

// Returns a blank row object with the correct keys for the given system.
// Kept for the initial-state case (page load, before any species are known)
// where "meoac" is a safe static default. Everywhere a config may be dynamic
// (custom-typed reactions), use emptyManualRowForConfig(config) instead.
const emptyManualRow = (system) =>
  MANUAL_ENTRY_CONFIG[system].fields.reduce((o, f) => ({ ...o, [f]: '' }), {});

// [FIX] Same as emptyManualRow, but takes an already-resolved config object
// (static preset OR one built by buildDynamicManualEntryConfig) so it works
// for a custom, user-typed reaction system too.
const emptyManualRowForConfig = (config) =>
  config.fields.reduce((o, f) => ({ ...o, [f]: '' }), {});

// ─────────────────────────────────────────────────────────────────────────
// Downloads a starter CSV for the given (already-resolved) config: header
// row (identical to the CSV-upload template and the manual-entry table) +
// a few representative data rows when available. Lets a user switching
// between Manual Entry and CSV Upload see the exact same column structure
// either way (Action item #1/#4). [FIX] Takes a config object (not a system
// string key) so it also works for a dynamically-built custom-species config.
// ─────────────────────────────────────────────────────────────────────────
const downloadCsvTemplate = (config, filenameHint = 'custom') => {
  if (!config) return;
  const rows = config.templateRows || [];
  const csvText = [config.csvHeader.join(','), ...rows.map(r => r.join(','))].join('\n');
  const url = URL.createObjectURL(new Blob([csvText], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filenameHint}_template.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ═══════════════════════════════════════════════════════════════════════════
// MANUAL ENTRY MODAL
// ─────────────────────────────────────────────────────────────────────────
// Dynamically adapts to the active reaction system / reactor type:
//   • Batch systems (Methyl Acetate, DME) → time-series table (Time +
//     composition columns) PLUS Reaction Temperature & Pressure fields,
//     so each manual run records its T/P just like a CSV-derived run.
//   • PFR system (Acetic Acid) → per-run table with Pressure, T, tau,
//     inlet composition and outlet composition columns — matching the
//     AA CSV upload template exactly, so multiple temperature/pressure
//     conditions can be entered coherently (one row = one run).
// ═══════════════════════════════════════════════════════════════════════════
const ManualEntryModal = ({
  show, onClose, onSubmit, reactionSystem, activeSpecies = [], reactorMode = 'batch',
  manualSystem, setManualSystem, manualRows, setManualRows,
  reactionTempC, setReactionTempC,
  reactionPressure, setReactionPressure,
  pressureUnit, setPressureUnit,
  // 24-Aug-2026 ADD (Madhura doc, item 1 — "the right side shows mass
  // fractions, but the table still shows mole fractions"): this modal
  // previously had no idea which Concentration Basis the main Data Input
  // page had selected, so the Inlet/Outlet group headers below always said
  // "Mole Fractions" even when Mass Fraction was selected. Defaults to
  // 'mole_fraction' so any caller that doesn't pass it yet keeps the
  // previous (correct-for-that-default) label.
  concentrationBasis = 'mole_fraction',
  forRunLabel = null,
  registeredComponents = [],
}) => {
  // The manual-entry system always mirrors whatever reaction system is
  // already selected on the main Data Input page — there's no reason to
  // ask again inside the modal, and previously this let a stale/mismatched
  // system (e.g. "Methyl Acetate" showing while DME is selected outside)
  // linger until manually switched. Auto-sync on open / reactionSystem change.
  const targetSystem = reactionSystem === 'acetic_acid' ? 'acetic_acid' : reactionSystem;
  useEffect(() => {
    if (show && targetSystem && targetSystem !== manualSystem) {
      // FIX (item 3): pass reactorMode through so a custom system running as
      // PFR gets built with inlet/outlet columns, not the batch default.
      const cfg = getManualEntryConfig(targetSystem, activeSpecies, reactorMode);
      setManualSystem(targetSystem);
      setManualRows(Array.from({ length: cfg.type === 'pfr' ? 3 : 5 }, () => emptyManualRowForConfig(cfg)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, targetSystem, reactorMode]);

  if (!show) return null;
  // [FIX] Resolve via getManualEntryConfig (not a raw MANUAL_ENTRY_CONFIG[key]
  // lookup) so a custom, user-typed reaction system gets its own dynamically
  // built column set instead of silently falling back to MeOAc's.
  // reactorMode is passed through here too, since re-opening the modal after
  // switching Batch<->PFR (without reactionSystem itself changing) must still
  // rebuild the correct shape.
  const config = getManualEntryConfig(manualSystem, activeSpecies, reactorMode);
  const { fields, type } = config;
  // 24-Aug-2026 ADD (Madhura doc, item 1) — single source of truth for the
  // "Mole" vs "Mass" word used in the group headers / validation text
  // below, driven by the Concentration Basis toggle instead of being
  // hard-coded to "Mole".
  const fractionWord = concentrationBasis === 'mass_fraction' ? 'Mass' : 'Mole';
  // ADD (same Madhura doc item 1 — "if you mention P, you must mention the
  // units"): config.displayCols is a static array baked into
  // MANUAL_ENTRY_CONFIG / buildDynamicManualEntryConfig, so it can't
  // reflect the live Pressure Unit toggle — it always showed a bare "P"
  // (or a hardcoded unit for the Acetic Acid preset regardless of what's
  // actually selected). Resolve just the P column's unit dynamically here.
  const displayCols = type === 'pfr' && (config.displayCols[0] === 'P' || /^P\s*\(/.test(config.displayCols[0]))
    ? [`P (${pressureUnit || 'atm'})`, ...config.displayCols.slice(1)]
    : config.displayCols;

  // ── Copy/paste support (client requirement): pasting a block copied from
  // Excel/Google Sheets/another tool — tab-separated columns, newline-
  // separated rows — into any cell fills outward from that cell in both
  // directions: across columns (row-wise) AND down rows (column-wise).
  // Extra rows are appended automatically if the pasted block runs past the
  // last row. A plain single-value paste (no tabs/newlines) is left to the
  // browser's normal single-cell paste behaviour.
  //
  // GAP FIX (Madhura, 24-Aug-2026 doc, item 4 — the blank-first-row
  // screenshot): the previous filter `line !== ''` only dropped a pasted
  // line that was a literal empty string — it did NOT catch a line that's
  // blank but still contains tab characters (e.g. a leading "\t\t\t\t"
  // line, which Excel/Sheets sometimes includes for a header row or a
  // formatting artifact at the top of a copied range). That line passed
  // the filter, consumed a row, and left every cell in it blank — exactly
  // the leftover blank row 1 in her second screenshot. Now a line is
  // treated as blank (and skipped, not counted as a row) if every one of
  // its tab-separated cells is empty after trimming, not just if the raw
  // line was the empty string.
  //
  // Separately — "I copied only 3 rows but it pasted ALL of them": if the
  // clipboard genuinely contained 9 rows of real values (as her follow-up
  // screenshot showed), that's however many rows Excel put on the
  // clipboard for whatever range was actually selected there; this handler
  // faithfully reproduces whatever it's given rather than silently
  // dropping rows, since there's no reliable way to guess "the user meant
  // fewer rows than they copied." The per-row ✕ delete button below (see
  // item 4's second half) is the fix for after the fact — remove whichever
  // rows a paste brought in that you didn't want.
  //
  // FIX (Madhura — "row-paste / paste special" report, hit again reordering
  // a 3-reaction custom system's data): a single-row copy, or a "Paste
  // Special" copy of a non-contiguous subset of cells (e.g. only rows
  // 1/3/5), still lands in the clipboard as one contiguous tab/newline-
  // separated block — Excel/Sheets don't tell the browser which cells were
  // skipped. The old code wrote every cell in that block verbatim,
  // including blanks, so a blank cell in the pasted block CLEARED whatever
  // was already sitting in that grid cell — which is what "pastes
  // everything [instead of the selective subset]" actually was: not extra
  // rows, but existing values getting silently wiped by blanks riding along
  // in the copied range. Now, mirroring Excel/Sheets' own "Paste Special →
  // Skip Blanks" behaviour, a blank cell in the pasted block leaves the
  // destination cell untouched instead of overwriting it with ''.
  const handleGridPaste = (e, startRi, startFi) => {
    const text = e.clipboardData?.getData('text') ?? '';
    if (!text.includes('\t') && !text.includes('\n')) return; // single value — default paste is fine
    e.preventDefault();
    const pasteRows = text.replace(/\r/g, '').split('\n')
      .filter(line => line.split('\t').some(cell => cell.trim() !== ''));
    if (pasteRows.length === 0) return;
    setManualRows(prev => {
      const rows = prev.map(r => ({ ...r }));
      pasteRows.forEach((lineText, dr) => {
        const targetRi = startRi + dr;
        while (targetRi >= rows.length) rows.push(emptyManualRowForConfig(config));
        const cells = lineText.split('\t');
        cells.forEach((cellVal, dc) => {
          const targetField = fields[startFi + dc];
          const trimmed = cellVal.trim();
          // Skip blanks — don't clobber an existing value with an empty
          // cell from the pasted block (see FIX note above).
          if (targetField != null && trimmed !== '') rows[targetRi][targetField] = trimmed;
        });
      });
      return rows;
    });
  };

  // Per-row validation: timeseries → composition columns sum to 1;
  // pfr → inlet columns sum to 1 AND outlet columns sum to 1 (independently).
  const rowStatus = (row) => {
    if (type === 'pfr') {
      const inNums  = config.inletFields.map(f => parseFloat(row[f])).filter(v => !isNaN(v));
      const outNums = config.outletFields.map(f => parseFloat(row[f])).filter(v => !isNaN(v));
      const inSum  = inNums.reduce((a, b) => a + b, 0);
      const outSum = outNums.reduce((a, b) => a + b, 0);
      const inOk  = inNums.length  === 0 || Math.abs(inSum  - 1) < 0.01;
      const outOk = outNums.length === 0 || Math.abs(outSum - 1) < 0.01;
      return inOk && outOk;
    }
    const nums = config.compositionFields.map(f => parseFloat(row[f])).filter(v => !isNaN(v));
    const sum = nums.reduce((a, b) => a + b, 0);
    return nums.length === 0 || Math.abs(sum - 1) < 0.01;
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(61,31,48,0.6)', zIndex: 9990,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, animation: 'fadeIn 0.15s ease' }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`,
        borderTop: `3px solid ${C.accent}`, borderRadius: 8, padding: '18px 22px',
        width: '100%', maxWidth: type === 'pfr' ? 980 : 680, maxHeight: '85vh', overflowY: 'auto',
        boxShadow: `0 24px 64px ${C.accent}22` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: font.ui }}>
            Manual Data Entry
            <span style={{ fontWeight: 400, color: C.textMuted }}> – {type === 'pfr' ? 'PFR' : 'Batch'}</span>
            {forRunLabel && <span style={{ fontWeight: 400, color: C.textMuted }}> — {forRunLabel}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Btn variant="ghost" style={{ fontSize: 13 }}
              onClick={() => downloadCsvTemplate(config, manualSystem)}>⬇  Download CSV Template</Btn>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.textMuted,
              fontSize: 16, cursor: 'pointer', padding: '2px 6px', borderRadius: 4 }}>✕</button>
          </div>
        </div>

        {/* ── Batch systems: Reaction T / P apply to the whole run ───────── */}
        {type === 'timeseries' && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, color: C.textDim, fontFamily: font.ui }}>
              These conditions apply to every row below in this data set.
            </div>
          </div>
        )}

        {/* ── PFR (Acetic Acid): each row is an independent steady-state run ── */}
        {type === 'pfr' && (
          <div style={{ marginTop: 8 }}>
            {/* Condensed to one line — the Operating/Inlet/Outlet grouping is
                already shown directly on the table's own column-group header
                row just below, so repeating it here as a separate 3-box
                legend was pure duplication (Madhura Img-2 feedback: reduce
                label text taking up space in Manual Data Entry). */}
            <div style={{ background: C.panel, border: `1px solid ${C.borderLight}`,
              borderLeft: `3px solid ${C.accent}`, borderRadius: 5,
              padding: '6px 10px', marginBottom: 8, fontSize: 12,
              color: C.textMid, fontFamily: font.ui, lineHeight: 1.5 }}>
              <strong>One row = one steady-state (T, P, τ) run.</strong>
            </div>
          </div>
        )}

        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table style={{
            width: '100%', minWidth: type === 'pfr' ? 760 : 560,
            borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed',
          }}>
            {type === 'pfr' ? (
              <colgroup>
                <col style={{ width: 38 }} />
                <col span={3} style={{ width: 96 }} />
                <col span={config.inletFields?.length || 0} style={{ width: 106 }} />
                <col span={config.outletFields?.length || 0} style={{ width: 106 }} />
                <col style={{ width: 48 }} />
              </colgroup>
            ) : (
              <colgroup>
                <col style={{ width: 38 }} />
                <col span={displayCols.length} style={{ width: 108 }} />
                <col style={{ width: 48 }} />
              </colgroup>
            )}
            <thead>
              {/* Column group header row — AA PFR only (Madhura Jun 10 feedback) */}
              {type === 'pfr' && (
                <tr>
                  <th style={{ padding: '3px 4px', background: C.shell, borderBottom: `1px solid ${C.border}` }} />
                  {[
                    { label: 'Operating Conditions', span: 3, color: C.accent },
                    { label: `Inlet ${fractionWord} Fractions`, span: config.inletFields?.length || 0, color: C.cyan },
                    { label: `Outlet ${fractionWord} Fractions`, span: config.outletFields?.length || 0, color: C.success },
                    { label: '', span: 1, color: 'transparent' },
                  ].filter(g => g.span > 0 || g.label === 'Operating Conditions' || g.label === '').map((g, gi) => (
                    <th key={gi} colSpan={g.span} style={{
                      padding: '3px 8px', background: g.color + '18',
                      borderBottom: `2px solid ${g.color}`,
                      borderLeft: gi > 0 ? `1px solid ${g.color}44` : 'none',
                      color: g.color, fontSize: 12, fontWeight: 700,
                      textAlign: 'center', fontFamily: font.ui,
                      letterSpacing: '0.05em', textTransform: 'uppercase',
                    }}>{g.label}</th>
                  ))}
                </tr>
              )}
              {/* Column group header row — Batch systems, mirroring the PFR
                  group row above (client feedback, Sept-2026 doc): the Time
                  column is grouped under "Batch Time" and the composition
                  columns are grouped under "Mole/Mass Fractions" (matching
                  fractionWord, same as PFR's Inlet/Outlet groups) instead of
                  showing an un-grouped, x-prefixed column list. */}
              {type === 'timeseries' && (
                <tr>
                  <th style={{ padding: '3px 4px', background: C.shell, borderBottom: `1px solid ${C.border}` }} />
                  {[
                    { label: 'Batch Time', span: 1, color: C.accent },
                    { label: `${fractionWord} Fractions`, span: displayCols.length - 1, color: C.cyan },
                    { label: '', span: 1, color: 'transparent' },
                  ].map((g, gi) => (
                    <th key={gi} colSpan={g.span} style={{
                      padding: '3px 8px', background: g.color + '18',
                      borderBottom: `2px solid ${g.color}`,
                      borderLeft: gi > 0 ? `1px solid ${g.color}44` : 'none',
                      color: g.color, fontSize: 12, fontWeight: 700,
                      textAlign: 'center', fontFamily: font.ui,
                      letterSpacing: '0.05em', textTransform: 'uppercase',
                    }}>{g.label}</th>
                  ))}
                </tr>
              )}
              <tr style={{ background: C.panel }}>
                <th style={{ padding: '5px 8px', color: C.textMuted, borderBottom: `1px solid ${C.border}`,
                  fontSize: 13, textAlign: 'center', width: 28, textTransform: 'uppercase' }}>#</th>
                {displayCols.map((h, hi) => {
                  const inletCount = config.inletFields?.length || 0;
                  const outletStart = type === 'pfr' ? 3 + inletCount : -1;
                  // For PFR, group boundaries are derived from the actual
                  // component count instead of the previous hard-coded AA=7
                  // layout, so custom 1–N species systems render correctly.
                  const groupBorder = type === 'pfr' && (hi === 3 || hi === outletStart)
                    ? { borderLeft: `2px solid ${hi === 3 ? C.cyan : C.success}55` } : {};
                  // The CSV/header value remains the exact species key, but the
                  // visible manual-entry heading uses the full resolved name.
                  const label = manualEntryHeaderLabel(h, type, registeredComponents);
                  return (
                    <th key={h} style={{ padding: '5px 8px', color: C.textMuted,
                      borderBottom: `1px solid ${C.border}`,
                      fontSize: 12, textAlign: 'center',
                      letterSpacing: '0.02em',
                      // Full species names must remain readable in manual-entry
                      // headers (Madhura: do not truncate names such as
                      // Isopropyl ethanoate). Allow the header to wrap while
                      // keeping the table columns fixed and aligned.
                      whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip',
                      wordBreak: 'break-word', overflowWrap: 'anywhere', lineHeight: 1.25,
                      minHeight: 34, ...groupBorder }}>{label}</th>
                  );
                })}
                <th style={{ width: 46 }} />
              </tr>
            </thead>
            <tbody>
              {manualRows.map((row, ri) => {
                const ok = rowStatus(row);
                return (
                  <tr key={ri} style={{ background: ri % 2 ? C.card : C.surface }}>
                    <td style={{ padding: '3px 8px', textAlign: 'center', fontSize: 12, color: C.textDim, fontFamily: font.ui }}>{ri + 1}</td>
                    {fields.map((f, fi) => {
                      // PFR: add visual separator at the actual Inlet/Outlet
                      // boundary based on the number of declared species.
                      const inletCount = config.inletFields?.length || 0;
                      const outletStart = type === 'pfr' ? 3 + inletCount : -1;
                      const tdBorder = type === 'pfr' && (fi === 3 || fi === outletStart)
                        ? { borderLeft: `2px solid ${fi === 3 ? C.cyan : C.success}44` } : {};
                      return (
                        <td key={f} style={{ padding: '3px 6px', ...tdBorder, textAlign: 'center' }}>
                          <Input style={{ fontSize: 12, padding: '3px 6px', minWidth: 64 }} type="number" step="0.001" value={row[f]}
                            onPaste={e => handleGridPaste(e, ri, fi)}
                            onChange={e => setManualRows(prev => { const u = [...prev]; u[ri] = { ...u[ri], [f]: e.target.value }; return u; })} />
                        </td>
                      );
                    })}
                    <td style={{ padding: '3px 4px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 12, color: ok ? C.textDim : cssVar('--color-warning-dark') }}>{!ok ? '⚠' : ''}</span>
                      {/* ADD (Madhura, 24-Aug-2026 doc, item 4 — "there should
                          be a way to delete any rows if you don't need them"):
                          per-row delete, e.g. for a stray blank row left over
                          from a paste, or extra rows the user doesn't want.
                          Guards against deleting the last remaining row —
                          "Clear All" already covers wiping the whole table. */}
                      {manualRows.length > 1 && (
                        <button
                          onClick={() => setManualRows(prev => prev.filter((_, i) => i !== ri))}
                          title="Delete this row"
                          style={{ marginLeft: 6, background: 'none', border: 'none',
                            color: C.textMuted, cursor: 'pointer', fontSize: 13, padding: '0 2px',
                            lineHeight: 1 }}
                          onMouseEnter={e => e.currentTarget.style.color = cssVar('--color-danger') || '#e05252'}
                          onMouseLeave={e => e.currentTarget.style.color = C.textMuted}
                        >✕</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 7, marginTop: 14, alignItems: 'center' }}>
          <Btn variant="ghost" style={{ fontSize: 12 }}
            onClick={() => setManualRows(r => [...r, emptyManualRowForConfig(config)])}>+ Row</Btn>
          <Btn variant="ghost" style={{ fontSize: 12 }}
            onClick={() => setManualRows(Array.from({ length: type === 'pfr' ? 3 : 5 }, () => emptyManualRowForConfig(config)))}>Clear All</Btn>
          <div style={{ flex: 1 }} />
          <Btn variant="ghost" onClick={onClose} style={{ fontSize: 12 }}>Cancel</Btn>
          <Btn variant="success" onClick={onSubmit} style={{ fontSize: 13, background: C.accent, color: '#fff' }}>✓  Use This Data</Btn>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: C.textDim, fontFamily: font.ui }}>
          {type === 'pfr'
            ? <>
                <strong style={{ color: C.textMid }}>Validation:</strong>
                {' '}inlet and outlet {fractionWord.toLowerCase()} fractions must each sum to 1.0 per row —
                rows with ⚠ are auto-normalised on submit.
              </>
            : `${fractionWord} fractions should sum to 1.0 per row. Rows with ⚠ will be auto-normalized.`}
          <br/>
          <strong style={{ color: C.textMid }}>Tip:</strong> copy a block of cells from Excel or
          Google Sheets and paste into any cell here, it fills across columns and down rows
          from that point, adding new rows automatically if needed.
        </div>
      </div>
    </div>
  );
};

// ─── Hash → panel index map (defined outside component to avoid stale-closure lint warning) ───
// What goes into a run's saved input snapshot: every persisted form field
// (see `inputValues` in the component) except the component registry, which
// is refilled from the database on each page load — only the user-defined
// entries are kept.
const INPUT_SNAPSHOT_OPTIONS = { skip: [], transform: { registeredComponents: onlyUserComponents } };

const HASH_TO_PANEL = {
  '#input':   0,
  '#graph':   1,
  '#output':  2,
  '#optimize': 3,
  '#appendix': 4,
  '#compare': 5,   // Compare Fit — our own embedded tab, index 5 so every
                    // existing `activePanel === N` conditional elsewhere
                    // keeps working unchanged.
};

// ═══════════════════════════════════════════════════════════════════════════
// ROOT COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const DataFittingPage = () => {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '';

  // BUG FIX (Shankar, 25-Aug): with no token at all, this page used to just
  // render normally — every authenticated fetch/axios call in it would then
  // fail with 401 one at a time (Compare Fits showed a raw "resolve default
  // project failed: 401" for exactly this reason), but the run controls
  // stayed fully clickable the whole time, so a logged-out user could still
  // sit here and press "Run Fitting" with nothing happening but a vague
  // error popup. If there's no token at mount, there's no page to show —
  // go straight to login, matching the redirect the idle-session-timeout
  // path already uses below (see handleSessionLogout).
  //
  // BUG FIX (eslint react-hooks/rules-of-hooks, 25-Aug): this used to
  // `return null` right here, immediately after the redirect effect — but
  // every hook below it (useState/useEffect throughout the rest of this
  // component) would then simply not run on a no-token render, which
  // violates the Rules of Hooks (hook count must be identical on every
  // render). The redirect effect still lives here, next to `token`; the
  // "don't render the page" half of the fix moved to a plain `if` right
  // before the final `return (` at the bottom of this component — after
  // every hook has unconditionally run — so hook count stays constant
  // and only the JSX output changes.
  useEffect(() => {
    if (!token) window.location.href = '/';
  }, [token]);

  // ── Theme — dark is the client-confirmed default; persisted across visits ──
  const [theme, setTheme] = useState(() => {
    if (typeof localStorage === 'undefined') return 'dark';
    return localStorage.getItem('kinetiq-theme') || 'dark';
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    if (typeof localStorage !== 'undefined') localStorage.setItem('kinetiq-theme', theme);
  }, [theme]);

  // ── Session timeout — auto-logout after inactivity, with a "stay logged
  // in?" grace prompt. See §3.3 of the dev spec for the duration decision. ──
  const handleSessionLogout = React.useCallback(() => {
    if (typeof localStorage !== 'undefined') localStorage.removeItem('token');
    // BUG FIX (Shankar, 25-Aug): '/login' 404s — Login lives at '/' (App.js).
    window.location.href = '/';
  }, []);
  const { showPrompt: showSessionPrompt, loggedOut: sessionLoggedOut, stayLoggedIn, dismissLoggedOut } = useIdleSessionTimer(handleSessionLogout);

  // ── Privileged-access state ─────────────────────────────────────────────
  // [FIX] Acetic Acid is no longer a proprietary/restricted system — it now
  // uses published literature data (see SYSTEM_META.acetic_acid) — so
  // GET /api/user/accessible-systems always includes it and canAccessAA is
  // effectively always true. Left as a derived value (rather than a bare
  // `true`) so it keeps working unchanged if the backend ever needs to gate
  // a *different* system the same way in the future.
  const [accessibleSystems, setAccessibleSystems] = useState(['meoac', 'dme', 'acetic_acid']);
  const canAccessAA = accessibleSystems.includes('acetic_acid');

  // [FIX] AA access-request state removed — nothing reads or sets it any
  // more now that Acetic Acid isn't gated (see accessible-systems /
  // canAccessAA above). Was previously `const [aaRequestStatus, ...]`.

  // FIX (client tracker item 7: "clicking Appendix shows the Input screen first"):
  // this used to start at 0 (Input) and only switch to the tab named in the URL hash
  // inside an effect AFTER the first render, so Input (the heaviest tab) painted and
  // mounted first and the requested tab appeared a moment later. It now starts on the
  // tab the URL asks for (#graph, #appendix, ...); the effect below still keeps it in
  // sync when the hash changes while the page is open.
  const [activePanel, setActivePanel] = useState(() => HASH_TO_PANEL[window.location.hash] ?? 0);
  // Client feedback (Input tab, 03-Aug-2026): "New Custom System" as a
  // separate button was redundant — the custom-system UI/results path
  // already exists (PanelOutputs/PanelOptimize/PanelAppendix all key off
  // `reactionSystem`), so there's no second implementation to maintain by
  // making it the default. "Initialize with Example" is now the only entry
  // point for the 3 built-in reference systems (meoac/dme/acetic_acid);
  // everything else starts here, blank, as a custom system.
  const [reactionSystem, setReactionSystem]         = usePersistedState('reactionSystem', 'custom');
  const [fitMode, setFitMode]                       = usePersistedState('fitMode', 'keq_literature');
  const [reactorMode, setReactorMode]               = usePersistedState('reactorMode', 'batch');
  const [concentrationBasis, setConcentrationBasis] = usePersistedState('concentrationBasis', 'mole_fraction');
  const [reactionPhase, setReactionPhase]           = usePersistedState('reactionPhase', 'liquid');
  const [reactionPressure, setReactionPressure]     = usePersistedState('reactionPressure', '1');
  // Action item #10 (call tracker, 02-Sep): default Pressure must not be
  // "1 psi" (~0.068 atm — effectively a vacuum, not atmospheric) despite
  // reactionPressure's own default of '1' being fine on its face — the unit
  // it was paired with made the actual default non-atmospheric. Madhura:
  // "either [unit] is fine as long as it isn't 0" — atm keeps 1 == 1 atm,
  // the simplest atmospheric-equivalent default.
  const [pressureUnit, setPressureUnit]             = usePersistedState('pressureUnit', 'atm');
  // PFR uses psi internally. When switching from Batch (which may be using
  // atm/bar/kPa/mmHg), convert the existing numeric value before changing the
  // unit. The previous code changed only the unit, so a persisted default of
  // 1 atm could silently become 1 psi (~0.068 atm) in PFR.
  useEffect(() => {
    if (reactorMode === 'pfr' && pressureUnit !== 'psi') {
      setReactionPressure(convertPressure(reactionPressure, pressureUnit, 'psi'));
      setPressureUnit('psi');
    }
  }, [reactorMode, pressureUnit, reactionPressure, setReactionPressure, setPressureUnit]);
  const [file, setFile]                             = usePersistedState('file', null);
  const [dataPreview, setDataPreview]               = usePersistedState('dataPreview', null);
  const [autoDetect, setAutoDetect]                 = usePersistedState('autoDetect', { system: null, runNumber: null, matched: false, label: '', source: '', warning: '' });
  const [tempWarning, setTempWarning]               = usePersistedState('tempWarning', '');
  // Client feedback: the auto-detect banner / temperature note / data-preview
  // table are CSV-upload-only affordances (they're about *reading* an
  // uploaded file back to the user). Manual entry has no file to preview and
  // the system is already known from the picker, so none of this should
  // appear after a manual-entry submit. Tracks which path last populated
  // dataPreview/autoDetect so PanelInput can gate on it below.
  const [dataSourceType, setDataSourceType]         = usePersistedState('dataSourceType', null); // 'csv' | 'manual' | null
  const [moleFractionWarnings, setMoleFractionWarnings] = usePersistedState('moleFractionWarnings', []);
  const [normalizedCsvBlob, setNormalizedCsvBlob]   = usePersistedState('normalizedCsvBlob', null);
  const [nMixMol, setNMixMol]     = usePersistedState('nMixMol', '6.0');
  const [wCatG, setWCatG]         = usePersistedState('wCatG', '24.0');
  const [rateBasis, setRateBasis] = usePersistedState('rateBasis', 'cat_weight');
  const [vRxL, setVRxL]           = usePersistedState('vRxL', '0.25');
  const [fitScope, setFitScope]   = usePersistedState('fitScope', 'single');
  const [globalRuns, setGlobalRuns] = usePersistedState('globalRuns', []);

  // Manual multi-dataset entries are persisted as plain JSON fields
  // (manualCsvText/manualRowsSnapshot). A browser File object itself cannot be
  // restored from JSON, so recreate the File after session restore. This keeps
  // the existing backend upload pipeline unchanged while making manual data
  // refresh-safe.
  useEffect(() => {
    if (!Array.isArray(globalRuns) || globalRuns.length === 0 || typeof File === 'undefined') return;
    let changed = false;
    const restored = globalRuns.map((run, idx) => {
      if (!run?.manualCsvText) return run;
      const hasRealFile = run.file instanceof File && run.file.size > 0;
      if (hasRealFile) return run;
      changed = true;
      const name = run.fileName || `manual_custom_run_${idx + 1}.csv`;
      return {
        ...run,
        file: new File([run.manualCsvText], name, { type: 'text/csv' }),
        fileName: name,
      };
    });
    if (changed) setGlobalRuns(restored);
    // setGlobalRuns is the stable setter returned by usePersistedState.
  }, [globalRuns, setGlobalRuns]);
  const [optimizeExponents, setOptimizeExponents] = usePersistedState('optimizeExponents', true);
  // PDF defaults: Powerlaw first, Reversible first, Fixed-at-1 exponents, Regress Keq
  const [reversibility, setReversibility]         = usePersistedState('reversibility', 'reversible');       // 'reversible' | 'irreversible'
  const [modelType, setModelType]                 = usePersistedState('modelType', 'powerlaw');          // 'powerlaw' | 'lhhw'
  const [exponentMode, setExponentMode]           = usePersistedState('exponentMode', 'fixed_1');          // 'fixed_1' | 'regress' | 'user_specified'  — DEFAULT fixed_1 per PDF
  const [keqMode, setKeqMode]                     = usePersistedState('keqMode', 'regress');          // 'regress' | 'user_specified'
  // keqUserValues — one entry per reaction; AA system has 3 reactions
  const [keqUserValues, setKeqUserValues]         = usePersistedState('keqUserValues', Array.from({ length: 10 }, () => ({ X: '', Y: '' })));
  // customLhhwKiValues — LHHW adsorption constants Kᵢ for a user-defined
  // (reactionSystem==='custom') system, keyed by species name. Unlike the
  // MeOAc/DME/AA panel (whose Ki inputs are placeholder-only display, not
  // wired to state), this one is a real controlled input map since a
  // custom system has no literature Ki table to fall back on — the value
  // typed here is what actually gets fitted with. Missing/blank species
  // default to Ki=1.0 server-side (see generic_kinetics_objective.py
  // _lhhw_denominator), so an empty map is a valid "no preference" start.
  const [customLhhwKiValues, setCustomLhhwKiValues] = usePersistedState('customLhhwKiValues', {});
  // Action item #10 (call tracker, 02-Sep): default Temperature should be
  // 25°C, not 0/blank — the old '' default rendered the TemperatureSlider
  // pinned at its min (0°C) until the user touched it, which is not a
  // physically meaningful starting point.
  const [reactionTempC, setReactionTempC]         = usePersistedState('reactionTempC', '25');                // T (°C) — feed condition
  // ── Stoichiometry state — controlled inputs so Examples can auto-populate ──
  const [rxn1Reactants, setRxn1Reactants]         = usePersistedState('rxn1Reactants', '');
  const [rxn1Products,  setRxn1Products]          = usePersistedState('rxn1Products', '');
  const [rxn2Reactants, setRxn2Reactants]         = usePersistedState('rxn2Reactants', '');
  const [rxn2Products,  setRxn2Products]          = usePersistedState('rxn2Products', '');
  const [rxn3Reactants, setRxn3Reactants]         = usePersistedState('rxn3Reactants', '');
  const [rxn3Products,  setRxn3Products]          = usePersistedState('rxn3Products', '');
  const [rxn4Reactants, setRxn4Reactants]         = usePersistedState('rxn4Reactants', '');
  const [rxn4Products,  setRxn4Products]          = usePersistedState('rxn4Products', '');
  const [rxn5Reactants, setRxn5Reactants]         = usePersistedState('rxn5Reactants', '');
  const [rxn5Products,  setRxn5Products]          = usePersistedState('rxn5Products', '');
  const [rxn6Reactants, setRxn6Reactants]         = usePersistedState('rxn6Reactants', '');
  const [rxn6Products,  setRxn6Products]          = usePersistedState('rxn6Products', '');
  const [rxn7Reactants, setRxn7Reactants]         = usePersistedState('rxn7Reactants', '');
  const [rxn7Products,  setRxn7Products]          = usePersistedState('rxn7Products', '');
  const [rxn8Reactants, setRxn8Reactants]         = usePersistedState('rxn8Reactants', '');
  const [rxn8Products,  setRxn8Products]          = usePersistedState('rxn8Products', '');
  const [rxn9Reactants, setRxn9Reactants]         = usePersistedState('rxn9Reactants', '');
  const [rxn9Products,  setRxn9Products]          = usePersistedState('rxn9Products', '');
  const [rxn10Reactants, setRxn10Reactants]       = usePersistedState('rxn10Reactants', '');
  const [rxn10Products,  setRxn10Products]         = usePersistedState('rxn10Products', '');
  const [inertComponents, setInertComponents]     = usePersistedState('inertComponents', '');
  const currentReactionRows = [
    { index: 1, reactants: rxn1Reactants, products: rxn1Products },
    { index: 2, reactants: rxn2Reactants, products: rxn2Products },
    { index: 3, reactants: rxn3Reactants, products: rxn3Products },
    { index: 4, reactants: rxn4Reactants, products: rxn4Products },
    { index: 5, reactants: rxn5Reactants, products: rxn5Products },
    { index: 6, reactants: rxn6Reactants, products: rxn6Products },
    { index: 7, reactants: rxn7Reactants, products: rxn7Products },
    { index: 8, reactants: rxn8Reactants, products: rxn8Products },
    { index: 9, reactants: rxn9Reactants, products: rxn9Products },
    { index: 10, reactants: rxn10Reactants, products: rxn10Products },
  ];
  const [catWtUnit, setCatWtUnit]                 = usePersistedState('catWtUnit', 'g');               // g or kg
  const [maxIterations, setMaxIterations]         = usePersistedState('maxIterations', 100);               // Lever 2: max BO iterations
  const [r2Target, setR2Target]                   = usePersistedState('r2Target', 0.90);              // Lever 1: R² early-stop target
  const [warmStart, setWarmStart]                 = usePersistedState('warmStart', false);             // Lever 4: seed from last optimum
  const [selectedDataSets, setSelectedDataSets]   = usePersistedState('selectedDataSets', []);                // Checkboxes in Improve Fit
  // 18-Aug-2026 FIX (client-reported bug) — selectedDataSets stores specific
  // run IDs, and was never cleared when the underlying set of runs it refers
  // to changes wholesale (switching reaction system, e.g. MeOAc → Acetic
  // Acid, or switching Fitting Scope). A stale ID from a previous system's
  // runs would then silently match none of the new runs, making
  // runsSelectedCount compute to 0 for a perfectly valid fresh upload — the
  // Run button would look stuck disabled/mislabeled "Run Multi-1 CSV Fit"
  // with no visible explanation why. Switching either of these two things
  // means "start a new run selection", so reset here.
  useEffect(() => {
    setSelectedDataSets([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reactionSystem, fitScope]);

  // reactionOrders — one entry per reaction; AA system has 3 reactions
  const [reactionOrders, setReactionOrders]       = usePersistedState('reactionOrders', Array.from({ length: 10 }, () => ({ a: 1.0, b: 0.5 })));
  // Backward-compatible upgrade for sessions persisted by versions that only
  // allocated three reaction-order/Keq entries.
  useEffect(() => {
    setReactionOrders(prev => Array.from({ length: 10 }, (_, i) => prev?.[i] || ({ a: 1.0, b: 0.5 })));
    setKeqUserValues(prev => Array.from({ length: 10 }, (_, i) => prev?.[i] || ({ X: '', Y: '' })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [useInitialGuess, setUseInitialGuess]     = usePersistedState('useInitialGuess', false);
  // initialGuesses — one entry per reaction; AA system has 3 reactions
  const [initialGuesses, setInitialGuesses]       = usePersistedState('initialGuesses', [{ A: '', Ea: '' }, { A: '', Ea: '' }, { A: '', Ea: '' }]);
  // PDF default: Product Focus — products ×2, reactants ×1
  // ── [FIX] Include ALL species keys up-front so weights are never undefined
  // regardless of which system the user starts with or switches to.
  // Keys: MeOAc system + DME system + AA system. productFocus defaults for each.
  const [speciesWeights, setSpeciesWeights]       = usePersistedState('speciesWeights', {
    // Balanced default (23-Oct-2026): every fitted species starts at 1; N2 is
    // the AA system's inert and stays at 0 (excluded from the fit).
    MeOAc: 1, HOAc: 1, MeOH: 1, H2O: 1,
    DME: 1,
    C2H6: 1, O2: 1, AA: 1, ET: 1, CO2: 1, N2: 0,
  });
  const [selectedPreset, setSelectedPreset]       = usePersistedState('selectedPreset', DEFAULT_WEIGHT_PRESET);    // Balanced is the default
  // ── Per-row exclusion state (Jun-25 2026) — Set of 1-indexed row numbers to
  // exclude, sent to the backend as 'excluded_rows'. Replaces the old
  // excludedPoints/setExcludedPoints (ResidualsPanel, Madhura UI branch),
  // which was display-only and never reached the fitting endpoints.
  const [excludedRows, setExcludedRows]   = usePersistedState('excludedRows', new Set());
  const toggleExcludedRow = React.useCallback((rowNum) => {
    setExcludedRows(prev => {
      const next = new Set(prev);
      next.has(rowNum) ? next.delete(rowNum) : next.add(rowNum);
      return next;
    });
  }, [setExcludedRows]);
  const resetExcludedRows = React.useCallback(() => setExcludedRows(new Set()), [setExcludedRows]);
  // ── Req 2: Component registry — DB is the source of truth ────────────────
  // Keep only user-defined entries from the persisted registry. Built-in
  // species are refreshed from the DB every time this page loads.
  const [registeredComponents, setRegisteredComponents] = usePersistedState('registeredComponents', []);

  // Hidden exact species identity for explicitly selected stoichiometry tokens.
  // Visible text stays user-friendly; species_key is the computational identity.
  const [speciesIdentityMap, setSpeciesIdentityMap] = usePersistedState('speciesIdentityMapV2', {});

  // "Add a new component" pop-up (client item #11). Opened from the search
  // dropdown / Component Registry through requestAddComponent().
  const [addCompOpts, setAddCompOpts] = useState(null);
  useEffect(() => {
    addComponentHandler = (opts) => setAddCompOpts(opts || {});
    return () => { addComponentHandler = null; };
  }, []);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/species-catalog?limit=10000`, {
          headers: token ? { Authorization: token } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const dbComponents = normalizeSpeciesCatalog(data.species || []);
        // This login's own components (separate table). Best effort: if the
        // endpoint is unavailable the main catalog still loads.
        let myComponents = [];
        try {
          const ur = await userComponentsFetch('/user-components');
          if (ur.ok) myComponents = normalizeUserComponents(ur.data.components || []);
        } catch (_) { /* keep going with the main catalog only */ }
        if (cancelled) return;

        setRegisteredComponents(prev => {
          const dbIds = new Set(dbComponents.map(c => c.id.toUpperCase()));
          const serverMine = myComponents.filter(c => !dbIds.has(String(c.id || '').toUpperCase()));
          const serverIds = new Set(serverMine.map(c => c.id.toUpperCase()));
          // Older, browser-only custom entries (added before this feature) are kept.
          const legacyLocal = (Array.isArray(prev) ? prev : []).filter(c =>
            c?.source === 'user' && !c.server_id
            && !dbIds.has(String(c.id || '').toUpperCase())
            && !serverIds.has(String(c.id || '').toUpperCase()));
          return [...dbComponents, ...serverMine, ...legacyLocal];
        });
      } catch (e) {
        console.error('Failed to load species catalog:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [token, setRegisteredComponents]);
  // ── Req 4: AA multi-CSV global runs ───────────────────────────────────────
  const [aaGlobalRuns, setAaGlobalRuns]       = usePersistedState('aaGlobalRuns', []);
  // ── Fitting run lifecycle — backed by the module-level fittingSessionStore
  // instead of plain useState. react-router unmounts this whole component on
  // every route change, which used to silently kill the run's UI state (the
  // network request itself kept going, but nothing was left to update).
  // Reading/writing through the store means Reset/Stop/Run Fitting, the
  // locked-input overlay, and the result panel all keep working exactly as
  // before — including for Reset (fittingSession.js's resetFittingSession) —
  // but now also survive navigating to Help/Training/Dashboard/Contact and
  // coming back, and apply the same way whether the run was started from
  // "Initialize with Example" or a custom reaction system. ─────────────────
  const fittingSession = useFittingSession();
  const result       = fittingSession.result;
  const loading       = fittingSession.loading;
  const estimatedRuntimeSec = fittingSession.estimatedRuntimeSec;
  const popup         = fittingSession.popup;
  // 10-Aug-2026 ADD — real backend progress (Madhura's "runtime check"
  // follow-up). runId is generated once per submit and stored in the same
  // cross-navigation session object as loading/result/etc (see
  // fittingSessionStore.js) so a page navigation away and back still polls
  // the same run. backendProgress itself is just the latest poll response —
  // no need to persist it across navigation, the poll effect below refills
  // it immediately on mount as long as runId + loading are still set.
  const runId = fittingSession.runId;
  const setRunId = (v) => FittingSession.setFittingSessionState(
    s => ({ runId: typeof v === 'function' ? v(s.runId) : v }));
  const [backendProgress, setBackendProgress] = useState(null); // {phase, iter_done, n_iter, best_loss, r2_now}
  const setResult = (v) => FittingSession.setFittingSessionState(
    s => ({ result: typeof v === 'function' ? v(s.result) : v }));
  const setLoading = (v) => FittingSession.setFittingSessionState(
    s => ({ loading: typeof v === 'function' ? v(s.loading) : v }));
  const setRunStartedAt = (v) => FittingSession.setFittingSessionState(
    s => ({ runStartedAt: typeof v === 'function' ? v(s.runStartedAt) : v }));
  const setEstimatedRuntimeSec = (v) => FittingSession.setFittingSessionState(
    s => ({ estimatedRuntimeSec: typeof v === 'function' ? v(s.estimatedRuntimeSec) : v }));
  const setPopup = (v) => FittingSession.setFittingSessionState(
    s => ({ popup: typeof v === 'function' ? v(s.popup) : v }));
  // abortControllerRef.current reads/writes straight through to the store so
  // Stop still cancels the real in-flight request no matter which mount of
  // this page (or none at all) is currently on screen.
  const abortControllerRef = {
    get current() { return FittingSession.getFittingSessionState().abortController; },
    set current(v) { FittingSession.setFittingSessionState({ abortController: v }); },
  };
  const [activePlot, setActivePlot]       = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualSystem, setManualSystem]       = useState('meoac');
  const [manualRows, setManualRows]           = useState(
    Array.from({ length: 5 }, () => emptyManualRow('meoac'))
  );
  // Manual entry for the "Multiple data sets" case (GlobalRunManager /
  // AAMultiCsvManager): when set, ManualEntryModal fills THIS run's file
  // instead of the top-level single-CSV `file` state (Madhura, follow-up
  // call 8-Jul-2026, item A — manual-entry support for multiple data sets).
  const [manualEntryTargetRunId, setManualEntryTargetRunId] = useState(null);

  // GAP 2 FIX: Examples modal state
  const [showExamples, setShowExamples] = useState(false);
  // [FIX] Incremented on every "Initialize with Example" selection so
  // PanelInput can hard-reset its visible reaction-row count (1/2/3) to
  // match the newly-loaded example, instead of only ever growing.
  const [exampleLoadTick, setExampleLoadTick] = useState(0);
  // Incremented on Reset to invalidate any in-flight session restore.
  const [resetVersion, setResetVersion] = useState(0);

  // ── Saved inputs + session restore (client items #23 and "Persistence") ──
  // formSessionStore / fittingSessionStore are module-level, so they survive a
  // route change but NOT a browser refresh — after a refresh every field and
  // result was gone (Sagar's report). The backend already keeps one session per
  // (user, reaction system); useInputSession restores it on load and autosaves
  // to it. It also applies a run the user chose to "Reopen" from Run History.
  const inputValues = {
    reactionSystem,
    fitMode,
    reactorMode,
    concentrationBasis,
    reactionPhase,
    reactionPressure,
    pressureUnit,
    file,
    dataPreview,
    autoDetect,
    tempWarning,
    dataSourceType,
    moleFractionWarnings,
    nMixMol,
    wCatG,
    rateBasis,
    vRxL,
    fitScope,
    globalRuns,
    optimizeExponents,
    reversibility,
    modelType,
    exponentMode,
    keqMode,
    keqUserValues,
    customLhhwKiValues,
    reactionTempC,
    rxn1Reactants,
    rxn1Products,
    rxn2Reactants,
    rxn2Products,
    rxn3Reactants,
    rxn3Products,
    rxn4Reactants,
    rxn4Products,
    rxn5Reactants,
    rxn5Products,
    rxn6Reactants,
    rxn6Products,
    rxn7Reactants,
    rxn7Products,
    rxn8Reactants,
    rxn8Products,
    rxn9Reactants,
    rxn9Products,
    rxn10Reactants,
    rxn10Products,
    inertComponents,
    catWtUnit,
    maxIterations,
    r2Target,
    warmStart,
    selectedDataSets,
    reactionOrders,
    useInitialGuess,
    initialGuesses,
    speciesWeights,
    selectedPreset,
    excludedRows,
    registeredComponents,
    speciesIdentityMap,
    aaGlobalRuns,
  };
  const inputSetters = {
    reactionSystem: setReactionSystem,
    fitMode: setFitMode,
    reactorMode: setReactorMode,
    concentrationBasis: setConcentrationBasis,
    reactionPhase: setReactionPhase,
    reactionPressure: setReactionPressure,
    pressureUnit: setPressureUnit,
    file: setFile,
    dataPreview: setDataPreview,
    autoDetect: setAutoDetect,
    tempWarning: setTempWarning,
    dataSourceType: setDataSourceType,
    moleFractionWarnings: setMoleFractionWarnings,
    nMixMol: setNMixMol,
    wCatG: setWCatG,
    rateBasis: setRateBasis,
    vRxL: setVRxL,
    fitScope: setFitScope,
    globalRuns: setGlobalRuns,
    optimizeExponents: setOptimizeExponents,
    reversibility: setReversibility,
    modelType: setModelType,
    exponentMode: setExponentMode,
    keqMode: setKeqMode,
    keqUserValues: setKeqUserValues,
    customLhhwKiValues: setCustomLhhwKiValues,
    reactionTempC: setReactionTempC,
    rxn1Reactants: setRxn1Reactants,
    rxn1Products: setRxn1Products,
    rxn2Reactants: setRxn2Reactants,
    rxn2Products: setRxn2Products,
    rxn3Reactants: setRxn3Reactants,
    rxn3Products: setRxn3Products,
    rxn4Reactants: setRxn4Reactants,
    rxn4Products: setRxn4Products,
    rxn5Reactants: setRxn5Reactants,
    rxn5Products: setRxn5Products,
    rxn6Reactants: setRxn6Reactants,
    rxn6Products: setRxn6Products,
    rxn7Reactants: setRxn7Reactants,
    rxn7Products: setRxn7Products,
    rxn8Reactants: setRxn8Reactants,
    rxn8Products: setRxn8Products,
    rxn9Reactants: setRxn9Reactants,
    rxn9Products: setRxn9Products,
    rxn10Reactants: setRxn10Reactants,
    rxn10Products: setRxn10Products,
    inertComponents: setInertComponents,
    catWtUnit: setCatWtUnit,
    maxIterations: setMaxIterations,
    r2Target: setR2Target,
    warmStart: setWarmStart,
    selectedDataSets: setSelectedDataSets,
    reactionOrders: setReactionOrders,
    useInitialGuess: setUseInitialGuess,
    initialGuesses: setInitialGuesses,
    speciesWeights: setSpeciesWeights,
    selectedPreset: setSelectedPreset,
    excludedRows: setExcludedRows,
    registeredComponents: setRegisteredComponents,
    speciesIdentityMap: setSpeciesIdentityMap,
    aaGlobalRuns: setAaGlobalRuns,
  };
  const applyInputSnapshot = (snapshot) => {
    const restored = deserializeInputs(snapshot);
    Object.entries(restored).forEach(([key, value]) => {
      if (key === 'registeredComponents') {
        // Merge, don't overwrite: the catalogue load owns the DB entries.
        setRegisteredComponents(prev => mergeUserComponents(prev, value));
      } else if (inputSetters[key]) {
        inputSetters[key](value);
      }
    });
    setNormalizedCsvBlob(null);
    // Same row-count resync an "Initialize with Example" triggers, so the
    // additional reaction rows show up when restored inputs contain them.
    setExampleLoadTick(t => t + 1);
  };
  const { ready: sessionReady } = useInputSession({
    enabled: Boolean(token),
    resetKey: resetVersion,
    values: inputValues,
    valueDeps: Object.values(inputValues),
    serializeOptions: INPUT_SNAPSHOT_OPTIONS,
    applySnapshot: applyInputSnapshot,
    result,
    loading,
    runMeta: fittingSession.runMeta,
    setResult,
  });

  // Auto-populate all fields when user selects an example
  const handleExampleSelect = (ex) => {
    setReactionSystem(ex.system);
    setReactorMode(ex.reactorMode);
    setReactionPhase(ex.reactionPhase);
    setConcentrationBasis(ex.concentrationBasis);
    setReversibility(ex.reversibility);
    setModelType(ex.modelType);
    setExponentMode(ex.exponentMode);
    setKeqMode(ex.keqMode);
    setFitScope(ex.fitScope);
    // Presets supply chemistry/model/conditions only. Clear dataset files from
    // a prior system/session so a new PFR or Batch setup never inherits stale rows.
    setGlobalRuns([]);
    setAaGlobalRuns([]);
    if (ex.nMixMol)          setNMixMol(ex.nMixMol);
    if (Object.prototype.hasOwnProperty.call(ex, 'wCatG')) { setWCatG(ex.wCatG); setCatWtUnit('g'); }
    if (Object.prototype.hasOwnProperty.call(ex, 'vRxL')) setVRxL(ex.vRxL);
    if (Object.prototype.hasOwnProperty.call(ex, 'reactionTempC')) setReactionTempC(ex.reactionTempC);
    if (ex.reactionPressure) setReactionPressure(ex.reactionPressure);
    if (ex.pressureUnit)     setPressureUnit(ex.pressureUnit);
    if (ex.rateBasis)        setRateBasis(ex.rateBasis);
    // ── Stoichiometry auto-populate ────────────────────────────────
    // [FIX] Always set (even to '') so switching from a 3-reaction example
    // (e.g. Acetic Acid) to a 1-reaction example (e.g. MeOAc/DME) actually
    // clears the stale Reaction 2 / Reaction 3 fields instead of leaving
    // them populated with the previous example's values.
    setRxn1Reactants(ex.rxn1Reactants || '');
    setRxn1Products(ex.rxn1Products || '');
    setRxn2Reactants(ex.rxn2Reactants || '');
    setRxn2Products(ex.rxn2Products || '');
    setRxn3Reactants(ex.rxn3Reactants || '');
    setRxn3Products(ex.rxn3Products || '');
    setRxn4Reactants(ex.rxn4Reactants || '');
    setRxn4Products(ex.rxn4Products || '');
    setRxn5Reactants(ex.rxn5Reactants || '');
    setRxn5Products(ex.rxn5Products || '');
    setRxn6Reactants(ex.rxn6Reactants || '');
    setRxn6Products(ex.rxn6Products || '');
    setRxn7Reactants(ex.rxn7Reactants || '');
    setRxn7Products(ex.rxn7Products || '');
    setRxn8Reactants(ex.rxn8Reactants || '');
    setRxn8Products(ex.rxn8Products || '');
    setRxn9Reactants(ex.rxn9Reactants || '');
    setRxn9Products(ex.rxn9Products || '');
    setRxn10Reactants(ex.rxn10Reactants || '');
    setRxn10Products(ex.rxn10Products || '');
    // Bind the Example's visible research notation to the exact DB identity.
    setSpeciesIdentityMap(buildExampleSpeciesIdentityMap(ex));
    // Reset Manual Entry to the selected system so columns cannot carry over
    // from the previous reaction system.
    setManualSystem(ex.system);
    const exampleManualConfig =
      ex.system === 'acetic_acid'
        ? MANUAL_ENTRY_CONFIG.acetic_acid
        : MANUAL_ENTRY_CONFIG[ex.system];
    if (exampleManualConfig) {
      setManualRows(Array.from({ length: 5 }, () => emptyManualRowForConfig(exampleManualConfig)));
    }
    if (ex.inerts !== undefined) setInertComponents(ex.inerts);
    // Bump a tick so PanelInput resets its "how many reaction rows are
    // visible" count to match this example, even if the count had been
    // manually increased (+ Add Reaction) or increased by a previous
    // example before this one was picked.
    setExampleLoadTick(t => t + 1);
    // ── [FIX] Reset speciesWeights to productFocus defaults for the selected system
    // so that Objective Function Weights (Model Input) and Tune Species Weights
    // (Optimize panel) always reflect the correct system after an example is loaded.
    setSpeciesWeights(prev => ({ ...prev, ...balancedWeightsForSystem(ex.system) }));
    setSelectedPreset(DEFAULT_WEIGHT_PRESET);
    // Examples are configuration presets only: never preload experimental rows.
    // Always clear both uploaded-file and preview state so a previous system's
    // file cannot silently follow the user into this new setup.
    setFile(null);
    setDataPreview(null);
    setAutoDetect({ system: null, runNumber: null, matched: false, label: '', source: '', warning: '' });
    setDataSourceType(null);
    setNormalizedCsvBlob(null);
    setMoleFractionWarnings([]);
    setTempWarning('');
    setPopup({
      show: true,
      message: `Setup loaded: "${ex.cardLabel || ex.label || ex.id}". Upload a CSV or choose Enter Manually, then run fitting.`,
      type: 'info',
    });
  };

  // handleNewCustomSystem removed (03-Aug-2026 feedback) — its job (blank
  // reactionSystem='custom' state) is now the Input tab's default on load,
  // set directly in the reactionSystem useState above, so there's no
  // button handler left to call it.


  // ── [FIX] When reactionSystem changes, merge productFocus defaults for the new
  //    system into speciesWeights so Model Input and Optimize panel are always in
  //    sync. We merge (not replace) to preserve any customisations the user made
  //    to other-system weights if they switch back.
  useEffect(() => {
    const defaults = BALANCED_SYSTEM_WEIGHTS[reactionSystem];
    if (defaults) {
      setSpeciesWeights(prev => ({ ...prev, ...defaults }));
      setSelectedPreset(DEFAULT_WEIGHT_PRESET);
    }
  }, [reactionSystem]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync activePanel from URL hash (driven by app-wide Sidebar.js) ──────────
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch allowed systems for this user immediately after mount.
  // Drives canAccessAA which filters the RadioGroup and SYSTEM_SPECIES_MAP.
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/user/accessible-systems`, { headers: { Authorization: token } })
      .then(r => r.json())
      .then(data => {
        setAccessibleSystems(data.systems || ['meoac', 'dme']);
        // If the currently-selected system is no longer allowed, fall back to meoac.
        // 'custom' is exempt — it's a client-side-only concept with no backend
        // permission gate, so it will never appear in the server's allow-list.
        if (data.systems && reactionSystem !== 'custom' && !data.systems.includes(reactionSystem)) {
          setReactionSystem('meoac');
        }
      })
      .catch(() => { /* keep defaults — no AA access on network error */ });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reaction-system resolution for Compare Fits/Save Fit — moved below,
  // after `activeSpecies` is computed (see the effect right before the
  // `return` of this component). See that effect's comment for the bug
  // this fixes (fitRecordStore.js's reactionSystemId being resolved once
  // and never re-checked when the user switches reaction systems).

  useEffect(() => {
    const panel = HASH_TO_PANEL[location.hash];
    if (panel !== undefined) {
      // Always honor the requested panel. Result panels are responsible for
      // rendering their own empty state when there is no completed fit.
      setActivePanel(panel);
    }
  }, [location.hash]);

  // ── [FIX] Custom reaction systems now support LHHW as well as Power Law
  // (29-Jul-2026 follow-up; see generic_kinetics_objective.py SECTION 3b +
  // /api/run/generic-fitting), for both reversible and irreversible
  // reactions and both reactor modes (batch/PFR) — so modelType is no
  // longer snapped back to 'powerlaw' when switching into a custom system.

  // ── [FIX] Sync fitMode whenever the model-input toggles change ─────────────
  // Previously this was done inside the Model Input render body (anti-pattern).
  // A useEffect fires regardless of which sub-tab is active, so fitMode is
  // always correct when handleSubmit reads it.
  //
  // BUG FIX (Madhura, "Exponent Treatment control not synced between Optimize
  // and Model Input"): this used to bail out for every reaction system except
  // acetic_acid, on the assumption "MeOAc/DME derive fitMode differently" —
  // but nothing else ever actually derived it for them. fitMode for MeOAc/DME
  // was left as whatever the Optimize tab's OWN "Change Fitting Mode" card
  // last set (a second, disconnected control over the same underlying
  // setting), so Model Input's Exponent Treatment radio could say "Fixed at 1"
  // while the value actually submitted to the backend still said "Regress"
  // (root cause of the "Fixed at 1 doesn't fix exponents" bug too). Now every
  // non-custom system derives fitMode from the single exponentMode state
  // Model Input owns; custom systems still submit exponentMode directly and
  // never read fitMode (see handleSubmit).
  useEffect(() => {
    if (reactionSystem === 'custom') return; // custom submits exponentMode directly — see handleSubmit
    const isFree = exponentMode !== 'fixed_1';
    let next;
    if (reactionSystem === 'acetic_acid') {
      const isRev = reversibility === 'reversible';
      next =
        isRev  && !isFree ? 'powerlaw_reversible_fixed_exp' :
        isRev  &&  isFree ? 'powerlaw_reversible_free_exp'  :
        !isRev && !isFree ? 'powerlaw_fixed_exp'            :
                            'powerlaw_free_exp';
    } else {
      // MeOAc / DME — legacy 2-mode vocabulary that /api/run/data-fitting,
      // /api/run/meoac-global-fitting, and /api/run/dme-global-fitting expect.
      next = isFree ? 'optimize_kb' : 'unit_exponents';
    }
    if (next !== fitMode) setFitMode(next);
  }, [reactionSystem, reversibility, exponentMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-detect whether an AA "Single data set" CSV spans multiple
  // temperatures (Madhura, follow-up call 8-Jul-2026, item A: dropping the
  // separate "Global" option since a single CSV can already contain rows at
  // multiple temperatures). If it does, the backend needs the same
  // 'fit_scope: global' flag the old Global option used to send, so the
  // regression shares Ea/Af across all the temperature rows instead of
  // treating them independently. Returns false (single-temperature) on any
  // parse failure so the UI degrades to the plain single-run request. ──────
  const detectAAMultiTemp = async (fileOrBlob) => {
    if (!fileOrBlob) return false;
    try {
      const text = await fileOrBlob.text();
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 3) return false;
      const header = lines[0].split(',').map(h => h.trim());
      const tIdx = header.findIndex(h => h.toLowerCase() === 't');
      if (tIdx < 0) return false;
      const temps = new Set(
        lines.slice(1).map(l => parseFloat(l.split(',')[tIdx])).filter(Number.isFinite)
      );
      return temps.size > 1;
    } catch {
      return false;
    }
  };

  // ── [FIX] Helper: derive fit mode fresh at submit time (belt-and-suspenders) ─
  // Guards against any remaining edge-case where the useEffect above hasn't
  // fired yet (e.g. React batching on first render, or a click in Optimize's
  // "Change Fitting Mode" card and Re-run Fitting landing in the same batch).
  // Covers AA and MeOAc/DME — both derive their submitted mode from the same
  // exponentMode state Model Input owns; custom systems ignore this (they
  // send exponentMode directly, never fitMode — see handleSubmit).
  const deriveAAModeNow = () => {
    if (reactionSystem === 'custom') return fitMode; // unused for custom
    const isFree = exponentMode !== 'fixed_1';
    if (reactionSystem === 'acetic_acid') {
      const isRev = reversibility === 'reversible';
      if (isRev  && !isFree) return 'powerlaw_reversible_fixed_exp';
      if (isRev  &&  isFree) return 'powerlaw_reversible_free_exp';
      if (!isRev && !isFree) return 'powerlaw_fixed_exp';
      return 'powerlaw_free_exp';
    }
    // MeOAc / DME
    return isFree ? 'optimize_kb' : 'unit_exponents';
  };

  // ── Custom-system parameter count (mirrors build_param_spec() in
  //    generic_kinetics_objective.py exactly) — so the Optimize panel's
  //    Fixed/Regress Exponents cards can show real "(N params)" text instead
  //    of the generic wording AA's fixed 3-reaction/2-species shape doesn't
  //    need. Per reaction: 2 params (Af, Ea) always, +2 more (Ab, Eb) if that
  //    reaction is reversible; if regressing, +1 exponent per reactant
  //    species, and +1 more per product species if reversible. ────────────
  const customParamCounts = (() => {
    // Mirrors the same "how many reactions are actually filled in" logic
    // InputPanel uses for its own meoacDmeReactionCount state (line ~2935) —
    // duplicated locally rather than lifted, since that state is local to
    // InputPanel and isn't passed down as a prop.
    const activeReactionCount = Math.max(1, ...currentReactionRows
      .map((r, idx) => (String(r.reactants || '').trim() || String(r.products || '').trim()) ? idx + 1 : 0));
    const rxnPairs = currentReactionRows.slice(0, activeReactionCount)
      .map(r => [r.reactants, r.products]);
    const isRev = reversibility === 'reversible';
    let fixed = 0, regress = 0;
    rxnPairs.forEach(([reactantsStr, productsStr]) => {
      const nReactants = Object.keys(parseStoicSide(reactantsStr || '')).length || 1;
      const nProducts  = Object.keys(parseStoicSide(productsStr  || '')).length || 1;
      const base = isRev ? 4 : 2; // Af,Ea[,Ab,Eb]
      fixed   += base;
      regress += base + nReactants + (isRev ? nProducts : 0);
    });
    return { fixed, regress };
  })();

  // ── Keyboard shortcut: Alt+1…5 switches active panel ─────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && e.key >= '1' && e.key <= '5') {
        const idx = parseInt(e.key) - 1;
        if (idx <= 1 || result) setActivePanel(idx);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [result]);

  // Run progress display is driven by backend optimizer iteration counts.

  // 10-Aug-2026 FIX — the block below USED TO derive progressFraction purely
  // from an elapsed-time estimate, which was a client-side GUESS made before
  // the run even starts, with zero connection to what the backend is
  // actually doing. That's why the bar kept climbing normally even during
  // the multi-hour hang: it was pure wall-clock, not backend state.
  //
  // Fix: poll the real backend progress endpoint (see
  // /api/run/progress/<run_id> in openmkm_api.py) while a run is in flight.
  // runId is generated once per submit (see handleSubmit below) and lives
  // in FittingSession alongside loading/result, so this poll effect picks
  // back up correctly even if the user navigated away and back mid-run.
  useEffect(() => {
    if (!loading || !runId) { setBackendProgress(null); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await axios.get(`${API_BASE}/run/progress/${runId}`);
        if (!cancelled) setBackendProgress(res.data);
      } catch (_e) {
        // Transient poll failure (e.g. a moment of network hiccup) — skip
        // this tick only. Never fail the actual run over a missed poll.
      }
    };
    poll();
    const iv = setInterval(poll, 1500);
    return () => { cancelled = true; clearInterval(iv); };
  }, [loading, runId]);

  // Progress is measured by backend optimizer iterations, never by elapsed
  // wall-clock time. Every fitting route in the merged backend now reports
  // iter_done/n_iter; if a route fails to report, show an honest waiting state
  // rather than a fabricated percentage.
  const progressFraction = (() => {
    if (!loading) return 0;
    if (!backendProgress || backendProgress.phase === 'unknown') return 0.02;
    switch (backendProgress.phase) {
      case 'validating': return 0.05;
      case 'initializing': return 0.08;
      case 'optimizing': {
        const { iter_done = 0, n_iter = 0 } = backendProgress;
        return n_iter > 0 ? Math.min(0.94, 0.1 + 0.8 * (iter_done / n_iter)) : 0.12;
      }
      case 'plotting': return 0.96;
      case 'done':      return 1;
      case 'error':     return 0; // handled via the popup/error path, not the bar
      default:          return 0.02;
    }
  })();
  const fittingProgress = !loading ? '' :
    !backendProgress || backendProgress.phase === 'unknown'
      ? `Waiting for optimizer progress… iter 0/${maxIterations || '?'}`
      : backendProgress.phase === 'validating' ? `Validating input… iter 0/${backendProgress.n_iter || maxIterations || '?'}`
      : backendProgress.phase === 'initializing' ? `Preparing optimizer… iter ${backendProgress.iter_done ?? 0}/${backendProgress.n_iter || maxIterations || '?'}`
      : backendProgress.phase === 'optimizing'
        ? `Running optimization… iter ${backendProgress.iter_done ?? 0}/${backendProgress.n_iter || maxIterations || '?'}`
        : backendProgress.phase === 'plotting' ? `Preparing results… iter ${backendProgress.iter_done ?? backendProgress.n_iter ?? 0}/${backendProgress.n_iter || maxIterations || '?'}`
        : backendProgress.phase === 'done' ? `Completed… iter ${backendProgress.iter_done ?? backendProgress.n_iter ?? 0}/${backendProgress.n_iter || maxIterations || '?'}`
        : 'Running…';

  // Client request: "add an option to remove an uploaded data set (e.g. an X next to the file)"
  // so a wrong CSV doesn't force the user to reset the whole system and re-enter every
  // reaction. Clears ONLY the data-set state - reactions, species, conditions and model
  // settings are untouched. Mirrors the clearing the example loader already does, and for
  // manually entered data resets the grid the same way the manual-entry "Clear All" does.
  const handleRemoveFile = () => {
    if (dataSourceType === 'manual') {
      const cfg = getManualEntryConfig(manualSystem, activeSpecies, reactorMode);
      setManualRows(Array.from({ length: cfg.type === 'pfr' ? 3 : 5 }, () => emptyManualRowForConfig(cfg)));
    }
    setFile(null);
    setDataPreview(null);
    setAutoDetect({ system: null, runNumber: null, matched: false, label: '', source: '', warning: '' });
    setTempWarning('');
    setDataSourceType(null);
    setMoleFractionWarnings([]);
    setNormalizedCsvBlob(null);
  };

  const handleFileChange = async (e) => {
    const rawFile = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same filename later
    if (!rawFile) return;
    try {
      // Excel (.xlsx/.xls) — including a Google Sheets ".xlsx" export —
      // is converted to CSV text in-browser first; everything below runs
      // exactly as it already did for a native CSV upload.
      const uploadedFile = await convertSpreadsheetToCsvFile(rawFile);
      const text = await uploadedFile.text();
      const lines = text.split('\n').filter(l => l.trim());
      const header = lines[0].split(',').map(h => h.trim());
      setDataPreview(lines.slice(0, 6).map(l => l.split(',')));

      const headerStr = header.join(' ').toLowerCase();
      let system = null;
      if (headerStr.includes('dme')) system = 'dme';
      else if (headerStr.includes('meoac') || headerStr.includes('hoac')) system = 'meoac';

      const fnMatch = uploadedFile.name.match(/[Rr]un[_\s#-]*(\d+)/);
      const runNumber = fnMatch ? parseInt(fnMatch[1], 10) : null;

      let matched = false, meta = null, label = '', source = '', warning = '';

      if (system === 'dme') {
        source = 'Song 1998, Table 6 (DME)';
        setReactionSystem('dme');
        if (runNumber && SONG_DME_RUNS[runNumber]) {
          meta = SONG_DME_RUNS[runNumber]; matched = true;
          label = `DME ${meta.label}`;
          if (meta.caution) warning = meta.caution_msg;
        } else warning = 'DME detected. Enter n_mix and W_cat manually.';
      } else if (system === 'meoac') {
        source = 'Song 1998, Table 1 (MeOAc)';
        setReactionSystem('meoac');
        if (runNumber && SONG_MEOAC_RUNS[runNumber]) {
          meta = SONG_MEOAC_RUNS[runNumber]; matched = true; label = `MeOAc ${meta.label}`;
        } else warning = 'MeOAc detected. Enter n_mix and W_cat manually.';
      } else warning = 'Could not detect system. Please set reaction system manually.';

      if (matched && meta) {
        setNMixMol(String(meta.n_mix ?? meta.n_mix_mol));
        setWCatG(String(meta.W_cat ?? meta.W_cat_g));
      }
      setAutoDetect({ system, runNumber, matched, label, source, warning });

      const tIdx = header.findIndex(h => h.toLowerCase().includes('temp') || h.toLowerCase() === 't');
      if (tIdx >= 0 && lines.length > 1) {
        const temps = lines.slice(1).map(l => parseFloat(l.split(',')[tIdx])).filter(v => !isNaN(v));
        const avg = temps.reduce((a, b) => a + b, 0) / temps.length;
        if (avg < 200) setTempWarning(`Temperature ~${avg.toFixed(1)}°C — will auto-convert to Kelvin`);
        else setTempWarning(`Temperature ~${avg.toFixed(1)} K = ${(avg - 273.15).toFixed(1)}°C`);
      }

      setFile(uploadedFile);
      setMoleFractionWarnings([]);
      setNormalizedCsvBlob(null);
      setDataSourceType('csv');
    } catch (err) {
      setPopup({ show: true, message: `Failed to read CSV: ${err.message}`, type: 'error', errorCode: 'run_error' });
    }
  };

  const handleManualEntrySubmit = () => {
    // [FIX] Resolve dynamically (matches whatever's actually typed into
    // Reaction 1–10 + Inerts) instead of only recognizing the 3 static
    // presets. reactorMode is passed through (item 3) so a PFR-typed custom
    // system resolves to the inlet/outlet shape, not the batch default.
    const config = getManualEntryConfig(manualSystem, activeSpecies, reactorMode);

    // For PFR (Acetic Acid/custom PFR) each row is an independent run, keyed
    // on T/Pressure being present. For Batch systems each row is a sampling
    // time, keyed on `time` being present. Keep the ORIGINAL row objects too,
    // because they are what we need to restore the manual grid when the user
    // re-opens the same dataset.
    const enteredRows = config.type === 'pfr'
      ? manualRows.filter(r => r.T !== '' || r.Pressure !== '')
      : manualRows.filter(r => r.time !== '');
    const dataRows = enteredRows
      .map(r => config.fields.map(f => r[f] || '0').join(','));

    // Batch needs at least two time points; PFR allows one steady-state row.
    const minRows = config.type === 'pfr' ? 1 : 2;
    if (dataRows.length < minRows) {
      alert(config.type === 'pfr'
        ? 'Please enter at least 1 run (Pressure, T, tau, inlet & outlet compositions).'
        : 'Please enter at least 2 Batch time points.');
      return;
    }

    // A stale target ID should never silently close the modal without storing
    // the user's data. This can happen after Reset/remove/route changes.
    const targetIndex = manualEntryTargetRunId != null
      ? globalRuns.findIndex(r => r.id === manualEntryTargetRunId)
      : -1;
    if (manualEntryTargetRunId != null && targetIndex < 0) {
      alert('The selected data set is no longer available. Please close this entry and open the data set again.');
      return;
    }

    const targetRun = targetIndex >= 0 ? globalRuns[targetIndex] : null;
    const dataSetNumber = targetIndex >= 0 ? targetIndex + 1 : globalRuns.length + 1;
    const autoLabel = `Data Set ${dataSetNumber}`;
    // Previous versions generated exactly the same `manual_custom.csv` for
    // every custom dataset. That made two valid manual datasets fail the
    // unique label/file validation even though their datapoints were correct.
    // Use a deterministic per-run filename and replace that old auto-label.
    const existingAutoLabel = /^manual_[^\s]+$/i.test(String(targetRun?.label || '').trim());
    const finalLabel = targetRun?.label && !existingAutoLabel
      ? String(targetRun.label).trim()
      : autoLabel;
    // CSV header matches the corresponding upload template exactly, so manual
    // entry and CSV upload are processed identically downstream.
    const fileName = `manual_${manualSystem}_run_${dataSetNumber}.csv`;
    const manualCsvText = [config.csvHeader.join(','), ...dataRows].join('\n');
    // Build the File directly from the final CSV string. This avoids an
    // unnecessary Blob→File nesting and gives us one canonical text payload
    // that can be validated/rebuilt again immediately before multipart upload.
    const f = new File([manualCsvText], fileName, { type: 'text/csv' });

    // "Multiple data sets" case: fill ONLY the targeted run. Store both a
    // plain-text CSV and the row objects so the data survives browser refresh
    // / session restore (File objects themselves are not JSON-persistable).
    if (manualEntryTargetRunId != null) {
      const manualRowsSnapshot = enteredRows.map(row => ({ ...row }));
      setGlobalRuns(r => r.map(x => x.id === manualEntryTargetRunId
        ? {
            ...x,
            file: f,
            fileName,
            label: finalLabel,
            dataSourceType: 'manual',
            dataPreview: [config.csvHeader, ...dataRows.slice(0, 10).map(r => r.split(','))],
            manualCsvText,
            manualRowsSnapshot,
          }
        : x));
      setManualEntryTargetRunId(null);
      setShowManualEntry(false);
      return;
    }

    setFile(f);
    setDataPreview([config.csvHeader, ...dataRows.slice(0, 10).map(r => r.split(','))]);

    // Keep the active reaction system in sync with the manually-entered data,
    // and dynamically switch reactor mode (batch vs PFR) to match.
    setReactionSystem(manualSystem);
    setReactorMode(config.type === 'pfr' ? 'pfr' : 'batch');

    setAutoDetect({
      system: manualSystem, runNumber: null, matched: false,
      label: config.label, source: 'manual',
      warning: config.type === 'pfr'
        ? 'Manual entry. Each row is one steady-state run; verify inlet and outlet mole fractions each sum to 1.'
        : `Manual entry. Verify n_mix and W_cat (Reaction T = ${reactionTempC || '—'}°C, P = ${reactionPressure || '—'} ${pressureUnit}).`,
    });
    setMoleFractionWarnings([]);
    setNormalizedCsvBlob(null);
    setDataSourceType('manual');
    setShowManualEntry(false);
  };

  // Heuristic upfront runtime estimate — shown to the user before/while a
  // run executes, and used to pace the progress bar/status label. Not a
  // guarantee; based on the two biggest real drivers of fit duration: how
  // many datasets are being fit together and the iteration budget.
  const estimateRuntimeSeconds = () => {
    const nDatasets = fitScope === 'multiple' ? Math.max(1, globalRuns.length) : 1;
    const nIter = parseInt(maxIterations, 10) || 100;
    const base = 6;                          // fixed overhead: upload, parse, validate
    const perIter = nIter * 0.18;             // optimizer cost scales with iteration budget
    const perDataset = (nDatasets - 1) * 4;   // extra datasets add roughly-linear cost
    return Math.max(8, Math.min(240, Math.round(base + perIter + perDataset)));
  };

  // Shared by "Save this fit" and by Stop (client: stopped runs must show in the
  // dashboard's Stopped count). `res` defaults to the current result; Stop passes
  // null because an aborted run has no result of its own.
  const buildFitPayload = async ({ result: res = result, status = 'converged' } = {}) => ({
                  // Compare Fits — POST /api/fit-records body. Kept as an
                  // inline closure here (rather than a separate top-level
                  // function) so it can safely read the same in-scope
                  // state this JSX block already uses, without guessing at
                  // variable names/locations elsewhere in this large file.
                  //
                  // NOTE: exponent_mode mapping below is a best-effort
                  // translation from this app's existing fitMode/reactionSystem
                  // vocabulary to the fixed_1/regress vocabulary
                  // fit_records_api.py expects. model_type/reversibility are
                  // no longer guessed from fitMode — see fix note below.
                  // BUG FIX (Save this fit always saving as POWER LAW):
                  // this used to derive model_type/reversibility by string-
                  // matching fitMode, but fitMode only ever holds one of
                  // 'powerlaw_fixed_exp' / 'powerlaw_free_exp' / 'unit_exponents' /
                  // 'optimize_kb' — the Optimize tab's Fixed/Regress Exponents
                  // toggle. None of those strings contain "lhhw" or
                  // "irreversible", so the .includes() checks always fell
                  // through to the powerlaw/reversible defaults regardless
                  // of what was actually picked on the Model Input tab.
                  // The real selections live in the modelType and
                  // reversibility state (both already in scope here, and
                  // already what's sent to the fitting endpoint itself in
                  // handleSubmit) — read those instead.
                  // Client items #23 / #24: every saved run carries the INPUTS
                  // it was produced from (form fields + the uploaded CSV text),
                  // so it can be reopened months later; and it's flagged when
                  // it came from a built-in example system so the dashboard can
                  // list a user's own fits ahead of example runs.
                  input_snapshot: (await serializeInputs(inputValues, INPUT_SNAPSHOT_OPTIONS)).snapshot,
                  is_example: reactionSystem !== 'custom',
                  model_type: modelType,
                  reversibility: reversibility,
                  exponent_mode: reactionSystem === 'custom' ? exponentMode
                    : (fitMode === 'unit_exponents' || fitMode === 'powerlaw_fixed_exp') ? 'fixed_1' : 'regress',
                  dataset_selection: { selected: selectedDataSets },
                  objective_weights: speciesWeights,
                  optimizer_config: { n_iterations: maxIterations, r2_target: r2Target, warm_start: warmStart },
                  fitted_parameters: toFittedParametersDict(res?.fit_results || res?.aspen_ready),
                  plot_data: {
                    predicted_timeseries: getSavablePredictedTimeseries(res),
                    per_run_plots: res?.per_run_plots || null,
                    main_plot_base64: res?.main_plot_base64 || '',
                  },
                  metrics: {
                    // BUG FIX (Compare Fit — "Overall R²" always blank for
                    // Acetic Acid fits specifically): AA's fitting response
                    // computes the exact same aggregate/per-species R²
                    // values as MeOAc/DME, but under DIFFERENT key names —
                    // openmkm_api.py's perform_aa_fitting returns
                    // metrics.final_r2 / metrics.r2_per_species, while
                    // MeOAc/DME return metrics.overall_r2 / metrics.r2_summary.
                    // This wasn't missing data, just a naming mismatch this
                    // save step never reconciled — fall back across both.
                    overall_r2: res?.metrics?.overall_r2 ?? res?.metrics?.final_r2,
                    per_species_r2: res?.metrics?.r2_summary ?? res?.metrics?.r2_per_species,
                    weighted_mse: res?.metrics?.weighted_mse,
                    // Compare Fits graphical overlay (new sidebar page) needs
                    // per-datapoint predicted-vs-experimental values to draw
                    // anything — fit_records has no dedicated column for this,
                    // so it rides along inside metrics_json instead of
                    // re-simulating from fitted_parameters at compare time
                    // (which would need to know which of several forward-model
                    // codepaths — generic powerlaw/lhhw, AA, DME, MeOAc — this
                    // fit came from). Every /api/run/* endpoint already
                    // computes and returns this on `result` today (see
                    // openmkm_api.py's `response["predicted_timeseries"]`); this
                    // is the first place anything persists it.
                    predicted_timeseries: getSavablePredictedTimeseries(res),
                    component_names: Object.fromEntries(
                      registeredComponents.flatMap(c => {
                        if (!c?.name) return [];
                        return [c.id, c.formula, c.name, ...(Array.isArray(c.aliases) ? c.aliases : [])]
                          .filter(Boolean).map(alias => [String(alias), c.name]);
                      })
                    ),
                  },
    status,
  });

  // Cancels the in-flight fitting request (Stop/Abort button). Safe to call
  // even if nothing is running.
  //
  // 13-Aug-2026 FIX (client-reported bug) — this previously only called
  // abortControllerRef.current.abort(), which cancels THIS BROWSER TAB's
  // in-flight HTTP request. It does nothing to the Flask thread on the
  // server actually running the BO optimization loop, which has no idea the
  // client gave up and just keeps iterating (and keeps reporting progress)
  // until it finishes on its own — exactly the "still running in the
  // background" behaviour reported. Now this also POSTs to the new
  // /api/run/stop/<run_id> endpoint, which flips a flag the optimizer polls
  // once per iteration (see fitting_stop_store.py / optimizer.py's
  // cancel_check) and actually halts the loop server-side.
  const handleStopFitting = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (runId) {
      axios.post(`${API_BASE}/run/stop/${runId}`, null, {
        headers: token ? { Authorization: token } : undefined,
      }).then((r) => {
        console.info('[stop] server acknowledged stop for run', runId, r && r.data);
      }).catch((e) => {
        console.warn('[stop] POST /api/run/stop failed for run', runId, e && (e.message || e));
        // Best-effort — the client-side abort() above already stops this
        // tab from waiting on/showing the run either way, so a failed stop
        // call here (network hiccup, run already finished, etc.) shouldn't
        // block the UI or need a user-facing error.
      });
    }
  };

  // ── Reinitialize (client requirement, Run Controls gap) ──────────────────
  // "Reset" (ResetConfirmModal → window.location.reload(), above) is
  // deliberately a full restart: it wipes everything, including uploaded
  // datasets, for starting on a brand new system. That's fine for that case
  // but was previously the ONLY reset-like action available, so a user who
  // just wanted to try a clean optimization pass on the SAME data — say,
  // after experimenting with custom weights/iteration settings — had no
  // option that didn't also throw away their upload.
  // Reinitialize fills that gap: it restores the model/optimization inputs
  // (species weights, exponent mode, iteration budget, initial guesses,
  // reaction orders, current result) to system defaults, but never touches
  // `file` / `globalRuns` / the reaction system or stoichiometry — so the
  // uploaded data survives and the user can just click Run Fitting again.
  const handleReinitialize = () => {
    // 13-Aug-2026 FIX — Reinitialize is documented (and expected by users)
    // to let you adjust settings and re-run without losing data. But if a
    // fit was actively running when clicked, the old backend kept grinding
    // away on the pre-Reinitialize settings in the background — the exact
    // same real-cancellation gap Stop had. Reuse the same stop call so
    // Reinitialize genuinely halts any in-flight run before resetting.
    if (loading) {
      handleStopFitting();
    }
    setSpeciesWeights(prev => ({ ...prev, ...balancedWeightsForSystem(reactionSystem) }));
    setSelectedPreset(DEFAULT_WEIGHT_PRESET);
    setExponentMode('fixed_1');
    setOptimizeExponents(true);
    setMaxIterations(100);
    setR2Target(0.90);
    setWarmStart(false);
    setReactionOrders(Array.from({ length: 10 }, () => ({ a: 1.0, b: 0.5 })));
    setUseInitialGuess(false);
    setInitialGuesses([{ A: '', Ea: '' }, { A: '', Ea: '' }, { A: '', Ea: '' }]);
    setExcludedRows(new Set());
    // 18-Aug-2026 FIX — selectedDataSets ("Data Sets for Regression"
    // checkboxes) is persisted state that was never being reset anywhere.
    // Since it stores specific run IDs (empty array means "all selected",
    // not literally "none"), a stale ID left over from a previous session
    // could silently leave 0 or the wrong subset selected once new runs
    // (fresh Date.now() IDs) are added, with no visual cue why. Clearing it
    // here — a fitting *setting*, not the uploaded data itself, so this
    // belongs in Reinitialize's scope — restores the "all selected" default.
    setSelectedDataSets([]);
    // BUG FIX (24-Sep-2026 — Madhura: "requires a full system reset" to
    // recover from the manual-entry runtime error): if a manual-entry
    // interaction ever fails partway (the item #1 crash above, or any future
    // one), manualEntryTargetRunId/showManualEntry could be left pointing at
    // a run/open state the user can no longer act on normally. Reinitialize
    // is the documented, expected in-app recovery action ("adjust settings
    // and re-run without losing data" per the popup below) — it should be
    // ABLE to clear this, not just fitting-parameter state, so a stuck
    // manual-entry modal doesn't force a full page reload to escape.
    setManualEntryTargetRunId(null);
    setShowManualEntry(false);
    // [FIX, Run Controls gap] Previously: setResult(null) here. That's the
    // same bug the Run/Re-run fix above addressed, just for Reinitialize:
    // the Optimise tab renders `if (!result) return <"Complete an initial
    // fitting run first" placeholder>` — and that placeholder branch is the
    // ONLY place the tab's Stop / Reinitialize / Re-run Fitting actions live.
    // So nulling result the instant Reinitialize was clicked took those same
    // buttons away with it, forcing the user back to the Input tab just to
    // run again. The previous result is now left in place (it's clearly
    // stale once the popup below tells the user settings were reset) and
    // gets replaced the normal way once a new run completes.
    setPopup({
      show: true,
      message: 'Model inputs reinitialized to defaults. Your uploaded data was kept — adjust settings and click Run Fitting (or Re-run Fitting) when ready.',
      type: 'info',
    });
  };

  // ── Reset (full, in place) ───────────────────────────────────────────────
  // Previously Reset = window.location.reload(). Because useInputSession
  // restores the saved session on every mount, the reload brought back the
  // previous fit run's inputs and selections (selectedDataSets, excludedRows,
  // speciesWeights, reaction fields, uploaded data, last result...). Reset
  // now returns every persisted input to its first-load default and clears
  // the run/result state in place; the resulting blank values are then what
  // the session autosave stores, so a later refresh restores a clean page.
  // KEEP IN SYNC with the usePersistedState defaults above.
  // The component registry (species catalogue) is left alone — it is
  // DB-owned and refilled from /api/species-catalog, not a run selection.
  const handleResetAll = () => {
    // Invalidate an in-flight restore before clearing any fields.
    setResetVersion(v => v + 1);
    clearFormState();
    if (loading) handleStopFitting();

    // ── Persisted form fields → first-load defaults ──────────────────────
    setReactionSystem('custom');
    setFitMode('keq_literature');
    setReactorMode('batch');
    setConcentrationBasis('mole_fraction');
    setReactionPhase('liquid');
    setReactionPressure('1');
    setPressureUnit('atm');
    setFile(null);
    setDataPreview(null);
    setAutoDetect({ system: null, runNumber: null, matched: false, label: '', source: '', warning: '' });
    setTempWarning('');
    setDataSourceType(null);
    setMoleFractionWarnings([]);
    setNormalizedCsvBlob(null);
    setNMixMol('6.0');
    setWCatG('24.0');
    setRateBasis('cat_weight');
    setVRxL('0.25');
    setFitScope('single');
    setGlobalRuns([]);
    setOptimizeExponents(true);
    setReversibility('reversible');
    setModelType('powerlaw');
    setExponentMode('fixed_1');
    setKeqMode('regress');
    setKeqUserValues(Array.from({ length: 10 }, () => ({ X: '', Y: '' })));
    setCustomLhhwKiValues({});
    setReactionTempC('25');
    setRxn1Reactants(''); setRxn1Products('');
    setRxn2Reactants(''); setRxn2Products('');
    setRxn3Reactants(''); setRxn3Products('');
    setRxn4Reactants(''); setRxn4Products('');
    setRxn5Reactants(''); setRxn5Products('');
    setRxn6Reactants(''); setRxn6Products('');
    setRxn7Reactants(''); setRxn7Products('');
    setRxn8Reactants(''); setRxn8Products('');
    setRxn9Reactants(''); setRxn9Products('');
    setRxn10Reactants(''); setRxn10Products('');
    setInertComponents('');
    setCatWtUnit('g');
    setMaxIterations(100);
    setR2Target(0.90);
    setWarmStart(false);
    setSelectedDataSets([]);
    setReactionOrders(Array.from({ length: 10 }, () => ({ a: 1.0, b: 0.5 })));
    setUseInitialGuess(false);
    setInitialGuesses([{ A: '', Ea: '' }, { A: '', Ea: '' }, { A: '', Ea: '' }]);
    setSpeciesWeights({
      MeOAc: 1, HOAc: 1, MeOH: 1, H2O: 1,
      DME: 1,
      C2H6: 1, O2: 1, AA: 1, ET: 1, CO2: 1, N2: 0,
    });
    setSelectedPreset(DEFAULT_WEIGHT_PRESET);
    setExcludedRows(new Set());
    setSpeciesIdentityMap({});
    setAaGlobalRuns([]);

    // ── Saved session (backend) ──────────────────────────────────────────
    // Wipe the saved form + last run for the system currently loaded, now,
    // so a refresh (or any later load that lands on it) can't restore the
    // previous fit run's selections. Saved fits are not affected.
    FitRecords.clearSessionState();

    // ── Run / result state (module-level fittingSessionStore) ────────────
    FittingSession.setFittingSessionState({ runMeta: null, abortController: null });
    setRunId(null);
    setRunStartedAt(null);
    setBackendProgress(null);
    setResult(null);
    setLoading(false);
    setPopup(p => ({ ...p, show: false }));

    // ── Local UI state that also carried the previous run ────────────────
    setActivePanel(0);
    setActivePlot('');
    setManualSystem('meoac');
    setManualRows(Array.from({ length: 5 }, () => emptyManualRow('meoac')));
    setManualEntryTargetRunId(null);
    setShowManualEntry(false);
    // PanelInput resyncs its visible reaction-row count off this tick.
    setExampleLoadTick(t => t + 1);
  };

  const canonicalizeStoichSide = (side, scope) => {
    return String(side || '').split('+').map((term, tokenIdx) => {
      const raw = term.trim();
      if (!raw) return '';
      const parsed = parseStoicTermText(raw);
      const coeff = parsed.coeff !== 1 ? `${parsed.coeff} ` : '';
      const species = parsed.species;

      const selectedKey = getIdentityForToken(speciesIdentityMap, scope, tokenIdx);
      if (selectedKey) return `${coeff}${selectedKey}`;

      const candidates = findSpeciesCandidates(species, registeredComponents);
      if (candidates.length === 1) return `${coeff}${candidates[0].id}`;
      if (candidates.length > 1) {
        throw new Error(
          `Multiple species match "${species}". Please select the intended compound from the dropdown.`
        );
      }
      return raw;
    }).filter(Boolean).join(' + ');
  };

  const buildCanonicalStoichiometry = () => currentReactionRows
    .map(r => ({
      ...r,
      reversible: reversibility === 'reversible',
      reactantScope: `r${r.index}.reactants`,
      productScope: `r${r.index}.products`,
    }))
    .filter(r => String(r.reactants || '').trim() && String(r.products || '').trim())
    .map(r => ({
      reactants: canonicalizeStoichSide(r.reactants, r.reactantScope),
      products: canonicalizeStoichSide(r.products, r.productScope),
      reversible: r.reversible,
    }));

  const handleSubmit = async () => {
    const rxnsToCheck = reactionSystem === 'acetic_acid'
      ? currentReactionRows.slice(0, 3).map((r, idx) => ({ ...r, label: `Reaction ${idx + 1}` }))
      : reactionSystem === 'custom'
        ? currentReactionRows.map((r, idx) => ({ ...r, label: `Reaction ${idx + 1}` }))
        : [{ label: 'Reaction 1', reactants: rxn1Reactants, products: rxn1Products, index: 1 }];

    const incompleteReaction = rxnsToCheck.find(r => {
      const hasReactants = Boolean(String(r.reactants || '').trim());
      const hasProducts = Boolean(String(r.products || '').trim());
      return hasReactants !== hasProducts;
    });
    if (incompleteReaction) {
      setPopup({
        show: true,
        message: `${incompleteReaction.label}: enter both reactants and products, or clear the unused reaction row.`,
        type: 'error',
        errorCode: 'input_incomplete',
      });
      return;
    }

    // The optimizer's per-reaction parameters follow the serialized reaction
    // order. Do not silently compress a blank middle row (e.g. Reaction 1 is
    // empty but Reaction 2 is filled), or the UI's Reaction 2 orders would be
    // applied to optimizer Reaction 1. Trailing blank rows remain valid.
    if (reactionSystem === 'custom') {
      const populated = currentReactionRows.map(r =>
        Boolean(String(r.reactants || '').trim() || String(r.products || '').trim())
      );
      const lastPopulated = populated.lastIndexOf(true);
      const gapIndex = populated.slice(0, lastPopulated).findIndex(v => !v);
      if (gapIndex >= 0) {
        const laterIndex = populated.findIndex((v, i) => i > gapIndex && v);
        setPopup({
          show: true,
          message: `Reaction ${gapIndex + 1} is empty, but Reaction ${laterIndex + 1} contains an equation. Fill or clear reactions in order so fitted parameters stay aligned with their reaction numbers.`,
          type: 'error',
          errorCode: 'reaction_order_gap',
        });
        return;
      }
    }

    const balanceIssues = rxnsToCheck
      .map((r, i) => ({
        ...r,
        check: checkReactionBalance(
          r.reactants,
          r.products,
          registeredComponents,
          `r${r.index || i + 1}.reactants`,
          `r${r.index || i + 1}.products`,
          speciesIdentityMap,
        ),
      }))
      .filter(r => !r.check.incomplete && !r.check.balanced);

    if (balanceIssues.length > 0) {
      const detail = balanceIssues.map(r => {
        if (r.check.ambiguousSpecies?.length > 0) {
          return `${r.label}: ambiguous species (${r.check.ambiguousSpecies.join(', ')}) — select the intended compound from the dropdown`;
        }
        if (r.check.unresolvedSpecies.length > 0) {
          return `${r.label}: unrecognized species (${r.check.unresolvedSpecies.join(', ')}) — add to Component Registry`;
        }
        return `${r.label}: ${r.reactants} → ${r.products} — atom imbalance ${r.check.unbalancedAtoms.join(', ')}`;
      }).join('  |  ');
      setPopup({
        show: true,
        message: `Cannot run: equation not balanced. ${detail}`,
        type: 'error',
        errorCode: 'stoichiometry_imbalance',
      });
      return;
    }

    // ── Run Fitting UX: start the real elapsed-time clock, compute the
    // upfront runtime estimate that paces the progress bar, and set up a
    // fresh AbortController so the Stop button can cancel this specific
    // in-flight request. ─────────────────────────────────────────────────
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setEstimatedRuntimeSec(estimateRuntimeSeconds());
    setRunStartedAt(Date.now());
    // 10-Aug-2026 ADD — one run_id per submit, sent to the backend so it can
    // report real progress to /api/run/progress/<run_id> (see the polling
    // effect above and openmkm_api.py). crypto.randomUUID() is a native
    // browser API — no new dependency needed.
    const newRunId = crypto.randomUUID();
    setRunId(newRunId);
    setBackendProgress(null);
    // [FIX, Run Controls gap] Previously: setLoading(true); setResult(null);
    // — nulling result here blanked the Output/Optimize panels (and, if the
    // run was kicked off from the Optimise tab, that very tab's own content)
    // the instant Run/Re-run Fitting was clicked. The prior result is kept
    // visible (each panel already reads `loading` alongside `result` to show
    // a spinner/disabled state) until this run's result replaces it below.
    setLoading(true);
    setPopup(p => ({ ...p, show: false }));
    FittingSession.setFittingSessionState({
      runMeta: {
        reactionSystem,
        fitScope,
        label: fitScope === 'multiple' ? 'multi-dataset fit' : 'fit',
      },
    });

    try {
      // ── AA "Multiple data sets": 2+ separately-uploaded CSVs (Madhura, 24-Jun-2026, item i) ──
      if (fitScope === 'multiple' && reactionSystem === 'acetic_acid') {
        // 18-Aug-2026 FIX (client-reported bug) — selectedDataSets (the
        // "Data Sets for Regression" checkboxes on the Optimise tab) was
        // previously read ONLY to render each checkbox's checked state; it
        // was never consulted here, so unchecking a dataset and clicking
        // Re-run Fitting silently re-fit (and reported results for) every
        // uploaded CSV anyway — exactly the "excluded 1 of 2, still got 2
        // dataset results" bug. dsId here mirrors the checkbox's own
        // `run.id ?? idx` derivation exactly, so a run's checked/unchecked
        // state matches what actually gets submitted.
        const runsToFit = selectedDataSets.length === 0
          ? globalRuns
          : globalRuns.filter((r, idx) => selectedDataSets.includes(r.id ?? idx));

        if (globalRuns.length < 2 || globalRuns.some(r => !r.file)) {
          setPopup({ show: true, message: 'Please add at least 2 CSVs and upload a file for each.', type: 'error' });
          setLoading(false); return;
        }
        if (runsToFit.length < 2) {
          // The AA multi-CSV backend endpoint hard-requires 2+ files (see
          // openmkm_api.py's run_aa_multi_csv_global_fitting). Rather than
          // silently falling back to using every dataset again (the original
          // bug) or guessing at a single-CSV re-route, give a clear,
          // actionable message so the user knows exactly what to do.
          setPopup({
            show: true,
            message: runsToFit.length === 0
              ? 'No data sets are selected for regression — check at least 2 boxes under "Data Sets for Regression" and try again.'
              : 'Only 1 data set is selected for regression. Select at least 2 to run a multi-dataset fit, or switch Fitting Scope to "Single data set" to fit just this one.',
            type: 'error',
          });
          setLoading(false); return;
        }

        const formData = new FormData();
        // FIX (Madhura, 17-Jul-2026 call): Reactor Volume typed into each
        // dataset's own row was previously dropped — only the single
        // global right-panel value was sent. The backend
        // (aa_kinetics_objective.py) already reads a per-dataset
        // run_info['V_rx_L'] override; the frontend just wasn't sending it.
        const runMeta = runsToFit.map(r => ({
          label:    r.label || r.fileName,
          W_cat_g:  r.W_cat_g === '' || r.W_cat_g == null ? null : parseFloat(r.W_cat_g),
          V_rx_L:   r.V_rx_L === '' || r.V_rx_L == null ? null : parseFloat(r.V_rx_L),
          weight:   r.weight,
        }));
        runsToFit.forEach(r => formData.append('files[]', r.file));
        formData.append('run_meta', JSON.stringify(runMeta));

        const _aaModeMulti = deriveAAModeNow();
        formData.append('mode', _aaModeMulti);
        formData.append('reaction_type', reversibility);
        formData.append('exponents', exponentMode === 'fixed_1' ? 'fixed' : 'free');
        formData.append('reversibility', reversibility);
        formData.append('model_type', modelType);
        formData.append('exponent_mode', exponentMode);
        formData.append('keq_mode', keqMode);
        if (keqMode === 'user_specified') {
          formData.append('keq_user_values', JSON.stringify(keqUserValues));
        }
        formData.append('rate_basis', rateBasis);
        if (rateBasis === 'volume') formData.append('V_rx_L', vRxL);
        formData.append('species_weights', JSON.stringify(speciesWeights));
        formData.append('r2_target', r2Target);
        formData.append('n_iter', maxIterations);
        formData.append('warm_start', warmStart ? 'true' : 'false');
        formData.append('run_id', newRunId);
        // ── Row exclusion (Jul-08 2026 FIX) ─────────────────────────────────
        // This was missing entirely — checked rows in ResidualTable were
        // never sent to the multi-CSV endpoint, so a re-run always used
        // every row from every CSV regardless of exclusions.
        if (excludedRows.size > 0)
          formData.append('excluded_rows', JSON.stringify([...excludedRows]));

        const res = await axios.post(`${API_BASE}/run/aa-multi-csv-global-fitting`, formData, {
          headers: { Authorization: token },
          signal: controller.signal,
        });
        const d = res.data;
        setResult(d);
        setExcludedRows(new Set());
        setActivePlot('Overview');
        setActivePanel(1);
        setPopup({ show: true, message: `Run Multi-Data Set Fit Completed — ${d.n_csvs} CSVs fit to one shared parameter set.`, type: 'success' });

      } else if (fitScope === 'multiple' && reactionSystem === 'custom') {
        // ── Generic Custom Multi-Dataset fitting ────────────────────────────
        // All selected datasets share ONE reaction definition / parameter
        // vector. Dataset-specific operating conditions remain independent
        // (Batch: T, n_mix, W/V; PFR: fallback T/P plus V/W, with row-level
        // T/P/tau taking precedence when present in the CSV).
        const runsToFit = selectedDataSets.length === 0
          ? globalRuns
          : globalRuns.filter((r, idx) => selectedDataSets.includes(r.id ?? idx));

        if (globalRuns.length < 2) {
          setPopup({
            show: true,
            message: 'Add at least 2 data sets before starting a custom multi-dataset fit.',
            type: 'error', errorCode: 'input_incomplete',
          });
          setLoading(false); return;
        }
        if (runsToFit.length < 2) {
          setPopup({
            show: true,
            message: runsToFit.length === 0
              ? 'No data sets are selected for regression — select at least 2 data sets.'
              : 'Only 1 data set is selected. Select at least 2 data sets for a multi-dataset fit.',
            type: 'error', errorCode: 'multi_dataset_selection',
          });
          setLoading(false); return;
        }
        if (runsToFit.some(r => !r.file)) {
          setPopup({
            show: true,
            message: 'Every selected data set must have a CSV file or completed manual entry.',
            type: 'error', errorCode: 'input_incomplete',
          });
          setLoading(false); return;
        }

        // Important multi-dataset metadata validation: every selected run must
        // have a unique label and valid reactor-specific scaling inputs. This
        // prevents a partial/ambiguous payload from reaching the optimizer.
        const normalizedLabels = runsToFit.map((r, i) =>
          String(r.label || r.fileName || `Data Set ${i + 1}`).trim().toLowerCase()
        );
        if (new Set(normalizedLabels).size !== normalizedLabels.length) {
          setPopup({
            show: true,
            message: 'Each selected data set must have a unique label/file name.',
            type: 'error', errorCode: 'multi_dataset_metadata',
          });
          setLoading(false); return;
        }

        const invalidRun = runsToFit.find((r) => {
          const n = Number(r.n_mix_mol);
          const w = Number(r.W_cat_g);
          const v = Number(r.V_rx_L);
          const t = Number(r.T_C);
          const p = Number(r.P_psi);
          if (reactorMode === 'batch') {
            if (!Number.isFinite(t) || t + 273.15 <= 0) return true;
            if (!Number.isFinite(n) || n <= 0) return true;
            if (rateBasis === 'cat_weight') return !Number.isFinite(w) || w <= 0;
            return !Number.isFinite(v) || v <= 0;
          }
          // PFR n_mix is derived per row from P·V/R·T. V is therefore always
          // required. W is required only for catalyst-weight rate constants.
          if (!Number.isFinite(v) || v <= 0) return true;
          if (rateBasis === 'cat_weight' && (!Number.isFinite(w) || w <= 0)) return true;
          // Fallback T/P are optional when every CSV row provides its own
          // valid T/Pressure; backend validation gives the authoritative
          // row-aware error when a fallback is actually required.
          if (r.T_C !== '' && r.T_C != null && (!Number.isFinite(t) || t + 273.15 <= 0)) return true;
          if (r.P_psi !== '' && r.P_psi != null && (!Number.isFinite(p) || p <= 0)) return true;
          return false;
        });
        if (invalidRun) {
          setPopup({
            show: true,
            message: reactorMode === 'batch'
              ? 'Every selected Batch data set needs valid T, Initial Charge, and the selected rate-basis sizing input.'
              : 'Every selected PFR data set needs a positive Reactor Volume, and Catalyst Weight when Rate Basis is Catalyst weight. Optional fallback T/Pressure must be positive when entered.',
            type: 'error', errorCode: 'multi_dataset_conditions',
          });
          setLoading(false); return;
        }

        let stoichiometryPayload;
        try {
          stoichiometryPayload = buildCanonicalStoichiometry();
        } catch (identityErr) {
          setPopup({ show: true, message: identityErr.message, type: 'error', errorCode: 'species_ambiguous' });
          setLoading(false);
          return;
        }
        if (stoichiometryPayload.length === 0) {
          setPopup({ show: true, message: 'Define at least one reaction (reactants + products) in Components & Stoichiometry first.', type: 'error', errorCode: 'input_incomplete' });
          setLoading(false); return;
        }

        const submitInertSpeciesKeys = getInertSpeciesKeys(reactionSystem, inertComponents);
        const effectiveSpeciesWeights = buildEffectiveSpeciesWeights(
          activeSpecies,
          speciesWeights,
          submitInertSpeciesKeys,
        );
        const hasPositiveReactiveWeight = activeSpecies
          .filter(sp => !submitInertSpeciesKeys.some(inert => normalizeSpeciesToken(inert) === normalizeSpeciesToken(sp)))
          .some(sp => Number(effectiveSpeciesWeights?.[sp]) > 0);
        if (!hasPositiveReactiveWeight) {
          setPopup({
            show: true,
            message: 'At least one reactive species must have a positive Objective Function Weight before a multi-dataset fit can run.',
            type: 'error', errorCode: 'invalid_weights',
          });
          setLoading(false); return;
        }

        // Build the actual browser upload objects immediately before the request.
        // Manual datasets are stored in plain-text `manualCsvText` as the durable
        // source of truth; a File object can be lost/emptied by session hydration,
        // refresh, or older persisted state. Rebuilding it here guarantees that the
        // multipart request contains the exact rows the user entered.
        const uploadFiles = [];
        for (let i = 0; i < runsToFit.length; i += 1) {
          const run = runsToFit[i];
          const label = String(run.label || run.fileName || `Data Set ${i + 1}`).trim();

          // Durable manual-data sources, in priority order:
          //   1) manualCsvText committed by the Manual Entry modal;
          //   2) manualRowsSnapshot committed with the same run;
          //   3) an actual uploaded File selected from disk.
          // This deliberately does NOT trust the File object alone because
          // File instances disappear during session hydration/refresh.
          let uploadText = '';
          let uploadName = run.fileName || `dataset_${i + 1}.csv`;

          if (typeof run.manualCsvText === 'string' && run.manualCsvText.trim()) {
            uploadText = run.manualCsvText;
          } else if (Array.isArray(run.manualRowsSnapshot) && run.manualRowsSnapshot.length > 0) {
            // Last-resort reconstruction for older/session-restored records
            // that retained the row snapshot but not manualCsvText.
            const cfg = getManualEntryConfig('custom', activeSpecies, reactorMode);
            const enteredRows = reactorMode === 'pfr'
              ? run.manualRowsSnapshot.filter(r => r && (r.T !== '' || r.Pressure !== ''))
              : run.manualRowsSnapshot.filter(r => r && r.time !== '');
            const rows = enteredRows.map(r => cfg.fields.map(f => r?.[f] ?? '').map(v => v === '' ? '0' : v).join(','));
            if (rows.length > 0) {
              uploadText = [cfg.csvHeader.join(','), ...rows].join('\n');
              uploadName = uploadName || `manual_custom_run_${i + 1}.csv`;
            }
          }

          if (!uploadText) {
            const fileCandidate = run.file;
            if (!fileCandidate || typeof fileCandidate.text !== 'function') {
              throw new Error(`${label}: no uploadable CSV is attached. Reopen "Enter Manually" and click "Use This Data".`);
            }
            uploadText = await fileCandidate.text();
            uploadName = fileCandidate.name || uploadName;
          }

          // Never send an empty dataset. Validate the actual CSV text that will
          // be uploaded, not merely a File object or filename.
          const csvLines = uploadText
            .replace(/^\uFEFF/, '')
            .split(/\r?\n/)
            .filter(line => line.trim() !== '');
          if (csvLines.length < 2) {
            throw new Error(`${label}: the CSV contains no data rows. Reopen "Enter Manually", verify the rows, and click "Use This Data".`);
          }

          // Rebuild from the exact validated text so the bytes checked here are
          // the same bytes sent to Flask/Werkzeug.
          uploadFiles.push(new File([uploadText], uploadName, { type: 'text/csv' }));
        }

        const formData = new FormData();
        // Same chemistry metadata contract as the single custom endpoint.
        // One canonical species identity payload is shared by every dataset;
        // the backend resolves/normalizes each CSV against this exact set.
        const reactionIdentitySpecs = {};
        const reactionSidesForIdentity = currentReactionRows.flatMap(r => [
          [`r${r.index}.reactants`, r.reactants],
          [`r${r.index}.products`, r.products],
        ]);
        reactionSidesForIdentity.forEach(([scope, side]) => {
          String(side || '').split('+').forEach((term, tokenIdx) => {
            const raw = term.trim();
            if (!raw) return;
            const token = parseStoicTermText(raw).species;
            if (!token) return;
            const selectedKey = getIdentityForToken(speciesIdentityMap, scope, tokenIdx);
            const selected = selectedKey ? componentBySpeciesKey(selectedKey, registeredComponents) : null;
            const candidates = findSpeciesCandidates(token, registeredComponents);
            const component = selected || (candidates.length === 1 ? candidates[0] : null);
            if (!component) return;
            const key = component.species_key || component.id;
            if (!key) return;
            const baseAliases = Array.isArray(component.aliases) ? component.aliases : [];
            const existing = reactionIdentitySpecs[key] || {
              species_key: key, species_name: component.name, formula: component.formula,
              cas_number: component.cas, aliases: [...baseAliases],
            };
            if (token && normalizeSpeciesToken(token) !== normalizeSpeciesToken(component.name)) {
              existing.aliases.push(token);
            }
            reactionIdentitySpecs[key] = existing;
          });
        });
        const speciesIdentityPayload = Object.values(reactionIdentitySpecs).map(row => ({
          ...row, aliases: [...new Set((row.aliases || []).filter(Boolean))],
        }));
        if (speciesIdentityPayload.length > 0) {
          formData.append('species_identity', JSON.stringify(speciesIdentityPayload));
        }
        const selectedSpeciesKeys = new Set(speciesIdentityPayload.map(row => String(row.species_key || '').trim()).filter(Boolean));
        const inertSpeciesList = (inertComponents || '').split(/[,+]/).map(s => s.trim()).filter(Boolean);
        const selectedReactionTokens = reactionSidesForIdentity
          .map(([, side]) => String(side || '').split('+'))
          .flat()
          .map(term => term.trim())
          .filter(Boolean)
          .map(raw => {
            return parseStoicTermText(raw).species;
          });
        const componentNameMap = {};
        const selectedComponentKeys = new Set([...selectedSpeciesKeys, ...inertSpeciesList]);
        registeredComponents.forEach(c => {
          const key = String(c?.species_key || c?.id || '').trim();
          if (!c?.name || !key) return;
          const selectedByKey = selectedComponentKeys.has(key);
          const selectedByToken = selectedReactionTokens.some(token => {
            const n = normalizeSpeciesToken(token);
            return [c.species_key, c.id, c.formula, c.cas, c.name, ...(Array.isArray(c.aliases) ? c.aliases : [])]
              .filter(Boolean).some(v => normalizeSpeciesToken(v) === n);
          });
          if (!selectedByKey && !selectedByToken) return;
          [c.id, c.species_key, c.formula, c.cas, c.name, ...(Array.isArray(c.aliases) ? c.aliases : [])]
            .filter(Boolean).forEach(alias => { componentNameMap[String(alias)] = c.name; });
        });
        if (Object.keys(componentNameMap).length > 0) {
          const componentNamesJson = JSON.stringify(componentNameMap);
          if (componentNamesJson.length <= 200000) formData.append('component_names', componentNamesJson);
        }
        if (inertSpeciesList.length > 0) formData.append('inert_components', JSON.stringify(inertSpeciesList));

        // The one chemistry definition and one parameterization are shared by
        // ALL selected datasets. Only experimental data and operating metadata
        // vary from dataset to dataset.
        formData.append('reaction_system', 'custom');
        formData.append('stoichiometry', JSON.stringify(stoichiometryPayload));
        formData.append('model_type', modelType);
        formData.append('reactor_mode', reactorMode);
        formData.append('exponent_mode', exponentMode);
        formData.append('keq_mode', keqMode);
        if (keqMode === 'user_specified') formData.append('keq_user_values', JSON.stringify(keqUserValues));
        formData.append('rate_basis', rateBasis);
        formData.append('concentration_basis', concentrationBasis);
        formData.append('species_weights', JSON.stringify(effectiveSpeciesWeights));
        formData.append('r2_target', r2Target);
        formData.append('n_iter', maxIterations);
        formData.append('warm_start', warmStart ? 'true' : 'false');
        // User-Specified exponent mode means "use these a/b values as the
        // initial BO seed"; the backend still regresses the exponents.
        formData.append('reaction_orders', JSON.stringify(reactionOrders));
        if (modelType === 'lhhw') {
          const kiPayload = Object.fromEntries(
            Object.entries(customLhhwKiValues)
              .filter(([, v]) => v !== '' && v != null && !Number.isNaN(parseFloat(v)))
              .map(([k, v]) => [k, parseFloat(v)])
          );
          formData.append('lhhw_ki_values', JSON.stringify(kiPayload));
        }

        const runMeta = runsToFit.map((r, i) => ({
          dataset_index: i,
          label: String(r.label || r.fileName || `Data Set ${i + 1}`).trim(),
          T_C: r.T_C === '' || r.T_C == null ? null : Number(r.T_C),
          n_mix_mol: r.n_mix_mol === '' || r.n_mix_mol == null ? null : Number(r.n_mix_mol),
          W_cat_g: r.W_cat_g === '' || r.W_cat_g == null ? null : Number(r.W_cat_g),
          V_rx_L: r.V_rx_L === '' || r.V_rx_L == null ? null : Number(r.V_rx_L),
          P_psi: r.P_psi === '' || r.P_psi == null ? null : Number(r.P_psi),
          // P_atm is retained only for legacy Batch multi-dataset routes.
          // Custom PFR uses the explicit P_psi field or the global pressure
          // fallback; sending a default 1 atm here could otherwise override
          // an intentionally blank PFR pressure fallback.
          ...(reactorMode === 'batch' ? {
            P_atm: r.P_atm === '' || r.P_atm == null ? null : Number(r.P_atm),
          } : {}),
          weight: r.weight === '' || r.weight == null ? 1 : Number(r.weight),
        }));
        uploadFiles.forEach(f => formData.append('files[]', f, f.name));
        formData.append('run_meta', JSON.stringify(runMeta));
        // Keep fallbacks for datasets created before the PFR manager became
        // reactor-aware; the backend's per-dataset meta is authoritative.
        if (reactorMode === 'batch') {
          formData.append('reaction_temp_c', reactionTempC);
          formData.append('n_mix_mol', nMixMol);
          formData.append('W_cat_g', convertWeight(wCatG, catWtUnit, 'g'));
          if (rateBasis === 'volume') formData.append('V_rx_L', vRxL);
        } else {
          formData.append('reaction_temp_c', reactionTempC);
          formData.append('W_cat_g', convertWeight(wCatG, catWtUnit, 'g'));
          formData.append('V_rx_L', vRxL);
          formData.append('pressure', reactionPressure);
          formData.append('pressure_unit', pressureUnit);
        }
        if (excludedRows.size > 0) formData.append('excluded_rows', JSON.stringify([...excludedRows]));
        formData.append('run_id', newRunId);

        // Backend contract: files[] contains the validated CSV bytes, while
        // run_meta supplies per-dataset operating conditions.
        const res = await axios.post(`${API_BASE}/run/generic-multi-fitting`, formData, {
          headers: { Authorization: token },
          signal: controller.signal,
        });
        const d = res.data;
        setResult(d);
        setExcludedRows(new Set());
        setActivePlot('Overview');
        setActivePanel(1);
        setPopup({
          show: true,
          message: `Custom multi-dataset fit completed — ${d.n_runs || runsToFit.length} data sets fitted to one shared parameter set.`,
          type: 'success',
        });

      } else if (fitScope === 'multiple') {
        // ── DME / MeOAc "Multiple data sets": multi-run CSVs via GlobalRunManager ──
        // (AA "Multiple data sets" is fully handled by the branch above, and
        // custom systems are fully handled by the guard branch above this one —
        // reaching here with reactionSystem === 'acetic_acid' or 'custom' can't happen.)
        if (globalRuns.length === 0 || globalRuns.some(r => !r.file)) {
          setPopup({ show: true, message: 'Please add and upload a CSV for each run.', type: 'error', errorCode: 'input_incomplete' });
          setLoading(false); return;
        }

        // 18-Aug-2026 FIX (client-reported bug, same root cause as the AA
        // multi-CSV branch above) — selectedDataSets was never consulted
        // here either, so unchecking a run in "Data Sets for Regression"
        // and re-running still fit every uploaded CSV.
        const runsToFit = selectedDataSets.length === 0
          ? globalRuns
          : globalRuns.filter((r, idx) => selectedDataSets.includes(r.id ?? idx));
        // 18-Aug-2026 FIX (round 2) — previously only required >=1 here,
        // which is what let a "Multiple data sets" fit actually run against
        // just 1 selected dataset (client screenshot: 1 of 3 checked,
        // "Running optimization…" proceeded anyway). Multi-dataset scope now
        // requires >=2 selected everywhere, matching AA multi-CSV's rule —
        // the canRun check above already disables the button for this case,
        // this is the server-request-time backstop.
        if (runsToFit.length < 2) {
          setPopup({
            show: true,
            message: runsToFit.length === 0
              ? 'No data sets are selected for regression — check at least 2 boxes under "Data Sets for Regression" and try again.'
              : 'Only 1 data set is selected for regression. Select at least 2 to run a multi-dataset fit, or switch Fitting Scope to "Single data set" to fit just this one.',
            type: 'error',
          });
          setLoading(false); return;
        }

        const formData = new FormData();
        // FIX (Madhura, 17-Jul-2026 call — "there might be a different...
        // reaction volume value" per data set): Reactor Volume typed into
        // this run's own "Reactor Volume (L)" field was previously dropped
        // entirely — only the single global right-panel value (vRxL) was
        // ever sent, so a per-run override here silently did nothing. It's
        // now included per run, with the global value as a fallback for
        // any run that leaves it blank (backend: boa_kinetics_objective.py,
        // kinetics_objective_meoac_global / _dme_global).
        const runMeta = runsToFit.map(r => ({
          T_C: r.T_C, n_mix_mol: r.n_mix_mol,
          V_rx_L: (r.V_rx_L === '' || r.V_rx_L == null) ? vRxL : r.V_rx_L,
          W_cat_g: r.W_cat_g, weight: r.weight,
          // ADD (Madhura, 24-Aug-2026 doc, item 5) — Pressure is now
          // collected per run (see GlobalRunManager's new "Pressure (atm)"
          // field); included here for parity with T_C/n_mix_mol/W_cat_g/
          // V_rx_L even though the current Batch ODE (boa_kinetics_objective.py)
          // doesn't yet consume a per-run pressure — same "captured, not
          // yet required by the physics" status the single-dataset Reaction
          // Pressure field already has for Batch mode.
          P_atm: (r.P_atm === '' || r.P_atm == null) ? null : r.P_atm,
        }));
        runsToFit.forEach(r => formData.append('files[]', r.file));
        formData.append('run_meta', JSON.stringify(runMeta));
        // [FIX] Derive mode fresh at submit time (see deriveAAModeNow) rather
        // than trusting fitMode directly — same stale-mode bug as the
        // single-dataset branch below, just for the "Multiple data sets" path.
        formData.append('mode', deriveAAModeNow());
        // ── Model Input parameters (PDF-aligned) ─────────────────────────
        formData.append('reversibility', reversibility);
        formData.append('model_type', modelType);
        formData.append('exponent_mode', exponentMode);
        formData.append('keq_mode', keqMode);
        if (keqMode === 'user_specified') {
          formData.append('keq_user_values', JSON.stringify(keqUserValues));
        }
        formData.append('reaction_orders', JSON.stringify(reactionOrders));
        formData.append('rate_basis', rateBasis);
        formData.append('V_rx_L', vRxL);
        // ── Lever 3: species weights ─────────────────────────────────────────
        Object.entries(speciesWeights).forEach(([sp, w]) => {
          formData.append(`wt_${sp.toLowerCase()}`, w);
        });
        formData.append('species_weights', JSON.stringify(speciesWeights));
        // ── Lever 1: R² target ──────────────────────────────────────────────
        formData.append('r2_target', r2Target);
        // ── Lever 2: Max iterations ─────────────────────────────────────────
        formData.append('n_iter', maxIterations);
        // ── Lever 4: warm start + initial guesses ───────────────────────────
        formData.append('warm_start', warmStart ? 'true' : 'false');
        if (useInitialGuess && initialGuesses.length > 0) {
          formData.append('initial_guesses', JSON.stringify(initialGuesses));
        }
        // ── Row exclusion (09-Aug-2026 — generalised from AA-only) ──────────
        // This was previously only sent on the AA multi-CSV and single-
        // dataset (AA/MeOAc/DME) branches — a DME/MeOAc "Multiple data sets"
        // re-run always used every row from every CSV regardless of what was
        // checked in ResidualTable, because this field was never appended
        // here at all. 'excludedRows' holds GLOBAL 1-indexed row numbers
        // across the concatenated CSVs (same numbering the combined parity
        // table uses), matching what the backend's
        // apply_row_exclusions_multi() expects.
        if (excludedRows.size > 0)
          formData.append('excluded_rows', JSON.stringify([...excludedRows]));
        // 10-Aug-2026 ADD, 13-Aug-2026 backend wiring completed — dme-global-
        // fitting/meoac-global-fitting now call set_progress() the same way
        // aa-fitting/generic-fitting do, so this run_id is no longer a no-op:
        // multi-dataset runs now report real "iter X/N" progress too, not
        // just the client-side percentage estimate.
        formData.append('run_id', newRunId);

        const endpoint = reactionSystem === 'dme'
          ? '/run/dme-global-fitting'
          : '/run/meoac-global-fitting';

        const res = await axios.post(`${API_BASE}${endpoint}`, formData, {
          headers: { Authorization: token },
          signal: controller.signal,
        });
        const d = res.data;
        setResult(d);
        setExcludedRows(new Set());   // reset: new result = fresh residual table, no carried-over exclusions
        setActivePlot('Overview');
        setActivePanel(1);
        setPopup({ show: true, message: `Run Multi-Data Set Fit Completed — ${d.n_runs} runs processed.`, type: 'success' });

      } else if (reactionSystem === 'custom') {
        // ── Custom user-defined reaction system — supports both Power Law
        // and LHHW rate laws (29-Jul-2026 follow-up; see
        // generic_kinetics_objective.py SECTION 3b). Both reactor modes are
        // supported: 'batch' (mole-fraction time-series ODE) and 'pfr'
        // (per-row steady-state experiments — own tau/T/inlet/outlet per
        // CSV row). See generic_kinetics_objective.py / /api/run/generic-fitting.
        if (!file) { setPopup({ show: true, message: 'Please upload a CSV file first.', type: 'error', errorCode: 'input_incomplete' }); setLoading(false); return; }

        // Only send reactions that actually have both a reactant and a
        // product typed in — rxn2/rxn3 may be blank for a single-reaction
        // system (like propane combustion), and the backend derives the
        // species list purely from whichever of these are populated.
        let stoichiometryPayload;
        try {
          stoichiometryPayload = buildCanonicalStoichiometry();
        } catch (identityErr) {
          setPopup({ show: true, message: identityErr.message, type: 'error', errorCode: 'species_ambiguous' });
          setLoading(false);
          return;
        }

        if (stoichiometryPayload.length === 0) {
          setPopup({ show: true, message: 'Define at least one reaction (reactants + products) in Components & Stoichiometry first.', type: 'error', errorCode: 'input_incomplete' });
          setLoading(false); return;
        }
        // GAP FIX (30-Jul-2026 walkthrough — "Batch ODE scale factor
        // should be absorbed directly into the rate equations, like PFR"):
        // the Reaction Conditions panel collects Catalyst Weight/Reactor
        // Volume for BOTH reactor modes, so both need those sizing inputs.
        // Initial Charge (n_mix), however, is now validated per reactor
        // mode rather than unconditionally — GAP FIX ("Initial charge /
        // initial moles field should not appear at all for PFR", 30-Jul-
        // 2026 walkthrough): PFR derives n_mix per row from the ideal gas
        // law instead of a user-entered scalar (see pfr_scale_factor_row in
        // generic_kinetics_objective.py), so it no longer needs — or shows
        // — this field at all. Batch still genuinely needs it (moles
        // physically charged into the closed vessel).
        if (reactorMode === 'batch' && (!nMixMol || parseFloat(nMixMol) <= 0)) {
          setPopup({ show: true, message: 'Enter a positive Initial Charge (n_mix, mol) — it\'s the total moles charged into the batch reactor, so it can\'t be 0.', type: 'error', errorCode: 'input_incomplete' });
          setLoading(false); return;
        }
        // PFR's ideal-gas n_mix calc always needs Reactor Volume, regardless
        // of Rate Basis (unlike batch, where Volume is only required for a
        // 'volume' rate basis) — see the Reactor Volume field's info tooltip.
        if (reactorMode === 'pfr' && !(vRxL && parseFloat(vRxL) > 0)) {
          setPopup({ show: true, message: 'Enter a positive Reactor Volume — PFR needs it to derive n_mix (P·V/R·T), regardless of Rate Basis.', type: 'error', errorCode: 'input_incomplete' });
          setLoading(false); return;
        }
        if (rateBasis === 'volume' ? !(vRxL && parseFloat(vRxL) > 0) : !(wCatG && parseFloat(wCatG) > 0)) {
          setPopup({
            show: true,
            message: rateBasis === 'volume'
              ? 'Enter a positive Reactor Volume for the "Reaction volume" rate basis.'
              : 'Enter a positive Catalyst Weight for the "Catalyst weight" rate basis.',
            type: 'error', errorCode: 'input_incomplete',
          });
          setLoading(false); return;
        }

        const formData = new FormData();
        formData.append('file', normalizedCsvBlob || file);
        formData.append('reaction_system', 'custom');
        formData.append('stoichiometry', JSON.stringify(stoichiometryPayload));

        // Send metadata for every uniquely-resolved species appearing in the
        // reaction, not only species selected from the dropdown. This makes
        // manual typing and externally-created CSV headers work through the
        // same canonical species_key pipeline.
        const reactionIdentitySpecs = {};
        const reactionSidesForIdentity = currentReactionRows.flatMap(r => [
          [`r${r.index}.reactants`, r.reactants],
          [`r${r.index}.products`, r.products],
        ]);

        reactionSidesForIdentity.forEach(([scope, side]) => {
          String(side || '').split('+').forEach((term, tokenIdx) => {
            const raw = term.trim();
            if (!raw) return;
            const token = parseStoicTermText(raw).species;
            if (!token) return;

            const selectedKey = getIdentityForToken(speciesIdentityMap, scope, tokenIdx);
            const selected = selectedKey
              ? componentBySpeciesKey(selectedKey, registeredComponents)
              : null;
            const candidates = findSpeciesCandidates(token, registeredComponents);
            const component = selected || (candidates.length === 1 ? candidates[0] : null);
            if (!component) return;

            const key = component.species_key || component.id;
            if (!key) return;
            const baseAliases = Array.isArray(component.aliases) ? component.aliases : [];
            const existing = reactionIdentitySpecs[key] || {
              species_key: key,
              species_name: component.name,
              formula: component.formula,
              cas_number: component.cas,
              aliases: [...baseAliases],
            };
            if (token && normalizeSpeciesToken(token) !== normalizeSpeciesToken(component.name)) {
              existing.aliases.push(token);
            }
            reactionIdentitySpecs[key] = existing;
          });
        });

        const speciesIdentityPayload = Object.values(reactionIdentitySpecs).map(row => ({
          ...row,
          aliases: [...new Set((row.aliases || []).filter(Boolean))],
        }));
        if (speciesIdentityPayload.length > 0) {
          formData.append('species_identity', JSON.stringify(speciesIdentityPayload));
        }
        const selectedSpeciesKeys = new Set(speciesIdentityPayload.map(row => String(row.species_key || '').trim()).filter(Boolean));

        // Client feedback (Graph tab, 30-Jul-2026 call): subplot titles for a
        // custom system should show the full component name, never the raw
        // formula.
        //
        // 25-Sep-2026 FIX: only send metadata for species used in THIS reaction.
        // The previous implementation serialized the entire 6k+ species catalog
        // into one multipart form field. That can exceed Flask/Werkzeug's
        // MAX_FORM_MEMORY_SIZE and produce HTTP 413 before the endpoint runs.
        const componentNameMap = {};
        const inertSpeciesListFallback = (inertComponents || '')
          .split(/[,+]/).map(s => s.trim()).filter(Boolean);

        const selectedComponentKeys = new Set([
          ...selectedSpeciesKeys,
          ...inertSpeciesListFallback,
        ].filter(Boolean).map(String));

        const selectedReactionTokens = currentReactionRows
          .flatMap(r => [r.reactants, r.products])
          .flatMap(side => String(side || '').split('+'))
          .map(term => {
            const raw = term.trim();
            if (!raw) return '';
            return parseStoicTermText(raw).species;
          })
          .filter(Boolean);

        registeredComponents.forEach(c => {
          const key = String(c?.species_key || c?.id || '').trim();
          if (!c?.name || !key) return;

          const selectedByKey = selectedComponentKeys.has(key);
          const selectedByToken = selectedReactionTokens.some(token => {
            const n = normalizeSpeciesToken(token);
            return [
              c.species_key, c.id, c.formula, c.cas, c.name,
              ...(Array.isArray(c.aliases) ? c.aliases : [])
            ]
              .filter(Boolean)
              .some(v => normalizeSpeciesToken(v) === n);
          });

          if (!selectedByKey && !selectedByToken) return;

          [c.id, c.species_key, c.formula, c.cas, c.name,
           ...(Array.isArray(c.aliases) ? c.aliases : [])]
            .filter(Boolean)
            .forEach(alias => { componentNameMap[String(alias)] = c.name; });
        });

        if (Object.keys(componentNameMap).length > 0) {
          const componentNamesJson = JSON.stringify(componentNameMap);
          if (componentNamesJson.length <= 200000) {
            formData.append('component_names', componentNamesJson);
          }
        }
        // BUG FIX (22-Sep-2026 — "missing inert component in Graph section,
        // Fit Quality R² section"): inertComponents (the "Inert 1"/"Inert 2"
        // fields) was previously only ever read client-side — to exclude
        // inerts from the Species Weights panel — and never sent to the
        // backend at all, so a declared inert like N2 was completely
        // invisible to /api/run/generic-fitting: no CSV column lookup, no
        // R², no parity-plot tab. generic_kinetics_objective.py now reads
        // this same "N2, Ar" / "N2 + Ar" field (see compute_inert_species_
        // metrics_batch/_pfr) and reports each inert species' own CSV
        // column vs. its own inlet value held constant — the same
        // "[inert — mass balance]" convention the built-in AA system's N2
        // already uses — folded into the normal per_species_r2/predicted/
        // observed maps so the Fit Quality/Graph tabs pick it up with no
        // further frontend changes.
        const inertSpeciesList = (inertComponents || '')
          .split(/[,+]/).map(s => s.trim()).filter(Boolean);
        if (inertSpeciesList.length > 0) {
          formData.append('inert_components', JSON.stringify(inertSpeciesList));
        }
        formData.append('model_type', modelType); // 'powerlaw' | 'lhhw'
        formData.append('reactor_mode', reactorMode); // 'batch' | 'pfr'
        formData.append('exponent_mode', exponentMode);
        // 16-Sep-2026 ADD (action items #6/#7) — this branch (custom/generic
        // reaction systems, /api/run/generic-fitting) was the one fitting
        // endpoint that never sent keq_mode/keq_user_values at all, even
        // though the Keq Treatment control above is shown and editable for
        // custom systems too (see the "User-Specified" screenshot flagged
        // 10-Sep) — so picking "User-Specified" and typing Keq X/Y for a
        // custom system had no effect; the backend now also reads this (see
        // generic_kinetics_objective.py's KEQ_USER_SPECIFIED /
        // openmkm_api.py run_generic_fitting). Same fields, same shape as
        // the other three fitting branches above.
        formData.append('keq_mode', keqMode);
        if (keqMode === 'user_specified') {
          formData.append('keq_user_values', JSON.stringify(keqUserValues));
        }
        formData.append('reaction_temp_c', reactionTempC);
        formData.append('species_weights', JSON.stringify(speciesWeights));
        formData.append('r2_target', r2Target);
        formData.append('n_iter', maxIterations);
        formData.append('warm_start', warmStart ? 'true' : 'false');
        if (modelType === 'lhhw') {
          // Only send Ki entries the user actually typed a number for —
          // blanks fall back to Kᵢ=1 server-side (see
          // generic_kinetics_objective.py _lhhw_denominator), so there's
          // no need to send a placeholder value for an untouched species.
          const kiPayload = Object.fromEntries(
            Object.entries(customLhhwKiValues)
              .filter(([, v]) => v !== '' && v != null && !Number.isNaN(parseFloat(v)))
              .map(([k, v]) => [k, parseFloat(v)])
          );
          formData.append('lhhw_ki_values', JSON.stringify(kiPayload));
        }
        // GAP FIX (30-Jul-2026 walkthrough): previously gated on
        // isCustomPfr, so a Batch custom-system fit never sent rate_basis/
        // n_mix_mol/W_cat_g/V_rx_L at all — fit_generic_powerlaw now needs
        // these for batch too (same scale-factor fix as above), and the
        // panel already collects them for both reactor modes, so send them
        // unconditionally for a single-dataset custom-system run.
        // Same names/units the backend already reads for MeOAc/DME/AA
        // (rate_basis, n_mix_mol, W_cat_g/V_rx_L), so fit_generic_powerlaw
        // / fit_generic_powerlaw_pfr use exactly what was entered on
        // screen, nothing defaulted silently.
        formData.append('rate_basis', rateBasis);
        formData.append('W_cat_g', convertWeight(wCatG, catWtUnit, 'g'));
        // 18-Aug-2026 FIX (client requirement — Y-axis labels should
        // dynamically reflect mole vs mass fraction, not be hard-coded):
        // this branch never sent concentration_basis at all, even though
        // the Concentration Basis toggle is shown for every reaction
        // system including custom. The AA branch below already sends it;
        // this brings the generic/custom branch in line so
        // _build_generic_parity_plot / _build_generic_pfr_parity_plot can
        // stop hard-coding "Mole fraction" (see openmkm_api.py).
        formData.append('concentration_basis', concentrationBasis);
        // GAP FIX ("Initial charge / initial moles field should not appear
        // at all for PFR", 30-Jul-2026 walkthrough): n_mix_mol only sent
        // for batch now — PFR derives it per row server-side via the ideal
        // gas law instead (see fit_generic_powerlaw_pfr / pfr_scale_factor_row
        // in generic_kinetics_objective.py). PFR sends a fallback pressure
        // instead, for any CSV row without its own 'Pressure' column — same
        // "own column, else this fallback" pattern reaction_temp_c already
        // uses for a missing 'T' column. Reactor Volume is sent for every
        // PFR run regardless of Rate Basis (the ideal-gas n_mix calc always
        // needs it), whereas batch only needs it for a 'volume' rate basis.
        if (reactorMode === 'batch') {
          formData.append('n_mix_mol', nMixMol);
          if (rateBasis === 'volume') formData.append('V_rx_L', vRxL);
        } else {
          formData.append('V_rx_L', vRxL);
          formData.append('pressure', reactionPressure);
          formData.append('pressure_unit', pressureUnit);
        }
        // ── Row exclusion (09-Aug-2026 — generalised from AA-only) ──────────
        // Custom/generic systems never sent this at all — checked rows in
        // ResidualTable had no effect on a custom-system re-run for either
        // reactor mode. Backend: run_generic_fitting reads this the same way
        // as every other endpoint (apply_row_exclusions), covering both
        // fit_generic_powerlaw (batch) and fit_generic_powerlaw_pfr (pfr).
        if (excludedRows.size > 0)
          formData.append('excluded_rows', JSON.stringify([...excludedRows]));
        formData.append('run_id', newRunId);  // 10-Aug-2026 ADD — real progress (see openmkm_api.py run_generic_fitting)

        const res = await axios.post(`${API_BASE}/run/generic-fitting`, formData, {
          headers: { Authorization: token },
          signal: controller.signal,
        });
        const d = res.data;
        setResult(d);
        setExcludedRows(new Set());   // reset: new result = fresh residual table, no carried-over exclusions
        setActivePlot('Overview');
        setActivePanel(1);
        setPopup({
          show: true,
          // ACTION ITEM #3/#4 (10-Sep-2026 call): this popup used to show
          // "Fitting Complete. Overall R² = 0.5." — the only one of the
          // four reaction systems that surfaced any R² in its completion
          // popup at all (AA/MeOAc/DME just say "Fitting completed
          // successfully!"). Dropped the Overall R² here so all four
          // systems show the same plain completion message; the
          // per-species R² breakdown is still available in the Fit
          // Quality / Fit Diagnostics panels, unchanged.
          message: 'Fitting completed successfully!',
          type: 'success',
        });

      } else {
        if (!file) { setPopup({ show: true, message: 'Please upload a CSV file first.', type: 'error', errorCode: 'input_incomplete' }); setLoading(false); return; }

        const formData = new FormData();
        formData.append('file', normalizedCsvBlob || file);
        formData.append('reaction_system', reactionSystem);
        // ── [FIX] Derive mode fresh at submit time ──────────────────────────────
        const _aaModeSngl = deriveAAModeNow();
        formData.append('mode', _aaModeSngl);
        formData.append('fit_mode', _aaModeSngl);
        // ── [FIX] Send explicit reaction_type + exponents for backend compose ───
        formData.append('reaction_type', reversibility);                               // 'reversible' | 'irreversible'
        formData.append('exponents',     exponentMode === 'fixed_1' ? 'fixed' : 'free'); // 'fixed' | 'free'
        // ── Model Input parameters (PDF-aligned) ──────────────────────────────
        formData.append('reversibility', reversibility);
        formData.append('model_type', modelType);
        formData.append('exponent_mode', exponentMode);
        formData.append('keq_mode', keqMode);
        if (keqMode === 'user_specified') {
          formData.append('keq_user_values', JSON.stringify(keqUserValues));
        }
        formData.append('n_mix_mol', nMixMol);
        const wCatGramsForApi = convertWeight(wCatG, catWtUnit, 'g');
        formData.append('W_cat_g', wCatGramsForApi);
        formData.append('w_cat_g', wCatGramsForApi);
        formData.append('rate_basis', rateBasis);
        if (rateBasis === 'volume') formData.append('V_rx_L', vRxL);
        // BUG FIX ("User specified" k-value mode unlocks fields
        // inconsistently, 30-Jul-2026 walkthrough): this single-run
        // MeOAc/DME/AA submission never sent lhhw_ki_values at all, even
        // though the backend (kinetics_objective_methyl_acetate / _dme,
        // openmkm_api.py) has always looked for it to override the Song
        // 1998 defaults. Only the custom-system branch below sent this
        // field, so a built-in-system user's "user-specified" adsorption
        // constants were entered on screen but never reached the fit.
        // Same "only send what was actually typed" convention as the
        // custom-system branch, so blanks correctly fall back server-side.
        if (modelType === 'lhhw') {
          const kiPayload = Object.fromEntries(
            Object.entries(customLhhwKiValues)
              .filter(([, v]) => v !== '' && v != null && !Number.isNaN(parseFloat(v)))
              .map(([k, v]) => [k, parseFloat(v)])
          );
          formData.append('lhhw_ki_values', JSON.stringify(kiPayload));
        }
        formData.append('optimize_exponents', optimizeExponents);
        formData.append('reaction_phase', reactionPhase);
        formData.append('pressure', reactionPressure);
        formData.append('pressure_unit', pressureUnit);
        formData.append('concentration_basis', concentrationBasis);
        Object.entries(speciesWeights).forEach(([sp, w]) => {
          formData.append(`wt_${sp.toLowerCase()}`, w);
        });
        formData.append('species_weights', JSON.stringify(speciesWeights));
        // ── Lever 1: R² target ──────────────────────────────────────────────
        formData.append('r2_target', r2Target);
        // ── Lever 2: Max iterations ─────────────────────────────────────────
        formData.append('n_iter', maxIterations);
        // ── Lever 4: warm start + initial guesses (physical → backend converts to log10) ─
        formData.append('warm_start', warmStart ? 'true' : 'false');
        if (useInitialGuess && initialGuesses.length > 0) {
          formData.append('initial_guesses', JSON.stringify(initialGuesses));
        }
        formData.append('reaction_orders', JSON.stringify(reactionOrders));
        // ── Row exclusion (Jun-25 2026) ─────────────────────────────────────
        if (excludedRows.size > 0)
          formData.append('excluded_rows', JSON.stringify([...excludedRows]));

        // ── AA "Single data set": if the CSV spans multiple temperatures,
        // transparently request the shared-Ea/Af (formerly "Global") fit —
        // no separate option for the user to pick any more (item A). ──────
        let isSharedEaAf = false;
        if (reactionSystem === 'acetic_acid') {
          isSharedEaAf = await detectAAMultiTemp(normalizedCsvBlob || file);
          if (isSharedEaAf) formData.append('fit_scope', 'global');
        }

        const singleEndpoint = reactionSystem === 'acetic_acid'
          ? `${API_BASE}/run/aa-fitting`
          : `${API_BASE}/run/data-fitting`;
        formData.append('run_id', newRunId);  // 10-Aug-2026 ADD — real progress for aa-fitting; harmless no-op for data-fitting until that route is also wired
        const res = await axios.post(singleEndpoint, formData, {
          headers: { Authorization: token },
          signal: controller.signal,
        });
        const d = res.data;
        setResult(d);
        setExcludedRows(new Set());   // reset: new result = fresh residual table, no carried-over exclusions
        setActivePlot('Overview');
        setActivePanel(1);
        setPopup({
          show: true,
          message: isSharedEaAf
            ? 'Fitting complete! Shared Ea/Af fitted across all temperatures in the data set.'
            : 'Fitting completed successfully!',
          type: 'success',
        });
      }
    } catch (err) {
      // User pressed Stop — this is an expected, non-error outcome, so show
      // a neutral notice instead of the red error styling.
      if (axios.isCancel(err) || err.code === 'ERR_CANCELED' || err.name === 'CanceledError') {
        setPopup({ show: true, message: 'Fitting stopped.', type: 'warning' });
        // Record the stopped run so the dashboard's Stopped count includes it.
        // Best effort: a failed save must never get in the way of stopping.
        (async () => {
          try {
            const payload = await buildFitPayload({ result: null, status: 'stopped' });
            payload.label = `Stopped run - ${new Date().toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`;
            await FitRecords.saveFitRecord(payload);
          } catch (e) { console.warn('[stop] could not save stopped run:', e); }
        })();
      } else if (err.response?.data?.timed_out) {
        // The backend owns the hard runtime limit. This is a controlled stop,
        // not a browser/network failure, so tell the user exactly why the
        // run stopped and make the failure guidance actionable.
        const timeoutMsg = err.response?.data?.error
          || 'Fitting stopped automatically after the maximum runtime.';
        setPopup({
          show: true,
          message: timeoutMsg,
          type: 'error',
          errorCode: 'fit_timeout',
        });
      } else if (err.response?.status === 401) {
        // BUG FIX (Shankar, 25-Aug): an expired/missing token during Run
        // used to just surface as a generic "Fitting failed" popup, and
        // the Run button stayed clickable — a logged-out user could sit
        // here retrying indefinitely with no path back to a working
        // session. A 401 here means the session is over; route through
        // the same logout path the idle-session-timeout uses.
        handleSessionLogout();
      } else {
        const msg = err.response?.data?.error || err.message || 'Fitting failed';
        setPopup({ show: true, message: msg, type: 'error', errorCode: 'run_error' });
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
      FittingSession.setFittingSessionState({ runMeta: null });
    }
  };

  const SYSTEM_SPECIES_MAP = {
    meoac:       ['MeOAc', 'HOAc', 'MeOH', 'H2O'],
    dme:         ['DME', 'MeOH', 'H2O'],
    // AA species list only populated for privileged users — prevents
    // species-weight panel and export functions from leaking AA data.
    acetic_acid: canAccessAA ? ['C2H6', 'O2', 'AA', 'ET', 'CO2', 'H2O', 'N2'] : [],
  };
  // [FIX] Same derivation as PanelInput — Manual Entry, CSV template, and
  // species-weights here must all agree on what's actually been typed in.
  const activeSpecies = reactionSystem === 'acetic_acid'
    ? SYSTEM_SPECIES_MAP.acetic_acid
    : deriveSpeciesFromReactions(
        currentReactionRows,
        inertComponents,
        SYSTEM_SPECIES_MAP[reactionSystem] || SYSTEM_SPECIES_MAP.meoac,
        registeredComponents,
        speciesIdentityMap,
      );

  // BUG FIX (Compare Fits, 19-Aug merge, extended 20-Aug, debounced 24-Aug):
  // "Save this fit" did nothing because fitRecordStore's reactionSystemId
  // was never resolved. The first fix called ensureReactionSystem() exactly
  // once on mount with no signature — which fixed the "never resolved"
  // problem but introduced a second one: that single resolved id was then
  // reused for every fit saved for the rest of the session, EVEN AFTER the
  // user switched to a genuinely different reaction system (different
  // species/reactions, Batch↔PFR, Liquid↔Vapor). Every save kept
  // referencing the OLD reaction_systems row/project_id mapping.
  //
  // Second bug (Shankar, 24-Aug — reaction_systems rows piling up in the DB
  // for one user who never intentionally created more than one system):
  // firing this effect immediately on every dependency change meant it also
  // fired during mount hydration — rxn1Reactants/rxn1Products/etc. start as
  // '' (or whatever their hook default is) and only pick up their real
  // persisted value once formSessionStore.js's usePersistedState effect
  // runs a tick later. That's TWO different signatures within the same
  // page load, and every distinct signature this endpoint sees becomes (or
  // matches) a reaction_systems row — so a single visit could create a
  // spurious row for the transient empty-string state before ever creating
  // the real one.
  //
  // Fix: debounce. Only the signature that's still current 600ms after the
  // last change actually gets sent — mount hydration settles well within
  // that window, so only the SETTLED signature ever reaches the backend.
  useEffect(() => {
    // sessionReady: wait until the saved session (if any) has been restored.
    // Otherwise the page's blank defaults would resolve — or create — a
    // reaction_systems row before the user's real one is known.
    if (!token || !sessionReady) return;
    const timer = setTimeout(() => {
      (async () => {
        const projectId = await FitRecords.resolveDefaultProjectId();
        if (!projectId) return;
        const signature = {
          reactor_mode: reactorMode === 'pfr' ? 'PFR' : 'Batch',
          reaction_phase: reactionPhase === 'vapor' ? 'Vapor' : 'Liquid',
          species: activeSpecies,
          reactions: reactionSystem === 'acetic_acid'
            ? [{ system: 'acetic_acid' }]
            : currentReactionRows.filter(r => (r.reactants || '').trim() || (r.products || '').trim())
                .map(r => ({ reactants: r.reactants, products: r.products })),
        };
        const systemId = await FitRecords.ensureReactionSystem(projectId, signature);
        if (systemId) FitRecords.refreshFitRecords();
      })();
    }, 600);
    return () => clearTimeout(timer);
    // activeSpecies is a plain array recomputed every render — depending on
    // its joined contents (not identity) keeps this from refiring every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, sessionReady, reactorMode, reactionPhase, reactionSystem, activeSpecies.join('|'),
      rxn1Reactants, rxn1Products, rxn2Reactants, rxn2Products, rxn3Reactants, rxn3Products,
      rxn4Reactants, rxn4Products, rxn5Reactants, rxn5Products, rxn6Reactants, rxn6Products,
      rxn7Reactants, rxn7Products, rxn8Reactants, rxn8Products, rxn9Reactants, rxn9Products,
      rxn10Reactants, rxn10Products]);

  // See the "BUG FIX (eslint react-hooks/rules-of-hooks...)" comment near
  // the top of this component, next to `token` — this is the render-time
  // half of that fix. Every hook above has now run unconditionally, so
  // returning null here (instead of earlier) doesn't change hook count
  // between renders.
  if (!token) return null;

  return (
    <div style={{
      position: 'fixed',
      inset:    0,
      display:  'flex',
      height:   '100vh',
      width:    '100vw',
      overflow: 'hidden',
    }}>

      <Sidebar />

      {addCompOpts && (
        <AddComponentModal
          opts={addCompOpts}
          registeredComponents={registeredComponents}
          setRegisteredComponents={setRegisteredComponents}
          onClose={() => setAddCompOpts(null)}
        />
      )}

      {/* Right column: fixed topbar + scrollable content */}
      <div style={{
        marginLeft:    'var(--sidebar-w)',
        width:         'calc(100% - var(--sidebar-w))',
        display:       'flex',
        flexDirection: 'column',
        height:        '100vh',
        overflow:      'hidden',
      }}>

        {/* App topbar: logo + profile dropdown — Navbar is position:fixed
            internally, so it takes no space in this flex column. We must
            explicitly offset content below it or it gets visually covered. */}
        <Navbar activePage="app" />

        {/* Theme toggle — floating overlay since Navbar is a separate,
            unmodified component. TODO(dev): once Navbar.jsx is touched for
            other work, consider moving this into the Navbar's own topbar
            row instead of floating it here. */}
        <div style={{ position: 'fixed', top: 14, right: 24, zIndex: 500 }}>
          <ThemeToggle theme={theme} setTheme={setTheme} />
        </div>

        <SessionTimeoutModal show={showSessionPrompt} onStay={stayLoggedIn} onLogout={handleSessionLogout} />
        <LoggedOutModal show={sessionLoggedOut} onLogBackIn={dismissLoggedOut} />

        {/* Page content — panel header stays pinned;
            only the inner PanelShell body scrolls (single scroll container).
            marginTop accounts for the fixed Navbar's height (68px). */}
        <div style={{
          flex:      1,
          marginTop: '68px',
          overflow:  'hidden',
          minHeight: 0,
        }}>

      <div style={{ display: 'flex', flexDirection: 'column', height: '100%',
        background: C.bg, overflow: 'hidden', fontFamily: font.ui }}>

        <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>

          <div style={{ flex: 1, overflow: 'hidden', minWidth: 0, background: C.bg, height: '100%' }}>
            {activePanel === 0 && (
              <PanelInput
                reactionSystem={reactionSystem} setReactionSystem={setReactionSystem}
                fitMode={fitMode} setFitMode={setFitMode}
                reactorMode={reactorMode} setReactorMode={setReactorMode}
                concentrationBasis={concentrationBasis} setConcentrationBasis={setConcentrationBasis}
                reactionPhase={reactionPhase} setReactionPhase={setReactionPhase}
                reactionPressure={reactionPressure} setReactionPressure={setReactionPressure}
                pressureUnit={pressureUnit} setPressureUnit={setPressureUnit}
                file={file} dataPreview={dataPreview} autoDetect={autoDetect}
                tempWarning={tempWarning} moleFractionWarnings={moleFractionWarnings}
                normalizedCsvBlob={normalizedCsvBlob} dataSourceType={dataSourceType}
                onFileChange={handleFileChange} onRemoveFile={handleRemoveFile} onManualEntry={() => {
                  // Sync the manual entry table to the currently-selected
                  // reaction system / reactor type before opening.
                  const sys = reactionSystem === 'acetic_acid'
                    ? 'acetic_acid'
                    : (manualSystem === 'acetic_acid' ? 'meoac' : manualSystem);
                  if (sys !== manualSystem) {
                    setManualSystem(sys);
                    // BUG FIX (24-Sep-2026 — Madhura: runtime error during
                    // manual entry that left the app unresponsive): this used
                    // to index the STATIC preset map directly —
                    // MANUAL_ENTRY_CONFIG[sys].type — which only has entries
                    // for the 3 hardcoded systems. For any other `sys` (e.g.
                    // 'custom', or a stale manualSystem value left over from
                    // an earlier session) MANUAL_ENTRY_CONFIG[sys] is
                    // undefined and `.type` threw a TypeError synchronously
                    // inside this onClick handler — never caught (this isn't
                    // inside handleSubmit's try/catch), so setShowManualEntry
                    // below never ran and the modal silently never opened.
                    // getManualEntryConfig() is the exact dynamic-aware
                    // resolver built for this (see its own comment a few
                    // lines up: "use getManualEntryConfig, not a raw
                    // MANUAL_ENTRY_CONFIG[key] lookup") — already used
                    // correctly at every OTHER call site (handleManualEntrySubmit,
                    // ManualEntryModal) except these two.
                    const cfg = getManualEntryConfig(sys, activeSpecies, reactorMode);
                    setManualRows(Array.from(
                      { length: cfg.type === 'pfr' ? 3 : 5 },
                      () => emptyManualRowForConfig(cfg)
                    ));
                  }
                  setShowManualEntry(true);
                }}
                onManualEntryForRun={(runId) => {
                  // Same sync as onManualEntry, but scoped to fill ONE run in
                  // the "Multiple data sets" list instead of the top-level
                  // single-CSV state.
                  const sys = reactionSystem === 'acetic_acid'
                    ? 'acetic_acid'
                    : (manualSystem === 'acetic_acid' ? 'meoac' : manualSystem);
                  // BUG FIX (client doc, Sept-2026 — "Manual Data Entry currently
                  // only allows one data set to be entered for a custom system.
                  // Even if you 'Add a Run', it pastes the same data."): manualRows
                  // is one table shared by every run's "Enter Manually" button.
                  // Previously it was only reset when the *system* changed, so a
                  // new run showed the previous run's rows. Now each run opens
                  // with its own saved grid (or a blank one) — see below.
                  // BUG FIX (24-Sep-2026): getManualEntryConfig(...) must be
                  // called with (sys, activeSpecies, reactorMode) — same crash
                  // as onManualEntry reported by Madhura in manual submission.
                  const targetRun = globalRuns.find(r => r.id === runId);
                  if (!targetRun) return;

                  const cfg = getManualEntryConfig(sys, activeSpecies, reactorMode);
                  if (sys !== manualSystem) setManualSystem(sys);

                  // IMPORTANT: manualRows is shared by the modal, but the
                  // committed data is run-specific. When the user opens a
                  // dataset again, restore THAT dataset's grid instead of
                  // resetting to blanks. This fixes the reported behaviour
                  // where Run 1 data appeared to vanish before entering Run 2.
                  const savedRows = Array.isArray(targetRun.manualRowsSnapshot)
                    ? targetRun.manualRowsSnapshot
                        .filter(row => row && typeof row === 'object')
                        .map(row => ({ ...emptyManualRowForConfig(cfg), ...row }))
                    : [];
                  setManualRows(
                    savedRows.length > 0
                      ? savedRows
                      : Array.from(
                          { length: cfg.type === 'pfr' ? 3 : 5 },
                          () => emptyManualRowForConfig(cfg)
                        )
                  );
                  setManualEntryTargetRunId(runId);
                  setShowManualEntry(true);
                }}
                nMixMol={nMixMol} setNMixMol={setNMixMol}
                wCatG={wCatG} setWCatG={setWCatG}
                vRxL={vRxL} setVRxL={setVRxL}
                rateBasis={rateBasis} setRateBasis={setRateBasis}
                fitScope={fitScope} setFitScope={setFitScope}
                globalRuns={globalRuns} setGlobalRuns={setGlobalRuns}
                optimizeExponents={optimizeExponents} setOptimizeExponents={setOptimizeExponents}
                exponentMode={exponentMode} setExponentMode={setExponentMode}
                reversibility={reversibility} setReversibility={setReversibility}
                modelType={modelType} setModelType={setModelType}
                keqMode={keqMode} setKeqMode={setKeqMode}
                keqUserValues={keqUserValues} setKeqUserValues={setKeqUserValues}
                customLhhwKiValues={customLhhwKiValues} setCustomLhhwKiValues={setCustomLhhwKiValues}
                reactionTempC={reactionTempC} setReactionTempC={setReactionTempC}
                catWtUnit={catWtUnit} setCatWtUnit={setCatWtUnit}
                speciesWeights={speciesWeights} setSpeciesWeights={setSpeciesWeights}
                selectedPreset={selectedPreset} setSelectedPreset={setSelectedPreset}
                reactionOrders={reactionOrders} setReactionOrders={setReactionOrders}
                useInitialGuess={useInitialGuess} setUseInitialGuess={setUseInitialGuess}
                initialGuesses={initialGuesses} setInitialGuesses={setInitialGuesses}
                onSubmit={handleSubmit} loading={loading} fittingProgress={fittingProgress}
                onStop={handleStopFitting} progressFraction={progressFraction}
                onReinitialize={handleReinitialize}
                onResetAll={handleResetAll}
                estimatedRuntimeSec={estimatedRuntimeSec}
                runMeta={fittingSession.runMeta}
                excludedRows={excludedRows}
                selectedDataSets={selectedDataSets}
                onShowExamples={() => setShowExamples(true)}
                exampleLoadTick={exampleLoadTick}
                rxn1Reactants={rxn1Reactants} setRxn1Reactants={setRxn1Reactants}
                rxn1Products={rxn1Products}   setRxn1Products={setRxn1Products}
                rxn2Reactants={rxn2Reactants} setRxn2Reactants={setRxn2Reactants}
                rxn2Products={rxn2Products}   setRxn2Products={setRxn2Products}
                rxn3Reactants={rxn3Reactants} setRxn3Reactants={setRxn3Reactants}
                rxn3Products={rxn3Products}   setRxn3Products={setRxn3Products}
                rxn4Reactants={rxn4Reactants} setRxn4Reactants={setRxn4Reactants}
                rxn4Products={rxn4Products} setRxn4Products={setRxn4Products}
                rxn5Reactants={rxn5Reactants} setRxn5Reactants={setRxn5Reactants}
                rxn5Products={rxn5Products} setRxn5Products={setRxn5Products}
                rxn6Reactants={rxn6Reactants} setRxn6Reactants={setRxn6Reactants}
                rxn6Products={rxn6Products} setRxn6Products={setRxn6Products}
                rxn7Reactants={rxn7Reactants} setRxn7Reactants={setRxn7Reactants}
                rxn7Products={rxn7Products} setRxn7Products={setRxn7Products}
                rxn8Reactants={rxn8Reactants} setRxn8Reactants={setRxn8Reactants}
                rxn8Products={rxn8Products} setRxn8Products={setRxn8Products}
                rxn9Reactants={rxn9Reactants} setRxn9Reactants={setRxn9Reactants}
                rxn9Products={rxn9Products} setRxn9Products={setRxn9Products}
                rxn10Reactants={rxn10Reactants} setRxn10Reactants={setRxn10Reactants}
                rxn10Products={rxn10Products} setRxn10Products={setRxn10Products}
                inertComponents={inertComponents} setInertComponents={setInertComponents}
                registeredComponents={registeredComponents} setRegisteredComponents={setRegisteredComponents}
                speciesIdentityMap={speciesIdentityMap} setSpeciesIdentityMap={setSpeciesIdentityMap}
              />
            )}
            {activePanel === 1 && (
              <PanelGraph result={result} activePlot={activePlot}
                setActivePlot={setActivePlot} loading={loading}
                registeredComponents={registeredComponents} />
            )}
            {activePanel === 2 && (
              <PanelOutputs result={result} fitMode={fitMode} reactionSystem={reactionSystem}
                excludedRows={excludedRows} onToggleRow={toggleExcludedRow}
                onResetExcluded={resetExcludedRows}
                rateBasis={rateBasis} reversibility={reversibility} keqMode={keqMode}
                keqUserValues={keqUserValues}
                rxn1Reactants={rxn1Reactants} rxn1Products={rxn1Products} loading={loading}
                registeredComponents={registeredComponents} />
            )}
            {activePanel === 3 && (
              <PanelOptimize result={result} reactionSystem={reactionSystem}
                registeredComponents={registeredComponents}
                fitMode={fitMode} setFitMode={setFitMode}
                exponentMode={exponentMode} setExponentMode={setExponentMode}
                customParamCounts={customParamCounts}
                optimizeExponents={optimizeExponents} setOptimizeExponents={setOptimizeExponents}
                speciesWeights={speciesWeights} setSpeciesWeights={setSpeciesWeights}
                activeSpecies={activeSpecies}
                reactionOrders={reactionOrders} setReactionOrders={setReactionOrders}
                useInitialGuess={useInitialGuess} setUseInitialGuess={setUseInitialGuess}
                initialGuesses={initialGuesses} setInitialGuesses={setInitialGuesses}
                maxIterations={maxIterations} setMaxIterations={setMaxIterations}
                r2Target={r2Target} setR2Target={setR2Target}
                warmStart={warmStart} setWarmStart={setWarmStart}
                selectedDataSets={selectedDataSets} setSelectedDataSets={setSelectedDataSets}
                globalRuns={globalRuns} fitScope={fitScope}
                onSubmit={handleSubmit} loading={loading} fittingProgress={fittingProgress}
                onStop={handleStopFitting} onReinitialize={handleReinitialize}
                buildSaveFitPayload={() => buildFitPayload()} />
            )}
            {activePanel === 4 && <PanelAppendix result={result} reactionSystem={reactionSystem}
              registeredComponents={registeredComponents} setRegisteredComponents={setRegisteredComponents}
              aaGlobalRuns={aaGlobalRuns}
              setAaGlobalRuns={setAaGlobalRuns}
              rxn1Reactants={rxn1Reactants} rxn1Products={rxn1Products}
              rxn2Reactants={rxn2Reactants} rxn2Products={rxn2Products}
              rxn3Reactants={rxn3Reactants} rxn3Products={rxn3Products}
              reactionRows={currentReactionRows}
              speciesIdentityMap={speciesIdentityMap}
              reactorMode={reactorMode}
              fitScope={fitScope}
              file={file}
              dataPreview={dataPreview}
              dataSourceType={dataSourceType}
              globalRuns={globalRuns}
              reactionTempC={reactionTempC}
              reactionPressure={reactionPressure}
              pressureUnit={pressureUnit}
              nMixMol={nMixMol}
              wCatG={wCatG}
              catWtUnit={catWtUnit}
              vRxL={vRxL}
              rateBasis={rateBasis} />}
            {activePanel === 5 && <PanelCompare registeredComponents={registeredComponents} />}
          </div>
        </div>
      </div>   {/* end page content inner */}
        </div>   {/* end pageContent scroll container */}
      </div>     {/* end rightCol */}

      <ManualEntryModal show={showManualEntry}
        onClose={() => { setShowManualEntry(false); setManualEntryTargetRunId(null); }}
        onSubmit={handleManualEntrySubmit} reactionSystem={reactionSystem} activeSpecies={activeSpecies}
        reactorMode={reactorMode}
        manualSystem={manualSystem} setManualSystem={setManualSystem}
        manualRows={manualRows} setManualRows={setManualRows}
        reactionTempC={reactionTempC} setReactionTempC={setReactionTempC}
        reactionPressure={reactionPressure} setReactionPressure={setReactionPressure}
        pressureUnit={pressureUnit} setPressureUnit={setPressureUnit}
        concentrationBasis={concentrationBasis}
        forRunLabel={null}
        registeredComponents={registeredComponents} />

      {/* GAP 2 FIX: Examples modal */}
      <ExamplesModal
        show={showExamples}
        onClose={() => setShowExamples(false)}
        onSelect={handleExampleSelect}
      />

      <Popup popup={popup} onClose={() => setPopup(p => ({ ...p, show: false }))}
        // [FIX] "Review" opened the Graph panel's content but the Sidebar
        // still highlighted "Input" — Sidebar (rendered separately, above)
        // derives its own active-tab highlight from the URL hash, not from
        // this component's local activePanel state. setActivePanel(1) alone
        // updated the content but never touched the URL, so the two stayed
        // out of sync. Navigating to the hash updates the URL (which
        // Sidebar reads) and, via the location.hash effect above, keeps
        // activePanel in sync too — same as before Requirement work removed
        // the navigate() call here.
        onReview={() => { setPopup(p => ({ ...p, show: false })); navigate('/data-fitting#graph'); }} />
    </div>       
  );
  
};

export default DataFittingPage;